<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import AiCloserAPI from 'dashboard/api/aiCloser';
import getWootAxios from 'dashboard/helper/getWootAxios';
import {
  ATTRIBUTE_OVERRIDE_DRILLDOWN_QUERY_KEYS,
  buildAttributeOverrideDrilldownQuery,
  clearAttributeOverrideDrilldownQuery,
  parseAttributeOverrideDrilldownQuery,
} from '../../lib/attributeOverrideDrilldown';
import {
  loadRepairBundleRuns,
  summarizeRepairBundleConfidence,
} from '../../lib/repairBundleConfidence';
import {
  advanceRollbackSessionContext,
  buildOperatorNoteTimelineBadges,
  buildOperatorNoteTimelineVersionDialogContext,
  buildRollbackEvidenceCompareBundle,
  buildRollbackBreadcrumbBadges,
  buildOperatorNoteTimelineContextLabel,
  ensureRollbackSessionContext,
  findBatchBaseline,
  findReleaseReviewSnapshot,
  mergeOperatorNoteTimelineEntries,
  normalizeRollbackSessionContext,
  normalizeBatchBaselines,
  rollbackSessionContextFromEntry,
} from '../../lib/releaseLoopSnapshots';

const props = defineProps({
  assistant: {
    type: Object,
    default: null,
  },
});

const route = useRoute();
const router = useRouter();
const { accountId } = useAccount();
const store = useStore();
const alert = useAlert();
const releaseReadinessSummary = ref({});
const isPersistingRollbackFollowup = ref(false);

const assistantId = computed(() => props.assistant?.id);

const openTab = tab => {
  const nextQuery = { ...(route.query || {}), tab };
  [
    ...ATTRIBUTE_OVERRIDE_DRILLDOWN_QUERY_KEYS,
    ...ANALYZE_SEED_QUERY_KEYS,
    ...ANALYZE_FOLLOWUP_QUERY_KEYS,
    ...ANALYZE_OWNER_FOCUS_QUERY_KEYS,
    ...CONTENT_SEED_QUERY_KEYS,
    ...GUIDANCE_SEED_QUERY_KEYS,
    ...DEPLOY_SEED_QUERY_KEYS,
    ...DEPLOY_COMPARE_QUERY_KEYS,
    ...ESCALATION_SEED_QUERY_KEYS,
  ].forEach(key => {
    delete nextQuery[key];
  });
  delete nextQuery.test_query;
  delete nextQuery.test_inbox_id;
  delete nextQuery.test_reason;
  delete nextQuery.test_source;
  router.replace({ query: nextQuery });
};
const openTestWorkspace = () => openTab('test_preview');
const openContentWorkspace = () => openTab('train_content');
const openGuidanceWorkspace = () => openTab('train_guidance');
const openDeployWorkspace = () => openTab('deploy_channels');
const openEscalationWorkspace = () => openTab('train_escalation');
const CONTENT_SEED_QUERY_KEYS = [
  'content_seed',
  'content_seed_source',
  'content_seed_focus',
  'content_seed_query',
  'content_seed_reason',
  'content_seed_scope_warning',
];
const GUIDANCE_SEED_QUERY_KEYS = [
  'guidance_seed',
  'guidance_seed_source',
  'guidance_seed_editor',
  'guidance_seed_reason',
  'guidance_seed_query',
];
const DEPLOY_SEED_QUERY_KEYS = [
  'deploy_seed',
  'deploy_seed_source',
  'deploy_seed_focus',
  'deploy_seed_reason',
  'deploy_seed_inbox_id',
  'deploy_seed_confidence_level',
  'deploy_seed_confidence_runs',
  'deploy_seed_confidence_title',
  'deploy_seed_batch_baseline_key',
  'deploy_seed_batch_set_key',
  'deploy_seed_batch_title',
  'deploy_seed_batch_overall_level',
  'deploy_seed_batch_total_cases',
  'deploy_seed_batch_ready_cases',
  'deploy_seed_batch_watch_cases',
  'deploy_seed_batch_blocked_cases',
  'deploy_seed_batch_ran_at',
  'deploy_seed_batch_top_blocker',
  'deploy_seed_batch_top_watch_signal',
  'deploy_seed_followup_intent',
  'deploy_seed_target_version_label',
  'deploy_seed_compare_version_label',
  'deploy_seed_prefill_note',
  'deploy_seed_open_versions',
  'deploy_seed_session_id',
  'deploy_seed_session_label',
  'deploy_seed_session_step',
  'deploy_seed_session_origin',
  'deploy_seed_session_item_key',
];
const DEPLOY_COMPARE_QUERY_KEYS = [
  'deploy_seed_bundle',
  'deploy_seed_title',
  'deploy_seed_focus_label',
  'deploy_seed_strategy_group_title',
  'deploy_seed_recommendation_code',
  'deploy_seed_owner_workspace',
  'deploy_seed_owner_action_title',
  'deploy_seed_guidance_editor',
  'deploy_seed_escalation_focus',
  'deploy_seed_hotspot_summary',
  'deploy_seed_resolved_summary',
  'deploy_seed_escalated_summary',
  'deploy_seed_validation_summary',
  'deploy_seed_stage',
  'deploy_seed_rollback_trigger',
];
const ESCALATION_SEED_QUERY_KEYS = [
  'escalation_seed',
  'escalation_seed_source',
  'escalation_seed_focus',
  'escalation_seed_reason',
  'escalation_seed_query',
  'escalation_seed_label',
];
const ANALYZE_SEED_QUERY_KEYS = [
  'analyze_seed',
  'analyze_source',
  'analyze_time_window',
  'analyze_inbox_id',
  'analyze_content_gap_reason',
  'analyze_article_scope_warning',
  'analyze_delivery_mode',
];
const ANALYZE_FOLLOWUP_QUERY_KEYS = [
  'analyze_followup_intent',
  'analyze_followup_target_version_label',
  'analyze_followup_compare_version_label',
  'analyze_followup_session_id',
  'analyze_followup_session_label',
  'analyze_followup_session_step',
  'analyze_followup_session_origin',
  'analyze_followup_session_item_key',
];
const ANALYZE_OWNER_FOCUS_QUERY_KEYS = ['analyze_owner_focus'];
const TEST_COMPARE_QUERY_KEYS = [
  'test_compare_bundle',
  'test_compare_title',
  'test_compare_focus_label',
  'test_compare_strategy_group_title',
  'test_compare_recommendation_code',
  'test_compare_owner_workspace',
  'test_compare_owner_action_title',
  'test_compare_guidance_editor',
  'test_compare_escalation_focus',
  'test_compare_hotspot_summary',
  'test_compare_resolved_summary',
  'test_compare_escalated_summary',
  'test_compare_validation_summary',
  'test_compare_stage',
];
const TEST_FOLLOWUP_QUERY_KEYS = [
  'test_followup_intent',
  'test_followup_target_version_label',
  'test_followup_compare_version_label',
  'test_followup_session_id',
  'test_followup_session_label',
  'test_followup_session_step',
  'test_followup_session_origin',
  'test_followup_session_item_key',
];
const readAnalyzeRouteRollbackSession = () =>
  normalizeRollbackSessionContext({
    sessionId: route.query?.analyze_followup_session_id,
    sessionLabel: route.query?.analyze_followup_session_label,
    sessionStep: route.query?.analyze_followup_session_step,
    sessionOrigin: route.query?.analyze_followup_session_origin,
  });
const currentAnalyzeRollbackSessionItemKey = () =>
  String(
    route.query?.analyze_followup_session_item_key || route.query?.rollback_session_item_key || ''
  ).trim();
const currentAnalyzeRollbackSession = () => {
  const routeSession = readAnalyzeRouteRollbackSession();
  if (routeSession.sessionId) return routeSession;
  const historySession = rollbackSessionContextFromEntry(
    rollbackEvidenceHistoryEntries.value?.[0] || null
  );
  return historySession.sessionId ? historySession : normalizeRollbackSessionContext();
};
const ensureAnalyzeRollbackSession = nextWorkspace =>
  ensureRollbackSessionContext({
    current: currentAnalyzeRollbackSession(),
    sourceWorkspace: 'Analyze',
    nextWorkspace,
  });
const advanceAnalyzeRollbackSession = nextWorkspace =>
  advanceRollbackSessionContext({
    current: currentAnalyzeRollbackSession(),
    sourceWorkspace: 'Analyze',
    nextWorkspace,
  });
const buildRollbackFollowupCompareBundle = outcome => ({
  title: String(outcome?.bundle_title || outcome?.bundle_label || '').trim(),
  focusLabel: String(outcome?.focus_label || '').trim(),
  strategyGroupTitle: '',
  recommendationCode: String(outcome?.recommendation_code || '').trim(),
  ownerWorkspace: 'analyze',
  ownerActionTitle: String(
    outcome?.operator_actions?.[0]?.intent_label ||
      outcome?.recommended_action_label ||
      'Rollback follow-up'
  ).trim(),
  guidanceEditor: '',
  escalationFocus: '',
  hotspotSummary: '',
  resolvedSummary: '',
  escalatedSummary: '',
  validationSummary:
    outcome?.target_version_label && outcome?.previous_version_label
      ? `${outcome.target_version_label} vs ${outcome.previous_version_label}`
      : '',
  stage: 'validation',
});
const buildRollbackFollowupNote = outcome =>
  [
    String(outcome?.summary || '').trim(),
    String(outcome?.operator_note || '').trim(),
    String(outcome?.recommended_action_label || '').trim(),
    outcome?.target_version_label
      ? `${outcome.target_version_label}${outcome?.previous_version_label ? ` vs ${outcome.previous_version_label}` : ''}`
      : '',
  ]
    .filter(Boolean)
    .join(' • ');
const buildRollbackFollowupHistoryEntry = outcome => ({
  ...ensureAnalyzeRollbackSession('Deploy'),
  kind: 'rollback_followup',
  hopKind: 'evidence_note',
  actorActionLabel: 'Persist rollback follow-up',
  workspaceLabel: 'Analyze',
  body: buildRollbackFollowupNote(outcome),
  source: 'Analyze',
  targetVersionLabel: String(outcome?.target_version_label || '').trim(),
  compareVersionLabel: String(
    outcome?.compare_version_label || outcome?.previous_version_label || ''
  ).trim(),
  recommendedActionLabel: String(outcome?.recommended_action_label || '').trim(),
  confidenceLabel: String(outcome?.confidence_label || '').trim(),
  nextWorkspace: 'Deploy',
});
const findRollbackEvidenceTargets = outcome => {
  const snapshotId = Number(outcome?.release_snapshot_id);
  const batchBaselineId = Number(outcome?.batch_baseline_id);
  const snapshot =
    currentReleaseSnapshots.value.find(
      row => Number(row?.id) > 0 && Number(row.id) === snapshotId
    ) ||
    findReleaseReviewSnapshot({
      snapshots: currentReleaseSnapshots.value,
      bundle: buildRollbackFollowupCompareBundle(outcome),
    }) ||
    null;
  const baseline =
    currentBatchBaselines.value.find(
      row => Number(row?.id) > 0 && Number(row.id) === batchBaselineId
    ) ||
    findBatchBaseline({
      baselines: currentBatchBaselines.value,
      baselineKey: String(outcome?.batch_baseline_key || '').trim(),
      setKey: String(outcome?.batch_set_key || '').trim(),
      bundle: buildRollbackFollowupCompareBundle(outcome),
    }) ||
    null;
  return { snapshot, baseline };
};
const persistRollbackFollowupToEvidence = async () => {
  if (isPersistingRollbackFollowup.value) return;
  const outcome = releaseReadinessSummary.value?.latest_rollback_outcome || null;
  if (!assistantId.value || !outcome) return;

  const note = buildRollbackFollowupNote(outcome);
  if (!note) {
    alert('No rollback follow-up note is available to persist yet');
    return;
  }

  const { snapshot, baseline } = findRollbackEvidenceTargets(outcome);
  const payload = {
    appendOperatorNoteEntry: buildRollbackFollowupHistoryEntry(outcome),
  };
  const writes = [];
  if (Number(snapshot?.id) > 0) {
    writes.push(AiCloserAPI.updateReleaseSnapshot(assistantId.value, snapshot.id, payload));
  }
  if (Number(baseline?.id) > 0) {
    writes.push(AiCloserAPI.updateBatchBaseline(assistantId.value, baseline.id, payload));
  }
  if (!writes.length) {
    alert('No linked release snapshot or batch baseline is available for this rollback outcome');
    return;
  }

  isPersistingRollbackFollowup.value = true;
  try {
    await Promise.all(writes);
    await refreshAll();
    alert(
      writes.length > 1
        ? 'Rollback follow-up appended to the linked snapshot and batch baseline'
        : snapshot?.id
          ? 'Rollback follow-up appended to the linked release snapshot'
          : 'Rollback follow-up appended to the linked batch baseline'
    );
  } catch (error) {
    alert(
      error?.response?.data?.errors?.join(', ') ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to save the rollback follow-up note'
    );
  } finally {
    isPersistingRollbackFollowup.value = false;
  }
};
const openTestWorkspaceWithSeed = ({
  queryText = '',
  inboxId = '',
  reason = '',
  source = '',
  compareBundle = null,
} = {}) => {
  const nextQuery = { ...(route.query || {}), tab: 'test_preview' };
  [
    ...ATTRIBUTE_OVERRIDE_DRILLDOWN_QUERY_KEYS,
    ...ANALYZE_SEED_QUERY_KEYS,
    ...ANALYZE_FOLLOWUP_QUERY_KEYS,
    ...ANALYZE_OWNER_FOCUS_QUERY_KEYS,
    ...CONTENT_SEED_QUERY_KEYS,
    ...GUIDANCE_SEED_QUERY_KEYS,
    ...DEPLOY_SEED_QUERY_KEYS,
    ...DEPLOY_COMPARE_QUERY_KEYS,
    ...ESCALATION_SEED_QUERY_KEYS,
  ].forEach(key => {
    delete nextQuery[key];
  });
  const normalizedQuery = String(queryText || '').trim();
  const normalizedInboxId = String(inboxId || '').trim();
  const normalizedReason = String(reason || '').trim();
  const normalizedSource = String(source || '').trim();

  if (normalizedQuery) nextQuery.test_query = normalizedQuery;
  else delete nextQuery.test_query;

  if (normalizedInboxId) nextQuery.test_inbox_id = normalizedInboxId;
  else delete nextQuery.test_inbox_id;

  if (normalizedReason) nextQuery.test_reason = normalizedReason;
  else delete nextQuery.test_reason;

  if (normalizedSource) nextQuery.test_source = normalizedSource;
  else delete nextQuery.test_source;

  TEST_COMPARE_QUERY_KEYS.forEach(key => {
    delete nextQuery[key];
  });
  const normalizedCompareBundle =
    compareBundle && typeof compareBundle === 'object' ? compareBundle : null;
  if (normalizedCompareBundle) {
    const compareTitle = String(normalizedCompareBundle.title || '').trim();
    const compareFocusLabel = String(normalizedCompareBundle.focusLabel || '').trim();
    const compareStrategyGroupTitle = String(
      normalizedCompareBundle.strategyGroupTitle || ''
    ).trim();
    const compareRecommendationCode = String(
      normalizedCompareBundle.recommendationCode || ''
    ).trim();
    const compareOwnerWorkspace = String(
      normalizedCompareBundle.ownerWorkspace || ''
    ).trim();
    const compareOwnerActionTitle = String(
      normalizedCompareBundle.ownerActionTitle || ''
    ).trim();
    const compareGuidanceEditor = String(
      normalizedCompareBundle.guidanceEditor || ''
    ).trim();
    const compareEscalationFocus = String(
      normalizedCompareBundle.escalationFocus || ''
    ).trim();
    const compareHotspotSummary = String(
      normalizedCompareBundle.hotspotSummary || ''
    ).trim();
    const compareResolvedSummary = String(
      normalizedCompareBundle.resolvedSummary || ''
    ).trim();
    const compareEscalatedSummary = String(
      normalizedCompareBundle.escalatedSummary || ''
    ).trim();
    const compareValidationSummary = String(
      normalizedCompareBundle.validationSummary || ''
    ).trim();
    const compareStage = String(normalizedCompareBundle.stage || '').trim();
    if (
      compareTitle ||
      compareHotspotSummary ||
      compareResolvedSummary ||
      compareEscalatedSummary ||
      compareValidationSummary
    ) {
      nextQuery.test_compare_bundle = '1';
    }
    if (compareTitle) nextQuery.test_compare_title = compareTitle;
    if (compareFocusLabel) nextQuery.test_compare_focus_label = compareFocusLabel;
    if (compareStrategyGroupTitle) {
      nextQuery.test_compare_strategy_group_title = compareStrategyGroupTitle;
    }
    if (compareRecommendationCode) {
      nextQuery.test_compare_recommendation_code = compareRecommendationCode;
    }
    if (compareOwnerWorkspace) nextQuery.test_compare_owner_workspace = compareOwnerWorkspace;
    if (compareOwnerActionTitle) {
      nextQuery.test_compare_owner_action_title = compareOwnerActionTitle;
    }
    if (compareGuidanceEditor) nextQuery.test_compare_guidance_editor = compareGuidanceEditor;
    if (compareEscalationFocus) {
      nextQuery.test_compare_escalation_focus = compareEscalationFocus;
    }
    if (compareHotspotSummary) nextQuery.test_compare_hotspot_summary = compareHotspotSummary;
    if (compareResolvedSummary) {
      nextQuery.test_compare_resolved_summary = compareResolvedSummary;
    }
    if (compareEscalatedSummary) {
      nextQuery.test_compare_escalated_summary = compareEscalatedSummary;
    }
    if (compareValidationSummary) {
      nextQuery.test_compare_validation_summary = compareValidationSummary;
    }
    if (compareStage) nextQuery.test_compare_stage = compareStage;
  }

  router.replace({ query: nextQuery });
};
const openContentWorkspaceWithSeed = ({
  queryText = '',
  reason = '',
  scopeWarning = '',
  focus = '',
  source = '',
} = {}) => {
  const nextQuery = { ...(route.query || {}), tab: 'train_content', content_seed: '1' };
  [
    ...ATTRIBUTE_OVERRIDE_DRILLDOWN_QUERY_KEYS,
    ...ANALYZE_SEED_QUERY_KEYS,
    ...ANALYZE_FOLLOWUP_QUERY_KEYS,
    ...ANALYZE_OWNER_FOCUS_QUERY_KEYS,
    ...CONTENT_SEED_QUERY_KEYS,
  ].forEach(key => {
    delete nextQuery[key];
  });
  delete nextQuery.test_query;
  delete nextQuery.test_inbox_id;
  delete nextQuery.test_reason;
  delete nextQuery.test_source;

  const normalizedQuery = String(queryText || '').trim();
  const normalizedReason = String(reason || '').trim();
  const normalizedScopeWarning = String(scopeWarning || '').trim();
  const normalizedFocus = String(focus || '').trim();
  const normalizedSource = String(source || '').trim();

  if (normalizedQuery) nextQuery.content_seed_query = normalizedQuery;
  else delete nextQuery.content_seed_query;

  if (normalizedReason) nextQuery.content_seed_reason = normalizedReason;
  else delete nextQuery.content_seed_reason;

  if (normalizedScopeWarning) nextQuery.content_seed_scope_warning = normalizedScopeWarning;
  else delete nextQuery.content_seed_scope_warning;

  if (normalizedFocus) nextQuery.content_seed_focus = normalizedFocus;
  else delete nextQuery.content_seed_focus;

  if (normalizedSource) nextQuery.content_seed_source = normalizedSource;
  else delete nextQuery.content_seed_source;

  router.replace({ query: nextQuery });
};
const openGuidanceWorkspaceWithSeed = ({
  editor = '',
  reason = '',
  queryText = '',
  source = '',
} = {}) => {
  const nextQuery = { ...(route.query || {}), tab: 'train_guidance', guidance_seed: '1' };
  [
    ...ATTRIBUTE_OVERRIDE_DRILLDOWN_QUERY_KEYS,
    ...ANALYZE_SEED_QUERY_KEYS,
    ...ANALYZE_FOLLOWUP_QUERY_KEYS,
    ...ANALYZE_OWNER_FOCUS_QUERY_KEYS,
    ...CONTENT_SEED_QUERY_KEYS,
    ...GUIDANCE_SEED_QUERY_KEYS,
    ...DEPLOY_SEED_QUERY_KEYS,
    ...DEPLOY_COMPARE_QUERY_KEYS,
    ...ESCALATION_SEED_QUERY_KEYS,
  ].forEach(key => {
    delete nextQuery[key];
  });
  delete nextQuery.test_query;
  delete nextQuery.test_inbox_id;
  delete nextQuery.test_reason;
  delete nextQuery.test_source;

  const normalizedEditor = String(editor || '').trim();
  const normalizedReason = String(reason || '').trim();
  const normalizedQuery = String(queryText || '').trim();
  const normalizedSource = String(source || '').trim();

  if (normalizedEditor) nextQuery.guidance_seed_editor = normalizedEditor;
  if (normalizedReason) nextQuery.guidance_seed_reason = normalizedReason;
  if (normalizedQuery) nextQuery.guidance_seed_query = normalizedQuery;
  if (normalizedSource) nextQuery.guidance_seed_source = normalizedSource;

  router.replace({ query: nextQuery });
};
const openDeployWorkspaceWithSeed = ({
  focus = '',
  reason = '',
  inboxId = '',
  source = '',
  confidenceLevel = '',
  confidenceRuns = '',
  confidenceTitle = '',
  compareBundle = null,
  batchBaseline = null,
  followupIntent = '',
  targetVersionLabel = '',
  compareVersionLabel = '',
  prefillNote = '',
  openVersions = false,
  sessionId = '',
  sessionLabel = '',
  sessionStep = '',
  sessionOrigin = '',
  sessionItemKey = '',
} = {}) => {
  const nextQuery = { ...(route.query || {}), tab: 'deploy_channels', deploy_seed: '1' };
  [
    ...ATTRIBUTE_OVERRIDE_DRILLDOWN_QUERY_KEYS,
    ...ANALYZE_SEED_QUERY_KEYS,
    ...ANALYZE_FOLLOWUP_QUERY_KEYS,
    ...ANALYZE_OWNER_FOCUS_QUERY_KEYS,
    ...CONTENT_SEED_QUERY_KEYS,
    ...GUIDANCE_SEED_QUERY_KEYS,
    ...DEPLOY_SEED_QUERY_KEYS,
    ...DEPLOY_COMPARE_QUERY_KEYS,
    ...ESCALATION_SEED_QUERY_KEYS,
  ].forEach(key => {
    delete nextQuery[key];
  });
  delete nextQuery.test_query;
  delete nextQuery.test_inbox_id;
  delete nextQuery.test_reason;
  delete nextQuery.test_source;
  TEST_COMPARE_QUERY_KEYS.forEach(key => {
    delete nextQuery[key];
  });
  TEST_FOLLOWUP_QUERY_KEYS.forEach(key => {
    delete nextQuery[key];
  });
  DEPLOY_COMPARE_QUERY_KEYS.forEach(key => {
    delete nextQuery[key];
  });

  const normalizedFocus = String(focus || '').trim();
  const normalizedReason = String(reason || '').trim();
  const normalizedInboxId = String(inboxId || '').trim();
  const normalizedSource = String(source || '').trim();
  const normalizedConfidenceLevel = String(confidenceLevel || '').trim();
  const normalizedConfidenceRuns = String(confidenceRuns || '').trim();
  const normalizedConfidenceTitle = String(confidenceTitle || '').trim();
  const normalizedFollowupIntent = String(followupIntent || '').trim();
  const normalizedTargetVersionLabel = String(targetVersionLabel || '').trim();
  const normalizedCompareVersionLabel = String(compareVersionLabel || '').trim();
  const normalizedPrefillNote = String(prefillNote || '').trim();
  const normalizedOpenVersions = openVersions === true;
  const normalizedSessionId = String(sessionId || '').trim();
  const normalizedSessionLabel = String(sessionLabel || '').trim();
  const normalizedSessionStep = String(sessionStep || '').trim();
  const normalizedSessionOrigin = String(sessionOrigin || '').trim();
  const normalizedSessionItemKey = String(sessionItemKey || '').trim();
  const normalizedCompareBundle =
    compareBundle && typeof compareBundle === 'object' ? compareBundle : null;
  const normalizedBatchBaseline =
    batchBaseline && typeof batchBaseline === 'object' ? batchBaseline : null;

  if (normalizedFocus) nextQuery.deploy_seed_focus = normalizedFocus;
  if (normalizedReason) nextQuery.deploy_seed_reason = normalizedReason;
  if (normalizedInboxId) nextQuery.deploy_seed_inbox_id = normalizedInboxId;
  if (normalizedSource) nextQuery.deploy_seed_source = normalizedSource;
  if (normalizedConfidenceLevel) {
    nextQuery.deploy_seed_confidence_level = normalizedConfidenceLevel;
  }
  if (normalizedConfidenceRuns) {
    nextQuery.deploy_seed_confidence_runs = normalizedConfidenceRuns;
  }
  if (normalizedConfidenceTitle) {
    nextQuery.deploy_seed_confidence_title = normalizedConfidenceTitle;
  }
  if (normalizedFollowupIntent) {
    nextQuery.deploy_seed_followup_intent = normalizedFollowupIntent;
  }
  if (normalizedTargetVersionLabel) {
    nextQuery.deploy_seed_target_version_label = normalizedTargetVersionLabel;
  }
  if (normalizedCompareVersionLabel) {
    nextQuery.deploy_seed_compare_version_label = normalizedCompareVersionLabel;
  }
  if (normalizedPrefillNote) {
    nextQuery.deploy_seed_prefill_note = normalizedPrefillNote;
  }
  if (normalizedOpenVersions) {
    nextQuery.deploy_seed_open_versions = '1';
  }
  if (normalizedSessionId) {
    nextQuery.deploy_seed_session_id = normalizedSessionId;
  }
  if (normalizedSessionLabel) {
    nextQuery.deploy_seed_session_label = normalizedSessionLabel;
  }
  if (normalizedSessionStep) {
    nextQuery.deploy_seed_session_step = normalizedSessionStep;
  }
  if (normalizedSessionOrigin) {
    nextQuery.deploy_seed_session_origin = normalizedSessionOrigin;
  }
  if (normalizedSessionItemKey) {
    nextQuery.deploy_seed_session_item_key = normalizedSessionItemKey;
  }
  if (normalizedSessionId) {
    nextQuery.rollback_session_key = normalizedSessionId;
  }
  if (normalizedSessionItemKey) {
    nextQuery.rollback_session_item_key = normalizedSessionItemKey;
  }
  if (normalizedBatchBaseline) {
    const batchBaselineKey = String(
      normalizedBatchBaseline.baseline_key || normalizedBatchBaseline.baselineKey || ''
    ).trim();
    const batchSetKey = String(
      normalizedBatchBaseline.set_key || normalizedBatchBaseline.setKey || ''
    ).trim();
    const batchTitle = String(normalizedBatchBaseline.title || '').trim();
    const batchOverallLevel = String(
      normalizedBatchBaseline.overall_level ||
        normalizedBatchBaseline.overallLevel ||
        ''
    ).trim();
    const batchTotalCases = String(
      normalizedBatchBaseline.total_cases || normalizedBatchBaseline.totalCases || ''
    ).trim();
    const batchReadyCases = String(
      normalizedBatchBaseline.ready_cases || normalizedBatchBaseline.readyCases || ''
    ).trim();
    const batchWatchCases = String(
      normalizedBatchBaseline.watch_cases || normalizedBatchBaseline.watchCases || ''
    ).trim();
    const batchBlockedCases = String(
      normalizedBatchBaseline.blocked_cases || normalizedBatchBaseline.blockedCases || ''
    ).trim();
    const batchRanAt = String(
      normalizedBatchBaseline.ran_at || normalizedBatchBaseline.ranAt || ''
    ).trim();
    const batchTopBlocker = String(
      normalizedBatchBaseline.top_blocker || normalizedBatchBaseline.topBlocker || ''
    ).trim();
    const batchTopWatchSignal = String(
      normalizedBatchBaseline.top_watch_signal ||
        normalizedBatchBaseline.topWatchSignal ||
        ''
    ).trim();
    if (batchBaselineKey) {
      nextQuery.deploy_seed_batch_baseline_key = batchBaselineKey;
    }
    if (batchSetKey) nextQuery.deploy_seed_batch_set_key = batchSetKey;
    if (batchTitle) nextQuery.deploy_seed_batch_title = batchTitle;
    if (batchOverallLevel) {
      nextQuery.deploy_seed_batch_overall_level = batchOverallLevel;
    }
    if (batchTotalCases) nextQuery.deploy_seed_batch_total_cases = batchTotalCases;
    if (batchReadyCases) nextQuery.deploy_seed_batch_ready_cases = batchReadyCases;
    if (batchWatchCases) nextQuery.deploy_seed_batch_watch_cases = batchWatchCases;
    if (batchBlockedCases) {
      nextQuery.deploy_seed_batch_blocked_cases = batchBlockedCases;
    }
    if (batchRanAt) nextQuery.deploy_seed_batch_ran_at = batchRanAt;
    if (batchTopBlocker) nextQuery.deploy_seed_batch_top_blocker = batchTopBlocker;
    if (batchTopWatchSignal) {
      nextQuery.deploy_seed_batch_top_watch_signal = batchTopWatchSignal;
    }
  }
  if (normalizedCompareBundle) {
    const compareTitle = String(normalizedCompareBundle.title || '').trim();
    const compareFocusLabel = String(normalizedCompareBundle.focusLabel || '').trim();
    const compareStrategyGroupTitle = String(
      normalizedCompareBundle.strategyGroupTitle || ''
    ).trim();
    const compareRecommendationCode = String(
      normalizedCompareBundle.recommendationCode || ''
    ).trim();
    const compareOwnerWorkspace = String(
      normalizedCompareBundle.ownerWorkspace || ''
    ).trim();
    const compareOwnerActionTitle = String(
      normalizedCompareBundle.ownerActionTitle || ''
    ).trim();
    const compareGuidanceEditor = String(
      normalizedCompareBundle.guidanceEditor || ''
    ).trim();
    const compareEscalationFocus = String(
      normalizedCompareBundle.escalationFocus || ''
    ).trim();
    const compareHotspotSummary = String(
      normalizedCompareBundle.hotspotSummary || ''
    ).trim();
    const compareResolvedSummary = String(
      normalizedCompareBundle.resolvedSummary || ''
    ).trim();
    const compareEscalatedSummary = String(
      normalizedCompareBundle.escalatedSummary || ''
    ).trim();
    const compareValidationSummary = String(
      normalizedCompareBundle.validationSummary || ''
    ).trim();
    const compareStage = String(normalizedCompareBundle.stage || '').trim();
    const compareRollbackTrigger = String(
      normalizedCompareBundle.rollbackTrigger ||
        normalizedCompareBundle.validationRollbackTrigger ||
        ''
    ).trim();
    if (
      compareTitle ||
      compareHotspotSummary ||
      compareResolvedSummary ||
      compareEscalatedSummary ||
      compareValidationSummary
    ) {
      nextQuery.deploy_seed_bundle = '1';
    }
    if (compareTitle) nextQuery.deploy_seed_title = compareTitle;
    if (compareFocusLabel) nextQuery.deploy_seed_focus_label = compareFocusLabel;
    if (compareStrategyGroupTitle) {
      nextQuery.deploy_seed_strategy_group_title = compareStrategyGroupTitle;
    }
    if (compareRecommendationCode) {
      nextQuery.deploy_seed_recommendation_code = compareRecommendationCode;
    }
    if (compareOwnerWorkspace) {
      nextQuery.deploy_seed_owner_workspace = compareOwnerWorkspace;
    }
    if (compareOwnerActionTitle) {
      nextQuery.deploy_seed_owner_action_title = compareOwnerActionTitle;
    }
    if (compareGuidanceEditor) {
      nextQuery.deploy_seed_guidance_editor = compareGuidanceEditor;
    }
    if (compareEscalationFocus) {
      nextQuery.deploy_seed_escalation_focus = compareEscalationFocus;
    }
    if (compareHotspotSummary) {
      nextQuery.deploy_seed_hotspot_summary = compareHotspotSummary;
    }
    if (compareResolvedSummary) {
      nextQuery.deploy_seed_resolved_summary = compareResolvedSummary;
    }
    if (compareEscalatedSummary) {
      nextQuery.deploy_seed_escalated_summary = compareEscalatedSummary;
    }
    if (compareValidationSummary) {
      nextQuery.deploy_seed_validation_summary = compareValidationSummary;
    }
    if (compareStage) nextQuery.deploy_seed_stage = compareStage;
    if (compareRollbackTrigger) {
      nextQuery.deploy_seed_rollback_trigger = compareRollbackTrigger;
    }
  }

  router.replace({ query: nextQuery });
};
const openDeployRollbackFollowup = () => {
  const outcome = releaseReadinessSummary.value?.latest_rollback_outcome || null;
  if (!outcome) return;
  const session = advanceAnalyzeRollbackSession('Deploy');
  openDeployWorkspaceWithSeed({
    focus: 'release',
    reason: 'rollback_followup_writeback',
    source: 'Analyze',
    confidenceLevel: String(outcome.confidence_level || '').trim(),
    confidenceTitle: String(outcome.intent_label || outcome.recommended_action_label || '').trim(),
    compareBundle: buildRollbackFollowupCompareBundle(outcome),
    batchBaseline: releaseReadinessSummary.value?.latest_batch_baseline || null,
    followupIntent: String(
      outcome.operator_actions?.[0]?.intent_label || outcome.recommended_action_label || ''
    ).trim(),
    targetVersionLabel: String(outcome.target_version_label || '').trim(),
    compareVersionLabel: String(
      outcome.compare_version_label || outcome.previous_version_label || ''
    ).trim(),
    prefillNote: buildRollbackFollowupNote(outcome),
    sessionId: session.sessionId,
    sessionLabel: session.sessionLabel,
    sessionStep: String(session.sessionStep || 1),
    sessionOrigin: session.sessionOrigin || 'Analyze',
    sessionItemKey: currentAnalyzeRollbackSessionItemKey(),
  });
};
const openEscalationWorkspaceWithSeed = ({
  focus = '',
  reason = '',
  queryText = '',
  label = '',
  source = '',
} = {}) => {
  const nextQuery = { ...(route.query || {}), tab: 'train_escalation', escalation_seed: '1' };
  [
    ...ATTRIBUTE_OVERRIDE_DRILLDOWN_QUERY_KEYS,
    ...ANALYZE_SEED_QUERY_KEYS,
    ...ANALYZE_FOLLOWUP_QUERY_KEYS,
    ...ANALYZE_OWNER_FOCUS_QUERY_KEYS,
    ...CONTENT_SEED_QUERY_KEYS,
    ...GUIDANCE_SEED_QUERY_KEYS,
    ...DEPLOY_SEED_QUERY_KEYS,
    ...DEPLOY_COMPARE_QUERY_KEYS,
    ...ESCALATION_SEED_QUERY_KEYS,
  ].forEach(key => {
    delete nextQuery[key];
  });
  delete nextQuery.test_query;
  delete nextQuery.test_inbox_id;
  delete nextQuery.test_reason;
  delete nextQuery.test_source;

  const normalizedFocus = String(focus || '').trim();
  const normalizedReason = String(reason || '').trim();
  const normalizedQuery = String(queryText || '').trim();
  const normalizedLabel = String(label || '').trim();
  const normalizedSource = String(source || '').trim();

  if (normalizedFocus) nextQuery.escalation_seed_focus = normalizedFocus;
  if (normalizedReason) nextQuery.escalation_seed_reason = normalizedReason;
  if (normalizedQuery) nextQuery.escalation_seed_query = normalizedQuery;
  if (normalizedLabel) nextQuery.escalation_seed_label = normalizedLabel;
  if (normalizedSource) nextQuery.escalation_seed_source = normalizedSource;

  router.replace({ query: nextQuery });
};
const openLearningLoopAction = action => {
  switch (action) {
    case 'test':
      openTestWorkspace();
      break;
    case 'content':
      openContentWorkspace();
      break;
    case 'guidance':
      openGuidanceWorkspace();
      break;
    case 'deploy':
      openDeployWorkspace();
      break;
    case 'escalation':
      openEscalationWorkspace();
      break;
    default:
      break;
  }
};
const retestFailureInWorkspace = tag => {
  openTestWorkspaceWithSeed({
    queryText: tag?.user_text || tag?.desired_text || '',
    inboxId: tag?.inbox_id || '',
    reason: tag?.content_gap_reason || tag?.source || 'missed_reply',
    source: 'Analyze',
  });
};
const retestAnalyzeHotspot = item => {
  openTestWorkspaceWithSeed({
    queryText: item?.testQuery || '',
    inboxId: item?.testInboxId || '',
    reason: item?.testReason || item?.title || 'analyze_hotspot',
    source: 'Analyze',
    compareBundle: item?.testCompareBundle || null,
  });
};
async function setAnalyzeOwnerFocusQuery(ownerLabel) {
  const nextQuery = { ...(route.query || {}), tab: 'analyze_decision_log' };
  const normalizedOwnerLabel = String(ownerLabel || '').trim();
  if (normalizedOwnerLabel) {
    nextQuery.analyze_owner_focus = normalizedOwnerLabel;
  } else {
    delete nextQuery.analyze_owner_focus;
  }
  await router.replace({ query: nextQuery });
}
const repairOwnerFocus = ref('');
const focusRepairOwner = async ownerLabel => {
  repairOwnerFocus.value = String(ownerLabel || '').trim();
  await setAnalyzeOwnerFocusQuery(repairOwnerFocus.value);
};
const clearRepairOwnerFocus = async () => {
  repairOwnerFocus.value = '';
  await setAnalyzeOwnerFocusQuery('');
};
const toggleAdvancedDiagnostics = () => {
  showAdvancedDiagnostics.value = !showAdvancedDiagnostics.value;
};
const openAnalyzeAction = item => {
  if (
    item?.action === 'content' &&
    (item?.contentQuery || item?.contentReason || item?.contentScopeWarning || item?.contentFocus)
  ) {
    openContentWorkspaceWithSeed({
      queryText: item?.contentQuery || '',
      reason: item?.contentReason || item?.title || '',
      scopeWarning: item?.contentScopeWarning || '',
      focus: item?.contentFocus || '',
      source: 'Analyze',
    });
    return;
  }

  if (item?.action === 'guidance' && (item?.guidanceEditor || item?.guidanceReason || item?.guidanceQuery)) {
    openGuidanceWorkspaceWithSeed({
      editor: item?.guidanceEditor || '',
      reason: item?.guidanceReason || item?.title || '',
      queryText: item?.guidanceQuery || '',
      source: 'Analyze',
    });
    return;
  }

  if (item?.action === 'deploy' && (item?.deployFocus || item?.deployReason || item?.deployInboxId)) {
    openDeployWorkspaceWithSeed({
      focus: item?.deployFocus || '',
      reason: item?.deployReason || item?.title || '',
      inboxId: item?.deployInboxId || '',
      source: 'Analyze',
      confidenceLevel: item?.deployConfidenceLevel || '',
      confidenceRuns: item?.deployConfidenceRuns || '',
      confidenceTitle: item?.deployConfidenceTitle || '',
      batchBaseline: item?.deployBatchBaseline || null,
      compareBundle: item?.deployCompareBundle || null,
    });
    return;
  }

  if (item?.action === 'escalation' && (item?.escalationFocus || item?.escalationReason || item?.escalationQuery || item?.escalationLabel)) {
    openEscalationWorkspaceWithSeed({
      focus: item?.escalationFocus || '',
      reason: item?.escalationReason || item?.title || '',
      queryText: item?.escalationQuery || '',
      label: item?.escalationLabel || '',
      source: 'Analyze',
    });
    return;
  }

  openLearningLoopAction(item?.action);
};
const strategyRecommendationTestCompareBundle = row => ({
  title:
    row?.repairBundleTitle ||
    row?.title ||
    `${row?.strategyGroupTitle || 'Strategy'} repair bundle`,
  focusLabel: row?.label || row?.key || '',
  strategyGroupTitle: row?.strategyGroupTitle || '',
  recommendationCode: row?.recommendationCode || '',
  ownerWorkspace:
    row?.repairBundleHotspotActionWorkspace || row?.targetWorkspace || '',
  ownerActionTitle: row?.repairBundleHotspotActionTitle || '',
  guidanceEditor:
    row?.repairBundleHotspotActionEditor || row?.guidanceEditor || '',
  escalationFocus:
    row?.repairBundleHotspotActionFocus || row?.escalationFocus || '',
  hotspotSummary: row?.repairBundleHotspotSummary || '',
  resolvedSummary: row?.repairBundleResolvedSummary || '',
  escalatedSummary: row?.repairBundleEscalatedSummary || '',
  validationSummary: row?.repairBundleValidationSummary || '',
  stage: 'validation',
  rollbackTrigger:
    row?.repairBundleValidationStageRollbackTrigger ||
    row?.repairBundleHotspotStageRollbackTrigger ||
    row?.repairBundleResolvedStageRollbackTrigger ||
    row?.repairBundleEscalatedStageRollbackTrigger ||
    '',
});
const strategyRecommendationConfidenceKey = row =>
  `${String(row?.strategyGroup || '').trim()}::${String(row?.key || '').trim()}`;
const strategyRecommendationReleaseEntryFor = row =>
  findReleaseReviewSnapshot({
    snapshots: currentReleaseSnapshots.value,
    bundle: strategyRecommendationTestCompareBundle(row),
  });
const strategyRecommendationBatchBaselineFor = row =>
  findBatchBaseline({
    baselines: currentBatchBaselines.value,
    bundle: strategyRecommendationTestCompareBundle(row),
  });
const strategyRecommendationConfidenceFor = row => {
  const runs = loadRepairBundleRuns({
    assistantId: assistantId.value,
    bundle: strategyRecommendationTestCompareBundle(row),
  });
  const confidence = summarizeRepairBundleConfidence(runs);
  return {
    ...confidence,
    runsCount: runs.length,
  };
};
const strategyRecommendationRepairStatusFor = row => {
  const confidence = strategyRecommendationConfidenceFor(row);
  const runs = loadRepairBundleRuns({
    assistantId: assistantId.value,
    bundle: strategyRecommendationTestCompareBundle(row),
  });
  const latestRun = runs[0] || null;
  const releaseEntry = strategyRecommendationReleaseEntryFor(row);
  const batchBaseline = strategyRecommendationBatchBaselineFor(row);
  const releaseStatus = String(releaseEntry?.status || '').trim();
  return {
    ...confidence,
    latestVerdict: String(latestRun?.status || '').trim() || 'pending',
    deployReady: confidence.level === 'ready',
    releaseStatus,
    releaseStatusLabel:
      releaseStatus === 'released'
        ? 'Released'
        : releaseStatus === 'rolled_back'
          ? 'Rollback triggered'
          : releaseStatus === 'reviewing'
            ? 'Review open'
            : '',
    releaseReviewedAt: String(releaseEntry?.reviewed_at || '').trim(),
    releaseNote: String(releaseEntry?.note || '').trim(),
    releaseRollbackTrigger: String(releaseEntry?.rollback_trigger || '').trim(),
    batchBaselineLevel: String(batchBaseline?.overall_level || '').trim(),
    batchBaselineTitle: String(batchBaseline?.title || '').trim(),
    batchBaselineRanAt: String(batchBaseline?.ran_at || '').trim(),
    batchBaselineTotalCases: Number(batchBaseline?.total_cases || 0),
    batchBaselineTopSignal:
      String(batchBaseline?.top_blocker || '').trim() ||
      String(batchBaseline?.top_watch_signal || '').trim(),
    batchBaseline,
  };
};
const strategyRecommendationDeployAction = row => {
  const repairStatus = strategyRecommendationRepairStatusFor(row);
  if (!repairStatus.deployReady) return null;

  return {
    action: 'deploy',
    actionLabel: 'Open Deploy',
    deployFocus: 'release',
    deployReason: 'repair_confidence_ready',
    deployInboxId:
      row?.repairBundleValidationActionInboxId ||
      row?.repairBundleHotspotActionInboxId ||
      topFailureSampleInboxId.value,
    deployConfidenceLevel: repairStatus.level,
    deployConfidenceRuns: repairStatus.runsCount,
    deployConfidenceTitle: repairStatus.title,
    deployBatchBaseline: repairStatus.batchBaseline,
    deployCompareBundle: strategyRecommendationTestCompareBundle(row),
  };
};
const strategyRecommendationAction = row => {
  const base = {
    title: row?.title || row?.label || 'Strategy recommendation',
    testQuery: topHotspotRetestQuery.value,
    testInboxId:
      row?.repairBundleValidationActionInboxId ||
      row?.repairBundleHotspotActionInboxId ||
      topFailureSampleInboxId.value,
    testReason: row?.recommendationCode || row?.strategyGroup || 'strategy_recommendation',
    testCompareBundle: strategyRecommendationTestCompareBundle(row),
  };
  if ((row?.repairBundleHotspotActionWorkspace || row?.targetWorkspace) === 'content') {
    return {
      ...base,
      action: 'content',
      actionLabel: row?.repairBundleHotspotActionTitle || 'Open Content',
      contentFocus: 'retrieval',
      contentReason: row?.label || row?.strategyGroupTitle || row?.strategyGroup || '',
      contentQuery: topHotspotRetestQuery.value,
    };
  }
  if (
    (row?.repairBundleHotspotActionWorkspace || row?.targetWorkspace) === 'escalation'
  ) {
    return {
      ...base,
      action: 'escalation',
      actionLabel: row?.repairBundleHotspotActionTitle || 'Open Escalation',
      escalationFocus:
        row?.repairBundleHotspotActionFocus || row?.escalationFocus || 'defaults',
      escalationReason: row?.label || row?.strategyGroupTitle || row?.strategyGroup || '',
      escalationQuery: topHotspotRetestQuery.value,
      escalationLabel: row?.strategyGroupTitle || '',
    };
  }
  if ((row?.repairBundleHotspotActionWorkspace || row?.targetWorkspace) === 'deploy') {
    return {
      ...base,
      action: 'deploy',
      actionLabel: row?.repairBundleHotspotActionTitle || 'Open Deploy',
      deployFocus: row?.repairBundleHotspotActionFocus || 'channels',
      deployReason: row?.label || row?.strategyGroupTitle || row?.strategyGroup || '',
      deployInboxId:
        row?.repairBundleHotspotActionInboxId || row?.latestInboxId || topFailureSampleInboxId.value,
    };
  }
  return {
    ...base,
    action: 'guidance',
    actionLabel: row?.repairBundleHotspotActionTitle || 'Open Guidance',
    guidanceEditor:
      row?.repairBundleHotspotActionEditor ||
      row?.guidanceEditor ||
      'response_guidelines',
    guidanceReason: row?.label || row?.strategyGroupTitle || row?.strategyGroup || '',
    guidanceQuery: topHotspotRetestQuery.value,
  };
};
const retrievalRollupAction = (row, reasonPrefix = 'retrieval_rollup') => ({
  action: 'content',
  actionLabel: 'Open Content',
  contentFocus: 'retrieval',
  contentQuery: topHotspotRetestQuery.value,
  contentReason: `${reasonPrefix}:${String(row?.key || row?.label || '').trim()}`,
  testQuery: topHotspotRetestQuery.value,
  testInboxId: row?.latestInboxId || row?.latestResolvedInboxId || topFailureSampleInboxId.value,
  testReason: `${reasonPrefix}:${String(row?.key || row?.label || '').trim()}`,
});

const applyStrategyRecommendationFilter = row => {
  const label = String(row?.label || row?.key || '').trim();
  if (!label) return;

  filters.value.answer_length_strategy = '';
  filters.value.directness_strategy = '';
  filters.value.sensitive_topics_strategy = '';
  filters.value.tone_enforcement_strategy = '';
  filters.value.memory_depth_strategy = '';
  filters.value.answer_confidence_strategy = '';
  filters.value.outside_knowledge_strategy = '';
  filters.value.promise_safety_strategy = '';
  filters.value.procedure_routing_precedence = '';
  filters.value.procedure_step_confirmation_strategy = '';
  filters.value.procedure_fallback_action = '';
  filters.value.channel_runtime_default_reply_mode = '';
  filters.value.channel_runtime_binding_scope = '';
  filters.value.channel_runtime_active_segment_type = '';
  filters.value.business_hours_active = '';
  filters.value.business_hours_reply_mode = '';
  filters.value.business_hours_response_behavior = '';
  filters.value.region_compliance_binding_scope = '';
  filters.value.region_compliance_active_country_code = '';
  filters.value.region_compliance_active_profile = '';
  filters.value.uncertain_action = '';
  filters.value.high_risk_action = '';

  switch (String(row?.strategyGroup || '').trim()) {
    case 'answer_length_strategy':
      filters.value.answer_length_strategy = label;
      break;
    case 'directness_strategy':
      filters.value.directness_strategy = label;
      break;
    case 'sensitive_topics_strategy':
      filters.value.sensitive_topics_strategy = label;
      break;
    case 'tone_enforcement_strategy':
      filters.value.tone_enforcement_strategy = label;
      break;
    case 'memory_depth_strategy':
      filters.value.memory_depth_strategy = label;
      break;
    case 'answer_confidence_strategy':
      filters.value.answer_confidence_strategy = label;
      break;
    case 'outside_knowledge_strategy':
      filters.value.outside_knowledge_strategy = label;
      break;
    case 'promise_safety_strategy':
      filters.value.promise_safety_strategy = label;
      break;
    case 'procedure_routing_precedence':
      filters.value.procedure_routing_precedence = label;
      break;
    case 'procedure_step_confirmation_strategy':
      filters.value.procedure_step_confirmation_strategy = label;
      break;
    case 'procedure_fallback_action':
      filters.value.procedure_fallback_action = label;
      break;
    case 'channel_runtime_default_reply_mode':
      filters.value.channel_runtime_default_reply_mode = label;
      break;
    case 'channel_runtime_binding_scope':
      filters.value.channel_runtime_binding_scope = label;
      break;
    case 'channel_runtime_active_segment_type':
      filters.value.channel_runtime_active_segment_type = label;
      break;
    case 'business_hours_reply_mode':
      filters.value.business_hours_reply_mode = label;
      break;
    case 'business_hours_response_behavior':
      filters.value.business_hours_response_behavior = label;
      break;
    case 'region_compliance_binding_scope':
      filters.value.region_compliance_binding_scope = label;
      break;
    case 'region_compliance_active_country_code':
      filters.value.region_compliance_active_country_code = label;
      break;
    case 'region_compliance_active_profile':
      filters.value.region_compliance_active_profile = label;
      break;
    case 'uncertain_action':
      filters.value.uncertain_action = label;
      break;
    case 'high_risk_action':
      filters.value.high_risk_action = label;
      break;
    default:
      break;
  }
};

const inspectStrategyRecommendationCase = async (row, outcome) => {
  const normalizedOutcome = String(outcome || '').trim();
  const isResolved = normalizedOutcome === 'resolved';
  const inboxId = Number(
    isResolved
      ? row?.repairBundleResolvedActionInboxId || row?.latestResolvedInboxId
      : row?.repairBundleEscalatedActionInboxId || row?.latestEscalatedInboxId
  );
  const conversationId = Number(
    isResolved
      ? row?.repairBundleResolvedActionConversationId || row?.latestResolvedConversationId
      : row?.repairBundleEscalatedActionConversationId || row?.latestEscalatedConversationId
  );
  if (!Number.isFinite(conversationId) || conversationId <= 0) return;

  applyStrategyRecommendationFilter(row);
  filters.value.conversation_id = conversationId;
  filters.value.inbox_id = Number.isFinite(inboxId) && inboxId > 0 ? inboxId : '';
  showAdvancedDiagnostics.value = true;
  await refreshAll();
  scrollToTraceSection();
};

const isLoadingSummary = ref(false);
const isLoadingTraces = ref(false);
const isLoadingFailures = ref(false);
const isLoadingConversationTimeline = ref(false);
const breakdownLoadingCount = ref(0);

const traceItems = ref([]);
const traceMeta = ref({ total: 0, limit: 50, offset: 0 });
const traceSampleItems = ref([]);
const failureItems = ref([]);
const failureMeta = ref({ total: 0, limit: 20, offset: 0 });
const failureSampleItems = ref([]);
const timeoutDailySeries = ref([]);
const deliveryBlockedDailySeries = ref([]);
const timeoutIntentBreakdown = ref([]);
const deliveryBlockedModeBreakdown = ref([]);
const articleScopeWarningBreakdown = ref([]);
const contentGapReasonBreakdown = ref([]);
const contentGapTopicBreakdown = ref([]);
const contentGapClusterBreakdown = ref([]);
const attributeSourceBreakdown = ref([]);
const attributeKeyBreakdown = ref([]);
const attributeOverrideDailySeries = ref([]);
const attributeOverrideLabelBreakdown = ref([]);
const attributeOverrideActionBreakdown = ref([]);
const attributeOverrideMatchTypeBreakdown = ref([]);
const attributeOverrideOutcomeBreakdown = ref([]);
const attributeOverrideInboxOutcomeRows = ref([]);
const attributeOverrideConditionOutcomeRows = ref([]);
const attributeOverrideGuardOutcomeRows = ref([]);
const attributeOverrideConditionRecommendationRows = ref([]);
const attributeOverrideGuardRecommendationRows = ref([]);
const attributeOverrideRecommendationRows = ref([]);
const strategyRecommendationRows = ref([]);
const previousStrategyRecommendationRows = ref([]);
const attributeOverrideRecentCaseRows = ref([]);
const forcedReplyLanguageBreakdown = ref([]);
const communicationStyleBreakdown = ref([]);
const channelRuntimeDefaultReplyModeBreakdown = ref([]);
const channelRuntimeBindingScopeBreakdown = ref([]);
const channelRuntimeActiveSegmentTypeBreakdown = ref([]);
const businessHoursActiveBreakdown = ref([]);
const businessHoursReplyModeBreakdown = ref([]);
const businessHoursResponseBehaviorBreakdown = ref([]);
const regionComplianceBindingScopeBreakdown = ref([]);
const regionComplianceActiveCountryCodeBreakdown = ref([]);
const regionComplianceActiveProfileBreakdown = ref([]);
const answerLengthStrategyBreakdown = ref([]);
const directnessStrategyBreakdown = ref([]);
const sensitiveTopicsStrategyBreakdown = ref([]);
const toneEnforcementStrategyBreakdown = ref([]);
const memoryDepthStrategyBreakdown = ref([]);
const answerConfidenceStrategyBreakdown = ref([]);
const outsideKnowledgeStrategyBreakdown = ref([]);
const promiseSafetyStrategyBreakdown = ref([]);
const uncertainActionBreakdown = ref([]);
const highRiskActionBreakdown = ref([]);
const procedureRoutingPrecedenceBreakdown = ref([]);
const procedureStepConfirmationBreakdown = ref([]);
const procedureFallbackActionBreakdown = ref([]);
const answerLengthOutcomeBreakdown = ref([]);
const directnessOutcomeBreakdown = ref([]);
const sensitiveTopicsOutcomeBreakdown = ref([]);
const toneEnforcementOutcomeBreakdown = ref([]);
const memoryDepthOutcomeBreakdown = ref([]);
const answerConfidenceOutcomeBreakdown = ref([]);
const outsideKnowledgeOutcomeBreakdown = ref([]);
const promiseSafetyOutcomeBreakdown = ref([]);
const uncertainActionOutcomeBreakdown = ref([]);
const highRiskActionOutcomeBreakdown = ref([]);
const procedureRoutingPrecedenceOutcomeBreakdown = ref([]);
const procedureStepConfirmationOutcomeBreakdown = ref([]);
const procedureFallbackActionOutcomeBreakdown = ref([]);
const channelRuntimeDefaultReplyModeOutcomeBreakdown = ref([]);
const channelRuntimeBindingScopeOutcomeBreakdown = ref([]);
const channelRuntimeActiveSegmentTypeOutcomeBreakdown = ref([]);
const businessHoursReplyModeOutcomeBreakdown = ref([]);
const businessHoursResponseBehaviorOutcomeBreakdown = ref([]);
const regionComplianceBindingScopeOutcomeBreakdown = ref([]);
const regionComplianceActiveCountryCodeOutcomeBreakdown = ref([]);
const regionComplianceActiveProfileOutcomeBreakdown = ref([]);
const guidanceCardOutcomeBreakdown = ref([]);
const guidanceScenarioOutcomeBreakdown = ref([]);
const retrievalTopPriorityBreakdownRows = ref([]);
const retrievalWinningSourceBreakdownRows = ref([]);
const retrievalSearchBalanceBreakdownRows = ref([]);
const retrievalCitationModeBreakdownRows = ref([]);
const retrievalFallbackBreakdownRows = ref([]);
const retrievalSegmentBindingBreakdownRows = ref([]);
const retrievalTopPriorityOutcomeRows = ref([]);
const retrievalWinningSourceOutcomeRows = ref([]);
const isCapturingRetrievalSnapshot = ref(false);
const retrievalSnapshotSummary = ref({});
const conversationTraceTimelineItems = ref([]);

const expandedTraceIds = ref(new Set());
const traceSectionRef = ref(null);
const operatingSummarySectionRef = ref(null);
const releaseReadinessSectionRef = ref(null);
const learningLoopSectionRef = ref(null);
const repairTeamQueueSectionRef = ref(null);
const repairBoardSectionRef = ref(null);
const optimizationCenterSectionRef = ref(null);
const analyzeInitialized = ref(false);
const hasAppliedOverrideDrilldown = ref(false);
const hasAppliedAnalyzeSeed = ref(false);
const showAdvancedDiagnostics = ref(false);
const activeAnalyzeSectionKey = ref('operating-summary');
const analyzeExpandedSectionOverrides = ref({});
let analyzeSectionSyncFrame = 0;

const isLoadingBreakdowns = computed(() => breakdownLoadingCount.value > 0);

const filters = ref({
  inbox_id: '',
  conversation_id: '',
  mode: '',
  time_window: '7d',
  decision_type_like: 'response.trace',
  source_kind: '',
  intent: '',
  action: '',
  response_kind: '',
  delivery_mode: '',
  auto_send_allowed: '',
  article_scope_warning: '',
  content_gap_reason: '',
  content_gap_cluster_key: '',
  attribute_key: '',
  attribute_value: '',
  attribute_override_rule_index: '',
  attribute_override_label: '',
  attribute_override_action: '',
  attribute_override_match_type: '',
  attribute_override_outcome: '',
  attribute_override_condition: '',
  attribute_override_guard: '',
  guidance_section: '',
  guidance_card: '',
  guidance_scenario: '',
  answer_length_strategy: '',
  directness_strategy: '',
  sensitive_topics_strategy: '',
  tone_enforcement_strategy: '',
  memory_depth_strategy: '',
  answer_confidence_strategy: '',
  outside_knowledge_strategy: '',
  promise_safety_strategy: '',
  procedure_routing_precedence: '',
  procedure_step_confirmation_strategy: '',
  procedure_fallback_action: '',
  forced_reply_language: '',
  communication_style_mode: '',
  channel_runtime_default_reply_mode: '',
  channel_runtime_binding_scope: '',
  channel_runtime_active_segment_type: '',
  business_hours_active: '',
  business_hours_reply_mode: '',
  business_hours_response_behavior: '',
  region_compliance_binding_scope: '',
  region_compliance_active_country_code: '',
  region_compliance_active_profile: '',
  uncertain_action: '',
  high_risk_action: '',
  failure_status: 'open',
});
const applyStrategyFiltersToParams = params => {
  if (filters.value.answer_length_strategy) {
    params.answer_length_strategy = filters.value.answer_length_strategy.trim();
  }
  if (filters.value.directness_strategy) {
    params.directness_strategy = filters.value.directness_strategy.trim();
  }
  if (filters.value.sensitive_topics_strategy) {
    params.sensitive_topics_strategy = filters.value.sensitive_topics_strategy.trim();
  }
  if (filters.value.tone_enforcement_strategy) {
    params.tone_enforcement_strategy = filters.value.tone_enforcement_strategy.trim();
  }
  if (filters.value.memory_depth_strategy) {
    params.memory_depth_strategy = filters.value.memory_depth_strategy.trim();
  }
  if (filters.value.answer_confidence_strategy) {
    params.answer_confidence_strategy = filters.value.answer_confidence_strategy.trim();
  }
  if (filters.value.outside_knowledge_strategy) {
    params.outside_knowledge_strategy = filters.value.outside_knowledge_strategy.trim();
  }
  if (filters.value.promise_safety_strategy) {
    params.promise_safety_strategy = filters.value.promise_safety_strategy.trim();
  }
  if (filters.value.procedure_routing_precedence) {
    params.procedure_routing_precedence =
      filters.value.procedure_routing_precedence.trim();
  }
  if (filters.value.procedure_step_confirmation_strategy) {
    params.procedure_step_confirmation_strategy =
      filters.value.procedure_step_confirmation_strategy.trim();
  }
  if (filters.value.procedure_fallback_action) {
    params.procedure_fallback_action = filters.value.procedure_fallback_action.trim();
  }
  if (filters.value.channel_runtime_default_reply_mode) {
    params.channel_runtime_default_reply_mode =
      filters.value.channel_runtime_default_reply_mode.trim();
  }
  if (filters.value.channel_runtime_binding_scope) {
    params.channel_runtime_binding_scope =
      filters.value.channel_runtime_binding_scope.trim();
  }
  if (filters.value.channel_runtime_active_segment_type) {
    params.channel_runtime_active_segment_type =
      filters.value.channel_runtime_active_segment_type.trim();
  }
  if (filters.value.business_hours_active) {
    params.business_hours_active = filters.value.business_hours_active.trim();
  }
  if (filters.value.business_hours_reply_mode) {
    params.business_hours_reply_mode = filters.value.business_hours_reply_mode.trim();
  }
  if (filters.value.business_hours_response_behavior) {
    params.business_hours_response_behavior =
      filters.value.business_hours_response_behavior.trim();
  }
  if (filters.value.region_compliance_binding_scope) {
    params.region_compliance_binding_scope =
      filters.value.region_compliance_binding_scope.trim();
  }
  if (filters.value.region_compliance_active_country_code) {
    params.region_compliance_active_country_code =
      filters.value.region_compliance_active_country_code.trim();
  }
  if (filters.value.region_compliance_active_profile) {
    params.region_compliance_active_profile =
      filters.value.region_compliance_active_profile.trim();
  }
  if (filters.value.uncertain_action) {
    params.uncertain_action = filters.value.uncertain_action.trim();
  }
  if (filters.value.high_risk_action) {
    params.high_risk_action = filters.value.high_risk_action.trim();
  }
};

const summary = ref({
  trace_total: 0,
  open_failures: 0,
  reviewed_failures: 0,
  converted_failures: 0,
  timeout_expired: 0,
  delivery_blocked: 0,
  attribute_override_hits: 0,
});
const retrievalRollupFilters = ref({
  top_priority_source: '',
  search_balance: '',
  citation_mode: '',
  active_binding_scope: '',
  no_evidence_behavior: '',
});
const RETRIEVAL_SOURCE_FILTER_OPTIONS = [
  { value: 'articles', label: 'Articles' },
  { value: 'snippets', label: 'Snippets' },
  { value: 'links', label: 'Website sync' },
  { value: 'media', label: 'Uploaded docs' },
];
const RETRIEVAL_SEARCH_BALANCE_FILTER_OPTIONS = [
  { value: 'keyword_first', label: 'Keyword first' },
  { value: 'balanced', label: 'Balanced' },
  { value: 'semantic_first', label: 'Semantic first' },
];
const RETRIEVAL_CITATION_FILTER_OPTIONS = [
  { value: 'auto', label: 'Auto' },
  { value: 'always', label: 'Always' },
  { value: 'never', label: 'Never' },
];
const RETRIEVAL_ACTIVE_SCOPE_FILTER_OPTIONS = [
  { value: 'selected_portal', label: 'Selected portal' },
  { value: 'inbox_portal', label: 'Inbox portal' },
];
const RETRIEVAL_NO_EVIDENCE_FILTER_OPTIONS = [
  { value: 'general_answer', label: 'General answer' },
  { value: 'say_unknown', label: 'Say unknown' },
  { value: 'escalate', label: 'Escalate' },
];

const aiCloserConfig = computed(() => store.getters['aiCloser/getConfig'] || {});
const currentReleaseLoop = computed(
  () => aiCloserConfig.value?.prompt_sections?.release_loop || {}
);
const currentReleaseSnapshots = computed(
  () =>
    releaseReadinessSummary.value?.release_snapshots?.length
      ? releaseReadinessSummary.value.release_snapshots
      : currentReleaseLoop.value?.review_snapshots || []
);
const currentBatchBaselines = computed(() =>
  normalizeBatchBaselines(
    releaseReadinessSummary.value?.batch_baselines?.length
      ? releaseReadinessSummary.value.batch_baselines
      : currentReleaseLoop.value?.batch_baselines || []
  )
);
const rollbackEvidenceHistoryEntries = computed(() => {
  const outcome = releaseReadinessSummary.value?.latest_rollback_outcome || null;
  if (!outcome?.summary) return [];
  const { snapshot, baseline } = findRollbackEvidenceTargets(outcome);
  return mergeOperatorNoteTimelineEntries({
    records: [snapshot, baseline].filter(Boolean),
    limit: 3,
  });
});
const rollbackEvidenceHistoryTotalCount = computed(() => {
  const outcome = releaseReadinessSummary.value?.latest_rollback_outcome || null;
  if (!outcome?.summary) return 0;
  const { snapshot, baseline } = findRollbackEvidenceTargets(outcome);
  return mergeOperatorNoteTimelineEntries({
    records: [snapshot, baseline].filter(Boolean),
    limit: 25,
  }).length;
});
const rollbackEvidenceHistoryEntryKey = (entry, index = 0) =>
  `${entry?.created_at || 'no-time'}::${entry?.source || 'unknown'}::${entry?.body || index}`;
const rollbackEvidenceHistoryEntryMeta = entry => {
  const parts = [];
  if (entry?.source) parts.push(`via ${entry.source}`);
  if (entry?.created_at) parts.push(formatDateTime(entry.created_at));
  const context = buildOperatorNoteTimelineContextLabel(entry);
  if (context) parts.push(context);
  return parts.join(' • ');
};
const rollbackEvidenceHistoryEntryBadges = entry =>
  buildOperatorNoteTimelineBadges(entry);
const rollbackEvidenceDrilldownBadges = computed(() => {
  const outcome = releaseReadinessSummary.value?.latest_rollback_outcome || null;
  if (!outcome?.summary) return [];
  const session = currentAnalyzeRollbackSession();
  return buildRollbackBreadcrumbBadges({
    source: 'Analyze',
    focus: 'release',
    intentLabel:
      outcome.operator_actions?.[0]?.intent_label || outcome.recommended_action_label,
    targetVersionLabel: outcome.target_version_label,
    compareVersionLabel: outcome.compare_version_label || outcome.previous_version_label,
    confidenceLabel: outcome.confidence_label,
    nextLabel: 'open deploy evidence',
    sessionLabel: session.sessionLabel,
    sessionStep: session.sessionStep,
    sessionOrigin: session.sessionOrigin,
  });
});
const buildRollbackEvidenceDrilldownPayload = ({ openVersions = false } = {}) => {
  const outcome = releaseReadinessSummary.value?.latest_rollback_outcome || null;
  if (!outcome?.summary) return null;
  const { snapshot, baseline } = findRollbackEvidenceTargets(outcome);
  const primaryRecord = snapshot || baseline || null;
  const primaryEntry = rollbackEvidenceHistoryEntries.value[0] || null;
  const session = advanceAnalyzeRollbackSession('Deploy');
  const versionContext = buildOperatorNoteTimelineVersionDialogContext({
    record: primaryRecord || buildRollbackFollowupCompareBundle(outcome),
    entry: primaryEntry,
    fallbackIntent:
      outcome.operator_actions?.[0]?.intent_label ||
      outcome.recommended_action_label ||
      'Rollback evidence timeline',
    sessionContext: session,
  });
  return {
    focus: 'release',
    reason: openVersions ? 'rollback_evidence_versions' : 'rollback_evidence_timeline',
    source: 'Analyze',
    confidenceLevel: String(outcome.confidence_level || '').trim(),
    confidenceTitle: String(outcome.confidence_label || outcome.confidence_note || '').trim(),
    compareBundle: primaryRecord
      ? buildRollbackEvidenceCompareBundle({
          record: primaryRecord,
          ownerWorkspace: 'analyze',
          ownerActionTitle: versionContext.intent_label,
        })
      : buildRollbackFollowupCompareBundle(outcome),
    batchBaseline: baseline || null,
    followupIntent: versionContext.intent_label,
    targetVersionLabel:
      versionContext.target_version_label || String(outcome.target_version_label || '').trim(),
    compareVersionLabel:
      versionContext.compare_version_label ||
      String(outcome.compare_version_label || outcome.previous_version_label || '').trim(),
    openVersions,
    sessionId: session.sessionId,
    sessionLabel: session.sessionLabel,
    sessionStep: String(session.sessionStep || 1),
    sessionOrigin: session.sessionOrigin || 'Analyze',
    sessionItemKey: currentAnalyzeRollbackSessionItemKey(),
  };
};
const openDeployRollbackEvidence = ({ openVersions = false } = {}) => {
  const payload = buildRollbackEvidenceDrilldownPayload({ openVersions });
  if (!payload) return;
  openDeployWorkspaceWithSeed(payload);
};
const publishedVersionId = computed(() => {
  const num = Number(
    releaseReadinessSummary.value?.published_version_id ??
      aiCloserConfig.value?.published_version_id
  );
  return Number.isFinite(num) && num > 0 ? num : null;
});
const draftVersionId = computed(() => {
  const num = Number(
    releaseReadinessSummary.value?.draft_version_id ??
      aiCloserConfig.value?.draft_version_id
  );
  return Number.isFinite(num) && num > 0 ? num : null;
});
const hasUnpublishedVersion = computed(
  () => draftVersionId.value && draftVersionId.value !== publishedVersionId.value
);
const releaseVersionContextNote = computed(() => {
  const rollbackFollowupIntent = String(route.query?.analyze_followup_intent || '').trim();
  const rollbackTargetVersion = String(
    route.query?.analyze_followup_target_version_label || ''
  ).trim();
  const rollbackCompareVersion = String(
    route.query?.analyze_followup_compare_version_label || ''
  ).trim();
  if (rollbackFollowupIntent || rollbackTargetVersion || rollbackCompareVersion) {
    return `${rollbackFollowupIntent || 'Rollback follow-up'}${rollbackTargetVersion ? ` • ${rollbackTargetVersion}` : ''}${rollbackCompareVersion ? ` vs ${rollbackCompareVersion}` : ''}.`;
  }
  if (hasUnpublishedVersion.value) {
    return `Draft version ID ${draftVersionId.value} is ahead of live version ID ${publishedVersionId.value || 'none'}. Use Deploy to publish or grey-release after repair review.`;
  }
  if (releaseReadinessSummary.value?.latest_rollback_outcome?.summary) {
    return `Latest rollback outcome: ${releaseReadinessSummary.value.latest_rollback_outcome.summary}${releaseReadinessSummary.value.latest_rollback_outcome.operator_note ? ` ${releaseReadinessSummary.value.latest_rollback_outcome.operator_note}` : ''}${releaseReadinessSummary.value.latest_rollback_outcome.confidence_label ? ` ${releaseReadinessSummary.value.latest_rollback_outcome.confidence_label}.` : ''}${releaseReadinessSummary.value.latest_rollback_outcome.recommended_action_label ? ` ${releaseReadinessSummary.value.latest_rollback_outcome.recommended_action_label}.` : ''}${releaseReadinessSummary.value.latest_rollback_outcome.target_version_label ? ` Compare ${releaseReadinessSummary.value.latest_rollback_outcome.target_version_label}${releaseReadinessSummary.value.latest_rollback_outcome.previous_version_label ? ` vs ${releaseReadinessSummary.value.latest_rollback_outcome.previous_version_label}` : ''}.` : ''}${releaseReadinessSummary.value.latest_rollback_outcome.operator_actions?.[0]?.intent_label ? ` Next in Deploy: ${releaseReadinessSummary.value.latest_rollback_outcome.operator_actions[0].intent_label}.` : releaseReadinessSummary.value.latest_rollback_outcome.operator_actions?.[0]?.label ? ` Next in Deploy: ${releaseReadinessSummary.value.latest_rollback_outcome.operator_actions[0].label}.` : ''}${Number(releaseReadinessSummary.value.latest_rollback_outcome.evidence_gap_count || 0) ? ` ${releaseReadinessSummary.value.latest_rollback_outcome.evidence_gap_count} evidence gap${Number(releaseReadinessSummary.value.latest_rollback_outcome.evidence_gap_count || 0) === 1 ? '' : 's'} still need operator follow-through.` : ''}${Array.isArray(releaseReadinessSummary.value?.bundle_rollback_outcomes) && releaseReadinessSummary.value.bundle_rollback_outcomes.length ? ` ${releaseReadinessSummary.value.bundle_rollback_outcomes.length} bundle rollback outcome${releaseReadinessSummary.value.bundle_rollback_outcomes.length === 1 ? '' : 's'} are currently tracked.` : ''}`;
  }
  if (Number(releaseReadinessSummary.value?.split_rollout_count || 0) > 0) {
    return `${releaseReadinessSummary.value.split_rollout_count} split rollout${releaseReadinessSummary.value.split_rollout_count === 1 ? '' : 's'} are active, and ${releaseReadinessSummary.value?.pinned_rollout_count || 0} pinned rollout${Number(releaseReadinessSummary.value?.pinned_rollout_count || 0) === 1 ? '' : 's'} still fully override specific inboxes.`;
  }
  if (Number(releaseReadinessSummary.value?.pinned_rollout_count || 0) > 0) {
    return `${releaseReadinessSummary.value.pinned_rollout_count} pinned rollout${releaseReadinessSummary.value.pinned_rollout_count === 1 ? '' : 's'} currently override specific inboxes even though percentage-based split traffic is not active.`;
  }
  if (releaseReadinessSummary.value?.latest_batch_baseline?.title) {
    return `${releaseReadinessSummary.value.latest_batch_baseline.title} is the latest durable batch baseline${releaseReadinessSummary.value.latest_batch_baseline.ran_at ? ` • ${formatDateTime(releaseReadinessSummary.value.latest_batch_baseline.ran_at)}` : ''}.`;
  }
  if (publishedVersionId.value) {
    return `Live version ID ${publishedVersionId.value} is the current release baseline for these repair decisions.`;
  }
  return 'No published version id is loaded yet, so release review is still missing a stable live baseline.';
});
const connectedInboxes = computed(
  () => store.getters['ailabInboxes/getRecords'] || []
);
const inboxById = computed(() => store.getters['inboxes/getInbox']);
const runtimeStatus = computed(() => aiCloserConfig.value?.status || 'inactive');
const assistantReplyMode = computed(
  () => props.assistant?.config?.wlab_ai_reply_mode || 'auto_reply'
);
const citationsEnabled = computed(() => {
  const raw = props.assistant?.config?.feature_citation;
  return raw === true || raw === 'true' || raw === 1 || raw === '1';
});

const resolveInboxName = id => {
  const num = Number(id);
  if (!Number.isFinite(num)) return '';
  const inbox = inboxById.value?.(num) || {};
  return inbox?.name || String(num);
};
const resolveOverrideTopInboxLabel = row => {
  const inboxId = Number(row?.topInboxId);
  if (!Number.isFinite(inboxId) || inboxId <= 0) return '';
  return resolveInboxName(inboxId) || `Inbox ${inboxId}`;
};
const resolveOverrideBranchTopInboxLabel = row => {
  const inboxId = Number(row?.topInboxId);
  if (!Number.isFinite(inboxId) || inboxId <= 0) return '';
  return resolveInboxName(inboxId) || `Inbox ${inboxId}`;
};
const normalizeOverrideOutcome = value => {
  const normalized = String(value || '').trim().toLowerCase();
  return ['resolved', 'escalated', 'pending'].includes(normalized) ? normalized : '';
};
const formatOverrideOutcomeLabel = value => {
  switch (normalizeOverrideOutcome(value)) {
    case 'resolved':
      return 'resolved';
    case 'escalated':
      return 'escalated';
    case 'pending':
      return 'pending';
    default:
      return 'all outcomes';
  }
};
const routeOverrideDrilldown = computed(() =>
  parseAttributeOverrideDrilldownQuery(route.query)
);
const routeAnalyzeSeed = computed(() => {
  const inboxId = String(route.query?.analyze_inbox_id || '').trim();
  const contentGapReason = String(route.query?.analyze_content_gap_reason || '').trim();
  const articleScopeWarning = String(route.query?.analyze_article_scope_warning || '').trim();
  const deliveryMode = String(route.query?.analyze_delivery_mode || '').trim();
  const timeWindow = String(route.query?.analyze_time_window || '').trim();
  const source = String(route.query?.analyze_source || '').trim();
  const enabled =
    String(route.query?.analyze_seed || '').trim() === '1' ||
    [inboxId, contentGapReason, articleScopeWarning, deliveryMode, timeWindow, source].some(Boolean);

  return {
    enabled,
    inboxId,
    contentGapReason,
    articleScopeWarning,
    deliveryMode,
    timeWindow,
    source,
  };
});
const routeAnalyzeOwnerFocus = computed(() =>
  String(route.query?.analyze_owner_focus || '').trim()
);
const analyzeSeedSummary = computed(() => {
  if (!routeAnalyzeSeed.value.enabled) return '';

  const parts = [];
  if (routeAnalyzeSeed.value.source) parts.push(`from ${routeAnalyzeSeed.value.source}`);
  if (routeAnalyzeSeed.value.inboxId) {
    parts.push(resolveInboxName(routeAnalyzeSeed.value.inboxId) || `Inbox ${routeAnalyzeSeed.value.inboxId}`);
  }
  if (routeAnalyzeSeed.value.contentGapReason) parts.push(`gap ${routeAnalyzeSeed.value.contentGapReason}`);
  if (routeAnalyzeSeed.value.articleScopeWarning) parts.push('scope warning');
  if (routeAnalyzeSeed.value.deliveryMode) parts.push(`delivery ${routeAnalyzeSeed.value.deliveryMode}`);
  if (routeAnalyzeSeed.value.timeWindow) parts.push(routeAnalyzeSeed.value.timeWindow);
  return parts.join(' • ');
});
const overrideDrilldownSummary = computed(() => {
  if (!routeOverrideDrilldown.value.enabled) return '';

  const parts = [];
  if (routeOverrideDrilldown.value.fixIntentTitle) {
    parts.push(`fix ${routeOverrideDrilldown.value.fixIntentTitle}`);
  }
  if (routeOverrideDrilldown.value.compareStageTitle) {
    parts.push(`stage ${routeOverrideDrilldown.value.compareStageTitle}`);
  }
  if (routeOverrideDrilldown.value.ruleIndex) {
    parts.push(`rule #${routeOverrideDrilldown.value.ruleIndex}`);
  }
  if (routeOverrideDrilldown.value.label) {
    parts.push(routeOverrideDrilldown.value.label);
  }
  if (routeOverrideDrilldown.value.inboxId) {
    parts.push(resolveInboxName(routeOverrideDrilldown.value.inboxId));
  }
  if (routeOverrideDrilldown.value.conversationId) {
    parts.push(`conversation ${routeOverrideDrilldown.value.conversationId}`);
  }
  if (routeOverrideDrilldown.value.outcome) {
    parts.push(formatOverrideOutcomeLabel(routeOverrideDrilldown.value.outcome));
  }
  if (routeOverrideDrilldown.value.condition) {
    parts.push(routeOverrideDrilldown.value.condition);
  }
  if (routeOverrideDrilldown.value.guard) {
    parts.push(`guard ${routeOverrideDrilldown.value.guard}`);
  }
  return parts.filter(Boolean).join(' • ');
});
const syncAnalyzeSeedFilters = seed => {
  if (seed.enabled) {
    showAdvancedDiagnostics.value = true;
    filters.value.decision_type_like = 'response.trace';
    filters.value.inbox_id = seed.inboxId;
    filters.value.content_gap_reason = seed.contentGapReason;
    filters.value.article_scope_warning = seed.articleScopeWarning;
    filters.value.delivery_mode = seed.deliveryMode;
    if (seed.timeWindow) filters.value.time_window = seed.timeWindow;
    hasAppliedAnalyzeSeed.value = true;
    return;
  }

  if (!hasAppliedAnalyzeSeed.value) return;

  filters.value.inbox_id = '';
  filters.value.content_gap_reason = '';
  filters.value.article_scope_warning = '';
  filters.value.delivery_mode = '';
  hasAppliedAnalyzeSeed.value = false;
};
const selectedOverrideOutcome = computed(() =>
  normalizeOverrideOutcome(filters.value.attribute_override_outcome)
);
const syncOverrideDrilldownFilters = drilldown => {
  if (drilldown.enabled) {
    showAdvancedDiagnostics.value = true;
    filters.value.decision_type_like = 'response.trace';
    filters.value.inbox_id = drilldown.inboxId;
    filters.value.conversation_id = drilldown.conversationId;
    filters.value.attribute_override_rule_index = drilldown.ruleIndex;
    filters.value.attribute_override_label = drilldown.label;
    filters.value.attribute_override_action = drilldown.action;
    filters.value.attribute_override_match_type = drilldown.matchType;
    filters.value.attribute_override_outcome = drilldown.outcome;
    filters.value.attribute_override_condition = drilldown.condition;
    filters.value.attribute_override_guard = drilldown.guard;
    if (drilldown.timeWindow) filters.value.time_window = drilldown.timeWindow;
    hasAppliedOverrideDrilldown.value = true;
    return;
  }

  if (!hasAppliedOverrideDrilldown.value) return;

  filters.value.inbox_id = '';
  filters.value.conversation_id = '';
  filters.value.attribute_override_rule_index = '';
  filters.value.attribute_override_label = '';
  filters.value.attribute_override_action = '';
  filters.value.attribute_override_match_type = '';
  filters.value.attribute_override_outcome = '';
  filters.value.attribute_override_condition = '';
  filters.value.attribute_override_guard = '';
  hasAppliedOverrideDrilldown.value = false;
};
const analyzeSectionRefForKey = key => {
  if (key === 'operating-summary') return operatingSummarySectionRef;
  if (key === 'release-readiness') return releaseReadinessSectionRef;
  if (key === 'learning-loop') return learningLoopSectionRef;
  if (key === 'repair-queue') return repairTeamQueueSectionRef;
  if (key === 'repair-board') return repairBoardSectionRef;
  if (key === 'optimization-center') return optimizationCenterSectionRef;
  if (key === 'diagnostics') return traceSectionRef;
  return null;
};
const analyzeVisibleSectionKeys = computed(() =>
  [
    'operating-summary',
    'repair-queue',
    'repair-board',
    'optimization-center',
    showAdvancedDiagnostics.value ? 'diagnostics' : '',
  ].filter(Boolean)
);
const scrollToAnalyzeSectionRef = sectionRef => {
  sectionRef?.value?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
};
const syncActiveAnalyzeSection = () => {
  if (typeof window === 'undefined') return;

  const threshold = 220;
  const sectionRows = analyzeVisibleSectionKeys.value
    .map(key => {
      const sectionRef = analyzeSectionRefForKey(key);
      const top = sectionRef?.value?.getBoundingClientRect?.().top;
      return {
        key,
        top: Number.isFinite(top) ? top : null,
      };
    })
    .filter(row => row.top !== null);

  if (!sectionRows.length) return;

  const passedRows = sectionRows.filter(row => row.top <= threshold);
  if (passedRows.length) {
    activeAnalyzeSectionKey.value = passedRows[passedRows.length - 1].key;
    return;
  }

  activeAnalyzeSectionKey.value = [...sectionRows].sort(
    (a, b) => Math.abs(a.top - threshold) - Math.abs(b.top - threshold)
  )[0].key;
};
const scheduleAnalyzeSectionSync = () => {
  if (typeof window === 'undefined') return;
  if (analyzeSectionSyncFrame) {
    window.cancelAnimationFrame(analyzeSectionSyncFrame);
  }
  analyzeSectionSyncFrame = window.requestAnimationFrame(() => {
    analyzeSectionSyncFrame = 0;
    syncActiveAnalyzeSection();
  });
};
const scrollToTraceSection = () => {
  scrollToAnalyzeSectionRef(traceSectionRef);
};
const jumpToAnalyzeSection = async key => {
  if (key === 'diagnostics' && !showAdvancedDiagnostics.value) {
    showAdvancedDiagnostics.value = true;
    await nextTick();
  }
  activeAnalyzeSectionKey.value = key;
  scrollToAnalyzeSectionRef(analyzeSectionRefForKey(key));
  scheduleAnalyzeSectionSync();
};
const openOverrideDrilldown = async options => {
  const nextQuery = {
    ...buildAttributeOverrideDrilldownQuery(route.query, {
      timeWindow: filters.value.time_window,
      ...options,
    }),
    tab: 'analyze_decision_log',
  };
  const unchanged = ATTRIBUTE_OVERRIDE_DRILLDOWN_QUERY_KEYS.every(
    key => String(nextQuery[key] || '') === String(route.query?.[key] || '')
  );

  await router.replace({ query: nextQuery });
  if (unchanged && String(route.query?.tab || '') === 'analyze_decision_log') {
    syncOverrideDrilldownFilters(routeOverrideDrilldown.value);
    await refreshAll();
    scrollToTraceSection();
  }
};
const clearOverrideDrilldown = async () => {
  const nextQuery = {
    ...clearAttributeOverrideDrilldownQuery(route.query),
    tab: 'analyze_decision_log',
  };
  const unchanged = ATTRIBUTE_OVERRIDE_DRILLDOWN_QUERY_KEYS.every(
    key => String(nextQuery[key] || '') === String(route.query?.[key] || '')
  );

  await router.replace({ query: nextQuery });
  if (unchanged && String(route.query?.tab || '') === 'analyze_decision_log') {
    syncOverrideDrilldownFilters(routeOverrideDrilldown.value);
    await refreshAll();
  }
};
const clearAnalyzeSeed = async () => {
  const nextQuery = { ...(route.query || {}), tab: 'analyze_decision_log' };
  ANALYZE_SEED_QUERY_KEYS.forEach(key => {
    delete nextQuery[key];
  });
  ANALYZE_FOLLOWUP_QUERY_KEYS.forEach(key => {
    delete nextQuery[key];
  });

  await router.replace({ query: nextQuery });
};
const clearAnalyzeWorkingContext = async () => {
  const nextQuery = { ...(route.query || {}), tab: 'analyze_decision_log' };
  [
    ...ANALYZE_SEED_QUERY_KEYS,
    ...ANALYZE_FOLLOWUP_QUERY_KEYS,
    ...ANALYZE_OWNER_FOCUS_QUERY_KEYS,
    ...ATTRIBUTE_OVERRIDE_DRILLDOWN_QUERY_KEYS,
  ].forEach(key => {
    delete nextQuery[key];
  });

  await router.replace({ query: nextQuery });
};
async function openAnalyzeContextAction(card) {
  if (!card?.actionKind) return;

  if (card.actionKind === 'toggle_diagnostics') {
    toggleAdvancedDiagnostics();
    return;
  }
  if (card.actionKind === 'clear_owner_focus') {
    await clearRepairOwnerFocus();
    return;
  }
  if (card.actionKind === 'clear_seed') {
    await clearAnalyzeSeed();
    return;
  }
  if (card.actionKind === 'clear_override') {
    await clearOverrideDrilldown();
  }
}
const inspectOverrideRule = row =>
  openOverrideDrilldown({
    ruleIndex: row?.ruleIndex,
    label: row?.label,
    action: row?.action,
    matchType: row?.matchType,
  });
const inspectOverrideEscalations = row =>
  openOverrideDrilldown({
    ruleIndex: row?.ruleIndex,
    label: row?.label,
    action: row?.action,
    matchType: row?.matchType,
    outcome: 'escalated',
  });
const inspectOverridePending = row =>
  openOverrideDrilldown({
    ruleIndex: row?.ruleIndex,
    label: row?.label,
    action: row?.action,
    matchType: row?.matchType,
    outcome: 'pending',
  });
const inspectOverrideCondition = row =>
  openOverrideDrilldown({
    ruleIndex: row?.ruleIndex,
    label: row?.ruleLabel,
    action: row?.action,
    matchType: row?.matchType,
    condition: row?.label,
  });
const inspectOverrideConditionTopInbox = row =>
  openOverrideDrilldown({
    ruleIndex: row?.ruleIndex,
    label: row?.ruleLabel,
    action: row?.action,
    matchType: row?.matchType,
    condition: row?.label,
    inboxId: row?.topInboxId,
  });
const inspectOverrideConditionLatestCase = row =>
  openOverrideDrilldown({
    ruleIndex: row?.ruleIndex,
    label: row?.ruleLabel,
    action: row?.action,
    matchType: row?.matchType,
    condition: row?.label,
    inboxId: row?.latestInboxId,
    conversationId: row?.latestConversationId,
    outcome: row?.latestOutcome,
  });
const inspectOverrideConditionResolvedCase = row =>
  openOverrideDrilldown({
    ruleIndex: row?.ruleIndex,
    label: row?.ruleLabel,
    action: row?.action,
    matchType: row?.matchType,
    condition: row?.label,
    inboxId: row?.latestResolvedInboxId,
    conversationId: row?.latestResolvedConversationId,
    outcome: 'resolved',
  });
const inspectOverrideConditionEscalatedCase = row =>
  openOverrideDrilldown({
    ruleIndex: row?.ruleIndex,
    label: row?.ruleLabel,
    action: row?.action,
    matchType: row?.matchType,
    condition: row?.label,
    inboxId: row?.latestEscalatedInboxId,
    conversationId: row?.latestEscalatedConversationId,
    outcome: 'escalated',
  });
const inspectOverrideGuard = row =>
  openOverrideDrilldown({
    ruleIndex: row?.ruleIndex,
    label: row?.ruleLabel,
    action: row?.action,
    matchType: row?.matchType,
    guard: row?.label,
  });
const inspectOverrideGuardTopInbox = row =>
  openOverrideDrilldown({
    ruleIndex: row?.ruleIndex,
    label: row?.ruleLabel,
    action: row?.action,
    matchType: row?.matchType,
    guard: row?.label,
    inboxId: row?.topInboxId,
  });
const inspectOverrideGuardLatestCase = row =>
  openOverrideDrilldown({
    ruleIndex: row?.ruleIndex,
    label: row?.ruleLabel,
    action: row?.action,
    matchType: row?.matchType,
    guard: row?.label,
    inboxId: row?.latestInboxId,
    conversationId: row?.latestConversationId,
    outcome: row?.latestOutcome,
  });
const inspectOverrideGuardResolvedCase = row =>
  openOverrideDrilldown({
    ruleIndex: row?.ruleIndex,
    label: row?.ruleLabel,
    action: row?.action,
    matchType: row?.matchType,
    guard: row?.label,
    inboxId: row?.latestResolvedInboxId,
    conversationId: row?.latestResolvedConversationId,
    outcome: 'resolved',
  });
const inspectOverrideGuardEscalatedCase = row =>
  openOverrideDrilldown({
    ruleIndex: row?.ruleIndex,
    label: row?.ruleLabel,
    action: row?.action,
    matchType: row?.matchType,
    guard: row?.label,
    inboxId: row?.latestEscalatedInboxId,
    conversationId: row?.latestEscalatedConversationId,
    outcome: 'escalated',
  });
const inspectOverrideHotspot = row =>
  openOverrideDrilldown({
    ruleIndex: row?.ruleIndex,
    label: row?.label,
    action: row?.action,
    matchType: row?.matchType,
    outcome: selectedOverrideOutcome.value || 'escalated',
    inboxId: row?.inboxId,
  });
const inspectOverrideCase = row =>
  openOverrideDrilldown({
    ruleIndex: row?.ruleIndex,
    label: row?.label,
    action: row?.action,
    matchType: row?.matchType,
    outcome: row?.outcome,
    inboxId: row?.inboxId,
    conversationId: row?.conversationId,
  });
const overrideRecommendationDrilldownOutcome = row => {
  switch (String(row?.recommendationCode || '').trim()) {
    case 'split_by_inbox':
    case 'reduce_escalation_pressure':
      return 'escalated';
    case 'investigate_pending_cases':
    case 'tighten_for_resolution':
      return 'pending';
    default:
      return '';
  }
};
const inspectOverrideRecommendation = row =>
  openOverrideDrilldown({
    ruleIndex: row?.ruleIndex,
    label: row?.label,
    action: row?.action,
    matchType: row?.matchType,
    outcome: overrideRecommendationDrilldownOutcome(row),
  });
const inspectOverrideBranchRecommendation = row =>
  openOverrideDrilldown({
    ruleIndex: row?.ruleIndex,
    label: row?.ruleLabel,
    action: row?.action,
    matchType: row?.matchType,
    outcome:
      row?.fixPresetDrilldownOutcome || overrideRecommendationDrilldownOutcome(row),
    inboxId: row?.fixPresetDrilldownInboxId,
    fixIntentCode: row?.fixIntentCode,
    fixIntentTitle: row?.fixIntentTitle,
    condition:
      String(row?.branchType || '').trim() === 'matched_condition' ? row?.label : '',
    guard:
      String(row?.branchType || '').trim() === 'passed_guard' ? row?.label : '',
  });
const inspectOverrideBranchCompare = row =>
  openOverrideDrilldown({
    ruleIndex: row?.ruleIndex,
    label: row?.ruleLabel,
    action: row?.action,
    matchType: row?.matchType,
    condition:
      String(row?.branchType || '').trim() === 'matched_condition' ? row?.label : '',
    guard:
      String(row?.branchType || '').trim() === 'passed_guard' ? row?.label : '',
  });
const buildOverrideBranchDrilldownOptions = row => ({
  ruleIndex: row?.ruleIndex,
  label: row?.ruleLabel || routeOverrideDrilldown.value.label,
  action: row?.action || routeOverrideDrilldown.value.action,
  matchType: row?.matchType || routeOverrideDrilldown.value.matchType,
  fixIntentCode: row?.fixIntentCode || routeOverrideDrilldown.value.fixIntentCode,
  fixIntentTitle: row?.fixIntentTitle || routeOverrideDrilldown.value.fixIntentTitle,
  compareStageCode: routeOverrideDrilldown.value.compareStageCode,
  compareStageTitle: routeOverrideDrilldown.value.compareStageTitle,
  condition:
    routeOverrideDrilldown.value.condition ||
    (String(row?.branchType || '').trim() === 'matched_condition' ? row?.label : ''),
  guard:
    routeOverrideDrilldown.value.guard ||
    (String(row?.branchType || '').trim() === 'passed_guard' ? row?.label : ''),
});
const repairBundleActionStageMetadata = (row, actionKey) => {
  switch (String(actionKey || '').trim()) {
    case 'hotspot':
      return row?.repairBundleHotspotStageCode
        ? {
            compareStageCode: row?.repairBundleHotspotStageCode,
            compareStageTitle: row?.repairBundleHotspotStageTitle,
            compareStageEvidence: row?.repairBundleHotspotStageEvidence,
            compareStageFocus: row?.repairBundleHotspotStageFocus,
            compareStageDecision: row?.repairBundleHotspotStageDecision,
            compareStageRepairMove: row?.repairBundleHotspotStageRepairMove,
            compareStageWatchpoint: row?.repairBundleHotspotStageWatchpoint,
            compareStageExecutionBrief:
              row?.repairBundleHotspotStageExecutionBrief,
            compareStageValidationFocus:
              row?.repairBundleHotspotStageValidationFocus,
            compareStageOwnerPacketTitle:
              row?.repairBundleHotspotStageOwnerPacketTitle,
            compareStageOwnerPacketBody:
              row?.repairBundleHotspotStageOwnerPacketBody,
            compareStageOwnerRole: row?.repairBundleHotspotStageOwnerRole,
            compareStageHandoffTarget:
              row?.repairBundleHotspotStageHandoffTarget,
            compareStageSuccessCheck:
              row?.repairBundleHotspotStageSuccessCheck,
            compareStageLedgerStatus:
              row?.repairBundleHotspotStageLedgerStatus,
            compareStageLastAction: row?.repairBundleHotspotStageLastAction,
            compareStageReopenRule: row?.repairBundleHotspotStageReopenRule,
            compareStageNextOwner: row?.repairBundleHotspotStageNextOwner,
            compareStageBlockedReason:
              row?.repairBundleHotspotStageBlockedReason,
            compareStageCompletionGate:
              row?.repairBundleHotspotStageCompletionGate,
            compareStageDoneState: row?.repairBundleHotspotStageDoneState,
            compareStageFailState: row?.repairBundleHotspotStageFailState,
            compareStageRollbackTrigger:
              row?.repairBundleHotspotStageRollbackTrigger,
            nextStageCode: row?.repairBundleHotspotNextStageCode,
            nextStageTitle: row?.repairBundleHotspotNextStageTitle,
          }
        : null;
    case 'resolved':
      return row?.repairBundleResolvedStageCode
        ? {
            compareStageCode: row?.repairBundleResolvedStageCode,
            compareStageTitle: row?.repairBundleResolvedStageTitle,
            compareStageEvidence: row?.repairBundleResolvedStageEvidence,
            compareStageFocus: row?.repairBundleResolvedStageFocus,
            compareStageDecision: row?.repairBundleResolvedStageDecision,
            compareStageRepairMove: row?.repairBundleResolvedStageRepairMove,
            compareStageWatchpoint: row?.repairBundleResolvedStageWatchpoint,
            compareStageExecutionBrief:
              row?.repairBundleResolvedStageExecutionBrief,
            compareStageValidationFocus:
              row?.repairBundleResolvedStageValidationFocus,
            compareStageOwnerPacketTitle:
              row?.repairBundleResolvedStageOwnerPacketTitle,
            compareStageOwnerPacketBody:
              row?.repairBundleResolvedStageOwnerPacketBody,
            compareStageOwnerRole: row?.repairBundleResolvedStageOwnerRole,
            compareStageHandoffTarget:
              row?.repairBundleResolvedStageHandoffTarget,
            compareStageSuccessCheck:
              row?.repairBundleResolvedStageSuccessCheck,
            compareStageLedgerStatus:
              row?.repairBundleResolvedStageLedgerStatus,
            compareStageLastAction: row?.repairBundleResolvedStageLastAction,
            compareStageReopenRule: row?.repairBundleResolvedStageReopenRule,
            compareStageNextOwner: row?.repairBundleResolvedStageNextOwner,
            compareStageBlockedReason:
              row?.repairBundleResolvedStageBlockedReason,
            compareStageCompletionGate:
              row?.repairBundleResolvedStageCompletionGate,
            compareStageDoneState: row?.repairBundleResolvedStageDoneState,
            compareStageFailState: row?.repairBundleResolvedStageFailState,
            compareStageRollbackTrigger:
              row?.repairBundleResolvedStageRollbackTrigger,
            nextStageCode: row?.repairBundleResolvedNextStageCode,
            nextStageTitle: row?.repairBundleResolvedNextStageTitle,
          }
        : null;
    case 'escalated':
      return row?.repairBundleEscalatedStageCode
        ? {
            compareStageCode: row?.repairBundleEscalatedStageCode,
            compareStageTitle: row?.repairBundleEscalatedStageTitle,
            compareStageEvidence: row?.repairBundleEscalatedStageEvidence,
            compareStageFocus: row?.repairBundleEscalatedStageFocus,
            compareStageDecision: row?.repairBundleEscalatedStageDecision,
            compareStageRepairMove: row?.repairBundleEscalatedStageRepairMove,
            compareStageWatchpoint: row?.repairBundleEscalatedStageWatchpoint,
            compareStageExecutionBrief:
              row?.repairBundleEscalatedStageExecutionBrief,
            compareStageValidationFocus:
              row?.repairBundleEscalatedStageValidationFocus,
            compareStageOwnerPacketTitle:
              row?.repairBundleEscalatedStageOwnerPacketTitle,
            compareStageOwnerPacketBody:
              row?.repairBundleEscalatedStageOwnerPacketBody,
            compareStageOwnerRole: row?.repairBundleEscalatedStageOwnerRole,
            compareStageHandoffTarget:
              row?.repairBundleEscalatedStageHandoffTarget,
            compareStageSuccessCheck:
              row?.repairBundleEscalatedStageSuccessCheck,
            compareStageLedgerStatus:
              row?.repairBundleEscalatedStageLedgerStatus,
            compareStageLastAction: row?.repairBundleEscalatedStageLastAction,
            compareStageReopenRule: row?.repairBundleEscalatedStageReopenRule,
            compareStageNextOwner: row?.repairBundleEscalatedStageNextOwner,
            compareStageBlockedReason:
              row?.repairBundleEscalatedStageBlockedReason,
            compareStageCompletionGate:
              row?.repairBundleEscalatedStageCompletionGate,
            compareStageDoneState: row?.repairBundleEscalatedStageDoneState,
            compareStageFailState: row?.repairBundleEscalatedStageFailState,
            compareStageRollbackTrigger:
              row?.repairBundleEscalatedStageRollbackTrigger,
            nextStageCode: row?.repairBundleEscalatedNextStageCode,
            nextStageTitle: row?.repairBundleEscalatedNextStageTitle,
          }
        : null;
    case 'validation':
      return row?.repairBundleValidationStageCode
        ? {
            compareStageCode: row?.repairBundleValidationStageCode,
            compareStageTitle: row?.repairBundleValidationStageTitle,
            compareStageEvidence: row?.repairBundleValidationStageEvidence,
            compareStageFocus: row?.repairBundleValidationStageFocus,
            compareStageDecision: row?.repairBundleValidationStageDecision,
            compareStageRepairMove: row?.repairBundleValidationStageRepairMove,
            compareStageWatchpoint: row?.repairBundleValidationStageWatchpoint,
            compareStageExecutionBrief:
              row?.repairBundleValidationStageExecutionBrief,
            compareStageValidationFocus:
              row?.repairBundleValidationStageValidationFocus,
            compareStageOwnerPacketTitle:
              row?.repairBundleValidationStageOwnerPacketTitle,
            compareStageOwnerPacketBody:
              row?.repairBundleValidationStageOwnerPacketBody,
            compareStageOwnerRole: row?.repairBundleValidationStageOwnerRole,
            compareStageHandoffTarget:
              row?.repairBundleValidationStageHandoffTarget,
            compareStageSuccessCheck:
              row?.repairBundleValidationStageSuccessCheck,
            compareStageLedgerStatus:
              row?.repairBundleValidationStageLedgerStatus,
            compareStageLastAction: row?.repairBundleValidationStageLastAction,
            compareStageReopenRule: row?.repairBundleValidationStageReopenRule,
            compareStageNextOwner: row?.repairBundleValidationStageNextOwner,
            compareStageBlockedReason:
              row?.repairBundleValidationStageBlockedReason,
            compareStageCompletionGate:
              row?.repairBundleValidationStageCompletionGate,
            compareStageDoneState: row?.repairBundleValidationStageDoneState,
            compareStageFailState: row?.repairBundleValidationStageFailState,
            compareStageRollbackTrigger:
              row?.repairBundleValidationStageRollbackTrigger,
            nextStageCode: row?.repairBundleValidationNextStageCode,
            nextStageTitle: row?.repairBundleValidationNextStageTitle,
          }
        : null;
    default:
      return null;
  }
};
const repairBundleActionDrilldownOptions = (row, actionKey) => {
  switch (String(actionKey || '').trim()) {
    case 'hotspot':
      return row?.repairBundleHotspotActionInboxId
        ? { inboxId: row.repairBundleHotspotActionInboxId }
        : null;
    case 'resolved':
      return row?.repairBundleResolvedActionTitle
        ? {
            inboxId: row?.repairBundleResolvedActionInboxId,
            conversationId: row?.repairBundleResolvedActionConversationId,
            outcome: row?.repairBundleResolvedActionOutcome,
          }
        : null;
    case 'escalated':
      return row?.repairBundleEscalatedActionTitle
        ? {
            inboxId: row?.repairBundleEscalatedActionInboxId,
            conversationId: row?.repairBundleEscalatedActionConversationId,
            outcome: row?.repairBundleEscalatedActionOutcome,
          }
        : null;
    case 'validation':
      return row?.repairBundleValidationActionTitle
        ? {
            inboxId: row?.repairBundleValidationActionInboxId,
            outcome: row?.repairBundleValidationActionOutcome,
          }
        : null;
    default:
      return null;
  }
};
const isActiveOverrideFixRecommendationStage = (row, actionKey) => {
  const stageMetadata = repairBundleActionStageMetadata(row, actionKey);
  if (!stageMetadata?.compareStageCode) return false;
  return (
    stageMetadata.compareStageCode === routeOverrideDrilldown.value.compareStageCode
  );
};
const repairBundleActionKeyForStageCode = stageCode => {
  switch (String(stageCode || '').trim()) {
    case 'hotspot_inbox':
      return 'hotspot';
    case 'resolved_anchor':
      return 'resolved';
    case 'escalated_anchor':
      return 'escalated';
    case 'validation_slice':
      return 'validation';
    default:
      return '';
  }
};
const inspectActiveOverrideFixRecommendationAction = (row, actionKey) => {
  const actionOptions = repairBundleActionDrilldownOptions(row, actionKey);
  if (!actionOptions) return null;
  return openOverrideDrilldown({
    ...buildOverrideBranchDrilldownOptions(row),
    ...repairBundleActionStageMetadata(row, actionKey),
    ...actionOptions,
  });
};

const latestTrace = computed(() => traceItems.value[0] || null);
const latestTracePayload = computed(() => latestTrace.value?.payload || {});
const handoffLikeCount = computed(
  () =>
    traceItems.value.filter(item => {
      const payload = item?.payload || {};
      return (
        payload?.source_kind === 'handoff' ||
        payload?.intent_risk_action === 'handoff' ||
        payload?.response_preview === 'conversation_handoff'
      );
    }).length
);

const traceSummaryCards = computed(() => [
  {
    label: 'Replies checked',
    value: summary.value.trace_total,
    note: `${timeWindowLabel.value} reply activity reviewed`,
  },
  {
    label: 'Needs review',
    value: summary.value.open_failures,
    note: 'Missed answers still waiting for review',
  },
  {
    label: 'Reviewed',
    value: summary.value.reviewed_failures,
    note: 'Already looked at by the team',
  },
  {
    label: 'Ready as fixes',
    value: summary.value.converted_failures,
    note: 'Misses already turned into candidate fixes',
  },
  {
    label: 'Human handoffs',
    value: handoffLikeCount.value,
    note: 'Recent replies that still needed a person',
  },
]);

const createdAfterIso = computed(() => {
  const now = new Date();
  const next = new Date(now);
  switch (filters.value.time_window) {
    case '24h':
      next.setHours(now.getHours() - 24);
      return next.toISOString();
    case '7d':
      next.setDate(now.getDate() - 7);
      return next.toISOString();
    case '30d':
      next.setDate(now.getDate() - 30);
      return next.toISOString();
    default:
      return '';
  }
});

const timeWindowLabel = computed(() => {
  switch (filters.value.time_window) {
    case '24h':
      return 'Last 24 hours';
    case '30d':
      return 'Last 30 days';
    case 'all':
      return 'All time';
    default:
      return 'Last 7 days';
  }
});
const retrievalSnapshotWeeks = computed(() => {
  switch (filters.value.time_window) {
    case '30d':
      return 4;
    case '24h':
      return 1;
    default:
      return 1;
  }
});
const comparisonWindowDurationMs = computed(() => {
  switch (filters.value.time_window) {
    case '24h':
      return 24 * 60 * 60 * 1000;
    case '30d':
      return 30 * 24 * 60 * 60 * 1000;
    case 'all':
      return 0;
    default:
      return 7 * 24 * 60 * 60 * 1000;
  }
});
const comparisonWindowLabel = computed(() => {
  switch (filters.value.time_window) {
    case '24h':
      return 'previous 24 hours';
    case '30d':
      return 'previous 30 days';
    case 'all':
      return '';
    default:
      return 'previous 7 days';
  }
});
const comparisonWindowRange = computed(() => {
  if (!createdAfterIso.value || !comparisonWindowDurationMs.value) return null;
  const currentStart = new Date(createdAfterIso.value);
  if (Number.isNaN(currentStart.getTime())) return null;

  return {
    createdAfter: new Date(
      currentStart.getTime() - comparisonWindowDurationMs.value
    ).toISOString(),
    createdBefore: currentStart.toISOString(),
    label: comparisonWindowLabel.value,
  };
});
const formatCountDelta = delta =>
  delta === 0 ? 'flat' : `${delta > 0 ? '+' : ''}${delta}`;
const formatDeltaDirection = (delta, label) => {
  if (!label) return 'No earlier comparison window is available.';
  if (delta === 0) return `Flat versus ${label}.`;
  return `${delta > 0 ? 'Up' : 'Down'} ${Math.abs(delta)} versus ${label}.`;
};

const operationalTrendLimit = computed(() => {
  switch (filters.value.time_window) {
    case '24h':
      return 2;
    case '30d':
      return 14;
    case 'all':
      return 14;
    default:
      return 7;
  }
});

const buildTopCounts = (items, getter, limit = 5) => {
  const counts = new Map();
  items.forEach(item => {
    const raw = getter(item);
    const key = raw === undefined || raw === null ? '' : String(raw).trim();
    if (!key) return;
    counts.set(key, (counts.get(key) || 0) + 1);
  });
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, limit)
    .map(([label, count]) => ({ label, count }));
};

const buildExpandedTopCounts = (items, getter, limit = 5) => {
  const counts = new Map();
  items.forEach(item => {
    const values = Array.isArray(getter(item)) ? getter(item) : [];
    values.forEach(value => {
      const key = value === undefined || value === null ? '' : String(value).trim();
      if (!key) return;
      counts.set(key, (counts.get(key) || 0) + 1);
    });
  });
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, limit)
    .map(([label, count]) => ({ label, count }));
};
const traceOverrideMatchedConditionKeys = item => {
  const payload = item?.payload || {};
  if (Array.isArray(payload?.attribute_override_matched_condition_keys)) {
    return payload.attribute_override_matched_condition_keys;
  }
  if (
    Array.isArray(
      payload?.escalation_refs?.attribute_override_matched_condition_keys
    )
  ) {
    return payload.escalation_refs.attribute_override_matched_condition_keys;
  }
  return [];
};
const traceOverrideMatchedConditionSummaries = item => {
  const payload = item?.payload || {};
  if (Array.isArray(payload?.attribute_override_matched_condition_summaries)) {
    return payload.attribute_override_matched_condition_summaries;
  }
  if (
    Array.isArray(
      payload?.escalation_refs?.attribute_override_matched_condition_summaries
    )
  ) {
    return payload.escalation_refs
      .attribute_override_matched_condition_summaries;
  }
  return [];
};
const traceOverridePassedGuardKeys = item => {
  const payload = item?.payload || {};
  if (Array.isArray(payload?.attribute_override_passed_guard_keys)) {
    return payload.attribute_override_passed_guard_keys;
  }
  if (
    Array.isArray(payload?.escalation_refs?.attribute_override_passed_guard_keys)
  ) {
    return payload.escalation_refs.attribute_override_passed_guard_keys;
  }
  return [];
};
const traceOverridePassedGuardSummaries = item => {
  const payload = item?.payload || {};
  if (Array.isArray(payload?.attribute_override_passed_guard_summaries)) {
    return payload.attribute_override_passed_guard_summaries;
  }
  if (
    Array.isArray(
      payload?.escalation_refs?.attribute_override_passed_guard_summaries
    )
  ) {
    return payload.escalation_refs.attribute_override_passed_guard_summaries;
  }
  return [];
};

const buildDailySeries = (items, getter, limit = 7) => {
  const counts = new Map();
  items.forEach(item => {
    const raw = getter(item);
    const date = raw ? new Date(raw) : null;
    if (!date || Number.isNaN(date.getTime())) return;
    const key = date.toISOString().slice(0, 10);
    counts.set(key, (counts.get(key) || 0) + 1);
  });
  return [...counts.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .slice(-limit)
    .map(([label, count]) => ({ label, count }));
};

const formatPercent = value => `${Math.round((Number(value) || 0) * 100)}%`;
const timestampMs = value => {
  if (!value) return 0;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
};
const formatDateTime = value => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';
  return parsed.toLocaleString();
};
const formatDurationSeconds = value => {
  const totalSeconds = Number(value);
  if (!Number.isFinite(totalSeconds) || totalSeconds < 0) return '';
  if (totalSeconds < 60) return `${totalSeconds}s`;

  const totalMinutes = Math.round(totalSeconds / 60);
  if (totalMinutes < 60) return `${totalMinutes}m`;

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
};
const formatOverrideOutcomeDetail = row => {
  const outcome = String(row?.outcome || 'pending').trim() || 'pending';
  if (!row?.outcomeAt) return `${outcome} • no final outcome yet`;

  const durationLabel = formatDurationSeconds(row?.timeToOutcomeSeconds);
  return `${outcome} at ${formatDateTime(row?.outcomeAt)}${durationLabel ? ` • ${durationLabel}` : ''}`;
};
const formatOverrideCaseSummary = row => {
  const inboxLabel = resolveInboxName(row?.inboxId) || `Inbox ${row?.inboxId}`;
  return `${inboxLabel} • ${formatDateTime(row?.occurredAt)} • ${formatOverrideOutcomeDetail(row)}`;
};
const formatOverrideBranchLatestCaseSummary = row => {
  const inboxId = Number(row?.latestInboxId);
  const inboxLabel = resolveInboxName(inboxId) || `Inbox ${inboxId}`;
  const outcome = String(row?.latestOutcome || 'pending').trim() || 'pending';
  if (!row?.latestOccurredAt) return '';
  if (!row?.latestOutcomeAt) {
    return `${inboxLabel} ${outcome} since ${formatDateTime(row?.latestOccurredAt)}`;
  }

  const durationLabel = formatDurationSeconds(row?.latestTimeToOutcomeSeconds);
  return `${inboxLabel} ${outcome} ${formatDateTime(row?.latestOccurredAt)} -> ${formatDateTime(row?.latestOutcomeAt)}${durationLabel ? ` (${durationLabel})` : ''}`;
};
const formatOverrideBranchComparedCaseSummary = (row, outcome) => {
  const normalizedOutcome = String(outcome || '').trim();
  const isResolved = normalizedOutcome === 'resolved';
  const inboxId = Number(
    isResolved ? row?.latestResolvedInboxId : row?.latestEscalatedInboxId
  );
  const inboxLabel = resolveInboxName(inboxId) || `Inbox ${inboxId}`;
  const occurredAt = isResolved
    ? row?.latestResolvedOccurredAt
    : row?.latestEscalatedOccurredAt;
  const outcomeAt = isResolved
    ? row?.latestResolvedOutcomeAt
    : row?.latestEscalatedOutcomeAt;
  const timeToOutcomeSeconds = isResolved
    ? row?.latestResolvedTimeToOutcomeSeconds
    : row?.latestEscalatedTimeToOutcomeSeconds;
  if (!occurredAt) return '';
  if (!outcomeAt) {
    return `${inboxLabel} ${normalizedOutcome} since ${formatDateTime(occurredAt)}`;
  }

  const durationLabel = formatDurationSeconds(timeToOutcomeSeconds);
  return `${inboxLabel} ${normalizedOutcome} ${formatDateTime(occurredAt)} -> ${formatDateTime(outcomeAt)}${durationLabel ? ` (${durationLabel})` : ''}`;
};
const outcomeCountForRow = row => {
  switch (selectedOverrideOutcome.value) {
    case 'resolved':
      return Number(row?.resolvedCount) || 0;
    case 'escalated':
      return Number(row?.escalatedCount) || 0;
    case 'pending':
      return Number(row?.unresolvedCount) || 0;
    default:
      return Number(row?.usedCount) || 0;
  }
};
const filteredOverrideMetricRows = rows => {
  const outcome = selectedOverrideOutcome.value;
  if (!outcome) return rows;

  return rows.filter(row => {
    switch (outcome) {
      case 'resolved':
        return (row?.resolvedCount || 0) > 0;
      case 'escalated':
        return (row?.escalatedCount || 0) > 0;
      case 'pending':
        return (row?.unresolvedCount || 0) > 0;
      default:
        return true;
    }
  });
};

const traceSourceBreakdown = computed(() =>
  buildTopCounts(traceSampleItems.value, item => item?.payload?.source_kind)
);
const traceReasonBreakdown = computed(() =>
  buildTopCounts(traceSampleItems.value, item => item?.payload?.reason)
);
const traceIntentBreakdown = computed(() =>
  buildTopCounts(traceSampleItems.value, item => item?.payload?.intent)
);
const traceActionBreakdown = computed(() =>
  buildTopCounts(
    traceSampleItems.value,
    item => item?.payload?.intent_risk_action || item?.payload?.action
  )
);
const responseKindBreakdown = computed(() =>
  buildTopCounts(traceSampleItems.value, item => item?.payload?.response_kind)
);
const traceStatusBreakdown = computed(() =>
  buildTopCounts(traceSampleItems.value, item => item?.payload?.status)
);
const traceDeliveryBreakdown = computed(() =>
  buildTopCounts(traceSampleItems.value, item => item?.payload?.delivery_mode)
);
const guidanceSectionBreakdown = computed(() =>
  buildExpandedTopCounts(
    traceSampleItems.value,
    item => item?.payload?.guidance_used_sections
  )
);
const traceGuidanceCardBreakdown = computed(() =>
  buildExpandedTopCounts(
    traceSampleItems.value,
    item => item?.payload?.guidance_card_keys
  )
);
const traceGuidanceScenarioBreakdown = computed(() =>
  buildExpandedTopCounts(
    traceSampleItems.value,
    item =>
      Array.isArray(item?.payload?.guidance_scenarios)
        ? item.payload.guidance_scenarios.map(scenario => scenario?.title || scenario?.key).filter(Boolean)
        : item?.payload?.guidance_scenario_keys
  )
);
const traceForcedLanguageBreakdown = computed(() =>
  buildTopCounts(traceSampleItems.value, item => item?.payload?.forced_reply_language)
);
const traceCommunicationStyleBreakdown = computed(() =>
  buildTopCounts(traceSampleItems.value, item => item?.payload?.communication_style_mode)
);
const traceAnswerLengthStrategyBreakdown = computed(() =>
  buildTopCounts(traceSampleItems.value, item => item?.payload?.answer_length_strategy)
);
const traceDirectnessStrategyBreakdown = computed(() =>
  buildTopCounts(traceSampleItems.value, item => item?.payload?.directness_strategy)
);
const traceSensitiveTopicsStrategyBreakdown = computed(() =>
  buildTopCounts(traceSampleItems.value, item => item?.payload?.sensitive_topics_strategy)
);
const traceToneEnforcementStrategyBreakdown = computed(() =>
  buildTopCounts(traceSampleItems.value, item => item?.payload?.tone_enforcement_strategy)
);
const traceMemoryDepthStrategyBreakdown = computed(() =>
  buildTopCounts(traceSampleItems.value, item => item?.payload?.memory_depth_strategy)
);
const traceAnswerConfidenceStrategyBreakdown = computed(() =>
  buildTopCounts(traceSampleItems.value, item => item?.payload?.answer_confidence_strategy)
);
const traceOutsideKnowledgeStrategyBreakdown = computed(() =>
  buildTopCounts(traceSampleItems.value, item => item?.payload?.outside_knowledge_strategy)
);
const tracePromiseSafetyStrategyBreakdown = computed(() =>
  buildTopCounts(traceSampleItems.value, item => item?.payload?.promise_safety_strategy)
);
const traceUncertainActionBreakdown = computed(() =>
  buildTopCounts(traceSampleItems.value, item => item?.payload?.uncertain_action)
);
const traceHighRiskActionBreakdown = computed(() =>
  buildTopCounts(traceSampleItems.value, item => item?.payload?.high_risk_action)
);
const retrievalTopSourceBreakdown = computed(
  () => retrievalTopPriorityBreakdownRows.value
);
const retrievalWinningSourceBreakdown = computed(
  () => retrievalWinningSourceBreakdownRows.value
);
const retrievalSearchBalanceBreakdown = computed(
  () => retrievalSearchBalanceBreakdownRows.value
);
const retrievalCitationModeBreakdown = computed(
  () => retrievalCitationModeBreakdownRows.value
);
const retrievalSegmentBindingBreakdown = computed(
  () => retrievalSegmentBindingBreakdownRows.value
);
const retrievalFallbackBreakdown = computed(
  () => retrievalFallbackBreakdownRows.value
);
const retrievalFocusedTraceCount = computed(() => {
  const winningTotal = retrievalWinningSourceBreakdown.value.reduce(
    (sum, row) => sum + Number(row?.count || 0),
    0
  );
  if (winningTotal > 0) return winningTotal;
  return retrievalTopSourceBreakdown.value.reduce(
    (sum, row) => sum + Number(row?.count || 0),
    0
  );
});
const retrievalStrategyRecommendationRows = computed(() =>
  strategyRecommendationRows.value
    .filter(row => String(row?.strategyGroup || '').startsWith('retrieval_'))
    .slice(0, 4)
);
const retrievalStrategyRepairRows = computed(() =>
  buildStrategyRepairBoardRows(
    retrievalStrategyRecommendationRows.value,
    strategyRecommendationRepairStatusFor
  )
);
const retrievalRepairSummary = computed(() => ({
  blocked: retrievalStrategyRepairRows.value.filter(row => row.repairLevel === 'blocked')
    .length,
  observe: retrievalStrategyRepairRows.value.filter(row => row.repairLevel === 'observe')
    .length,
  ready: retrievalStrategyRepairRows.value.filter(row => row.repairLevel === 'ready').length,
  unproven: retrievalStrategyRepairRows.value.filter(row => row.repairLevel === 'unproven')
    .length,
}));
const retrievalRollupCards = computed(() => [
  {
    label: 'Traces in focus',
    value: retrievalFocusedTraceCount.value,
    note: 'Server-side traces in the current Analyze window after the retrieval slice filters below.',
  },
  {
    label: 'Top retrieval source',
    value: retrievalTopSourceBreakdown.value[0]?.label || '—',
    note: retrievalTopSourceBreakdown.value[0]
      ? `${retrievalTopSourceBreakdown.value[0].count} traces currently keep this source at the top of retrieval priority.`
      : 'No retrieval top-priority data is available yet.',
  },
  {
    label: 'Search balance',
    value: retrievalSearchBalanceBreakdown.value[0]?.label || '—',
    note: retrievalSearchBalanceBreakdown.value[0]
      ? `${retrievalSearchBalanceBreakdown.value[0].count} traces currently use this retrieval balance most often.`
      : 'No retrieval search-balance data is available yet.',
  },
  {
    label: 'Winning source',
    value: retrievalWinningSourceBreakdown.value[0]?.label || '—',
    note: retrievalWinningSourceBreakdown.value[0]
      ? `${retrievalWinningSourceBreakdown.value[0].count} traces currently let this source win the final retrieval mix.`
      : 'No cross-source winning-source data is available yet.',
  },
]);
const topAttributeOverrideConditionOutcomeRows = computed(() =>
  [...attributeOverrideConditionOutcomeRows.value]
    .sort(
      (a, b) =>
        (b.escalatedCount || 0) - (a.escalatedCount || 0) ||
        (b.unresolvedCount || 0) - (a.unresolvedCount || 0) ||
        (b.usedCount || 0) - (a.usedCount || 0) ||
        (a.ruleIndex || 0) - (b.ruleIndex || 0) ||
        (a.label || '').localeCompare(b.label || '')
    )
    .slice(0, 6)
);
const topAttributeOverrideGuardOutcomeRows = computed(() =>
  [...attributeOverrideGuardOutcomeRows.value]
    .sort(
      (a, b) =>
        (b.escalatedCount || 0) - (a.escalatedCount || 0) ||
        (b.unresolvedCount || 0) - (a.unresolvedCount || 0) ||
        (b.usedCount || 0) - (a.usedCount || 0) ||
        (a.ruleIndex || 0) - (b.ruleIndex || 0) ||
        (a.label || '').localeCompare(b.label || '')
    )
    .slice(0, 6)
);
const attributeOverrideConditionRecommendationByKey = computed(() =>
  attributeOverrideConditionRecommendationRows.value.reduce((acc, row) => {
    acc[row.key] = row;
    return acc;
  }, {})
);
const attributeOverrideGuardRecommendationByKey = computed(() =>
  attributeOverrideGuardRecommendationRows.value.reduce((acc, row) => {
    acc[row.key] = row;
    return acc;
  }, {})
);
const activeOverrideFixRecommendation = computed(() => {
  const drilldown = routeOverrideDrilldown.value;
  if (!drilldown.enabled || !drilldown.fixIntentCode) return null;

  const ruleIndex = Number(drilldown.ruleIndex);
  const branchLabel = String(drilldown.condition || drilldown.guard || '').trim();
  const rows = drilldown.condition
    ? attributeOverrideConditionRecommendationRows.value
    : drilldown.guard
      ? attributeOverrideGuardRecommendationRows.value
      : [];

  return (
    rows.find(
      row =>
        row.ruleIndex === ruleIndex &&
        row.label === branchLabel &&
        row.fixIntentCode === drilldown.fixIntentCode
    ) ||
    rows.find(row => row.ruleIndex === ruleIndex && row.label === branchLabel) ||
    null
  );
});
const activeOverrideCompareStageTitle = computed(
  () => routeOverrideDrilldown.value.compareStageTitle || ''
);
const activeOverrideCompareStageGuide = computed(() => {
  const row = activeOverrideFixRecommendation.value;
  const stageCode = routeOverrideDrilldown.value.compareStageCode;
  if (!row || !stageCode) return null;

  return (
    ['hotspot', 'resolved', 'escalated', 'validation']
      .map(actionKey => repairBundleActionStageMetadata(row, actionKey))
      .find(entry => entry?.compareStageCode === stageCode) || null
  );
});
const overrideConditionRecommendationForRow = row =>
  attributeOverrideConditionRecommendationByKey.value?.[String(row?.key || '').trim()] ||
  null;
const overrideGuardRecommendationForRow = row =>
  attributeOverrideGuardRecommendationByKey.value?.[String(row?.key || '').trim()] || null;
const buildGuidanceOutcomeBreakdown = (items, field, limit = 5) =>
  [...items]
    .map(item => ({
      label: item?.label || '',
      count: Number(item?.[field]) || 0,
      usedCount: Number(item?.usedCount) || 0,
    }))
    .filter(item => item.label && item.count > 0)
    .sort((a, b) => b.count - a.count || b.usedCount - a.usedCount || a.label.localeCompare(b.label))
    .slice(0, limit);
const resolvedGuidanceCardBreakdown = computed(() =>
  buildGuidanceOutcomeBreakdown(guidanceCardOutcomeBreakdown.value, 'resolvedCount')
);
const escalatedGuidanceCardBreakdown = computed(() =>
  buildGuidanceOutcomeBreakdown(guidanceCardOutcomeBreakdown.value, 'escalatedCount')
);
const guidanceScenarioTitleMap = computed(() =>
  [...traceItems.value, ...traceSampleItems.value].reduce((acc, item) => {
    const scenarios = Array.isArray(item?.payload?.guidance_scenarios)
      ? item.payload.guidance_scenarios
      : [];
    scenarios.forEach(scenario => {
      const key = String(scenario?.key || '').trim();
      const title = String(scenario?.title || '').trim();
      if (key && title && !acc[key]) acc[key] = title;
    });
    return acc;
  }, {})
);
const resolveGuidanceScenarioLabel = key => {
  const normalizedKey = String(key || '').trim();
  if (!normalizedKey) return '';
  if (guidanceScenarioTitleMap.value?.[normalizedKey]) {
    return guidanceScenarioTitleMap.value[normalizedKey];
  }
  const legacyMatch = normalizedKey.match(/^(?:legacy:)?(.+):\d+$/);
  if (legacyMatch?.[1]) {
    return legacyMatch[1]
      .split('-')
      .filter(Boolean)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }
  return normalizedKey;
};
const resolvedGuidanceScenarioBreakdown = computed(() =>
  buildGuidanceOutcomeBreakdown(guidanceScenarioOutcomeBreakdown.value, 'resolvedCount').map(item => ({
    ...item,
    label: resolveGuidanceScenarioLabel(item.key || item.label),
  }))
);
const escalatedGuidanceScenarioBreakdown = computed(() =>
  buildGuidanceOutcomeBreakdown(guidanceScenarioOutcomeBreakdown.value, 'escalatedCount').map(item => ({
    ...item,
    label: resolveGuidanceScenarioLabel(item.key || item.label),
  }))
);
const topStrategyOutcomeRows = (items, limit = 4) =>
  [...items]
    .filter(row => (row?.usedCount || 0) > 0)
    .sort(
      (a, b) =>
        (b.usedCount || 0) - (a.usedCount || 0) ||
        (b.escalatedCount || 0) - (a.escalatedCount || 0) ||
        (a.label || '').localeCompare(b.label || '')
    )
    .slice(0, limit);
const highestEscalationStrategyRow = (items, minUsed = 2) =>
  [...items]
    .filter(row => (row?.usedCount || 0) >= minUsed && (row?.escalatedCount || 0) > 0)
    .sort(
      (a, b) =>
        (b.escalatedRate || 0) - (a.escalatedRate || 0) ||
        (b.escalatedCount || 0) - (a.escalatedCount || 0) ||
        (b.usedCount || 0) - (a.usedCount || 0) ||
        (a.label || '').localeCompare(b.label || '')
    )[0] || null;
const lowestResolutionStrategyRow = (items, minUsed = 2) =>
  [...items]
    .filter(row => (row?.usedCount || 0) >= minUsed)
    .sort(
      (a, b) =>
        (a.resolvedRate || 0) - (b.resolvedRate || 0) ||
        (b.unresolvedRate || 0) - (a.unresolvedRate || 0) ||
        (b.usedCount || 0) - (a.usedCount || 0) ||
        (a.label || '').localeCompare(b.label || '')
    )[0] || null;
const topAnswerLengthOutcomeRows = computed(() =>
  topStrategyOutcomeRows(answerLengthOutcomeBreakdown.value)
);
const topDirectnessOutcomeRows = computed(() =>
  topStrategyOutcomeRows(directnessOutcomeBreakdown.value)
);
const topSensitiveTopicsOutcomeRows = computed(() =>
  topStrategyOutcomeRows(sensitiveTopicsOutcomeBreakdown.value)
);
const topToneEnforcementOutcomeRows = computed(() =>
  topStrategyOutcomeRows(toneEnforcementOutcomeBreakdown.value)
);
const topMemoryDepthOutcomeRows = computed(() =>
  topStrategyOutcomeRows(memoryDepthOutcomeBreakdown.value)
);
const topAnswerConfidenceOutcomeRows = computed(() =>
  topStrategyOutcomeRows(answerConfidenceOutcomeBreakdown.value)
);
const topOutsideKnowledgeOutcomeRows = computed(() =>
  topStrategyOutcomeRows(outsideKnowledgeOutcomeBreakdown.value)
);
const topPromiseSafetyOutcomeRows = computed(() =>
  topStrategyOutcomeRows(promiseSafetyOutcomeBreakdown.value)
);
const topUncertainActionOutcomeRows = computed(() =>
  topStrategyOutcomeRows(uncertainActionOutcomeBreakdown.value)
);
const topHighRiskActionOutcomeRows = computed(() =>
  topStrategyOutcomeRows(highRiskActionOutcomeBreakdown.value)
);
const topProcedureRoutingPrecedenceOutcomeRows = computed(() =>
  topStrategyOutcomeRows(procedureRoutingPrecedenceOutcomeBreakdown.value)
);
const topProcedureStepConfirmationOutcomeRows = computed(() =>
  topStrategyOutcomeRows(procedureStepConfirmationOutcomeBreakdown.value)
);
const topProcedureFallbackActionOutcomeRows = computed(() =>
  topStrategyOutcomeRows(procedureFallbackActionOutcomeBreakdown.value)
);
const topChannelRuntimeDefaultReplyModeOutcomeRows = computed(() =>
  topStrategyOutcomeRows(channelRuntimeDefaultReplyModeOutcomeBreakdown.value)
);
const topChannelRuntimeBindingScopeOutcomeRows = computed(() =>
  topStrategyOutcomeRows(channelRuntimeBindingScopeOutcomeBreakdown.value)
);
const topChannelRuntimeActiveSegmentTypeOutcomeRows = computed(() =>
  topStrategyOutcomeRows(channelRuntimeActiveSegmentTypeOutcomeBreakdown.value)
);
const topBusinessHoursReplyModeOutcomeRows = computed(() =>
  topStrategyOutcomeRows(businessHoursReplyModeOutcomeBreakdown.value)
);
const topBusinessHoursResponseBehaviorOutcomeRows = computed(() =>
  topStrategyOutcomeRows(businessHoursResponseBehaviorOutcomeBreakdown.value)
);
const topRegionComplianceBindingScopeOutcomeRows = computed(() =>
  topStrategyOutcomeRows(regionComplianceBindingScopeOutcomeBreakdown.value)
);
const topRegionComplianceActiveCountryCodeOutcomeRows = computed(() =>
  topStrategyOutcomeRows(regionComplianceActiveCountryCodeOutcomeBreakdown.value)
);
const topRegionComplianceActiveProfileOutcomeRows = computed(() =>
  topStrategyOutcomeRows(regionComplianceActiveProfileOutcomeBreakdown.value)
);
const highestEscalationOutsideKnowledgeRow = computed(() =>
  highestEscalationStrategyRow(outsideKnowledgeOutcomeBreakdown.value)
);
const highestEscalationPromiseSafetyRow = computed(() =>
  highestEscalationStrategyRow(promiseSafetyOutcomeBreakdown.value)
);
const highestEscalationUncertainActionRow = computed(() =>
  highestEscalationStrategyRow(uncertainActionOutcomeBreakdown.value)
);
const highestEscalationHighRiskActionRow = computed(() =>
  highestEscalationStrategyRow(highRiskActionOutcomeBreakdown.value)
);
const lowestResolutionDirectnessRow = computed(() =>
  lowestResolutionStrategyRow(directnessOutcomeBreakdown.value)
);
const lowestResolutionAnswerLengthRow = computed(() =>
  lowestResolutionStrategyRow(answerLengthOutcomeBreakdown.value)
);
const lowestResolutionSensitiveTopicsRow = computed(() =>
  lowestResolutionStrategyRow(sensitiveTopicsOutcomeBreakdown.value)
);
const lowestResolutionToneEnforcementRow = computed(() =>
  lowestResolutionStrategyRow(toneEnforcementOutcomeBreakdown.value)
);
const lowestResolutionMemoryDepthRow = computed(() =>
  lowestResolutionStrategyRow(memoryDepthOutcomeBreakdown.value)
);
const lowestResolutionAnswerConfidenceRow = computed(() =>
  lowestResolutionStrategyRow(answerConfidenceOutcomeBreakdown.value)
);
const topStrategyRecommendation = computed(() => focusedRepairBoardRows.value[0] || null);
const strategyRecommendationConfidenceByKey = computed(() =>
  strategyRecommendationRows.value.reduce((acc, row) => {
    acc[strategyRecommendationConfidenceKey(row)] =
      strategyRecommendationConfidenceFor(row);
    return acc;
  }, {})
);
const strategyRecommendationConfidenceForRow = row =>
  strategyRecommendationConfidenceByKey.value?.[
    strategyRecommendationConfidenceKey(row)
  ] || null;
const strategyRecommendationRepairStatusByKey = computed(() =>
  strategyRecommendationRows.value.reduce((acc, row) => {
    acc[strategyRecommendationConfidenceKey(row)] =
      strategyRecommendationRepairStatusFor(row);
    return acc;
  }, {})
);
const strategyRecommendationRepairStatusForRow = row =>
  strategyRecommendationRepairStatusByKey.value?.[
    strategyRecommendationConfidenceKey(row)
  ] || null;
const topStrategyRecommendationRepairStatus = computed(() =>
  topStrategyRecommendation.value
    ? {
        level: topStrategyRecommendation.value.repairLevel,
        runsCount: topStrategyRecommendation.value.runsCount,
        latestVerdict: topStrategyRecommendation.value.latestVerdict,
      }
    : null
);
const repairStatusPriority = level => {
  switch (String(level || '').trim()) {
    case 'blocked':
      return 4;
    case 'observe':
      return 3;
    case 'unproven':
      return 2;
    case 'ready':
      return 1;
    default:
      return 0;
  }
};
const buildStrategyRepairBoardRows = (rows, repairStatusGetter) =>
  rows
    .map(row => {
      const repairStatus = repairStatusGetter(row);
      const testAction = strategyRecommendationAction(row);
      const deployAction = strategyRecommendationDeployAction(row);
      return {
        ...row,
        repairLevel: repairStatus?.level || 'unproven',
        repairTone: repairStatus?.tone || 'info',
        repairTitle: repairStatus?.title || '',
        repairBody: repairStatus?.body || '',
        repairNextStep: repairStatus?.nextStep || '',
        latestVerdict: repairStatus?.latestVerdict || 'pending',
        runsCount: repairStatus?.runsCount || 0,
        deployReady: Boolean(repairStatus?.deployReady),
        releaseStatus: repairStatus?.releaseStatus || '',
        releaseStatusLabel: repairStatus?.releaseStatusLabel || '',
        releaseReviewedAt: repairStatus?.releaseReviewedAt || '',
        releaseNote: repairStatus?.releaseNote || '',
        releaseRollbackTrigger: repairStatus?.releaseRollbackTrigger || '',
        batchBaselineLevel: repairStatus?.batchBaselineLevel || '',
        batchBaselineTitle: repairStatus?.batchBaselineTitle || '',
        batchBaselineRanAt: repairStatus?.batchBaselineRanAt || '',
        batchBaselineTotalCases: repairStatus?.batchBaselineTotalCases || 0,
        batchBaselineTopSignal: repairStatus?.batchBaselineTopSignal || '',
        testAction,
        deployAction,
        primaryAction: deployAction || testAction,
      };
    })
    .sort(
      (a, b) =>
        repairStatusPriority(b.repairLevel) - repairStatusPriority(a.repairLevel) ||
        (b.escalatedCount || 0) - (a.escalatedCount || 0) ||
        (b.usedCount || 0) - (a.usedCount || 0) ||
        (a.label || '').localeCompare(b.label || '')
    );
const strategyRepairBoardRows = computed(() =>
  buildStrategyRepairBoardRows(
    strategyRecommendationRows.value,
    strategyRecommendationRepairStatusForRow
  )
);
const previousStrategyRepairBoardRows = computed(() =>
  buildStrategyRepairBoardRows(
    previousStrategyRecommendationRows.value,
    strategyRecommendationRepairStatusFor
  )
);
const blockedRepairBoardRows = computed(() =>
  strategyRepairBoardRows.value.filter(row => row.repairLevel === 'blocked').slice(0, 4)
);
const observeRepairBoardRows = computed(() =>
  strategyRepairBoardRows.value.filter(row => row.repairLevel === 'observe').slice(0, 4)
);
const readyRepairBoardRows = computed(() =>
  strategyRepairBoardRows.value.filter(row => row.repairLevel === 'ready').slice(0, 4)
);
const unprovenRepairBoardRows = computed(() =>
  strategyRepairBoardRows.value.filter(row => row.repairLevel === 'unproven').slice(0, 4)
);
const repairOwnerLabel = row => {
  if (row?.deployReady) return 'Deploy review';
  const workspace = String(
    row?.repairBundleHotspotActionWorkspace || row?.targetWorkspace || ''
  ).trim();
  switch (workspace) {
    case 'content':
      return 'Content';
    case 'guidance':
      return 'Guidance';
    case 'escalation':
      return 'Escalation';
    case 'deploy':
      return 'Deploy';
    default:
      return 'Repair owner';
  }
};
const repairPriorityLane = row => {
  if (row?.deployReady) return 'ready_review';
  if (row?.repairLevel === 'blocked') return 'fix_now';
  return 'validate_next';
};
const repairPriorityScore = row => {
  const levelWeight =
    row?.repairLevel === 'blocked'
      ? 400
      : row?.repairLevel === 'observe'
        ? 260
        : row?.repairLevel === 'unproven'
          ? 220
          : 120;
  const severityWeight =
    row?.severity === 'danger' ? 80 : row?.severity === 'warning' ? 40 : 10;
  return (
    levelWeight +
    severityWeight +
    (Number(row?.escalatedCount) || 0) * 3 +
    (Number(row?.usedCount) || 0) +
    (Number(row?.runsCount) || 0)
  );
};
const buildRepairTeamQueueRows = rows =>
  rows
    .map(row => ({
      ...row,
      ownerLabel: repairOwnerLabel(row),
      priorityLane: repairPriorityLane(row),
      priorityScore: repairPriorityScore(row),
    }))
    .sort(
      (a, b) =>
        (b.priorityScore || 0) - (a.priorityScore || 0) ||
        (b.escalatedCount || 0) - (a.escalatedCount || 0) ||
        (b.usedCount || 0) - (a.usedCount || 0) ||
        (a.label || '').localeCompare(b.label || '')
    );
const repairTeamQueueRows = computed(() =>
  buildRepairTeamQueueRows(strategyRepairBoardRows.value)
);
const previousRepairTeamQueueRows = computed(() =>
  buildRepairTeamQueueRows(previousStrategyRepairBoardRows.value)
);
const availableRepairOwnerFocusLabels = computed(() =>
  [...new Set(repairTeamQueueRows.value.map(row => String(row?.ownerLabel || '').trim()))]
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b))
);
const activeRepairOwnerFocus = computed(() =>
  String(repairOwnerFocus.value || '').trim()
);
const repairTeamQueueCount = lane =>
  repairTeamQueueRows.value.filter(row => row.priorityLane === lane).length;
const fixNowRepairRows = computed(() =>
  repairTeamQueueRows.value.filter(row => row.priorityLane === 'fix_now').slice(0, 4)
);
const validateNextRepairRows = computed(() =>
  repairTeamQueueRows.value
    .filter(row => row.priorityLane === 'validate_next')
    .slice(0, 4)
);
const readyForReviewRepairRows = computed(() =>
  repairTeamQueueRows.value
    .filter(row => row.priorityLane === 'ready_review')
    .slice(0, 4)
);
const ownerLoadCountsForRows = rows =>
  rows.reduce((acc, row) => {
    const owner = String(row?.ownerLabel || repairOwnerLabel(row)).trim();
    acc[owner] = (acc[owner] || 0) + 1;
    return acc;
  }, {});
const currentOwnerLoadCounts = computed(() =>
  ownerLoadCountsForRows(repairTeamQueueRows.value)
);
const previousOwnerLoadCounts = computed(() =>
  ownerLoadCountsForRows(previousRepairTeamQueueRows.value)
);
const repairOwnerLoadCards = computed(() => {
  const counts = currentOwnerLoadCounts.value;
  return [
    {
      label: 'Guidance load',
      value: counts.Guidance || 0,
      note: 'Repairs whose next meaningful owner is Guidance.',
    },
    {
      label: 'Escalation load',
      value: counts.Escalation || 0,
      note: 'Repairs whose next meaningful owner is Escalation.',
    },
    {
      label: 'Deploy review load',
      value: counts['Deploy review'] || 0,
      note: 'Repairs already strong enough for release review.',
    },
  ];
});
const focusedRepairTeamQueueRows = computed(() =>
  activeRepairOwnerFocus.value
    ? repairTeamQueueRows.value.filter(
        row => row.ownerLabel === activeRepairOwnerFocus.value
      )
    : repairTeamQueueRows.value
);
const focusedRepairTeamQueueCount = lane =>
  focusedRepairTeamQueueRows.value.filter(
    row => row.priorityLane === lane
  ).length;
const focusedFixNowRepairRows = computed(() =>
  focusedRepairTeamQueueRows.value
    .filter(row => row.priorityLane === 'fix_now')
    .slice(0, 4)
);
const focusedValidateNextRepairRows = computed(() =>
  focusedRepairTeamQueueRows.value
    .filter(row => row.priorityLane === 'validate_next')
    .slice(0, 4)
);
const focusedReadyForReviewRepairRows = computed(() =>
  focusedRepairTeamQueueRows.value
    .filter(row => row.priorityLane === 'ready_review')
    .slice(0, 4)
);
const focusedRepairTeamQueueSections = computed(() => [
  {
    key: 'fix_now',
    title: 'Fix now',
    note: 'Highest-risk repairs that still look too close to escalation.',
    rows: focusedFixNowRepairRows.value,
    emptyNote: 'No repairs need immediate fixing right now.',
  },
  {
    key: 'validate_next',
    title: 'Validate next',
    note: 'Repairs that need another clean proof point before review.',
    rows: focusedValidateNextRepairRows.value,
    emptyNote: 'No repairs are waiting for another validation pass.',
  },
  {
    key: 'ready_review',
    title: 'Ready for review',
    note: 'Validated repairs that can move into deploy review now.',
    rows: focusedReadyForReviewRepairRows.value,
    emptyNote: 'No repairs are ready for deploy review yet.',
  },
]);
const focusedRepairTeamQueueSummaryCards = computed(() => [
  {
    label: 'Fix now',
    value: focusedRepairTeamQueueCount('fix_now'),
    note: 'Most urgent repairs to address first.',
  },
  {
    label: 'Validate next',
    value: focusedRepairTeamQueueCount('validate_next'),
    note: 'Repairs that need another test bundle pass.',
  },
  {
    label: 'Ready for review',
    value: focusedRepairTeamQueueCount('ready_review'),
    note: 'Repairs that can move into Deploy review.',
  },
]);
const focusedRepairOwnerLoadCards = computed(() => {
  const counts = ownerLoadCountsForRows(focusedRepairTeamQueueRows.value);
  return [
    {
      label: 'Guidance load',
      value: counts.Guidance || 0,
      note: 'Repairs whose next meaningful owner is Guidance.',
    },
    {
      label: 'Escalation load',
      value: counts.Escalation || 0,
      note: 'Repairs whose next meaningful owner is Escalation.',
    },
    {
      label: 'Deploy review load',
      value: counts['Deploy review'] || 0,
      note: 'Repairs already strong enough for release review.',
    },
  ];
});
const topOwnerLoadCard = computed(
  () => [...repairOwnerLoadCards.value]
    .sort(
      (a, b) =>
        (Number(b?.value) || 0) - (Number(a?.value) || 0) ||
        String(a?.label || '').localeCompare(String(b?.label || ''))
    )[0] || null
);
const repairRowsForOwnerLabel = (
  ownerLabel,
  rows = repairTeamQueueRows.value
) =>
  rows.filter(row => row.ownerLabel === ownerLabel);
const ownerBacklogTrendRows = computed(() =>
  ['Guidance', 'Escalation', 'Deploy review']
    .map(ownerLabel => {
      const currentCount = currentOwnerLoadCounts.value[ownerLabel] || 0;
      const previousCount = comparisonWindowRange.value
        ? previousOwnerLoadCounts.value[ownerLabel] || 0
        : 0;
      const delta = currentCount - previousCount;
      const sampleRow =
        repairRowsForOwnerLabel(ownerLabel)[0] ||
        repairRowsForOwnerLabel(
          ownerLabel,
          previousRepairTeamQueueRows.value
        )[0] ||
        null;
      return {
        ownerLabel,
        currentCount,
        previousCount,
        delta,
        deltaLabel: comparisonWindowRange.value
          ? formatCountDelta(delta)
          : 'n/a',
        note: comparisonWindowRange.value
          ? `${currentCount} now vs ${previousCount} in ${comparisonWindowRange.value.label}. ${formatDeltaDirection(delta, comparisonWindowRange.value.label)}`
          : 'Showing current owner load only. Switch away from All time to see a real change.',
        actionPayload: sampleRow?.primaryAction || null,
        testPayload: sampleRow?.testAction || null,
        sampleLabel: sampleRow?.label || '',
      };
    })
    .sort(
      (a, b) =>
        Math.abs(b.delta) - Math.abs(a.delta) ||
        b.currentCount - a.currentCount ||
        a.ownerLabel.localeCompare(b.ownerLabel)
    )
);
const topOwnerLoadTrendRow = computed(() => {
  const topOwnerLabel = String(topOwnerLoadCard.value?.label || '')
    .replace(' load', '')
    .trim();
  return (
    ownerBacklogTrendRows.value.find(row => row.ownerLabel === topOwnerLabel) ||
    null
  );
});
const operatingSummaryCards = computed(() => [
  {
    label: 'Active repairs',
    value: strategyRepairBoardRows.value.length,
    note: 'Current strategy repairs visible in this analyze window.',
  },
  {
    label: 'Fix now',
    value: repairTeamQueueCount('fix_now'),
    note: 'Highest-risk repairs that still need direct intervention.',
  },
  {
    label: 'Ready for review',
    value: repairTeamQueueCount('ready_review'),
    note: 'Repairs that can move into deploy review right now.',
  },
  {
    label: 'Top owner backlog',
    value: topOwnerLoadCard.value?.label?.replace(' load', '') || 'Calm',
    note:
      topOwnerLoadCard.value?.value > 0
        ? `${topOwnerLoadCard.value.value} repair${topOwnerLoadCard.value.value === 1 ? '' : 's'} currently point here next${topOwnerLoadTrendRow.value && comparisonWindowRange.value ? `, ${topOwnerLoadTrendRow.value.deltaLabel} versus ${comparisonWindowRange.value.label}` : '.'}`
        : 'No owner backlog is currently building.',
  },
]);
const operatingUrgentRows = computed(() =>
  repairTeamQueueRows.value
    .filter(row => row.priorityLane !== 'ready_review')
    .slice(0, 3)
);
const operatingReviewRows = computed(() =>
  repairTeamQueueRows.value
    .filter(row => row.priorityLane === 'ready_review')
    .slice(0, 3)
);
const operatingOwnerBacklogRows = computed(() =>
  ownerBacklogTrendRows.value.slice(0, 3)
);
const focusedRepairBoardRows = computed(() =>
  activeRepairOwnerFocus.value
    ? strategyRepairBoardRows.value.filter(
        row => repairOwnerLabel(row) === activeRepairOwnerFocus.value
      )
    : strategyRepairBoardRows.value
);
const focusedRepairBoardCount = level =>
  focusedRepairBoardRows.value.filter(row => row.repairLevel === level).length;
const focusedBlockedRepairBoardRows = computed(() =>
  focusedRepairBoardRows.value
    .filter(row => row.repairLevel === 'blocked')
    .slice(0, 4)
);
const focusedObserveRepairBoardRows = computed(() =>
  focusedRepairBoardRows.value
    .filter(row => row.repairLevel === 'observe')
    .slice(0, 4)
);
const focusedReadyRepairBoardRows = computed(() =>
  focusedRepairBoardRows.value
    .filter(row => row.repairLevel === 'ready')
    .slice(0, 4)
);
const focusedUnprovenRepairBoardRows = computed(() =>
  focusedRepairBoardRows.value
    .filter(row => row.repairLevel === 'unproven')
    .slice(0, 4)
);
const focusedRepairBoardSections = computed(() => [
  {
    key: 'blocked',
    title: 'Blocked',
    note: 'Fix these before trusting any release review.',
    rows: focusedBlockedRepairBoardRows.value,
    emptyNote: 'No blocked repairs right now.',
  },
  {
    key: 'observe',
    title: 'Observe',
    note: 'Improving, but still needs another clean proof point.',
    rows: focusedObserveRepairBoardRows.value,
    emptyNote: 'No repairs are currently in observe state.',
  },
  {
    key: 'ready',
    title: 'Ready for deploy',
    note: 'These have enough validation confidence for release review.',
    rows: focusedReadyRepairBoardRows.value,
    emptyNote: 'No repairs are ready for deploy review yet.',
  },
  {
    key: 'unproven',
    title: 'Unproven',
    note: 'These still need a first meaningful validation cycle.',
    rows: focusedUnprovenRepairBoardRows.value,
    emptyNote: 'No unproven repairs in this window.',
  },
]);
const focusedRepairBoardSummaryCards = computed(() => [
  {
    label: 'Blocked repairs',
    value: focusedRepairBoardCount('blocked'),
    note: 'Still too close to escalated behavior and should not move forward yet.',
  },
  {
    label: 'Observe',
    value: focusedRepairBoardCount('observe'),
    note: 'Showing partial improvement but not enough repeated proof yet.',
  },
  {
    label: 'Ready for deploy',
    value: focusedRepairBoardCount('ready'),
    note: 'Validation confidence is strong enough for release review.',
  },
  {
    label: 'Unproven',
    value: focusedRepairBoardCount('unproven'),
    note: 'No meaningful repeated validation history exists yet.',
  },
]);
const focusedReleaseReviewCounts = computed(() =>
  focusedRepairBoardRows.value.reduce(
    (acc, row) => {
      if (row?.releaseStatus === 'released') acc.released += 1;
      else if (row?.releaseStatus === 'rolled_back') acc.rolledBack += 1;
      else if (row?.releaseStatus === 'reviewing') acc.reviewing += 1;
      else if (row?.deployReady) acc.readyPending += 1;
      return acc;
    },
    { reviewing: 0, released: 0, rolledBack: 0, readyPending: 0 }
  )
);
const focusedBatchBaselineCounts = computed(() =>
  focusedRepairBoardRows.value.reduce(
    (acc, row) => {
      if (row?.batchBaselineLevel === 'ready') acc.ready += 1;
      else if (row?.batchBaselineLevel === 'watch') acc.watch += 1;
      else if (row?.batchBaselineLevel === 'blocked') acc.blocked += 1;
      else acc.missing += 1;
      return acc;
    },
    { ready: 0, watch: 0, blocked: 0, missing: 0 }
  )
);
const latestFocusedReleaseReviewRow = computed(() =>
  focusedRepairBoardRows.value
    .filter(row => row?.releaseReviewedAt)
    .slice()
    .sort(
      (a, b) =>
        new Date(b?.releaseReviewedAt || 0).getTime() -
        new Date(a?.releaseReviewedAt || 0).getTime()
    )[0] || null
);
const latestFocusedBatchBaselineRow = computed(() =>
  focusedRepairBoardRows.value
    .filter(row => row?.batchBaselineRanAt)
    .slice()
    .sort(
      (a, b) =>
        new Date(b?.batchBaselineRanAt || 0).getTime() -
        new Date(a?.batchBaselineRanAt || 0).getTime()
    )[0] || null
);
const humanBatchBaselineLabel = value => {
  switch (String(value || '').trim()) {
    case 'ready':
      return 'Ready';
    case 'watch':
      return 'Watch';
    case 'blocked':
      return 'Blocked';
    default:
      return 'Missing';
  }
};
const repairOwnerFocusSummary = computed(() => {
  if (!activeRepairOwnerFocus.value) return null;
  return {
    ownerLabel: activeRepairOwnerFocus.value,
    hasMatches: availableRepairOwnerFocusLabels.value.includes(
      activeRepairOwnerFocus.value
    ),
    queueVisible: focusedRepairTeamQueueRows.value.length,
    queueTotal: repairTeamQueueRows.value.length,
    boardVisible: focusedRepairBoardRows.value.length,
    boardTotal: strategyRepairBoardRows.value.length,
  };
});
const showAnalyzeContextBar = computed(() =>
  Boolean(
    repairOwnerFocusSummary.value ||
      routeAnalyzeSeed.value.enabled ||
      routeOverrideDrilldown.value.enabled ||
      showAdvancedDiagnostics.value
  )
);
const hasResettableAnalyzeContext = computed(() =>
  Boolean(
    repairOwnerFocusSummary.value ||
      routeAnalyzeSeed.value.enabled ||
      routeOverrideDrilldown.value.enabled
  )
);
const analyzeContextHeadline = computed(() => {
  const activeStates = [];

  if (repairOwnerFocusSummary.value) {
    activeStates.push(`owner ${repairOwnerFocusSummary.value.ownerLabel}`);
  }
  if (routeAnalyzeSeed.value.enabled) {
    activeStates.push('seeded runtime context');
  }
  if (routeOverrideDrilldown.value.enabled) {
    activeStates.push('override drilldown');
  }
  activeStates.push(
    showAdvancedDiagnostics.value
      ? 'advanced diagnostics open'
      : 'advanced diagnostics hidden'
  );

  return activeStates.join(' • ');
});
const suggestedAnalyzeSectionJump = computed(() => {
  if (routeAnalyzeSeed.value.enabled || routeOverrideDrilldown.value.enabled) {
    return {
      key: 'diagnostics',
      title: 'Diagnostics',
      reason: 'Imported runtime evidence is active',
      actionLabel: 'Open diagnostics',
    };
  }
  if (repairOwnerFocusSummary.value) {
    return {
      key: 'repair-queue',
      title: `${repairOwnerFocusSummary.value.ownerLabel} queue`,
      reason: 'Pinned owner workflow should start in the ordered queue',
      actionLabel: 'Go to repair queue',
    };
  }
  if (operatingUrgentRows.value.length) {
    return {
      key: 'operating-summary',
      title: 'Operating summary',
      reason: 'Start with the top urgent repairs and owner backlog change',
      actionLabel: 'Review summary',
    };
  }
  if (topStrategyRecommendation.value) {
    return {
      key: 'optimization-center',
      title: 'Optimization center',
      reason: 'A strategy recommendation is ready for review',
      actionLabel: 'Go to optimization',
    };
  }
  return {
    key: 'diagnostics',
    title: 'Diagnostics',
    reason: 'Open raw traces when you need deeper runtime detail',
    actionLabel: 'Open diagnostics',
  };
});
const activeAnalyzeSectionTitle = computed(() => {
  if (activeAnalyzeSectionKey.value === 'repair-queue') {
    return repairOwnerFocusSummary.value
      ? `${repairOwnerFocusSummary.value.ownerLabel} queue`
      : 'Repair team queue';
  }
  if (activeAnalyzeSectionKey.value === 'repair-board') {
    return 'Active repairs board';
  }
  if (activeAnalyzeSectionKey.value === 'optimization-center') {
    return 'Optimization center';
  }
  if (activeAnalyzeSectionKey.value === 'diagnostics') {
    return 'Recent reply diagnostics';
  }
  return 'Operating summary';
});
const analyzePresetSeededAvailable = computed(
  () => routeAnalyzeSeed.value.enabled || routeOverrideDrilldown.value.enabled
);
const activeAnalyzeFilterPresetKey = computed(() => {
  if (
    analyzePresetSeededAvailable.value &&
    showAdvancedDiagnostics.value &&
    String(filters.value.decision_type_like || '').trim() === 'response.trace'
  ) {
    return 'seeded_diagnostics';
  }
  if (
    activeRepairOwnerFocus.value === 'Deploy review' &&
    filters.value.failure_status === 'all' &&
    !showAdvancedDiagnostics.value
  ) {
    return 'ready_for_review';
  }
  if (
    topOwnerLoadCard.value &&
    activeRepairOwnerFocus.value ===
      String(topOwnerLoadCard.value.label || '').trim() &&
    filters.value.failure_status === 'all' &&
    !showAdvancedDiagnostics.value
  ) {
    return 'owner_backlog';
  }
  if (
    !activeRepairOwnerFocus.value &&
    filters.value.time_window === '24h' &&
    filters.value.failure_status === 'open' &&
    !showAdvancedDiagnostics.value
  ) {
    return 'urgent_only';
  }
  if (
    !activeRepairOwnerFocus.value &&
    filters.value.time_window === '7d' &&
    filters.value.failure_status === 'open' &&
    !showAdvancedDiagnostics.value
  ) {
    return 'overview';
  }
  return 'custom';
});
const activeAnalyzeFilterPresetLabel = computed(() => {
  if (activeAnalyzeFilterPresetKey.value === 'urgent_only') {
    return 'Urgent only';
  }
  if (activeAnalyzeFilterPresetKey.value === 'ready_for_review') {
    return 'Ready for review';
  }
  if (activeAnalyzeFilterPresetKey.value === 'owner_backlog') {
    return 'Owner backlog';
  }
  if (activeAnalyzeFilterPresetKey.value === 'seeded_diagnostics') {
    return 'Seeded diagnostics';
  }
  if (activeAnalyzeFilterPresetKey.value === 'overview') return 'Overview';
  return 'Custom view';
});
const analyzePresetCollapsedSectionKeys = computed(() => {
  if (activeAnalyzeFilterPresetKey.value === 'urgent_only') {
    return ['release-readiness', 'learning-loop', 'optimization-center'];
  }
  if (activeAnalyzeFilterPresetKey.value === 'ready_for_review') {
    return ['operating-summary', 'learning-loop', 'repair-board'];
  }
  if (activeAnalyzeFilterPresetKey.value === 'owner_backlog') {
    return ['release-readiness', 'repair-board'];
  }
  if (activeAnalyzeFilterPresetKey.value === 'seeded_diagnostics') {
    return [
      'operating-summary',
      'release-readiness',
      'learning-loop',
      'repair-queue',
      'repair-board',
      'optimization-center',
    ];
  }
  return [];
});
const analyzePresetCollapsedSectionCount = computed(
  () => analyzePresetCollapsedSectionKeys.value.length
);
const isAnalyzeSectionExpanded = key =>
  !analyzePresetCollapsedSectionKeys.value.includes(key) ||
  Boolean(analyzeExpandedSectionOverrides.value[key]);
const resetAnalyzeExpandedSections = () => {
  analyzeExpandedSectionOverrides.value = {};
};
const expandAnalyzeSection = async key => {
  analyzeExpandedSectionOverrides.value = {
    ...analyzeExpandedSectionOverrides.value,
    [key]: true,
  };
  await nextTick();
  scrollToAnalyzeSectionRef(analyzeSectionRefForKey(key));
};
const analyzeCollapsedSectionCards = computed(() => ({
  'operating-summary': {
    title: "Today's operating summary",
    body: 'Preset is compressing the executive rollup while you work inside a narrower slice.',
    badge: `${operatingUrgentRows.value.length} urgent`,
  },
  'release-readiness': {
    title: 'Release readiness',
    body: `${focusedReadyRepairBoardRows.value.length} ready repairs and ${focusedBlockedRepairBoardRows.value.length} blocked repairs are still tracked in the background.`,
    badge: `${focusedReadyRepairBoardRows.value.length} ready`,
  },
  'learning-loop': {
    title: 'Testing and improvement loop',
    body: focusedTopRepair.value
      ? `Top loop target is ${focusedTopRepair.value.label || focusedTopRepair.value.title || 'the current priority repair'}.`
      : 'No dominant repair loop target is active in this slice.',
    badge: focusedTopRepair.value ? 'Priority loop' : 'No active loop',
  },
  'repair-queue': {
    title: 'Repair team queue',
    body: `${focusedRepairTeamQueueRows.value.length} queue repairs remain tracked with ${focusedFixNowRepairRows.value.length} in fix now.`,
    badge: `${focusedRepairTeamQueueRows.value.length} queue`,
  },
  'repair-board': {
    title: 'Active repairs board',
    body: `${focusedBlockedRepairBoardRows.value.length} blocked, ${focusedObserveRepairBoardRows.value.length} observe, ${focusedReadyRepairBoardRows.value.length} ready.`,
    badge: `${focusedBlockedRepairBoardRows.value.length} blocked`,
  },
  'optimization-center': {
    title: 'Optimization center',
    body: topStrategyRecommendation.value
      ? `Top recommendation remains ${topStrategyRecommendation.value.label || topStrategyRecommendation.value.title || 'available'} while this preset stays focused elsewhere.`
      : 'Strategy recommendations remain available when you expand this section.',
    badge: `${strategyRecommendationRows.value.length} recs`,
  },
}));
const analyzeFilterPresetCards = computed(() => [
  {
    key: 'overview',
    title: 'Overview',
    body: 'Return to the default 7-day customer-safe workbench.',
    badge: timeWindowLabel.value,
    active: activeAnalyzeFilterPresetKey.value === 'overview',
    available: true,
  },
  {
    key: 'urgent_only',
    title: 'Urgent only',
    body: 'Prioritize fresh fixes and validations that need attention now.',
    badge: `${operatingUrgentRows.value.length} urgent`,
    active: activeAnalyzeFilterPresetKey.value === 'urgent_only',
    available: true,
  },
  {
    key: 'ready_for_review',
    title: 'Ready for review',
    body: 'Move into the Deploy review owner view and surface ready repairs.',
    badge: `${focusedReadyForReviewRepairRows.value.length} ready`,
    active: activeAnalyzeFilterPresetKey.value === 'ready_for_review',
    available: true,
  },
  {
    key: 'owner_backlog',
    title: 'Owner backlog',
    body: topOwnerLoadCard.value
      ? `Focus ${topOwnerLoadCard.value.label} and the current backlog leader.`
      : 'Focus the owner with the largest current queue load.',
    badge: topOwnerLoadCard.value
      ? `${topOwnerLoadCard.value.label} ${topOwnerLoadCard.value.value}`
      : 'No backlog lead',
    active: activeAnalyzeFilterPresetKey.value === 'owner_backlog',
    available: Boolean(topOwnerLoadCard.value),
  },
  {
    key: 'seeded_diagnostics',
    title: 'Seeded diagnostics',
    body: analyzePresetSeededAvailable.value
      ? 'Open raw trace diagnostics for the imported seed or override drilldown.'
      : 'Available when Analyze is opened from a seeded context or override drilldown.',
    badge: analyzePresetSeededAvailable.value ? 'Seed active' : 'Waiting',
    active: activeAnalyzeFilterPresetKey.value === 'seeded_diagnostics',
    available: analyzePresetSeededAvailable.value,
  },
]);
async function applyAnalyzeFilterPreset(key) {
  resetAnalyzeExpandedSections();
  if (key === 'seeded_diagnostics' && !analyzePresetSeededAvailable.value) {
    return;
  }

  if (key === 'overview') {
    await clearRepairOwnerFocus();
    filters.value.time_window = '7d';
    filters.value.failure_status = 'open';
    showAdvancedDiagnostics.value = false;
    await refreshAll();
    await nextTick();
    await jumpToAnalyzeSection('operating-summary');
    return;
  }

  if (key === 'urgent_only') {
    await clearRepairOwnerFocus();
    filters.value.time_window = '24h';
    filters.value.failure_status = 'open';
    showAdvancedDiagnostics.value = false;
    await refreshAll();
    await nextTick();
    await jumpToAnalyzeSection('operating-summary');
    return;
  }

  if (key === 'ready_for_review') {
    await focusRepairOwner('Deploy review');
    filters.value.failure_status = 'all';
    showAdvancedDiagnostics.value = false;
    await refreshAll();
    await nextTick();
    await jumpToAnalyzeSection('repair-queue');
    return;
  }

  if (key === 'owner_backlog') {
    if (topOwnerLoadCard.value?.label) {
      await focusRepairOwner(topOwnerLoadCard.value.label);
    }
    filters.value.failure_status = 'all';
    showAdvancedDiagnostics.value = false;
    await refreshAll();
    await nextTick();
    await jumpToAnalyzeSection('repair-queue');
    return;
  }

  if (key === 'seeded_diagnostics') {
    filters.value.decision_type_like = 'response.trace';
    showAdvancedDiagnostics.value = true;
    await refreshAll();
    await nextTick();
    await jumpToAnalyzeSection('diagnostics');
  }
}
const suggestedAnalyzeSectionActionLabel = computed(() =>
  activeAnalyzeSectionKey.value === suggestedAnalyzeSectionJump.value.key
    ? `Viewing ${suggestedAnalyzeSectionJump.value.title}`
    : suggestedAnalyzeSectionJump.value.actionLabel
);
const analyzeContextSummary = computed(() => {
  const presetCompressionNote =
    activeAnalyzeFilterPresetKey.value !== 'overview' &&
    activeAnalyzeFilterPresetKey.value !== 'custom' &&
    analyzePresetCollapsedSectionCount.value
      ? ` ${analyzePresetCollapsedSectionCount.value} sections are currently minimized by this preset.`
      : '';
  if (repairOwnerFocusSummary.value?.hasMatches) {
    return `Pinned to ${repairOwnerFocusSummary.value.ownerLabel} with ${repairOwnerFocusSummary.value.queueVisible}/${repairOwnerFocusSummary.value.queueTotal} queue repairs visible right now. You are currently reading ${activeAnalyzeSectionTitle.value}. Next stop: ${suggestedAnalyzeSectionJump.value.title}.${presetCompressionNote}`;
  }
  if (
    repairOwnerFocusSummary.value &&
    !repairOwnerFocusSummary.value.hasMatches
  ) {
    return `Pinned owner focus has no matches in this window, but the URL still preserves the view until you clear it. You are currently reading ${activeAnalyzeSectionTitle.value}. Next stop: ${suggestedAnalyzeSectionJump.value.title}.${presetCompressionNote}`;
  }
  if (routeAnalyzeSeed.value.enabled || routeOverrideDrilldown.value.enabled) {
    return `This workbench is carrying imported runtime evidence, so use the rail below to clear or narrow that state before changing filters. You are currently reading ${activeAnalyzeSectionTitle.value}. Next stop: ${suggestedAnalyzeSectionJump.value.title}.${presetCompressionNote}`;
  }
  return `Use this rail to open diagnostics only when you need raw traces, routing detail, or override drilldowns. You are currently reading ${activeAnalyzeSectionTitle.value}. Next stop: ${suggestedAnalyzeSectionJump.value.title}.${presetCompressionNote}`;
});
const analyzeContextCards = computed(() => {
  if (!showAnalyzeContextBar.value) return [];

  const cards = [];

  if (repairOwnerFocusSummary.value) {
    cards.push({
      key: 'owner-focus',
      tone: repairOwnerFocusSummary.value.hasMatches ? 'info' : 'warning',
      label: 'Owner focus',
      title: repairOwnerFocusSummary.value.ownerLabel,
      body: repairOwnerFocusSummary.value.hasMatches
        ? `Queue ${repairOwnerFocusSummary.value.queueVisible}/${repairOwnerFocusSummary.value.queueTotal} • board ${repairOwnerFocusSummary.value.boardVisible}/${repairOwnerFocusSummary.value.boardTotal}`
        : 'No current matches in this window',
      actionLabel: 'Clear focus',
      actionKind: 'clear_owner_focus',
    });
  }

  if (routeAnalyzeSeed.value.enabled) {
    cards.push({
      key: 'seeded-context',
      tone: 'info',
      label: 'Seeded context',
      title: routeAnalyzeSeed.value.source
        ? `From ${routeAnalyzeSeed.value.source}`
        : 'Imported view',
      body: analyzeSeedSummary.value || 'Real runtime context imported',
      actionLabel: 'Clear seeded context',
      actionKind: 'clear_seed',
    });
  }

  if (routeOverrideDrilldown.value.enabled) {
    cards.push({
      key: 'override-drilldown',
      tone: 'warning',
      label: 'Override drilldown',
      title:
        routeOverrideDrilldown.value.fixIntentTitle ||
        routeOverrideDrilldown.value.label ||
        (routeOverrideDrilldown.value.ruleIndex
          ? `Rule #${routeOverrideDrilldown.value.ruleIndex}`
          : 'Pinned rule'),
      body:
        overrideDrilldownSummary.value || 'Pinned to a specific rule outcome',
      actionLabel: 'Clear override drilldown',
      actionKind: 'clear_override',
    });
  }

  cards.push({
    key: 'diagnostics',
    tone: showAdvancedDiagnostics.value ? 'info' : 'muted',
    label: 'Diagnostics',
    title: showAdvancedDiagnostics.value ? 'Open' : 'Hidden',
    body: showAdvancedDiagnostics.value
      ? 'Raw traces and technical filters are visible'
      : 'Customer-safe overview only',
    actionLabel: showAdvancedDiagnostics.value
      ? 'Hide advanced diagnostics'
      : 'Show advanced diagnostics',
    actionKind: 'toggle_diagnostics',
  });

  return cards;
});
const analyzeSectionJumpCards = computed(() => {
  const recommendedKey = suggestedAnalyzeSectionJump.value.key;

  return [
    {
      key: 'operating-summary',
      label: 'Start',
      title: 'Operating summary',
      body: `${operatingUrgentRows.value.length} urgent rows • ${operatingReviewRows.value.length} review-ready rows`,
      badge: `${operatingUrgentRows.value.length} urgent`,
      secondaryBadge: `${operatingReviewRows.value.length} ready`,
      actionLabel: 'Jump to summary',
      recommended: recommendedKey === 'operating-summary',
      active: activeAnalyzeSectionKey.value === 'operating-summary',
    },
    {
      key: 'repair-queue',
      label: 'Work queue',
      title: repairOwnerFocusSummary.value
        ? `${repairOwnerFocusSummary.value.ownerLabel} queue`
        : 'Repair team queue',
      body: `${focusedRepairTeamQueueRows.value.length} visible queue repairs • ${focusedFixNowRepairRows.value.length} fix now`,
      badge: `${focusedRepairTeamQueueRows.value.length} queue`,
      secondaryBadge: `${focusedFixNowRepairRows.value.length} fix now`,
      actionLabel: 'Jump to queue',
      recommended: recommendedKey === 'repair-queue',
      active: activeAnalyzeSectionKey.value === 'repair-queue',
    },
    {
      key: 'repair-board',
      label: 'State map',
      title: 'Active repairs board',
      body: `${focusedRepairBoardRows.value.length} visible repair states across blocked, observe, ready, and unproven`,
      badge: `${focusedBlockedRepairBoardRows.value.length} blocked`,
      secondaryBadge: `${focusedReadyRepairBoardRows.value.length} ready`,
      actionLabel: 'Jump to board',
      recommended: recommendedKey === 'repair-board',
      active: activeAnalyzeSectionKey.value === 'repair-board',
    },
    {
      key: 'optimization-center',
      label: 'Optimize',
      title: 'Optimization center',
      body: topStrategyRecommendation.value
        ? `Top repair: ${topStrategyRecommendation.value.label || topStrategyRecommendation.value.title || 'recommended change'}`
        : 'Review strategy recommendations and deploy-ready candidates',
      badge: `${strategyRecommendationRows.value.length} recs`,
      secondaryBadge: topOwnerLoadCard.value
        ? `${topOwnerLoadCard.value.label} ${topOwnerLoadCard.value.value}`
        : 'No dominant backlog',
      actionLabel: 'Jump to optimization',
      recommended: recommendedKey === 'optimization-center',
      active: activeAnalyzeSectionKey.value === 'optimization-center',
    },
    {
      key: 'diagnostics',
      label: 'Trace detail',
      title: 'Recent reply diagnostics',
      body:
        routeAnalyzeSeed.value.enabled || routeOverrideDrilldown.value.enabled
          ? 'Imported runtime evidence is active here now'
          : showAdvancedDiagnostics.value
            ? 'Raw traces are already visible below'
            : 'Open diagnostics and jump straight to raw traces',
      badge: `${traceSampleItems.value.length} traces`,
      secondaryBadge: `${failureItems.value.length} misses`,
      actionLabel: showAdvancedDiagnostics.value
        ? 'Jump to diagnostics'
        : 'Open diagnostics',
      recommended: recommendedKey === 'diagnostics',
      active: activeAnalyzeSectionKey.value === 'diagnostics',
    },
  ];
});
const quickFiltersLeadNote = computed(() => {
  if (activeAnalyzeFilterPresetKey.value === 'custom') {
    return 'A custom rail preset mix is active. Keep narrowing manually, or switch back to Overview from the rail above.';
  }
  if (activeAnalyzeFilterPresetKey.value !== 'overview') {
    return `${activeAnalyzeFilterPresetLabel.value} preset is active. Quick filters can narrow this work view further.`;
  }
  if (repairOwnerFocusSummary.value) {
    return `The sticky rail above is already following ${repairOwnerFocusSummary.value.ownerLabel}. Use these filters only to narrow that view further.`;
  }
  if (routeAnalyzeSeed.value.enabled || routeOverrideDrilldown.value.enabled) {
    return 'The sticky rail above is carrying imported runtime context. Use these filters only if you want to narrow that imported view further.';
  }
  return 'Keep the default view simple: choose the inbox, time window, and review status first.';
});
const quickFiltersSideNote = computed(() => {
  if (activeAnalyzeFilterPresetKey.value === 'seeded_diagnostics') {
    return 'Seeded diagnostics preset is active, so raw trace filters are already open below.';
  }
  if (showAdvancedDiagnostics.value) {
    return 'Advanced diagnostics are already open from the rail above, so raw trace filters are available below.';
  }
  return 'Open advanced diagnostics from the rail above when you need raw traces, routing drilldowns, and technical filters.';
});
const focusedTopRepair = computed(
  () => focusedRepairBoardRows.value[0] || null
);
const focusedReadyRepair = computed(
  () =>
    focusedRepairBoardRows.value.find(row => row.repairLevel === 'ready') ||
    null
);
const focusedBlockedRepair = computed(
  () =>
    focusedRepairBoardRows.value.find(row => row.repairLevel === 'blocked') ||
    null
);
const topBlockedRepair = computed(
  () =>
    focusedRepairBoardRows.value.find(row => row.repairLevel === 'blocked') ||
    null
);
const topReadyRepair = computed(
  () =>
    focusedRepairBoardRows.value.find(row => row.repairLevel === 'ready') ||
    null
);
const topAttributeOverrideOutcomeRows = computed(() =>
  [...attributeOverrideOutcomeBreakdown.value]
    .sort(
      (a, b) =>
        (b.usedCount || 0) - (a.usedCount || 0) ||
        (b.escalatedCount || 0) - (a.escalatedCount || 0) ||
        (a.label || '').localeCompare(b.label || '')
    )
    .slice(0, 6)
);
const highEscalationAttributeOverrideRows = computed(() =>
  [...attributeOverrideOutcomeBreakdown.value]
    .filter(row => (row.usedCount || 0) >= 2 && (row.escalatedCount || 0) > 0)
    .sort(
      (a, b) =>
        (b.escalatedRate || 0) - (a.escalatedRate || 0) ||
        (b.escalatedCount || 0) - (a.escalatedCount || 0) ||
        (b.usedCount || 0) - (a.usedCount || 0) ||
        (a.label || '').localeCompare(b.label || '')
    )
    .slice(0, 5)
);
const lowResolutionAttributeOverrideRows = computed(() =>
  [...attributeOverrideOutcomeBreakdown.value]
    .filter(row => (row.usedCount || 0) >= 2)
    .sort(
      (a, b) =>
        (a.resolvedRate || 0) - (b.resolvedRate || 0) ||
        (b.usedCount || 0) - (a.usedCount || 0) ||
        (b.escalatedCount || 0) - (a.escalatedCount || 0) ||
        (a.label || '').localeCompare(b.label || '')
    )
    .slice(0, 5)
);
const filteredAttributeOverrideRecommendationRows = computed(() =>
  attributeOverrideRecommendationRows.value.filter(row => {
    if (!selectedOverrideOutcome.value) return true;
    const recommendationOutcome = overrideRecommendationDrilldownOutcome(row);
    return (
      !recommendationOutcome ||
      recommendationOutcome === selectedOverrideOutcome.value
    );
  })
);
const failureIntentBreakdown = computed(() =>
  buildTopCounts(failureSampleItems.value, item => item?.intent)
);
const failureStatusBreakdown = computed(() =>
  buildTopCounts(failureSampleItems.value, item => item?.status)
);
const traceDailySeries = computed(() =>
  buildDailySeries(traceSampleItems.value, item => item?.created_at)
);
const failureDailySeries = computed(() =>
  buildDailySeries(failureSampleItems.value, item => item?.created_at)
);

const maxSeriesCount = series =>
  Math.max(1, ...series.map(item => Number(item?.count) || 0));

const handoffLikeSampleCount = computed(
  () =>
    traceSampleItems.value.filter(item => {
      const payload = item?.payload || {};
      return (
        payload?.source_kind === 'handoff' ||
        payload?.intent_risk_action === 'handoff' ||
        payload?.response_preview === 'conversation_handoff'
      );
    }).length
);

const handoffLikeRate = computed(() => {
  if (!traceSampleItems.value.length) return 0;
  return handoffLikeSampleCount.value / traceSampleItems.value.length;
});

const topFailureIntent = computed(
  () => failureIntentBreakdown.value[0] || null
);
const topTraceSource = computed(() => traceSourceBreakdown.value[0] || null);
const topAttributeOverride = computed(
  () => attributeOverrideLabelBreakdown.value[0] || null
);
const topEscalatedAttributeOverride = computed(
  () => highEscalationAttributeOverrideRows.value[0] || null
);
const topLowResolutionAttributeOverride = computed(
  () => lowResolutionAttributeOverrideRows.value[0] || null
);
const topAttributeOverrideRecommendation = computed(
  () => filteredAttributeOverrideRecommendationRows.value[0] || null
);
const attributeOverrideInboxRowsByRule = computed(() =>
  filteredOverrideMetricRows(attributeOverrideInboxOutcomeRows.value).reduce(
    (acc, row) => {
      const ruleIndex = Number(row?.ruleIndex);
      if (!Number.isFinite(ruleIndex)) return acc;
      acc[ruleIndex] = [...(acc[ruleIndex] || []), row];
      return acc;
    },
    {}
  )
);
const filteredAttributeOverrideRecentCaseRows = computed(() =>
  attributeOverrideRecentCaseRows.value.filter(row => {
    if (!selectedOverrideOutcome.value) return true;
    return String(row?.outcome || '').trim() === selectedOverrideOutcome.value;
  })
);
const attributeOverrideRecentCasesByRule = computed(() =>
  filteredAttributeOverrideRecentCaseRows.value.reduce((acc, row) => {
    const ruleIndex = Number(row?.ruleIndex);
    if (!Number.isFinite(ruleIndex)) return acc;
    acc[ruleIndex] = [...(acc[ruleIndex] || []), row];
    return acc;
  }, {})
);
const topAttributeOverrideInboxHotspots = computed(() =>
  [...filteredOverrideMetricRows(attributeOverrideInboxOutcomeRows.value)]
    .sort(
      (a, b) =>
        outcomeCountForRow(b) - outcomeCountForRow(a) ||
        (b.escalatedCount || 0) - (a.escalatedCount || 0) ||
        (b.unresolvedCount || 0) - (a.unresolvedCount || 0) ||
        (b.usedCount || 0) - (a.usedCount || 0) ||
        (a.label || '').localeCompare(b.label || '') ||
        (a.inboxId || 0) - (b.inboxId || 0)
    )
    .slice(0, 6)
);
const topAttributeOverrideRecentCases = computed(() =>
  [...filteredAttributeOverrideRecentCaseRows.value]
    .sort(
      (a, b) =>
        timestampMs(b.occurredAt) - timestampMs(a.occurredAt) ||
        (b.decisionEventId || 0) - (a.decisionEventId || 0)
    )
    .slice(0, 6)
);
const conversationOverrideRecentCases = computed(() => {
  const conversationId = Number(filters.value.conversation_id);
  if (!Number.isFinite(conversationId) || conversationId <= 0) return [];
  return filteredAttributeOverrideRecentCaseRows.value
    .filter(row => Number(row?.conversationId) === conversationId)
    .sort(
      (a, b) =>
        timestampMs(a.occurredAt) - timestampMs(b.occurredAt) ||
        (a.decisionEventId || 0) - (b.decisionEventId || 0)
    );
});
const overrideRecentCasesForRule = (ruleIndex, limit = 3) => {
  const numericRuleIndex = Number(ruleIndex);
  if (!Number.isFinite(numericRuleIndex)) return [];
  return [...(attributeOverrideRecentCasesByRule.value[numericRuleIndex] || [])]
    .sort(
      (a, b) =>
        timestampMs(b.occurredAt) - timestampMs(a.occurredAt) ||
        (b.decisionEventId || 0) - (a.decisionEventId || 0)
    )
    .slice(0, limit);
};
const conversationTraceTimelineRows = computed(() =>
  [...conversationTraceTimelineItems.value].sort(
    (a, b) => timestampMs(a?.created_at) - timestampMs(b?.created_at) || (a?.id || 0) - (b?.id || 0)
  )
);
const conversationOverrideRecentCaseByDecisionEventId = computed(() =>
  conversationOverrideRecentCases.value.reduce((acc, row) => {
    const decisionEventId = Number(row?.decisionEventId);
    if (Number.isFinite(decisionEventId) && decisionEventId > 0) acc[decisionEventId] = row;
    return acc;
  }, {})
);

const healthAlerts = computed(() => {
  const alerts = [];
  if (handoffLikeRate.value >= 0.35) {
    alerts.push({
      tone: 'danger',
      title: 'High handoff pressure',
      body: `${Math.round(handoffLikeRate.value * 100)}% of sampled replies still hand off to a person in ${timeWindowLabel.value.toLowerCase()}.`,
    });
  }
  if (
    summary.value.open_failures > 0 &&
    summary.value.open_failures >= Math.max(3, summary.value.reviewed_failures)
  ) {
    alerts.push({
      tone: 'warning',
      title: 'Failure backlog growing',
      body: `${summary.value.open_failures} missed-answer items are still waiting for review.`,
    });
  }
  if (topFailureIntent.value && topFailureIntent.value.count >= 3) {
    alerts.push({
      tone: 'warning',
      title: 'One customer need is being missed often',
      body: `"${topFailureIntent.value.label}" appears in ${topFailureIntent.value.count} reviewed misses.`,
    });
  }
  if (topTraceSource.value) {
    alerts.push({
      tone: 'info',
      title: 'Most replies are leaning on one source',
      body: `"${topTraceSource.value.label}" is currently the main source in the sampled replies (${topTraceSource.value.count}).`,
    });
  }
  if (summary.value.timeout_expired >= 3) {
    alerts.push({
      tone: 'warning',
      title: 'Some automated steps are timing out',
      body: `${summary.value.timeout_expired} workflow timeouts were recorded in ${timeWindowLabel.value.toLowerCase()}.`,
    });
  }
  if (summary.value.delivery_blocked >= 3) {
    alerts.push({
      tone: 'info',
      title: 'Some replies were held back before send',
      body: `${summary.value.delivery_blocked} replies were blocked before send in ${timeWindowLabel.value.toLowerCase()}.`,
    });
  }
  if (topAttributeOverride.value && topAttributeOverride.value.count >= 3) {
    alerts.push({
      tone: 'info',
      title: 'One routing rule is handling much more volume',
      body: `"${topAttributeOverride.value.label}" matched ${topAttributeOverride.value.count} replies in ${timeWindowLabel.value.toLowerCase()}.`,
    });
  }
  if (
    topEscalatedAttributeOverride.value &&
    topEscalatedAttributeOverride.value.usedCount >= 3 &&
    topEscalatedAttributeOverride.value.escalatedRate >= 0.5
  ) {
    const inboxLabel = resolveOverrideTopInboxLabel(topEscalatedAttributeOverride.value);
    alerts.push({
      tone: 'warning',
      title: 'One routing rule sends too many chats to humans',
      body: `"${topEscalatedAttributeOverride.value.label}" escalated ${Math.round(topEscalatedAttributeOverride.value.escalatedRate * 100)}% of ${topEscalatedAttributeOverride.value.usedCount} matched replies${inboxLabel ? `, led by ${inboxLabel}` : ''}.`,
    });
  }
  if (
    topLowResolutionAttributeOverride.value &&
    topLowResolutionAttributeOverride.value.usedCount >= 3 &&
    topLowResolutionAttributeOverride.value.resolvedRate <= 0.2
  ) {
    const inboxLabel = resolveOverrideTopInboxLabel(topLowResolutionAttributeOverride.value);
    alerts.push({
      tone: 'info',
      title: 'One routing rule is not resolving cleanly',
      body: `"${topLowResolutionAttributeOverride.value.label}" resolved only ${Math.round(topLowResolutionAttributeOverride.value.resolvedRate * 100)}% of ${topLowResolutionAttributeOverride.value.usedCount} matched replies${inboxLabel ? `, with ${inboxLabel} contributing the most volume` : ''}.`,
    });
  }
  return alerts.slice(0, 5);
});

const customerActionCards = computed(() => {
  const actions = [];
  if (activeRepairOwnerFocus.value) {
    const focusedRow = focusedRepairBoardRows.value[0] || null;
    if (focusedRow) {
      actions.push({
        title: `${activeRepairOwnerFocus.value} next move`,
        body:
          focusedRow.repairBody ||
          `${focusedRow.label} is the strongest current repair for ${activeRepairOwnerFocus.value}.`,
        tone:
          focusedRow.repairLevel === 'blocked'
            ? 'warning'
            : focusedRow.repairLevel === 'ready'
              ? 'info'
              : 'warning',
        action: focusedRow.primaryAction?.action,
        actionLabel: focusedRow.primaryAction?.actionLabel || 'Open repair',
        guidanceEditor: focusedRow.primaryAction?.guidanceEditor,
        guidanceReason: focusedRow.primaryAction?.guidanceReason,
        guidanceQuery: focusedRow.primaryAction?.guidanceQuery,
        escalationFocus: focusedRow.primaryAction?.escalationFocus,
        escalationReason: focusedRow.primaryAction?.escalationReason,
        escalationQuery: focusedRow.primaryAction?.escalationQuery,
        escalationLabel: focusedRow.primaryAction?.escalationLabel,
        deployFocus: focusedRow.primaryAction?.deployFocus,
        deployReason: focusedRow.primaryAction?.deployReason,
        deployInboxId: focusedRow.primaryAction?.deployInboxId,
        deployConfidenceLevel: focusedRow.primaryAction?.deployConfidenceLevel,
        deployConfidenceRuns: focusedRow.primaryAction?.deployConfidenceRuns,
        deployConfidenceTitle: focusedRow.primaryAction?.deployConfidenceTitle,
        testQuery: focusedRow.testAction?.testQuery,
        testInboxId: focusedRow.testAction?.testInboxId,
        testReason: focusedRow.testAction?.testReason,
        testCompareBundle: focusedRow.testAction?.testCompareBundle,
      });
    } else {
      actions.push({
        title: `${activeRepairOwnerFocus.value} is calm right now`,
        body:
          'No active repair for this owner stands out in the current Analyze window.',
        tone: 'info',
      });
    }
  }
  if (summary.value.open_failures > 0) {
    actions.push({
      title: 'Review missed answers',
      body: `${summary.value.open_failures} items are still waiting for review in the failure queue.`,
      tone: 'warning',
    });
  }
  if (
    failureItems.value.some(
      tag => tag?.source === 'content_gap_auto' && !tag?.created_article_id
    )
  ) {
    actions.push({
      title: 'Turn repeated misses into help content',
      body: 'At least one repeated content gap can be turned into an article draft directly from the queue below.',
      tone: 'info',
    });
  }
  if (handoffLikeRate.value >= 0.35) {
    actions.push({
      title: 'Reduce handoffs',
      body: `${Math.round(handoffLikeRate.value * 100)}% of the sampled replies still need a human takeover.`,
      tone: 'danger',
    });
  }
  if (topAttributeOverrideRecommendation.value) {
    actions.push({
      title: 'Review rule-based routing',
      body:
        topAttributeOverrideRecommendation.value.suggestedChange ||
        topAttributeOverrideRecommendation.value.body ||
        'One routing rule likely needs cleanup.',
      tone:
        topAttributeOverrideRecommendation.value.severity === 'danger'
          ? 'warning'
          : 'info',
    });
  }
  if (!actions.length) {
    actions.push({
      title: 'No urgent action right now',
      body: `The ${timeWindowLabel.value.toLowerCase()} overview does not show a clear backlog spike.`,
      tone: 'info',
    });
  }
  return actions.slice(0, 3);
});
const formatStrategyLabel = value =>
  String(value || '')
    .trim()
    .replace(/_/g, ' ');
const latestGuidanceStrategySummary = computed(() => {
  const payload = latestTracePayload.value || {};
  const parts = [];
  if (payload?.answer_length_strategy) {
    parts.push(`answer length "${formatStrategyLabel(payload.answer_length_strategy)}"`);
  }
  if (payload?.directness_strategy) {
    parts.push(`directness "${formatStrategyLabel(payload.directness_strategy)}"`);
  }
  if (payload?.sensitive_topics_strategy) {
    parts.push(
      `sensitive-topic handling "${formatStrategyLabel(payload.sensitive_topics_strategy)}"`
    );
  }
  if (payload?.tone_enforcement_strategy) {
    parts.push(
      `tone enforcement "${formatStrategyLabel(payload.tone_enforcement_strategy)}"`
    );
  }
  if (payload?.memory_depth_strategy) {
    parts.push(
      `memory depth "${formatStrategyLabel(payload.memory_depth_strategy)}"`
    );
  }
  if (payload?.answer_confidence_strategy) {
    parts.push(
      `answer-confidence "${formatStrategyLabel(payload.answer_confidence_strategy)}"`
    );
  }
  if (payload?.outside_knowledge_strategy) {
    parts.push(
      `outside-knowledge rule "${formatStrategyLabel(payload.outside_knowledge_strategy)}"`
    );
  }
  if (payload?.promise_safety_strategy) {
    parts.push(
      `promise-safety "${formatStrategyLabel(payload.promise_safety_strategy)}"`
    );
  }
  return parts.join(', ');
});
const latestEscalationStrategySummary = computed(() => {
  const payload = latestTracePayload.value || {};
  const parts = [];
  if (payload?.procedure_routing_precedence) {
    parts.push(
      `procedure precedence "${formatStrategyLabel(payload.procedure_routing_precedence)}"`
    );
  }
  if (payload?.procedure_step_confirmation_strategy) {
    parts.push(
      `step confirmation "${formatStrategyLabel(payload.procedure_step_confirmation_strategy)}"`
    );
  }
  if (payload?.procedure_fallback_action) {
    parts.push(
      `procedure fallback "${formatStrategyLabel(payload.procedure_fallback_action)}"`
    );
  }
  if (payload?.channel_runtime_default_reply_mode) {
    parts.push(
      `deploy default "${formatStrategyLabel(payload.channel_runtime_default_reply_mode)}"`
    );
  }
  if (payload?.channel_runtime_binding_scope) {
    parts.push(`deploy scope "${formatStrategyLabel(payload.channel_runtime_binding_scope)}"`);
  }
  if (payload?.channel_runtime_active_segment_type) {
    parts.push(
      `deploy segment "${formatStrategyLabel(payload.channel_runtime_active_segment_type)}"`
    );
  }
  if (payload?.business_hours_reply_mode) {
    parts.push(
      `after-hours mode "${formatStrategyLabel(payload.business_hours_reply_mode)}"`
    );
  }
  if (payload?.business_hours_response_behavior) {
    parts.push(
      `after-hours behavior "${formatStrategyLabel(payload.business_hours_response_behavior)}"`
    );
  }
  if (payload?.region_compliance_binding_scope) {
    parts.push(
      `region scope "${formatStrategyLabel(payload.region_compliance_binding_scope)}"`
    );
  }
  if (payload?.region_compliance_active_country_code) {
    parts.push(
      `market "${formatStrategyLabel(payload.region_compliance_active_country_code)}"`
    );
  }
  if (payload?.region_compliance_active_profile) {
    parts.push(
      `compliance "${formatStrategyLabel(payload.region_compliance_active_profile)}"`
    );
  }
  if (payload?.uncertain_action) {
    parts.push(`uncertain route "${formatStrategyLabel(payload.uncertain_action)}"`);
  }
  if (payload?.high_risk_action) {
    parts.push(`high-risk default "${formatStrategyLabel(payload.high_risk_action)}"`);
  }
  return parts.join(', ');
});
const latestRetrievalStrategySummary = computed(() => {
  const payload = latestTracePayload.value || {};
  const parts = [];
  if (payload?.retrieval_top_priority_source) {
    parts.push(
      `top source "${formatStrategyLabel(payload.retrieval_top_priority_source)}"`
    );
  }
  if (payload?.retrieval_search_balance) {
    parts.push(
      `search balance "${formatStrategyLabel(payload.retrieval_search_balance)}"`
    );
  }
  if (payload?.retrieval_min_confidence_score !== undefined) {
    parts.push(
      `min confidence "${formatStrategyLabel(payload.retrieval_min_confidence_score)}"`
    );
  }
  if (payload?.retrieval_citation_mode) {
    parts.push(
      `citation "${formatStrategyLabel(payload.retrieval_citation_mode)}"`
    );
  }
  if (payload?.retrieval_active_segment_binding_scope) {
    parts.push(
      `segment binding "${formatStrategyLabel(payload.retrieval_active_segment_binding_scope)}"`
    );
  }
  if (payload?.retrieval_active_segment_binding_match_type) {
    parts.push(
      `attribute rule "${formatStrategyLabel(payload.retrieval_active_segment_binding_match_type)}"`
    );
  }
  if (payload?.retrieval_winning_source) {
    parts.push(
      `winning source "${formatStrategyLabel(payload.retrieval_winning_source)}"`
    );
  }
  if (payload?.retrieval_winning_reason) {
    parts.push(
      `winner reason "${formatStrategyLabel(payload.retrieval_winning_reason)}"`
    );
  }
  if (payload?.retrieval_article_strength_band) {
    parts.push(
      `article strength "${formatStrategyLabel(payload.retrieval_article_strength_band)}"`
    );
  }
  if (payload?.retrieval_article_confidence_gap !== undefined) {
    parts.push(
      `confidence gap "${formatStrategyLabel(payload.retrieval_article_confidence_gap)}"`
    );
  }
  if (payload?.retrieval_no_evidence_behavior) {
    parts.push(
      `no-evidence fallback "${formatStrategyLabel(payload.retrieval_no_evidence_behavior)}"`
    );
  }
  return parts.join(', ');
});

const learningLoopCards = computed(() => {
  if (repairOwnerFocusSummary.value && focusedTopRepair.value) {
    return [
      {
        label: '1. Validate',
        title: `Re-test ${repairOwnerFocusSummary.value.ownerLabel} priority repair`,
        body:
          focusedTopRepair.value.testAction?.testQuery
            ? `${focusedTopRepair.value.label} is the current top repair for ${repairOwnerFocusSummary.value.ownerLabel}. Re-run the focused Test bundle before changing anything else.`
            : `Use Test to recreate the top ${repairOwnerFocusSummary.value.ownerLabel} repair before making more edits.`,
        action: 'test',
        actionLabel: 'Open Test',
        testQuery: focusedTopRepair.value.testAction?.testQuery,
        testInboxId: focusedTopRepair.value.testAction?.testInboxId,
        testReason: focusedTopRepair.value.testAction?.testReason,
        testCompareBundle: focusedTopRepair.value.testAction?.testCompareBundle,
      },
      {
        label: '2. Repair',
        title: `Open ${repairOwnerFocusSummary.value.ownerLabel} repair workspace`,
        body:
          focusedTopRepair.value.repairBody ||
          `Use the owner workspace for ${focusedTopRepair.value.label} before moving on to lower-priority repairs.`,
        action: focusedTopRepair.value.primaryAction?.action,
        actionLabel:
          focusedTopRepair.value.primaryAction?.actionLabel || 'Open repair',
        guidanceEditor: focusedTopRepair.value.primaryAction?.guidanceEditor,
        guidanceReason: focusedTopRepair.value.primaryAction?.guidanceReason,
        guidanceQuery: focusedTopRepair.value.primaryAction?.guidanceQuery,
        escalationFocus: focusedTopRepair.value.primaryAction?.escalationFocus,
        escalationReason: focusedTopRepair.value.primaryAction?.escalationReason,
        escalationQuery: focusedTopRepair.value.primaryAction?.escalationQuery,
        escalationLabel: focusedTopRepair.value.primaryAction?.escalationLabel,
        deployFocus: focusedTopRepair.value.primaryAction?.deployFocus,
        deployReason: focusedTopRepair.value.primaryAction?.deployReason,
        deployInboxId: focusedTopRepair.value.primaryAction?.deployInboxId,
        deployConfidenceLevel:
          focusedTopRepair.value.primaryAction?.deployConfidenceLevel,
        deployConfidenceRuns:
          focusedTopRepair.value.primaryAction?.deployConfidenceRuns,
        deployConfidenceTitle:
          focusedTopRepair.value.primaryAction?.deployConfidenceTitle,
      },
      {
        label: '3. Review',
        title: focusedReadyRepair.value
          ? `Review ${repairOwnerFocusSummary.value.ownerLabel} deploy candidate`
          : `Keep ${repairOwnerFocusSummary.value.ownerLabel} under validation`,
        body: focusedReadyRepair.value
          ? `${focusedReadyRepair.value.label} already looks ready for deploy review.`
          : `No ${repairOwnerFocusSummary.value.ownerLabel} repair is fully ready yet, so keep validating the top repair until the risk state improves.`,
        action: focusedReadyRepair.value
          ? focusedReadyRepair.value.deployAction?.action
          : focusedTopRepair.value.primaryAction?.action,
        actionLabel: focusedReadyRepair.value
          ? focusedReadyRepair.value.deployAction?.actionLabel || 'Open Deploy'
          : focusedTopRepair.value.primaryAction?.actionLabel || 'Open repair',
        guidanceEditor: focusedTopRepair.value.primaryAction?.guidanceEditor,
        guidanceReason: focusedTopRepair.value.primaryAction?.guidanceReason,
        guidanceQuery: focusedTopRepair.value.primaryAction?.guidanceQuery,
        escalationFocus: focusedTopRepair.value.primaryAction?.escalationFocus,
        escalationReason: focusedTopRepair.value.primaryAction?.escalationReason,
        escalationQuery: focusedTopRepair.value.primaryAction?.escalationQuery,
        escalationLabel: focusedTopRepair.value.primaryAction?.escalationLabel,
        deployFocus:
          focusedReadyRepair.value?.deployAction?.deployFocus ||
          focusedTopRepair.value.primaryAction?.deployFocus,
        deployReason:
          focusedReadyRepair.value?.deployAction?.deployReason ||
          focusedTopRepair.value.primaryAction?.deployReason,
        deployInboxId:
          focusedReadyRepair.value?.deployAction?.deployInboxId ||
          focusedTopRepair.value.primaryAction?.deployInboxId,
        deployConfidenceLevel:
          focusedReadyRepair.value?.deployAction?.deployConfidenceLevel,
        deployConfidenceRuns:
          focusedReadyRepair.value?.deployAction?.deployConfidenceRuns,
        deployConfidenceTitle:
          focusedReadyRepair.value?.deployAction?.deployConfidenceTitle,
      },
      {
        label: '4. Reset view',
        title: 'Return to cross-team view when this owner is stable',
        body:
          'Clear owner focus after this queue is under control so you can compare the next owner backlog.',
        action: 'test',
        actionLabel: 'Open Test',
      },
    ];
  }

  return [
    {
      label: '1. Validate',
      title: 'Recreate priority conversations in Test',
      body:
        summary.value.trace_total > 0
          ? `${summary.value.trace_total} replies are already visible in Analyze. Re-run the most important ones in Test before you ship changes.`
          : 'Use Test to replay important customer questions before you publish a change.',
      action: 'test',
      actionLabel: 'Open Test',
    },
    {
      label: '2. Close gaps',
      title: 'Turn repeated misses into better knowledge',
      body:
        summary.value.open_failures > 0
          ? `${summary.value.open_failures} missed replies still need review. Convert repeated misses into Content updates or saved replies.${latestRetrievalStrategySummary.value ? ` Latest trace shows ${latestRetrievalStrategySummary.value}.` : ''}`
          : latestRetrievalStrategySummary.value
            ? `No urgent miss queue is open right now, but Content is still where repeated gaps should be fixed. Latest trace shows ${latestRetrievalStrategySummary.value}.`
            : 'No urgent miss queue is open right now, but Content is still where repeated gaps should be fixed.',
      action: 'content',
      actionLabel: 'Open Content',
      contentFocus: 'recommendations',
      contentQuery: topFailureSampleQuery.value,
      contentReason:
        summary.value.open_failures > 0 ? 'miss_queue' : 'knowledge_gap_review',
    },
    {
      label: '3. Refine behavior',
      title: 'Tighten answer quality in Guidance',
      body:
        latestGuidanceStrategySummary.value
          ? `Latest trace showed ${latestGuidanceStrategySummary.value}. Use Guidance to decide whether those answer rules are strong enough for real customers.`
          : handoffLikeRate.value >= 0.35
            ? `${Math.round(handoffLikeRate.value * 100)}% of sampled replies still needed a person, so tone, rules, or escalation behavior likely need tuning.`
            : 'Use Guidance when the AI answer is basically correct but still sounds weak, unclear, or too indirect.',
      action: 'guidance',
      actionLabel: 'Open Guidance',
      guidanceEditor:
        handoffLikeRate.value >= 0.35 ? 'guardrails' : 'response_guidelines',
      guidanceReason:
        handoffLikeRate.value >= 0.35 ? 'handoff_pressure' : 'answer_quality',
      guidanceQuery: topFailureSampleQuery.value,
    },
    {
      label: '4. Ship carefully',
      title: 'Review where changes should go live next',
      body:
        connectedInboxes.value.length > 0
          ? `This assistant is connected to ${connectedInboxes.value.length} inbox${connectedInboxes.value.length === 1 ? '' : 'es'}. Use Deploy to decide rollout order and channel behavior.`
          : 'No inbox is connected yet. Deploy is where channel binding and rollout decisions happen.',
      action: 'deploy',
      actionLabel: 'Open Deploy',
      deployFocus: connectedInboxes.value.length > 0 ? 'channels' : 'release',
      deployReason:
        connectedInboxes.value.length > 0 ? 'channel_rollout' : 'connect_inbox',
      deployInboxId: topFailureSampleInboxId.value,
    },
  ];
});

const releaseReadinessCards = computed(() => {
  if (repairOwnerFocusSummary.value) {
    return [
      {
        label: 'Runtime',
        value: runtimeStatus.value === 'active' ? 'Ready' : 'Blocked',
        note:
          runtimeStatus.value === 'active'
            ? 'AI runtime is active.'
            : 'Deploy still has runtime status set to inactive.',
      },
      {
        label: 'Channels',
        value: connectedInboxes.value.length,
        note:
          connectedInboxes.value.length > 0
            ? 'Connected inboxes can receive this assistant.'
            : 'No inbox is connected yet, so nothing can go live.',
      },
      {
        label: `${repairOwnerFocusSummary.value.ownerLabel} ready`,
        value: focusedReadyRepair.value ? 'Yes' : 'Not yet',
        note: focusedReadyRepair.value
          ? `${focusedReadyRepair.value.label} is the current release candidate for this owner.`
          : `No repair for ${repairOwnerFocusSummary.value.ownerLabel} is ready for deploy review yet.`,
      },
      {
        label: `${repairOwnerFocusSummary.value.ownerLabel} blocked`,
        value: focusedBlockedRepair.value ? 'Yes' : 'No',
        note: focusedBlockedRepair.value
          ? `${focusedBlockedRepair.value.label} still needs more work before release review.`
          : `No blocked repair currently dominates this owner focus.`,
      },
    ];
  }

  return [
    {
      label: 'Runtime',
      value: runtimeStatus.value === 'active' ? 'Ready' : 'Blocked',
      note:
        runtimeStatus.value === 'active'
          ? 'AI runtime is active.'
          : 'Deploy still has runtime status set to inactive.',
    },
    {
      label: 'Channels',
      value: connectedInboxes.value.length,
      note:
        connectedInboxes.value.length > 0
          ? 'Connected inboxes can receive this assistant.'
          : 'No inbox is connected yet, so nothing can go live.',
    },
    {
      label: 'Review backlog',
      value: summary.value.open_failures,
      note:
        summary.value.open_failures > 0
          ? 'Missed replies are still waiting for review.'
          : 'No open review backlog right now.',
    },
    {
      label: 'Citations',
      value: citationsEnabled.value ? 'On' : 'Off',
      note: citationsEnabled.value
        ? 'Citation feature is enabled for supported link/media replies.'
        : 'Citation feature is currently off.',
    },
  ];
});
const topFailureSample = computed(() => failureSampleItems.value[0] || failureItems.value[0] || null);
const topFailureSampleQuery = computed(() =>
  String(topFailureSample.value?.user_text || topFailureSample.value?.desired_text || '').trim()
);
const topFailureSampleInboxId = computed(() =>
  String(topFailureSample.value?.inbox_id || filters.value.inbox_id || '').trim()
);

const releaseChecklist = computed(() => {
  if (repairOwnerFocusSummary.value && focusedTopRepair.value) {
    const items = [];
    items.push({
      title: focusedReadyRepair.value
        ? `${repairOwnerFocusSummary.value.ownerLabel} has a release candidate`
        : `Keep ${repairOwnerFocusSummary.value.ownerLabel} in repair review`,
      body: focusedReadyRepair.value
        ? `${focusedReadyRepair.value.label} is ready to move into deploy review for this owner.`
        : focusedTopRepair.value.repairBody ||
          `No repair for ${repairOwnerFocusSummary.value.ownerLabel} is fully ready yet, so keep the top repair under validation.`,
      tone: focusedReadyRepair.value ? 'info' : 'warning',
      action: focusedReadyRepair.value
        ? focusedReadyRepair.value.deployAction?.action
        : focusedTopRepair.value.primaryAction?.action,
      actionLabel: focusedReadyRepair.value
        ? focusedReadyRepair.value.deployAction?.actionLabel || 'Open Deploy'
        : focusedTopRepair.value.primaryAction?.actionLabel || 'Open repair',
      guidanceEditor: focusedTopRepair.value.primaryAction?.guidanceEditor,
      guidanceReason: focusedTopRepair.value.primaryAction?.guidanceReason,
      guidanceQuery: focusedTopRepair.value.primaryAction?.guidanceQuery,
      escalationFocus: focusedTopRepair.value.primaryAction?.escalationFocus,
      escalationReason: focusedTopRepair.value.primaryAction?.escalationReason,
      escalationQuery: focusedTopRepair.value.primaryAction?.escalationQuery,
      escalationLabel: focusedTopRepair.value.primaryAction?.escalationLabel,
      deployFocus:
        focusedReadyRepair.value?.deployAction?.deployFocus ||
        focusedTopRepair.value.primaryAction?.deployFocus,
      deployReason:
        focusedReadyRepair.value?.deployAction?.deployReason ||
        focusedTopRepair.value.primaryAction?.deployReason,
      deployInboxId:
        focusedReadyRepair.value?.deployAction?.deployInboxId ||
        focusedTopRepair.value.primaryAction?.deployInboxId,
      deployConfidenceLevel:
        focusedReadyRepair.value?.deployAction?.deployConfidenceLevel,
      deployConfidenceRuns:
        focusedReadyRepair.value?.deployAction?.deployConfidenceRuns,
      deployConfidenceTitle:
        focusedReadyRepair.value?.deployAction?.deployConfidenceTitle,
      testQuery: focusedTopRepair.value.testAction?.testQuery,
      testInboxId: focusedTopRepair.value.testAction?.testInboxId,
      testReason: focusedTopRepair.value.testAction?.testReason,
      testCompareBundle: focusedTopRepair.value.testAction?.testCompareBundle,
    });
    items.push({
      title:
        runtimeStatus.value === 'active'
          ? 'Runtime is ready for this owner queue'
          : 'Activate runtime before shipping this owner queue',
      body:
        runtimeStatus.value === 'active'
          ? `Deploy currently allows the AI to run in ${assistantReplyMode.value}.`
          : 'The AI is still inactive, so this owner queue cannot go live yet.',
      tone: runtimeStatus.value === 'active' ? 'info' : 'danger',
      action: 'deploy',
      actionLabel: 'Open Deploy',
      deployFocus: 'release',
      deployReason:
        runtimeStatus.value === 'active' ? 'runtime_active' : 'runtime_inactive',
      deployInboxId:
        focusedReadyRepair.value?.deployAction?.deployInboxId ||
        focusedTopRepair.value.primaryAction?.deployInboxId,
    });
    items.push({
      title:
        connectedInboxes.value.length > 0
          ? 'Channels are connected for rollout review'
          : 'Connect at least one inbox before rollout',
      body:
        connectedInboxes.value.length > 0
          ? `${connectedInboxes.value.length} inbox${connectedInboxes.value.length === 1 ? '' : 'es'} currently use this assistant.`
          : 'Without a connected inbox, this focused owner queue cannot be reviewed for live rollout.',
      tone: connectedInboxes.value.length > 0 ? 'info' : 'warning',
      action: 'deploy',
      actionLabel: 'Open Deploy',
      deployFocus: connectedInboxes.value.length > 0 ? 'channels' : 'release',
      deployReason:
        connectedInboxes.value.length > 0 ? 'channels_connected' : 'connect_inbox',
      deployInboxId:
        focusedReadyRepair.value?.deployAction?.deployInboxId ||
        focusedTopRepair.value.primaryAction?.deployInboxId,
    });
    return items.slice(0, 4);
  }

  const items = [];
  items.push({
    title: runtimeStatus.value === 'active' ? 'Runtime is active' : 'Activate runtime before release',
    body:
      runtimeStatus.value === 'active'
        ? `Deploy currently allows the AI to run in ${assistantReplyMode.value}.`
        : 'The AI is still inactive, so changes will not go live yet.',
    tone: runtimeStatus.value === 'active' ? 'info' : 'danger',
    action: 'deploy',
    actionLabel: 'Open Deploy',
    deployFocus: 'release',
    deployReason: runtimeStatus.value === 'active' ? 'runtime_active' : 'runtime_inactive',
  });
  items.push({
    title:
      connectedInboxes.value.length > 0
        ? 'Channels are connected'
        : 'Connect at least one inbox',
    body:
      connectedInboxes.value.length > 0
        ? `${connectedInboxes.value.length} inbox${connectedInboxes.value.length === 1 ? '' : 'es'} currently use this assistant.`
        : 'Without a connected inbox, there is no live channel to publish against.',
    tone: connectedInboxes.value.length > 0 ? 'info' : 'warning',
    action: 'deploy',
    actionLabel: 'Open Deploy',
    deployFocus: connectedInboxes.value.length > 0 ? 'channels' : 'release',
    deployReason: connectedInboxes.value.length > 0 ? 'channels_connected' : 'connect_inbox',
  });
  if (summary.value.open_failures > 0) {
    items.push({
      title: 'Review the miss queue before wider rollout',
      body: `${summary.value.open_failures} missed replies still need review or conversion into reusable fixes.`,
      tone: 'warning',
      action: 'content',
      actionLabel: 'Open Content',
      contentFocus: 'recommendations',
      contentQuery: topFailureSampleQuery.value,
      contentReason: 'review_backlog',
      testQuery: topFailureSampleQuery.value,
      testInboxId: topFailureSampleInboxId.value,
      testReason: 'review_backlog',
    });
  }
  if (summary.value.delivery_blocked > 0 || handoffLikeRate.value >= 0.35) {
    items.push({
      title: 'Check routing pressure',
      body:
        summary.value.delivery_blocked > 0
          ? `${summary.value.delivery_blocked} recent replies were blocked by delivery/routing constraints.${latestEscalationStrategySummary.value ? ` Latest trace shows ${latestEscalationStrategySummary.value}.` : ''}`
          : `${Math.round(handoffLikeRate.value * 100)}% of sampled replies still needed a human handoff.${latestEscalationStrategySummary.value ? ` Latest trace shows ${latestEscalationStrategySummary.value}.` : ''}`,
      tone: 'warning',
      action:
        handoffLikeRate.value >= 0.35 ? 'escalation' : 'deploy',
      actionLabel:
        handoffLikeRate.value >= 0.35 ? 'Open Escalation' : 'Open Deploy',
      escalationFocus: handoffLikeRate.value >= 0.35 ? 'defaults' : '',
      escalationReason: handoffLikeRate.value >= 0.35 ? 'handoff_pressure' : '',
      escalationQuery: handoffLikeRate.value >= 0.35 ? topFailureSampleQuery.value : '',
      deployFocus: handoffLikeRate.value >= 0.35 ? '' : 'channels',
      deployReason:
        handoffLikeRate.value >= 0.35 ? '' : 'delivery_blocked',
      deployInboxId: topFailureSampleInboxId.value,
      testQuery: String(
        latestTracePayload.value?.content_gap_query_preview ||
          topContentGapTopic.value?.label ||
          topFailureSampleQuery.value ||
          ''
      ).trim(),
      testInboxId: topFailureSampleInboxId.value,
      testReason:
        handoffLikeRate.value >= 0.35 ? 'handoff_pressure' : 'delivery_blocked',
    });
  }
  if (!items.length) {
    items.push({
      title: 'Release signals look calm',
      body: 'No major release blocker is visible in the current Analyze window.',
      tone: 'info',
      action: 'test',
      actionLabel: 'Open Test',
    });
  }
  return items.slice(0, 4);
});

const topContentGapReason = computed(() => contentGapReasonBreakdown.value[0] || null);
const topContentGapTopic = computed(() => contentGapTopicBreakdown.value[0] || null);
const topArticleScopeWarning = computed(
  () => articleScopeWarningBreakdown.value[0] || null
);
const topBlockedDeliveryMode = computed(
  () => deliveryBlockedModeBreakdown.value[0] || null
);
const topOutsideKnowledgeStrategy = computed(
  () => outsideKnowledgeStrategyBreakdown.value[0] || null
);
const topSensitiveTopicsStrategy = computed(
  () => sensitiveTopicsStrategyBreakdown.value[0] || null
);
const topToneEnforcementStrategy = computed(
  () => toneEnforcementStrategyBreakdown.value[0] || null
);
const topMemoryDepthStrategy = computed(
  () => memoryDepthStrategyBreakdown.value[0] || null
);
const topAnswerConfidenceStrategy = computed(
  () => answerConfidenceStrategyBreakdown.value[0] || null
);
const topPromiseSafetyStrategy = computed(
  () => promiseSafetyStrategyBreakdown.value[0] || null
);
const topUncertainAction = computed(() => uncertainActionBreakdown.value[0] || null);
const topHighRiskAction = computed(() => highRiskActionBreakdown.value[0] || null);
const topHotspotRetestQuery = computed(() =>
  String(
    latestTracePayload.value?.content_gap_query_preview ||
      topContentGapTopic.value?.label ||
      topFailureSampleQuery.value ||
      ''
  ).trim()
);

const optimizationCenterCards = computed(() => {
  const cards = [];
  const isOwnerFocused = Boolean(activeRepairOwnerFocus.value);
  if (topBlockedRepair.value) {
    cards.push({
      title: `${
        isOwnerFocused ? `${activeRepairOwnerFocus.value}: ` : ''
      }Blocked repair: ${topBlockedRepair.value.label}`,
      body:
        topBlockedRepair.value.repairBody ||
        'This repair still looks too close to the escalated pattern and needs more work before release review.',
      action: topBlockedRepair.value.primaryAction?.action,
      actionLabel:
        topBlockedRepair.value.primaryAction?.actionLabel ||
        'Open Test bundle',
      tone: 'warning',
      guidanceEditor: topBlockedRepair.value.primaryAction?.guidanceEditor,
      guidanceReason: topBlockedRepair.value.primaryAction?.guidanceReason,
      guidanceQuery: topBlockedRepair.value.primaryAction?.guidanceQuery,
      escalationFocus: topBlockedRepair.value.primaryAction?.escalationFocus,
      escalationReason: topBlockedRepair.value.primaryAction?.escalationReason,
      escalationQuery: topBlockedRepair.value.primaryAction?.escalationQuery,
      escalationLabel: topBlockedRepair.value.primaryAction?.escalationLabel,
      deployFocus: topBlockedRepair.value.primaryAction?.deployFocus,
      deployReason: topBlockedRepair.value.primaryAction?.deployReason,
      deployInboxId: topBlockedRepair.value.primaryAction?.deployInboxId,
      deployConfidenceLevel:
        topBlockedRepair.value.primaryAction?.deployConfidenceLevel,
      deployConfidenceRuns:
        topBlockedRepair.value.primaryAction?.deployConfidenceRuns,
      deployConfidenceTitle:
        topBlockedRepair.value.primaryAction?.deployConfidenceTitle,
      testQuery: topBlockedRepair.value.testAction?.testQuery,
      testInboxId: topBlockedRepair.value.testAction?.testInboxId,
      testReason: topBlockedRepair.value.testAction?.testReason,
      testCompareBundle: topBlockedRepair.value.testAction?.testCompareBundle,
    });
  }
  if (topReadyRepair.value) {
    cards.push({
      title: `${
        isOwnerFocused ? `${activeRepairOwnerFocus.value}: ` : ''
      }Ready for deploy: ${topReadyRepair.value.label}`,
      body:
        topReadyRepair.value.repairBody ||
        'This repair already has enough validation confidence to move into release review.',
      action: topReadyRepair.value.primaryAction?.action,
      actionLabel:
        topReadyRepair.value.primaryAction?.actionLabel || 'Open Deploy',
      tone: 'info',
      guidanceEditor: topReadyRepair.value.primaryAction?.guidanceEditor,
      guidanceReason: topReadyRepair.value.primaryAction?.guidanceReason,
      guidanceQuery: topReadyRepair.value.primaryAction?.guidanceQuery,
      escalationFocus: topReadyRepair.value.primaryAction?.escalationFocus,
      escalationReason: topReadyRepair.value.primaryAction?.escalationReason,
      escalationQuery: topReadyRepair.value.primaryAction?.escalationQuery,
      escalationLabel: topReadyRepair.value.primaryAction?.escalationLabel,
      deployFocus: topReadyRepair.value.primaryAction?.deployFocus,
      deployReason: topReadyRepair.value.primaryAction?.deployReason,
      deployInboxId: topReadyRepair.value.primaryAction?.deployInboxId,
      deployConfidenceLevel:
        topReadyRepair.value.primaryAction?.deployConfidenceLevel,
      deployConfidenceRuns:
        topReadyRepair.value.primaryAction?.deployConfidenceRuns,
      deployConfidenceTitle:
        topReadyRepair.value.primaryAction?.deployConfidenceTitle,
      testQuery: topReadyRepair.value.testAction?.testQuery,
      testInboxId: topReadyRepair.value.testAction?.testInboxId,
      testReason: topReadyRepair.value.testAction?.testReason,
      testCompareBundle: topReadyRepair.value.testAction?.testCompareBundle,
    });
  }
  if (topStrategyRecommendation.value) {
    const deployAction = topStrategyRecommendation.value.deployAction;
    const action = deployAction
      ? {
          ...topStrategyRecommendation.value.primaryAction,
          ...deployAction,
        }
      : topStrategyRecommendation.value.primaryAction;
    cards.push({
      title:
        topStrategyRecommendation.value.title ||
        (isOwnerFocused
          ? `${activeRepairOwnerFocus.value} priority repair`
          : 'One strategy needs repair'),
      body:
        `${
          topStrategyRecommendation.value.evidenceSummary ||
          topStrategyRecommendation.value.body ||
          'One runtime strategy is underperforming.'
        }${
          topStrategyRecommendationRepairStatus.value
            ? ` Validation confidence is ${topStrategyRecommendationRepairStatus.value.level} across ${topStrategyRecommendationRepairStatus.value.runsCount} recent run${topStrategyRecommendationRepairStatus.value.runsCount === 1 ? '' : 's'}, with latest verdict ${topStrategyRecommendationRepairStatus.value.latestVerdict}.`
            : ''
        } Open the Test bundle to validate the fix against hotspot, resolved, and escalated examples.`,
      action: action.action,
      actionLabel: action.actionLabel,
      tone:
        topStrategyRecommendation.value.severity === 'danger'
          ? 'warning'
          : topStrategyRecommendation.value.severity || 'info',
      guidanceEditor: action.guidanceEditor,
      guidanceReason: action.guidanceReason,
      guidanceQuery: action.guidanceQuery,
      escalationFocus: action.escalationFocus,
      escalationReason: action.escalationReason,
      escalationQuery: action.escalationQuery,
      escalationLabel: action.escalationLabel,
      deployFocus: action.deployFocus,
      deployReason: action.deployReason,
      deployInboxId: action.deployInboxId,
      deployConfidenceLevel: action.deployConfidenceLevel,
      deployConfidenceRuns: action.deployConfidenceRuns,
      deployConfidenceTitle: action.deployConfidenceTitle,
      testQuery: action.testQuery,
      testInboxId: action.testInboxId,
      testReason: action.testReason,
      testCompareBundle: action.testCompareBundle,
    });
  }
  if (isOwnerFocused) {
    if (!cards.length) {
      cards.push({
        title: `${activeRepairOwnerFocus.value} has no dominant repair right now`,
        body:
          'This owner focus does not currently expose a repair hotspot stronger than the rest of the queue.',
        action: 'test',
        actionLabel: 'Open Test',
        tone: 'info',
      });
    }
    return cards.slice(0, 4);
  }
  if (
    latestTracePayload.value?.content_gap_detected &&
    !String(latestTracePayload.value?.outside_knowledge_strategy || '').trim()
  ) {
    cards.push({
      title: 'Missing rule for unknown knowledge',
      body:
        'Recent runtime trace hit a content gap, but it did not expose a rule for what the AI should do when knowledge is missing.',
      action: 'guidance',
      actionLabel: 'Open Guidance',
      tone: 'warning',
      guidanceEditor: 'guardrails',
      guidanceReason: 'missing_outside_knowledge_rule',
      guidanceQuery: topHotspotRetestQuery.value,
      testQuery: topHotspotRetestQuery.value,
      testInboxId: topFailureSampleInboxId.value,
      testReason: 'missing_outside_knowledge_rule',
    });
  }
  if (topOutsideKnowledgeStrategy.value) {
    cards.push({
      title: 'Missing-knowledge behavior is clustering',
      body: `${topOutsideKnowledgeStrategy.value.label} appeared ${topOutsideKnowledgeStrategy.value.count} time${topOutsideKnowledgeStrategy.value.count === 1 ? '' : 's'} in recent traces when guidance exposed an outside-knowledge rule.`,
      action: 'guidance',
      actionLabel: 'Open Guidance',
      tone: 'info',
      guidanceEditor: 'guardrails',
      guidanceReason: topOutsideKnowledgeStrategy.value.label || 'outside_knowledge_strategy',
      guidanceQuery: topHotspotRetestQuery.value,
      testQuery: topHotspotRetestQuery.value,
      testInboxId: topFailureSampleInboxId.value,
      testReason: topOutsideKnowledgeStrategy.value.label || 'outside_knowledge_strategy',
    });
  }
  if (topSensitiveTopicsStrategy.value) {
    cards.push({
      title: 'Sensitive-topic handling is clustering',
      body: `${topSensitiveTopicsStrategy.value.label} appeared ${topSensitiveTopicsStrategy.value.count} time${topSensitiveTopicsStrategy.value.count === 1 ? '' : 's'} in recent traces where reply guidance exposed a sensitive-topic rule.`,
      action: 'guidance',
      actionLabel: 'Open Guidance',
      tone: 'info',
      guidanceEditor: 'response_guidelines',
      guidanceReason:
        topSensitiveTopicsStrategy.value.label || 'sensitive_topics_strategy',
      guidanceQuery: topHotspotRetestQuery.value,
      testQuery: topHotspotRetestQuery.value,
      testInboxId: topFailureSampleInboxId.value,
      testReason:
        topSensitiveTopicsStrategy.value.label || 'sensitive_topics_strategy',
    });
  }
  if (topToneEnforcementStrategy.value) {
    cards.push({
      title: 'Tone-enforcement behavior is clustering',
      body: `${topToneEnforcementStrategy.value.label} appeared ${topToneEnforcementStrategy.value.count} time${topToneEnforcementStrategy.value.count === 1 ? '' : 's'} in recent traces where answer-style controls were active.`,
      action: 'guidance',
      actionLabel: 'Open Guidance',
      tone: 'info',
      guidanceEditor: 'response_guidelines',
      guidanceReason:
        topToneEnforcementStrategy.value.label || 'tone_enforcement_strategy',
      guidanceQuery: topHotspotRetestQuery.value,
      testQuery: topHotspotRetestQuery.value,
      testInboxId: topFailureSampleInboxId.value,
      testReason:
        topToneEnforcementStrategy.value.label || 'tone_enforcement_strategy',
    });
  }
  if (topMemoryDepthStrategy.value) {
    cards.push({
      title: 'Memory-depth choice is clustering',
      body: `${topMemoryDepthStrategy.value.label} appeared ${topMemoryDepthStrategy.value.count} time${topMemoryDepthStrategy.value.count === 1 ? '' : 's'} in recent traces where Guidance memory settings were active.`,
      action: 'guidance',
      actionLabel: 'Open Guidance',
      tone: 'info',
      guidanceEditor: 'response_guidelines',
      guidanceReason:
        topMemoryDepthStrategy.value.label || 'memory_depth_strategy',
      guidanceQuery: topHotspotRetestQuery.value,
      testQuery: topHotspotRetestQuery.value,
      testInboxId: topFailureSampleInboxId.value,
      testReason:
        topMemoryDepthStrategy.value.label || 'memory_depth_strategy',
    });
  }
  if (topAnswerConfidenceStrategy.value) {
    cards.push({
      title: 'Answer-confidence gating is clustering',
      body: `${topAnswerConfidenceStrategy.value.label} appeared ${topAnswerConfidenceStrategy.value.count} time${topAnswerConfidenceStrategy.value.count === 1 ? '' : 's'} in recent traces with Guidance confidence controls enabled.`,
      action: 'guidance',
      actionLabel: 'Open Guidance',
      tone: 'info',
      guidanceEditor: 'response_guidelines',
      guidanceReason:
        topAnswerConfidenceStrategy.value.label || 'answer_confidence_strategy',
      guidanceQuery: topHotspotRetestQuery.value,
      testQuery: topHotspotRetestQuery.value,
      testInboxId: topFailureSampleInboxId.value,
      testReason:
        topAnswerConfidenceStrategy.value.label || 'answer_confidence_strategy',
    });
  }
  if (topPromiseSafetyStrategy.value) {
    cards.push({
      title: 'Promise-safety posture is clustering',
      body: `${topPromiseSafetyStrategy.value.label} appeared ${topPromiseSafetyStrategy.value.count} time${topPromiseSafetyStrategy.value.count === 1 ? '' : 's'} in recent traces where guidance exposed a promise-safety rule.`,
      action: 'guidance',
      actionLabel: 'Open Guidance',
      tone: 'info',
      guidanceEditor: 'guardrails',
      guidanceReason:
        topPromiseSafetyStrategy.value.label || 'promise_safety_strategy',
      guidanceQuery: topHotspotRetestQuery.value,
      testQuery: topHotspotRetestQuery.value,
      testInboxId: topFailureSampleInboxId.value,
      testReason:
        topPromiseSafetyStrategy.value.label || 'promise_safety_strategy',
    });
  }
  if (highestEscalationOutsideKnowledgeRow.value) {
    cards.push({
      title: 'One unknown-knowledge rule escalates too often',
      body: `${highestEscalationOutsideKnowledgeRow.value.label} escalated ${highestEscalationOutsideKnowledgeRow.value.escalatedCount}/${highestEscalationOutsideKnowledgeRow.value.usedCount} conversations (${formatPercent(highestEscalationOutsideKnowledgeRow.value.escalatedRate)}).`,
      action: 'guidance',
      actionLabel: 'Open Guidance',
      tone: 'warning',
      guidanceEditor: 'guardrails',
      guidanceReason: highestEscalationOutsideKnowledgeRow.value.label || 'outside_knowledge_strategy',
      guidanceQuery: topHotspotRetestQuery.value,
      testQuery: topHotspotRetestQuery.value,
      testInboxId: topFailureSampleInboxId.value,
      testReason: highestEscalationOutsideKnowledgeRow.value.label || 'outside_knowledge_strategy',
    });
  }
  if (highestEscalationPromiseSafetyRow.value) {
    cards.push({
      title: 'One promise-safety rule escalates too often',
      body: `${highestEscalationPromiseSafetyRow.value.label} escalated ${highestEscalationPromiseSafetyRow.value.escalatedCount}/${highestEscalationPromiseSafetyRow.value.usedCount} conversations (${formatPercent(highestEscalationPromiseSafetyRow.value.escalatedRate)}).`,
      action: 'guidance',
      actionLabel: 'Open Guidance',
      tone: 'warning',
      guidanceEditor: 'guardrails',
      guidanceReason:
        highestEscalationPromiseSafetyRow.value.label || 'promise_safety_strategy',
      guidanceQuery: topHotspotRetestQuery.value,
      testQuery: topHotspotRetestQuery.value,
      testInboxId: topFailureSampleInboxId.value,
      testReason:
        highestEscalationPromiseSafetyRow.value.label || 'promise_safety_strategy',
    });
  }
  if (topUncertainAction.value || topHighRiskAction.value) {
    const topLabel = topUncertainAction.value?.label || topHighRiskAction.value?.label || '';
    const topCount = topUncertainAction.value?.count || topHighRiskAction.value?.count || 0;
    cards.push({
      title: 'Escalation defaults are shaping recent traces',
      body: topUncertainAction.value
        ? `Uncertain route ${topUncertainAction.value.label} appeared ${topUncertainAction.value.count} time${topUncertainAction.value.count === 1 ? '' : 's'} in the current window.${topHighRiskAction.value ? ` High-risk default ${topHighRiskAction.value.label} also appeared ${topHighRiskAction.value.count} time${topHighRiskAction.value.count === 1 ? '' : 's'}.` : ''}`
        : `High-risk default ${topLabel} appeared ${topCount} time${topCount === 1 ? '' : 's'} in the current window.`,
      action: 'escalation',
      actionLabel: 'Open Escalation',
      tone: 'warning',
      escalationFocus: 'defaults',
      escalationReason: topLabel || 'runtime_defaults',
      escalationQuery: topHotspotRetestQuery.value,
      testQuery: topHotspotRetestQuery.value,
      testInboxId: topFailureSampleInboxId.value,
      testReason: topLabel || 'runtime_defaults',
    });
  }
  if (highestEscalationUncertainActionRow.value || highestEscalationHighRiskActionRow.value) {
    const row =
      highestEscalationUncertainActionRow.value || highestEscalationHighRiskActionRow.value;
    cards.push({
      title: 'One runtime route is driving escalations',
      body: `${row.label} escalated ${row.escalatedCount}/${row.usedCount} conversations (${formatPercent(row.escalatedRate)}), which suggests the default route may be too aggressive.`,
      action: 'escalation',
      actionLabel: 'Open Escalation',
      tone: 'warning',
      escalationFocus: 'defaults',
      escalationReason: row.label || 'runtime_defaults',
      escalationQuery: topHotspotRetestQuery.value,
      testQuery: topHotspotRetestQuery.value,
      testInboxId: topFailureSampleInboxId.value,
      testReason: row.label || 'runtime_defaults',
    });
  }
  if (
    lowestResolutionDirectnessRow.value ||
    lowestResolutionAnswerLengthRow.value ||
    lowestResolutionSensitiveTopicsRow.value ||
    lowestResolutionToneEnforcementRow.value ||
    lowestResolutionMemoryDepthRow.value ||
    lowestResolutionAnswerConfidenceRow.value
  ) {
    const row =
      lowestResolutionDirectnessRow.value ||
      lowestResolutionAnswerLengthRow.value ||
      lowestResolutionSensitiveTopicsRow.value ||
      lowestResolutionToneEnforcementRow.value ||
      lowestResolutionMemoryDepthRow.value ||
      lowestResolutionAnswerConfidenceRow.value;
    cards.push({
      title: 'One answer strategy is underperforming',
      body: `${row.label} only resolved ${row.resolvedCount}/${row.usedCount} conversations (${formatPercent(row.resolvedRate)}), so the answer behavior may need retuning.`,
      action: 'guidance',
      actionLabel: 'Open Guidance',
      tone: 'info',
      guidanceEditor: 'response_guidelines',
      guidanceReason: row.label || 'answer_strategy',
      guidanceQuery: topHotspotRetestQuery.value,
      testQuery: topHotspotRetestQuery.value,
      testInboxId: topFailureSampleInboxId.value,
      testReason: row.label || 'answer_strategy',
    });
  }
  if (topContentGapReason.value) {
    cards.push({
      title: 'Most common content gap reason',
      body: `${topContentGapReason.value.label} appeared ${topContentGapReason.value.count} time${topContentGapReason.value.count === 1 ? '' : 's'} in the current window.`,
      action: 'content',
      actionLabel: 'Open Content',
      tone: 'warning',
      contentFocus: 'recommendations',
      contentQuery: topHotspotRetestQuery.value,
      contentReason: topContentGapReason.value.label,
      testQuery: topHotspotRetestQuery.value,
      testInboxId: topFailureSampleInboxId.value,
      testReason: topContentGapReason.value.label,
    });
  }
  if (topContentGapTopic.value) {
    cards.push({
      title: 'Top missing knowledge topic',
      body: `${topContentGapTopic.value.label} is the most repeated topic gap right now.`,
      action: 'content',
      actionLabel: 'Open Content',
      tone: 'info',
      contentFocus: 'recommendations',
      contentQuery: String(topContentGapTopic.value.label || '').trim(),
      contentReason: 'top_missing_topic',
      testQuery: String(topContentGapTopic.value.label || '').trim(),
      testInboxId: topFailureSampleInboxId.value,
      testReason: 'top_missing_topic',
    });
  }
  if (topArticleScopeWarning.value) {
    cards.push({
      title: 'Knowledge scope warning is still visible',
      body: `${topArticleScopeWarning.value.label} appeared ${topArticleScopeWarning.value.count} time${topArticleScopeWarning.value.count === 1 ? '' : 's'}.`,
      action: 'content',
      actionLabel: 'Open Content',
      tone: 'warning',
      contentFocus: 'retrieval',
      contentQuery: topHotspotRetestQuery.value,
      contentReason: 'article_scope_warning',
      contentScopeWarning: String(topArticleScopeWarning.value.label || '').trim(),
      testQuery: topHotspotRetestQuery.value,
      testInboxId: topFailureSampleInboxId.value,
      testReason: 'article_scope_warning',
    });
  }
  if (topBlockedDeliveryMode.value) {
    cards.push({
      title: 'One delivery path is getting blocked',
      body: `${topBlockedDeliveryMode.value.label} accounts for ${topBlockedDeliveryMode.value.count} blocked-delivery event${topBlockedDeliveryMode.value.count === 1 ? '' : 's'}.`,
      action: 'deploy',
      actionLabel: 'Open Deploy',
      tone: 'warning',
      deployFocus: 'channels',
      deployReason: topBlockedDeliveryMode.value.label || 'delivery_blocked',
      deployInboxId: topFailureSampleInboxId.value,
      testQuery: topHotspotRetestQuery.value,
      testInboxId: topFailureSampleInboxId.value,
      testReason: topBlockedDeliveryMode.value.label || 'delivery_blocked',
    });
  }
  if (topAttributeOverrideRecommendation.value) {
    cards.push({
      title: 'A routing rule still needs cleanup',
      body:
        topAttributeOverrideRecommendation.value.suggestedChange ||
        topAttributeOverrideRecommendation.value.body ||
        'One attribute-based routing rule still looks risky.',
      action: 'escalation',
      actionLabel: 'Open Escalation',
      tone: 'info',
      escalationFocus: 'rules',
      escalationReason: 'routing_rule_cleanup',
      escalationQuery: topFailureSampleQuery.value,
      escalationLabel:
        topAttributeOverrideRecommendation.value.label ||
        topAttributeOverrideRecommendation.value.fixIntentTitle ||
        '',
      testQuery: topFailureSampleQuery.value,
      testInboxId: topFailureSampleInboxId.value,
      testReason: 'routing_rule_cleanup',
    });
  }
  if (!cards.length) {
    cards.push({
      title: 'No major optimization hotspot right now',
      body: 'The current Analyze window does not show a dominant content, routing, or delivery hotspot.',
      action: 'test',
      actionLabel: 'Open Test',
      tone: 'info',
    });
  }
  return cards.slice(0, 4);
});

const formatFailureStatusLabel = value => {
  switch (String(value || '').trim()) {
    case 'open':
      return 'Needs review';
    case 'reviewed':
      return 'Reviewed';
    case 'converted':
      return 'Ready as fix';
    case 'ignored':
      return 'Ignored';
    default:
      return String(value || 'Unknown');
  }
};

const failureBadges = tag =>
  [
    tag?.intent ? `Need ${tag.intent}` : '',
    tag?.risk_tier ? `Risk ${tag.risk_tier}` : '',
    tag?.content_gap_reason ? `Gap ${tag.content_gap_reason}` : '',
    tag?.content_gap_topic ? `Topic ${tag.content_gap_topic}` : '',
  ].filter(Boolean);

const latestGuidanceRows = computed(() =>
  Object.entries({
    used_sections: latestTracePayload.value?.guidance_used_sections,
    used_sections_count: latestTracePayload.value?.guidance_used_sections_count,
    guidance_card_keys: latestTracePayload.value?.guidance_card_keys,
    guidance_card_count: latestTracePayload.value?.guidance_card_count,
    guidance_scenarios: latestTracePayload.value?.guidance_scenarios,
    guidance_scenario_keys: latestTracePayload.value?.guidance_scenario_keys,
    guidance_scenario_count: latestTracePayload.value?.guidance_scenario_count,
    instruction_blocks_used: latestTracePayload.value?.instruction_blocks_used,
    identity_fields_used: latestTracePayload.value?.identity_fields_used,
    communication_style_mode: latestTracePayload.value?.communication_style_mode,
    language_style_modes: latestTracePayload.value?.language_style_modes,
    forced_reply_language: latestTracePayload.value?.forced_reply_language,
    baseline_compliance_used: latestTracePayload.value?.baseline_compliance_used,
    structured_guideline_keys: latestTracePayload.value?.structured_guideline_keys,
    structured_guideline_count: latestTracePayload.value?.structured_guideline_count,
    answer_length_strategy: latestTracePayload.value?.answer_length_strategy,
    directness_strategy: latestTracePayload.value?.directness_strategy,
    sensitive_topics_strategy: latestTracePayload.value?.sensitive_topics_strategy,
    tone_enforcement_strategy: latestTracePayload.value?.tone_enforcement_strategy,
    memory_depth_strategy: latestTracePayload.value?.memory_depth_strategy,
    max_response_segments: latestTracePayload.value?.max_response_segments,
    answer_confidence_strategy: latestTracePayload.value?.answer_confidence_strategy,
    answer_confidence_threshold:
      latestTracePayload.value?.answer_confidence_threshold,
    answer_confidence_signal:
      latestTracePayload.value?.answer_confidence_signal,
    answer_confidence_gate_triggered:
      latestTracePayload.value?.answer_confidence_gate_triggered,
    answer_confidence_gate_reason:
      latestTracePayload.value?.answer_confidence_gate_reason,
    structured_guardrail_keys: latestTracePayload.value?.structured_guardrail_keys,
    structured_guardrail_count: latestTracePayload.value?.structured_guardrail_count,
    outside_knowledge_strategy: latestTracePayload.value?.outside_knowledge_strategy,
    promise_safety_strategy: latestTracePayload.value?.promise_safety_strategy,
    citation_mode: latestTracePayload.value?.citation_mode,
    citation_enabled: latestTracePayload.value?.citation_enabled,
    guardrails_count: latestTracePayload.value?.guidance_refs?.guardrails_count,
    response_guidelines_count: latestTracePayload.value?.guidance_refs?.response_guidelines_count,
    scenarios_count: latestTracePayload.value?.guidance_refs?.scenarios_count,
  }).filter(([, value]) => value !== undefined && value !== null && value !== '')
);

const latestContentGapRows = computed(() =>
  Object.entries({
    content_gap_detected: latestTracePayload.value?.content_gap_detected,
    content_gap_reason: latestTracePayload.value?.content_gap_reason,
    content_gap_topic: latestTracePayload.value?.content_gap_topic,
    content_gap_query_preview: latestTracePayload.value?.content_gap_query_preview,
    content_gap_cluster_key: latestTracePayload.value?.content_gap_cluster_key,
  }).filter(([, value]) => value !== undefined && value !== null && value !== '')
);

const latestAttributeRows = computed(() =>
  Object.entries({
    attribute_source: latestTracePayload.value?.attribute_source,
    attribute_detected_count: latestTracePayload.value?.attribute_detected_count,
    attribute_matched_keys: latestTracePayload.value?.attribute_matched_keys,
    attribute_detected_values: latestTracePayload.value?.attribute_detected_values,
  }).filter(([, value]) => value !== undefined && value !== null && value !== '')
);

const latestTraceSections = computed(() => [
  {
    title: 'Knowledge',
    rows: Object.entries({
      article_scope: latestTracePayload.value?.article_scope,
      article_scope_warning: latestTracePayload.value?.article_scope_warning,
      article_portal_ids: latestTracePayload.value?.article_portal_ids,
      article_ids: latestTracePayload.value?.context_refs?.article_ids,
      retrieval_source_priority: latestTracePayload.value?.retrieval_source_priority,
      retrieval_top_priority_source: latestTracePayload.value?.retrieval_top_priority_source,
      retrieval_min_confidence_score:
        latestTracePayload.value?.retrieval_min_confidence_score,
      retrieval_search_balance: latestTracePayload.value?.retrieval_search_balance,
      retrieval_citation_mode: latestTracePayload.value?.retrieval_citation_mode,
      retrieval_active_inbox_binding_scope:
        latestTracePayload.value?.retrieval_active_inbox_binding_scope,
      retrieval_active_segment_binding_match_type:
        latestTracePayload.value?.retrieval_active_segment_binding_match_type,
      retrieval_active_segment_binding_attribute_key:
        latestTracePayload.value?.retrieval_active_segment_binding_attribute_key,
      retrieval_active_segment_binding_scope:
        latestTracePayload.value?.retrieval_active_segment_binding_scope,
      retrieval_no_evidence_behavior:
        latestTracePayload.value?.retrieval_no_evidence_behavior,
      retrieval_winning_source: latestTracePayload.value?.retrieval_winning_source,
      retrieval_winning_reason: latestTracePayload.value?.retrieval_winning_reason,
      retrieval_article_match_kind:
        latestTracePayload.value?.retrieval_article_match_kind,
      retrieval_article_second_confidence:
        latestTracePayload.value?.retrieval_article_second_confidence,
      retrieval_article_confidence_gap:
        latestTracePayload.value?.retrieval_article_confidence_gap,
      retrieval_article_strength_band:
        latestTracePayload.value?.retrieval_article_strength_band,
      retrieval_article_hit_count:
        latestTracePayload.value?.retrieval_refs?.article_hit_count,
      retrieval_article_exact_phrase_hits:
        latestTracePayload.value?.retrieval_refs?.article_exact_phrase_hits,
      retrieval_snippet_top_score:
        latestTracePayload.value?.retrieval_refs?.snippet_top_score,
      retrieval_source_score_gap:
        latestTracePayload.value?.retrieval_refs?.source_score_gap,
      retrieval_link_domains:
        latestTracePayload.value?.retrieval_refs?.link_domains,
      retrieval_link_top_score:
        latestTracePayload.value?.retrieval_refs?.link_top_score,
      retrieval_link_top_label:
        latestTracePayload.value?.retrieval_refs?.link_top_label,
      retrieval_link_top_reason:
        latestTracePayload.value?.retrieval_refs?.link_top_reason,
      retrieval_media_samples:
        latestTracePayload.value?.retrieval_refs?.media_samples,
      retrieval_media_top_score:
        latestTracePayload.value?.retrieval_refs?.media_top_score,
      retrieval_media_top_label:
        latestTracePayload.value?.retrieval_refs?.media_top_label,
      retrieval_media_top_reason:
        latestTracePayload.value?.retrieval_refs?.media_top_reason,
      retrieval_max_sources: latestTracePayload.value?.retrieval_max_sources,
      retrieval_context_budget: latestTracePayload.value?.retrieval_context_budget,
    }).filter(([, value]) => value !== undefined && value !== null && value !== ''),
  },
  {
    title: 'Guidance',
    rows: latestGuidanceRows.value,
  },
  {
    title: 'Content gap',
    rows: latestContentGapRows.value,
  },
  {
    title: 'Attributes',
    rows: latestAttributeRows.value,
  },
  {
    title: 'Procedure',
    rows: Object.entries(latestTracePayload.value?.procedure_refs || {}),
  },
  {
    title: 'Escalation',
    rows: Object.entries(latestTracePayload.value?.escalation_refs || {}),
  },
]);

const traceTags = event => {
  const payload = event?.payload || {};
  const tags = [];
  const push = (key, label = key) => {
    const value = payload?.[key];
    if (value === undefined || value === null || String(value).trim() === '') return;
    tags.push(`${label}:${value}`);
  };

  push('mode');
  push('source_kind', 'src');
  push('delivery_mode', 'delivery');
  push('auto_send_allowed', 'auto_send');
  push('effective_mode', 'eff');
  push('forced_reply_language', 'forced_lang');
  push('communication_style_mode', 'style');
  push('channel_runtime_default_reply_mode', 'deploy_default');
  push('channel_runtime_binding_scope', 'deploy_scope');
  push('channel_runtime_active_segment_type', 'deploy_segment');
  push('business_hours_active', 'after_hours_active');
  push('business_hours_reply_mode', 'after_hours_mode');
  push('business_hours_response_behavior', 'after_hours_behavior');
  push('region_compliance_binding_scope', 'region_scope');
  push('region_compliance_active_country_code', 'market');
  push('region_compliance_active_profile', 'compliance');
  push('answer_length_strategy', 'answer_len');
  push('directness_strategy', 'direct');
  push('sensitive_topics_strategy', 'sensitive');
  push('tone_enforcement_strategy', 'tone_rule');
  push('memory_depth_strategy', 'memory');
  push('answer_confidence_strategy', 'confidence_rule');
  push('outside_knowledge_strategy', 'knowledge_rule');
  push('promise_safety_strategy', 'promise');
  push('intent');
  push('intent_risk_tier', 'tier');
  push('intent_risk_action', 'action');
  push('procedure_routing_precedence', 'proc_order');
  push('procedure_step_confirmation_strategy', 'proc_confirm');
  push('procedure_fallback_action', 'proc_fallback');
  push('uncertain_action', 'uncertain');
  push('high_risk_action', 'high_risk');
  push('response_kind', 'resp');
  push('status');
  push('article_scope', 'scope');
  push('article_scope_warning', 'scope_warn');
  push('retrieval_top_priority_source', 'retrieval_top');
  push('retrieval_min_confidence_score', 'retrieval_conf');
  push('retrieval_search_balance', 'retrieval_search');
  push('retrieval_citation_mode', 'retrieval_citation');
  push('retrieval_active_inbox_binding_scope', 'retrieval_binding');
  push('retrieval_active_segment_binding_scope', 'retrieval_segment');
  push('retrieval_no_evidence_behavior', 'retrieval_fallback');
  push('content_gap_reason', 'gap');
  push('content_gap_cluster_key', 'gap_cluster');
  push('attribute_source', 'attr_src');
  push('attribute_override_rule_index', 'override_rule');
  push('attribute_override_label', 'override');
  push('attribute_override_key', 'override_key');
  push('attribute_override_action', 'override_action');
  push('attribute_override_match_type', 'override_match');
  traceOverrideMatchedConditionKeys(event).slice(0, 1).forEach(key => {
    if (!key) return;
    tags.push(`override_cond:${key}`);
  });
  traceOverridePassedGuardKeys(event).slice(0, 1).forEach(key => {
    if (!key) return;
    tags.push(`override_guard:${key}`);
  });
  if (payload?.attribute_detected_count !== undefined) {
    tags.push(`attrs:${payload.attribute_detected_count}`);
  }
  if (Array.isArray(payload?.attribute_matched_keys)) {
    payload.attribute_matched_keys.slice(0, 2).forEach(key => {
      if (!key) return;
      tags.push(`attr:${key}`);
    });
  }
  if (Array.isArray(payload?.guidance_card_keys)) {
    payload.guidance_card_keys.slice(0, 2).forEach(card => {
      if (!card) return;
      tags.push(`card:${card}`);
    });
  }
  if (Array.isArray(payload?.guidance_scenarios)) {
    payload.guidance_scenarios.slice(0, 2).forEach(scenario => {
      const label = scenario?.title || scenario?.key;
      if (!label) return;
      tags.push(`scenario:${label}`);
    });
  }
  if (payload?.guidance_scenario_count !== undefined) {
    tags.push(`scenarios:${payload.guidance_scenario_count}`);
  }
  if (payload?.guidance_card_count !== undefined) {
    tags.push(`cards:${payload.guidance_card_count}`);
  }
  if (payload?.timed_out !== undefined) tags.push(`timed_out:${payload.timed_out}`);
  push('response_segments_count', 'segments');
  if (payload?.guidance_used_sections_count !== undefined) {
    tags.push(`guidance:${payload.guidance_used_sections_count}`);
  }
  return tags.slice(0, 8);
};
const normalizeOverrideTraceCondition = condition => ({
  attributeKey: String(condition?.attribute_key || '').trim(),
  matchType: String(condition?.match_type || '').trim(),
  configuredValue: String(condition?.configured_value || '').trim(),
  actualValue: String(condition?.actual_value || '').trim(),
  matched: Boolean(condition?.matched),
  summary: String(condition?.summary || '').trim(),
});
const normalizeOverrideTraceConditionGroups = groups =>
  Array.isArray(groups)
    ? groups
        .map(group => ({
          key: String(group?.key || '').trim().toLowerCase(),
          matched: Boolean(group?.matched),
          conditionCount: Number(group?.condition_count) || 0,
          matchedConditionCount: Number(group?.matched_condition_count) || 0,
          conditions: Array.isArray(group?.conditions)
            ? group.conditions.map(normalizeOverrideTraceCondition)
            : [],
        }))
        .filter(group => group.key && group.conditions.length)
    : [];
const traceAttributeOverride = event => {
  const payload = event?.payload || {};
  const escalationRefs = payload?.escalation_refs || {};
  const conditionGroups = normalizeOverrideTraceConditionGroups(
    payload?.attribute_override_condition_groups || escalationRefs?.attribute_override_condition_groups
  );
  const matched = Boolean(
    payload?.attribute_override_matched ?? escalationRefs?.attribute_override_matched
  );
  if (!matched && !conditionGroups.length) return null;

  const ruleIndex = Number(
    payload?.attribute_override_rule_index ?? escalationRefs?.attribute_override_rule_index
  );
  const conditionCount = Number(
    payload?.attribute_override_condition_count ?? escalationRefs?.attribute_override_condition_count
  );
  return {
    matched,
    ruleIndex: Number.isFinite(ruleIndex) ? ruleIndex : null,
    label: String(
      payload?.attribute_override_label || escalationRefs?.attribute_override_label || ''
    ).trim(),
    action: String(
      payload?.attribute_override_action || escalationRefs?.attribute_override_action || ''
    ).trim(),
    matchType: String(
      payload?.attribute_override_match_type || escalationRefs?.attribute_override_match_type || ''
    ).trim(),
    riskTier: String(
      payload?.attribute_override_risk_tier || escalationRefs?.attribute_override_risk_tier || ''
    ).trim(),
    conditionSummary: String(
      payload?.attribute_override_condition_summary ||
        escalationRefs?.attribute_override_condition_summary ||
        ''
    ).trim(),
    conditionCount: Number.isFinite(conditionCount) ? conditionCount : 0,
    conditionGroups,
  };
};
const formatOverrideTraceGroupLabel = key => {
  switch (key) {
    case 'all':
      return 'ALL';
    case 'any':
      return 'ANY';
    case 'not':
      return 'NOT';
    default:
      return key.toUpperCase();
  }
};
const formatOverrideTraceGroupStatus = group => {
  if (group?.key === 'not') return group?.matched ? 'guard passed' : 'blocked';
  return group?.matched ? 'matched' : 'not satisfied';
};
const MATCHED_OVERRIDE_LABEL = 'Matched override';
const overrideTraceConditionTone = (groupKey, matched) => {
  if (groupKey === 'not') return matched ? 'blocked' : 'guard';
  return matched ? 'matched' : 'inactive';
};
const formatOverrideTraceConditionStatus = (groupKey, matched) => {
  if (groupKey === 'not') return matched ? 'blocked' : 'guard passed';
  return matched ? 'matched' : 'not matched';
};
const formatOverrideTraceHeadline = override => {
  if (!override) return '';

  const label = override?.label || `Rule ${override?.ruleIndex ?? '-'}`;
  const parts = [label, `action ${override?.action || '-'}`];
  if (override?.riskTier) parts.push(`tier ${override.riskTier}`);
  else if (override?.matchType) parts.push(override.matchType);
  return parts.join(' • ');
};
const formatOverrideTraceConditionCountLabel = override =>
  `${override?.conditionCount || 0} conditions`;
const formatOverrideTraceGroupDetail = group =>
  `${formatOverrideTraceGroupStatus(group)} • ${group?.matchedConditionCount || 0}/${group?.conditionCount || 0}`;
const formatOverrideTraceConditionMeta = condition => {
  const parts = [];
  if (condition?.configuredValue) parts.push(`configured ${condition.configuredValue}`);
  if (condition?.actualValue) parts.push(`actual ${condition.actualValue}`);
  return parts.join(' • ');
};
const conversationTimelineCaseIds = computed(
  () => new Set(conversationOverrideRecentCases.value.map(row => Number(row?.decisionEventId)).filter(Boolean))
);
const isConversationTimelineHighlighted = event => {
  if (conversationTimelineCaseIds.value.has(Number(event?.id))) return true;

  const payload = event?.payload || {};
  const selectedRuleIndex = Number(filters.value.attribute_override_rule_index);
  if (Number.isFinite(selectedRuleIndex)) {
    const payloadRuleIndex = Number(
      payload?.attribute_override_rule_index ?? payload?.escalation_refs?.attribute_override_rule_index
    );
    if (Number.isFinite(payloadRuleIndex) && payloadRuleIndex === selectedRuleIndex) return true;
  }

  const selectedLabel = String(filters.value.attribute_override_label || '').trim();
  if (selectedLabel) {
    const payloadLabel = String(
      payload?.attribute_override_label || payload?.escalation_refs?.attribute_override_label || ''
    ).trim();
    if (payloadLabel && payloadLabel === selectedLabel) return true;
  }

  return false;
};

const toggleTraceExpanded = id => {
  const next = new Set(expandedTraceIds.value);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  expandedTraceIds.value = next;
};

const fetchDecisionEvents = async ({ offset = 0 } = {}) => {
  if (!assistantId.value) return;
  isLoadingTraces.value = true;
  try {
    const params = {
      assistant_id: assistantId.value,
      limit: traceMeta.value.limit || 50,
      offset,
    };
    if (filters.value.inbox_id) params.inbox_id = filters.value.inbox_id;
    if (filters.value.conversation_id) params.conversation_id = filters.value.conversation_id;
    if (filters.value.mode) params.mode = filters.value.mode;
    if (filters.value.decision_type_like) {
      params.decision_type_like = filters.value.decision_type_like.trim();
    }
    if (filters.value.source_kind) params.source_kind = filters.value.source_kind.trim();
    if (filters.value.intent) params.intent = filters.value.intent.trim();
    if (filters.value.action) params.action = filters.value.action.trim();
    if (filters.value.response_kind) {
      params.response_kind = filters.value.response_kind.trim();
    }
    if (filters.value.delivery_mode) params.delivery_mode = filters.value.delivery_mode.trim();
    if (filters.value.auto_send_allowed !== '') params.auto_send_allowed = filters.value.auto_send_allowed;
    if (filters.value.article_scope_warning) {
      params.article_scope_warning = filters.value.article_scope_warning.trim();
    }
    if (filters.value.content_gap_reason) {
      params.content_gap_reason = filters.value.content_gap_reason.trim();
    }
    if (filters.value.content_gap_cluster_key) {
      params.content_gap_cluster_key = filters.value.content_gap_cluster_key.trim();
    }
    if (filters.value.attribute_key) params.attribute_key = filters.value.attribute_key.trim();
    if (filters.value.attribute_value) params.attribute_value = filters.value.attribute_value.trim();
    if (filters.value.attribute_override_rule_index) {
      params.attribute_override_rule_index = filters.value.attribute_override_rule_index.trim();
    }
    if (filters.value.attribute_override_label) params.attribute_override_label = filters.value.attribute_override_label.trim();
    if (filters.value.attribute_override_action) params.attribute_override_action = filters.value.attribute_override_action.trim();
    if (filters.value.attribute_override_match_type) params.attribute_override_match_type = filters.value.attribute_override_match_type.trim();
    if (filters.value.attribute_override_outcome) {
      params.attribute_override_outcome = filters.value.attribute_override_outcome.trim();
    }
    if (filters.value.attribute_override_condition) {
      params.attribute_override_condition = filters.value.attribute_override_condition.trim();
    }
    if (filters.value.attribute_override_guard) {
      params.attribute_override_guard = filters.value.attribute_override_guard.trim();
    }
    if (filters.value.guidance_section) params.guidance_section = filters.value.guidance_section.trim();
    if (filters.value.guidance_card) params.guidance_card = filters.value.guidance_card.trim();
    if (filters.value.guidance_scenario) params.guidance_scenario = filters.value.guidance_scenario.trim();
    if (filters.value.forced_reply_language) params.forced_reply_language = filters.value.forced_reply_language.trim();
    if (filters.value.communication_style_mode) params.communication_style_mode = filters.value.communication_style_mode.trim();
    if (filters.value.channel_runtime_default_reply_mode) params.channel_runtime_default_reply_mode = filters.value.channel_runtime_default_reply_mode.trim();
    if (filters.value.channel_runtime_binding_scope) params.channel_runtime_binding_scope = filters.value.channel_runtime_binding_scope.trim();
    if (filters.value.channel_runtime_active_segment_type) params.channel_runtime_active_segment_type = filters.value.channel_runtime_active_segment_type.trim();
    applyStrategyFiltersToParams(params);
    if (createdAfterIso.value) params.created_after = createdAfterIso.value;
    const res = await getWootAxios().get(
      `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
      { params }
    );
    traceItems.value = res?.data?.data || [];
    traceMeta.value = res?.data?.meta || traceMeta.value;
  } catch (error) {
    alert(
      error?.response?.data?.error ||
        error?.message ||
        'Failed to load response traces'
    );
  } finally {
    isLoadingTraces.value = false;
  }
};

const fetchConversationTraceTimeline = async () => {
  const conversationId = Number(filters.value.conversation_id);
  if (!assistantId.value || !Number.isFinite(conversationId) || conversationId <= 0) {
    conversationTraceTimelineItems.value = [];
    return;
  }

  isLoadingConversationTimeline.value = true;
  try {
    const params = {
      assistant_id: assistantId.value,
      conversation_id: conversationId,
      decision_type_like: 'response.trace',
      limit: 100,
      offset: 0,
    };
    if (filters.value.mode) params.mode = filters.value.mode;
    if (createdAfterIso.value) params.created_after = createdAfterIso.value;

    const res = await getWootAxios().get(
      `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
      { params }
    );
    conversationTraceTimelineItems.value = res?.data?.data || [];
  } catch (error) {
    conversationTraceTimelineItems.value = [];
    alert(
      error?.response?.data?.error ||
        error?.message ||
        'Failed to load conversation override timeline'
    );
  } finally {
    isLoadingConversationTimeline.value = false;
  }
};

const fetchTraceSample = async () => {
  if (!assistantId.value) return;
  breakdownLoadingCount.value += 1;
  try {
    const params = {
      assistant_id: assistantId.value,
      limit: 200,
      offset: 0,
    };
    if (filters.value.inbox_id) params.inbox_id = filters.value.inbox_id;
    if (filters.value.conversation_id) params.conversation_id = filters.value.conversation_id;
    if (filters.value.mode) params.mode = filters.value.mode;
    if (filters.value.decision_type_like) {
      params.decision_type_like = filters.value.decision_type_like.trim();
    }
    if (filters.value.source_kind) params.source_kind = filters.value.source_kind.trim();
    if (filters.value.intent) params.intent = filters.value.intent.trim();
    if (filters.value.action) params.action = filters.value.action.trim();
    if (filters.value.response_kind) {
      params.response_kind = filters.value.response_kind.trim();
    }
    if (filters.value.delivery_mode) params.delivery_mode = filters.value.delivery_mode.trim();
    if (filters.value.auto_send_allowed !== '') params.auto_send_allowed = filters.value.auto_send_allowed;
    if (filters.value.article_scope_warning) {
      params.article_scope_warning = filters.value.article_scope_warning.trim();
    }
    if (filters.value.content_gap_reason) {
      params.content_gap_reason = filters.value.content_gap_reason.trim();
    }
    if (filters.value.content_gap_cluster_key) {
      params.content_gap_cluster_key = filters.value.content_gap_cluster_key.trim();
    }
    if (filters.value.attribute_key) params.attribute_key = filters.value.attribute_key.trim();
    if (filters.value.attribute_value) params.attribute_value = filters.value.attribute_value.trim();
    if (filters.value.attribute_override_rule_index) {
      params.attribute_override_rule_index = filters.value.attribute_override_rule_index.trim();
    }
    if (filters.value.attribute_override_label) params.attribute_override_label = filters.value.attribute_override_label.trim();
    if (filters.value.attribute_override_action) params.attribute_override_action = filters.value.attribute_override_action.trim();
    if (filters.value.attribute_override_match_type) params.attribute_override_match_type = filters.value.attribute_override_match_type.trim();
    if (filters.value.attribute_override_outcome) {
      params.attribute_override_outcome = filters.value.attribute_override_outcome.trim();
    }
    if (filters.value.attribute_override_condition) {
      params.attribute_override_condition = filters.value.attribute_override_condition.trim();
    }
    if (filters.value.attribute_override_guard) {
      params.attribute_override_guard = filters.value.attribute_override_guard.trim();
    }
    if (filters.value.guidance_section) params.guidance_section = filters.value.guidance_section.trim();
    if (filters.value.guidance_card) params.guidance_card = filters.value.guidance_card.trim();
    if (filters.value.guidance_scenario) params.guidance_scenario = filters.value.guidance_scenario.trim();
    if (filters.value.forced_reply_language) params.forced_reply_language = filters.value.forced_reply_language.trim();
    if (filters.value.communication_style_mode) params.communication_style_mode = filters.value.communication_style_mode.trim();
    if (filters.value.channel_runtime_default_reply_mode) params.channel_runtime_default_reply_mode = filters.value.channel_runtime_default_reply_mode.trim();
    if (filters.value.channel_runtime_binding_scope) params.channel_runtime_binding_scope = filters.value.channel_runtime_binding_scope.trim();
    if (filters.value.channel_runtime_active_segment_type) params.channel_runtime_active_segment_type = filters.value.channel_runtime_active_segment_type.trim();
    applyStrategyFiltersToParams(params);
    if (createdAfterIso.value) params.created_after = createdAfterIso.value;
    const res = await getWootAxios().get(
      `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
      { params }
    );
    traceSampleItems.value = res?.data?.data || [];
  } catch (error) {
    alert(
      error?.response?.data?.error ||
        error?.message ||
        'Failed to load trace breakdown sample'
    );
  } finally {
    breakdownLoadingCount.value = Math.max(0, breakdownLoadingCount.value - 1);
  }
};

const fetchFailureTags = async () => {
  if (!assistantId.value) return;
  isLoadingFailures.value = true;
  try {
    const params = {
      assistant_id: assistantId.value,
      limit: failureMeta.value.limit || 20,
      offset: 0,
    };
    if (filters.value.inbox_id) params.inbox_id = filters.value.inbox_id;
    if (filters.value.mode) params.mode = filters.value.mode;
    if (filters.value.intent) params.intent = filters.value.intent.trim();
    if (filters.value.failure_status && filters.value.failure_status !== 'all') {
      params.status = filters.value.failure_status;
    }
    if (createdAfterIso.value) params.created_after = createdAfterIso.value;

    const res = await getWootAxios().get(
      `/api/v1/accounts/${accountId.value}/ailab/ai_failure_tags`,
      { params }
    );
    failureItems.value = res?.data?.data || [];
    failureMeta.value = res?.data?.meta || failureMeta.value;
  } catch (error) {
    alert(
      error?.response?.data?.error ||
        error?.message ||
        'Failed to load failure queue'
    );
  } finally {
    isLoadingFailures.value = false;
  }
};

const fetchFailureSample = async () => {
  if (!assistantId.value) return;
  breakdownLoadingCount.value += 1;
  try {
    const params = {
      assistant_id: assistantId.value,
      limit: 200,
      offset: 0,
    };
    if (filters.value.inbox_id) params.inbox_id = filters.value.inbox_id;
    if (filters.value.mode) params.mode = filters.value.mode;
    if (filters.value.intent) params.intent = filters.value.intent.trim();
    if (createdAfterIso.value) params.created_after = createdAfterIso.value;
    const res = await getWootAxios().get(
      `/api/v1/accounts/${accountId.value}/ailab/ai_failure_tags`,
      { params }
    );
    failureSampleItems.value = res?.data?.data || [];
  } catch (error) {
    alert(
      error?.response?.data?.error ||
        error?.message ||
        'Failed to load failure breakdown sample'
    );
  } finally {
    breakdownLoadingCount.value = Math.max(0, breakdownLoadingCount.value - 1);
  }
};

const normalizeAggregateRows = response =>
  (response?.data?.data || []).map(row => ({
    label: String(row?.key || '').trim(),
    count: Number(row?.count) || 0,
  }));

const normalizeGuidanceOutcomeRows = response =>
  (response?.data?.data || []).map(row => {
    const usedCount = Number(row?.used_count) || 0;
    const resolvedCount = Number(row?.resolved_count) || 0;
    const escalatedCount = Number(row?.escalated_count) || 0;
    const unresolvedCount =
      row?.unresolved_count !== undefined
        ? Number(row?.unresolved_count) || 0
        : Math.max(usedCount - resolvedCount - escalatedCount, 0);
    const key = String(row?.key || '').trim();
    return {
      key,
      label: String(row?.label || key).trim() || key,
      usedCount,
      resolvedCount,
      escalatedCount,
      unresolvedCount,
      resolvedRate: usedCount > 0 ? resolvedCount / usedCount : 0,
      escalatedRate: usedCount > 0 ? escalatedCount / usedCount : 0,
      unresolvedRate: usedCount > 0 ? unresolvedCount / usedCount : 0,
    };
  });

const normalizeAttributeOverrideOutcomeRows = response =>
  (response?.data?.data || []).map(row => {
    const usedCount = Number(row?.used_count) || 0;
    const resolvedCount = Number(row?.resolved_count) || 0;
    const escalatedCount = Number(row?.escalated_count) || 0;
    const unresolvedCount = Number(row?.unresolved_count) || 0;
    const topInboxId = Number(row?.top_inbox_id);
    const topInboxUsedCount = Number(row?.top_inbox_used_count) || 0;
    const topInboxResolvedCount = Number(row?.top_inbox_resolved_count) || 0;
    const topInboxEscalatedCount = Number(row?.top_inbox_escalated_count) || 0;
    const key = String(row?.key || '').trim();
    const label = String(row?.label || '').trim() || `Rule ${key}`;
    return {
      key,
      label,
      matchType: String(row?.match_type || '').trim(),
      action: String(row?.action || '').trim(),
      conditionSummary: String(row?.condition_summary || '').trim(),
      usedCount,
      resolvedCount,
      escalatedCount,
      unresolvedCount,
      topInboxId: Number.isFinite(topInboxId) ? topInboxId : null,
      topInboxUsedCount,
      topInboxResolvedCount,
      topInboxEscalatedCount,
      resolvedRate: usedCount > 0 ? resolvedCount / usedCount : 0,
      escalatedRate: usedCount > 0 ? escalatedCount / usedCount : 0,
      topInboxResolvedRate:
        topInboxUsedCount > 0 ? topInboxResolvedCount / topInboxUsedCount : 0,
      topInboxEscalatedRate:
        topInboxUsedCount > 0 ? topInboxEscalatedCount / topInboxUsedCount : 0,
    };
  });

const normalizeAttributeOverrideInboxOutcomeRows = response =>
  (response?.data?.data || []).map(row => {
    const usedCount = Number(row?.used_count) || 0;
    const resolvedCount = Number(row?.resolved_count) || 0;
    const escalatedCount = Number(row?.escalated_count) || 0;
    const unresolvedCount = Number(row?.unresolved_count) || 0;
    const ruleIndex = Number(row?.rule_index);
    const inboxId = Number(row?.inbox_id);
    return {
      key: String(row?.key || '').trim(),
      ruleIndex: Number.isFinite(ruleIndex) ? ruleIndex : null,
      label: String(row?.label || '').trim() || `Rule ${ruleIndex}`,
      inboxId: Number.isFinite(inboxId) ? inboxId : null,
      matchType: String(row?.match_type || '').trim(),
      action: String(row?.action || '').trim(),
      conditionSummary: String(row?.condition_summary || '').trim(),
      usedCount,
      resolvedCount,
      escalatedCount,
      unresolvedCount,
      resolvedRate: usedCount > 0 ? resolvedCount / usedCount : 0,
      escalatedRate: usedCount > 0 ? escalatedCount / usedCount : 0,
    };
  });

const normalizeAttributeOverrideRecentCaseRows = response =>
  (response?.data?.data || []).map(row => {
    const ruleIndex = Number(row?.rule_index);
    const inboxId = Number(row?.inbox_id);
    const conversationId = Number(row?.conversation_id);
    const decisionEventId = Number(row?.decision_event_id);
    return {
      key: String(row?.key || '').trim(),
      ruleIndex: Number.isFinite(ruleIndex) ? ruleIndex : null,
      label: String(row?.label || '').trim() || `Rule ${ruleIndex}`,
      inboxId: Number.isFinite(inboxId) ? inboxId : null,
      conversationId: Number.isFinite(conversationId) ? conversationId : null,
      decisionEventId: Number.isFinite(decisionEventId) ? decisionEventId : null,
      matchType: String(row?.match_type || '').trim(),
      action: String(row?.action || '').trim(),
      conditionSummary: String(row?.condition_summary || '').trim(),
      matchedConditionKeys: Array.isArray(row?.matched_condition_keys)
        ? row.matched_condition_keys.map(value => String(value || '').trim()).filter(Boolean)
        : [],
      matchedConditionSummaries: Array.isArray(row?.matched_condition_summaries)
        ? row.matched_condition_summaries.map(value => String(value || '').trim()).filter(Boolean)
        : [],
      passedGuardKeys: Array.isArray(row?.passed_guard_keys)
        ? row.passed_guard_keys.map(value => String(value || '').trim()).filter(Boolean)
        : [],
      passedGuardSummaries: Array.isArray(row?.passed_guard_summaries)
        ? row.passed_guard_summaries.map(value => String(value || '').trim()).filter(Boolean)
        : [],
      outcome: String(row?.outcome || 'pending').trim() || 'pending',
      occurredAt: String(row?.occurred_at || '').trim(),
      outcomeAt: String(row?.outcome_at || '').trim(),
      timeToOutcomeSeconds: Number(row?.time_to_outcome_seconds) || 0,
    };
  });

const normalizeAttributeOverrideBranchOutcomeRows = response =>
  (response?.data?.data || []).map(row => {
    const usedCount = Number(row?.used_count) || 0;
    const resolvedCount = Number(row?.resolved_count) || 0;
    const escalatedCount = Number(row?.escalated_count) || 0;
    const unresolvedCount = Number(row?.unresolved_count) || 0;
    const ruleIndex = Number(row?.rule_index);
    const topInboxId = Number(row?.top_inbox_id);
    const latestInboxId = Number(row?.latest_inbox_id);
    const latestConversationId = Number(row?.latest_conversation_id);
    const latestDecisionEventId = Number(row?.latest_decision_event_id);
    const latestResolvedInboxId = Number(row?.latest_resolved_inbox_id);
    const latestResolvedConversationId = Number(
      row?.latest_resolved_conversation_id
    );
    const latestResolvedDecisionEventId = Number(
      row?.latest_resolved_decision_event_id
    );
    const latestEscalatedInboxId = Number(row?.latest_escalated_inbox_id);
    const latestEscalatedConversationId = Number(
      row?.latest_escalated_conversation_id
    );
    const latestEscalatedDecisionEventId = Number(
      row?.latest_escalated_decision_event_id
    );
    return {
      key: String(row?.key || '').trim(),
      ruleIndex: Number.isFinite(ruleIndex) ? ruleIndex : null,
      ruleLabel: String(row?.rule_label || '').trim() || `Rule ${ruleIndex}`,
      label: String(row?.label || '').trim(),
      matchType: String(row?.match_type || '').trim(),
      action: String(row?.action || '').trim(),
      conditionSummary: String(row?.condition_summary || '').trim(),
      usedCount,
      resolvedCount,
      escalatedCount,
      unresolvedCount,
      topInboxId: Number.isFinite(topInboxId) ? topInboxId : null,
      topInboxUsedCount: Number(row?.top_inbox_used_count) || 0,
      topInboxResolvedCount: Number(row?.top_inbox_resolved_count) || 0,
      topInboxEscalatedCount: Number(row?.top_inbox_escalated_count) || 0,
      topInboxUnresolvedCount: Number(row?.top_inbox_unresolved_count) || 0,
      latestInboxId: Number.isFinite(latestInboxId) ? latestInboxId : null,
      latestConversationId: Number.isFinite(latestConversationId)
        ? latestConversationId
        : null,
      latestDecisionEventId: Number.isFinite(latestDecisionEventId)
        ? latestDecisionEventId
        : null,
      latestOccurredAt: String(row?.latest_occurred_at || '').trim(),
      latestOutcome: String(row?.latest_outcome || '').trim(),
      latestOutcomeAt: String(row?.latest_outcome_at || '').trim(),
      latestTimeToOutcomeSeconds:
        Number(row?.latest_time_to_outcome_seconds) || 0,
      latestResolvedInboxId: Number.isFinite(latestResolvedInboxId)
        ? latestResolvedInboxId
        : null,
      latestResolvedConversationId: Number.isFinite(latestResolvedConversationId)
        ? latestResolvedConversationId
        : null,
      latestResolvedDecisionEventId: Number.isFinite(latestResolvedDecisionEventId)
        ? latestResolvedDecisionEventId
        : null,
      latestResolvedOccurredAt: String(row?.latest_resolved_occurred_at || '').trim(),
      latestResolvedOutcomeAt: String(row?.latest_resolved_outcome_at || '').trim(),
      latestResolvedTimeToOutcomeSeconds:
        Number(row?.latest_resolved_time_to_outcome_seconds) || 0,
      latestEscalatedInboxId: Number.isFinite(latestEscalatedInboxId)
        ? latestEscalatedInboxId
        : null,
      latestEscalatedConversationId: Number.isFinite(
        latestEscalatedConversationId
      )
        ? latestEscalatedConversationId
        : null,
      latestEscalatedDecisionEventId: Number.isFinite(
        latestEscalatedDecisionEventId
      )
        ? latestEscalatedDecisionEventId
        : null,
      latestEscalatedOccurredAt: String(
        row?.latest_escalated_occurred_at || ''
      ).trim(),
      latestEscalatedOutcomeAt: String(
        row?.latest_escalated_outcome_at || ''
      ).trim(),
      latestEscalatedTimeToOutcomeSeconds:
        Number(row?.latest_escalated_time_to_outcome_seconds) || 0,
      resolvedRate: usedCount > 0 ? resolvedCount / usedCount : 0,
      escalatedRate: usedCount > 0 ? escalatedCount / usedCount : 0,
    };
  });

const normalizeAttributeOverrideRecommendationRows = response =>
  (response?.data?.data || []).map(row => ({
    key: String(row?.key || '').trim(),
    ruleIndex: Number(row?.rule_index),
    label: String(row?.label || '').trim(),
    matchType: String(row?.match_type || '').trim(),
    action: String(row?.action || '').trim(),
    conditionSummary: String(row?.condition_summary || '').trim(),
    title: String(row?.title || '').trim(),
    body: String(row?.body || '').trim(),
    evidenceSummary: String(row?.evidence_summary || '').trim(),
    compareSummary: String(row?.compare_summary || '').trim(),
    compareFocusCode: String(row?.compare_focus_code || '').trim(),
    compareFocusTitle: String(row?.compare_focus_title || '').trim(),
    compareFocus: String(row?.compare_focus || '').trim(),
    compareNextStep: String(row?.compare_next_step || '').trim(),
    fixPresetCode: String(row?.fix_preset_code || '').trim(),
    fixPresetTitle: String(row?.fix_preset_title || '').trim(),
    fixPresetBody: String(row?.fix_preset_body || '').trim(),
    fixPresetDrilldownOutcome: String(
      row?.fix_preset_drilldown_outcome || ''
    ).trim(),
    fixPresetDrilldownInboxId: Number(row?.fix_preset_drilldown_inbox_id) || null,
    fixIntentCode: String(row?.fix_intent_code || '').trim(),
    fixIntentTitle: String(row?.fix_intent_title || '').trim(),
    fixIntentSummary: String(row?.fix_intent_summary || '').trim(),
    fixBriefTitle: String(row?.fix_brief_title || '').trim(),
    fixBriefBody: String(row?.fix_brief_body || '').trim(),
    fixBriefValidation: String(row?.fix_brief_validation || '').trim(),
    repairBundleTitle: String(row?.repair_bundle_title || '').trim(),
    repairBundleHotspotSummary: String(
      row?.repair_bundle_hotspot_summary || ''
    ).trim(),
    repairBundleResolvedSummary: String(
      row?.repair_bundle_resolved_summary || ''
    ).trim(),
    repairBundleEscalatedSummary: String(
      row?.repair_bundle_escalated_summary || ''
    ).trim(),
    repairBundleValidationSummary: String(
      row?.repair_bundle_validation_summary || ''
    ).trim(),
    suggestedChange: String(row?.suggested_change || '').trim(),
    nextStep: String(row?.next_step || '').trim(),
    severity: String(row?.severity || '').trim(),
    recommendationCode: String(row?.recommendation_code || '').trim(),
    usedCount: Number(row?.used_count) || 0,
    resolvedCount: Number(row?.resolved_count) || 0,
    escalatedCount: Number(row?.escalated_count) || 0,
    unresolvedCount: Number(row?.unresolved_count) || 0,
    resolvedRate: Number(row?.resolved_rate) || 0,
    escalatedRate: Number(row?.escalated_rate) || 0,
    unresolvedRate: Number(row?.unresolved_rate) || 0,
    topInboxId: Number(row?.top_inbox_id) || null,
    topInboxShare: Number(row?.top_inbox_share) || 0,
    topInboxResolvedRate: Number(row?.top_inbox_resolved_rate) || 0,
    topInboxEscalatedRate: Number(row?.top_inbox_escalated_rate) || 0,
  }));

const normalizeTraceStrategyRecommendationRows = response =>
  (response?.data?.data || []).map(row => ({
    key: String(row?.key || '').trim(),
    label: String(row?.label || '').trim(),
    strategyGroup: String(row?.strategy_group || '').trim(),
    strategyGroupTitle: String(row?.strategy_group_title || '').trim(),
    title: String(row?.title || '').trim(),
    body: String(row?.body || '').trim(),
    evidenceSummary: String(row?.evidence_summary || '').trim(),
    suggestedChange: String(row?.suggested_change || '').trim(),
    nextStep: String(row?.next_step || '').trim(),
    severity: String(row?.severity || '').trim(),
    recommendationCode: String(row?.recommendation_code || '').trim(),
    targetWorkspace: String(row?.target_workspace || '').trim(),
    guidanceEditor: String(row?.guidance_editor || '').trim(),
    escalationFocus: String(row?.escalation_focus || '').trim(),
    usedCount: Number(row?.used_count) || 0,
    resolvedCount: Number(row?.resolved_count) || 0,
    escalatedCount: Number(row?.escalated_count) || 0,
    unresolvedCount: Number(row?.unresolved_count) || 0,
    resolvedRate: Number(row?.resolved_rate) || 0,
    escalatedRate: Number(row?.escalated_rate) || 0,
    unresolvedRate: Number(row?.unresolved_rate) || 0,
    latestInboxId: Number(row?.latest_inbox_id) || null,
    latestConversationId: Number(row?.latest_conversation_id) || null,
    latestDecisionEventId: Number(row?.latest_decision_event_id) || null,
    latestOccurredAt: String(row?.latest_occurred_at || '').trim(),
    latestOutcome: String(row?.latest_outcome || '').trim(),
    latestOutcomeAt: String(row?.latest_outcome_at || '').trim(),
    latestTimeToOutcomeSeconds:
      Number(row?.latest_time_to_outcome_seconds) || 0,
    latestResolvedInboxId: Number(row?.latest_resolved_inbox_id) || null,
    latestResolvedConversationId:
      Number(row?.latest_resolved_conversation_id) || null,
    latestResolvedDecisionEventId:
      Number(row?.latest_resolved_decision_event_id) || null,
    latestResolvedOccurredAt: String(
      row?.latest_resolved_occurred_at || ''
    ).trim(),
    latestResolvedOutcomeAt: String(
      row?.latest_resolved_outcome_at || ''
    ).trim(),
    latestResolvedTimeToOutcomeSeconds:
      Number(row?.latest_resolved_time_to_outcome_seconds) || 0,
    latestEscalatedInboxId: Number(row?.latest_escalated_inbox_id) || null,
    latestEscalatedConversationId:
      Number(row?.latest_escalated_conversation_id) || null,
    latestEscalatedDecisionEventId:
      Number(row?.latest_escalated_decision_event_id) || null,
    latestEscalatedOccurredAt: String(
      row?.latest_escalated_occurred_at || ''
    ).trim(),
    latestEscalatedOutcomeAt: String(
      row?.latest_escalated_outcome_at || ''
    ).trim(),
    latestEscalatedTimeToOutcomeSeconds:
      Number(row?.latest_escalated_time_to_outcome_seconds) || 0,
    repairBundleTitle: String(row?.repair_bundle_title || '').trim(),
    repairBundleHotspotSummary: String(
      row?.repair_bundle_hotspot_summary || ''
    ).trim(),
    repairBundleHotspotActionTitle: String(
      row?.repair_bundle_hotspot_action_title || ''
    ).trim(),
    repairBundleHotspotActionWorkspace: String(
      row?.repair_bundle_hotspot_action_workspace || ''
    ).trim(),
    repairBundleHotspotActionEditor: String(
      row?.repair_bundle_hotspot_action_editor || ''
    ).trim(),
    repairBundleHotspotActionFocus: String(
      row?.repair_bundle_hotspot_action_focus || ''
    ).trim(),
    repairBundleHotspotActionInboxId:
      Number(row?.repair_bundle_hotspot_action_inbox_id) || null,
    repairBundleResolvedSummary: String(
      row?.repair_bundle_resolved_summary || ''
    ).trim(),
    repairBundleResolvedActionTitle: String(
      row?.repair_bundle_resolved_action_title || ''
    ).trim(),
    repairBundleResolvedActionInboxId:
      Number(row?.repair_bundle_resolved_action_inbox_id) || null,
    repairBundleResolvedActionConversationId:
      Number(row?.repair_bundle_resolved_action_conversation_id) || null,
    repairBundleResolvedActionOutcome: String(
      row?.repair_bundle_resolved_action_outcome || ''
    ).trim(),
    repairBundleEscalatedSummary: String(
      row?.repair_bundle_escalated_summary || ''
    ).trim(),
    repairBundleEscalatedActionTitle: String(
      row?.repair_bundle_escalated_action_title || ''
    ).trim(),
    repairBundleEscalatedActionInboxId:
      Number(row?.repair_bundle_escalated_action_inbox_id) || null,
    repairBundleEscalatedActionConversationId:
      Number(row?.repair_bundle_escalated_action_conversation_id) || null,
    repairBundleEscalatedActionOutcome: String(
      row?.repair_bundle_escalated_action_outcome || ''
    ).trim(),
    repairBundleValidationSummary: String(
      row?.repair_bundle_validation_summary || ''
    ).trim(),
    repairBundleValidationActionTitle: String(
      row?.repair_bundle_validation_action_title || ''
    ).trim(),
    repairBundleValidationActionInboxId:
      Number(row?.repair_bundle_validation_action_inbox_id) || null,
    repairBundleValidationActionOutcome: String(
      row?.repair_bundle_validation_action_outcome || ''
    ).trim(),
  }));

const normalizeAttributeOverrideBranchRecommendationRows = response =>
  (response?.data?.data || []).map(row => ({
    key: String(row?.key || '').trim(),
    ruleIndex: Number(row?.rule_index),
    ruleLabel: String(row?.rule_label || '').trim(),
    label: String(row?.label || '').trim(),
    branchType: String(row?.branch_type || '').trim(),
    matchType: String(row?.match_type || '').trim(),
    action: String(row?.action || '').trim(),
    conditionSummary: String(row?.condition_summary || '').trim(),
    title: String(row?.title || '').trim(),
    body: String(row?.body || '').trim(),
    evidenceSummary: String(row?.evidence_summary || '').trim(),
    suggestedChange: String(row?.suggested_change || '').trim(),
    nextStep: String(row?.next_step || '').trim(),
    severity: String(row?.severity || '').trim(),
    recommendationCode: String(row?.recommendation_code || '').trim(),
    usedCount: Number(row?.used_count) || 0,
    resolvedCount: Number(row?.resolved_count) || 0,
    escalatedCount: Number(row?.escalated_count) || 0,
    unresolvedCount: Number(row?.unresolved_count) || 0,
    resolvedRate: Number(row?.resolved_rate) || 0,
    escalatedRate: Number(row?.escalated_rate) || 0,
    unresolvedRate: Number(row?.unresolved_rate) || 0,
    topInboxId: Number(row?.top_inbox_id) || null,
    topInboxUsedCount: Number(row?.top_inbox_used_count) || 0,
    topInboxResolvedCount: Number(row?.top_inbox_resolved_count) || 0,
    topInboxEscalatedCount: Number(row?.top_inbox_escalated_count) || 0,
    topInboxUnresolvedCount: Number(row?.top_inbox_unresolved_count) || 0,
    topInboxShare: Number(row?.top_inbox_share) || 0,
    topInboxResolvedRate: Number(row?.top_inbox_resolved_rate) || 0,
    topInboxEscalatedRate: Number(row?.top_inbox_escalated_rate) || 0,
    latestInboxId: Number(row?.latest_inbox_id) || null,
    latestConversationId: Number(row?.latest_conversation_id) || null,
    latestDecisionEventId: Number(row?.latest_decision_event_id) || null,
    latestOccurredAt: String(row?.latest_occurred_at || '').trim(),
    latestOutcome: String(row?.latest_outcome || '').trim(),
    latestOutcomeAt: String(row?.latest_outcome_at || '').trim(),
    latestTimeToOutcomeSeconds:
      Number(row?.latest_time_to_outcome_seconds) || 0,
    latestResolvedInboxId: Number(row?.latest_resolved_inbox_id) || null,
    latestResolvedConversationId:
      Number(row?.latest_resolved_conversation_id) || null,
    latestResolvedDecisionEventId:
      Number(row?.latest_resolved_decision_event_id) || null,
    latestResolvedOccurredAt: String(
      row?.latest_resolved_occurred_at || ''
    ).trim(),
    latestResolvedOutcomeAt: String(
      row?.latest_resolved_outcome_at || ''
    ).trim(),
    latestResolvedTimeToOutcomeSeconds:
      Number(row?.latest_resolved_time_to_outcome_seconds) || 0,
    latestEscalatedInboxId: Number(row?.latest_escalated_inbox_id) || null,
    latestEscalatedConversationId:
      Number(row?.latest_escalated_conversation_id) || null,
    latestEscalatedDecisionEventId:
      Number(row?.latest_escalated_decision_event_id) || null,
    latestEscalatedOccurredAt: String(
      row?.latest_escalated_occurred_at || ''
    ).trim(),
    latestEscalatedOutcomeAt: String(
      row?.latest_escalated_outcome_at || ''
    ).trim(),
    latestEscalatedTimeToOutcomeSeconds:
      Number(row?.latest_escalated_time_to_outcome_seconds) || 0,
    fixPresetDrilldownOutcome: String(
      row?.fix_preset_drilldown_outcome || ''
    ).trim(),
    fixPresetDrilldownInboxId: Number(row?.fix_preset_drilldown_inbox_id) || null,
    fixIntentCode: String(row?.fix_intent_code || '').trim(),
    fixIntentTitle: String(row?.fix_intent_title || '').trim(),
    fixIntentSummary: String(row?.fix_intent_summary || '').trim(),
    fixBriefTitle: String(row?.fix_brief_title || '').trim(),
    fixBriefBody: String(row?.fix_brief_body || '').trim(),
    fixBriefValidation: String(row?.fix_brief_validation || '').trim(),
    repairBundleTitle: String(row?.repair_bundle_title || '').trim(),
    repairBundleHotspotSummary: String(
      row?.repair_bundle_hotspot_summary || ''
    ).trim(),
    repairBundleHotspotActionTitle: String(
      row?.repair_bundle_hotspot_action_title || ''
    ).trim(),
    repairBundleHotspotStageCode: String(
      row?.repair_bundle_hotspot_stage_code || ''
    ).trim(),
    repairBundleHotspotStageTitle: String(
      row?.repair_bundle_hotspot_stage_title || ''
    ).trim(),
    repairBundleHotspotStageEvidence: String(
      row?.repair_bundle_hotspot_stage_evidence || ''
    ).trim(),
    repairBundleHotspotStageFocus: String(
      row?.repair_bundle_hotspot_stage_focus || ''
    ).trim(),
    repairBundleHotspotStageDecision: String(
      row?.repair_bundle_hotspot_stage_decision || ''
    ).trim(),
    repairBundleHotspotStageRepairMove: String(
      row?.repair_bundle_hotspot_stage_repair_move || ''
    ).trim(),
    repairBundleHotspotStageWatchpoint: String(
      row?.repair_bundle_hotspot_stage_watchpoint || ''
    ).trim(),
    repairBundleHotspotStageExecutionBrief: String(
      row?.repair_bundle_hotspot_stage_execution_brief || ''
    ).trim(),
    repairBundleHotspotStageValidationFocus: String(
      row?.repair_bundle_hotspot_stage_validation_focus || ''
    ).trim(),
    repairBundleHotspotStageOwnerPacketTitle: String(
      row?.repair_bundle_hotspot_stage_owner_packet_title || ''
    ).trim(),
    repairBundleHotspotStageOwnerPacketBody: String(
      row?.repair_bundle_hotspot_stage_owner_packet_body || ''
    ).trim(),
    repairBundleHotspotStageOwnerRole: String(
      row?.repair_bundle_hotspot_stage_owner_role || ''
    ).trim(),
    repairBundleHotspotStageHandoffTarget: String(
      row?.repair_bundle_hotspot_stage_handoff_target || ''
    ).trim(),
    repairBundleHotspotStageSuccessCheck: String(
      row?.repair_bundle_hotspot_stage_success_check || ''
    ).trim(),
    repairBundleHotspotStageLedgerStatus: String(
      row?.repair_bundle_hotspot_stage_ledger_status || ''
    ).trim(),
    repairBundleHotspotStageLastAction: String(
      row?.repair_bundle_hotspot_stage_last_action || ''
    ).trim(),
    repairBundleHotspotStageReopenRule: String(
      row?.repair_bundle_hotspot_stage_reopen_rule || ''
    ).trim(),
    repairBundleHotspotStageNextOwner: String(
      row?.repair_bundle_hotspot_stage_next_owner || ''
    ).trim(),
    repairBundleHotspotStageBlockedReason: String(
      row?.repair_bundle_hotspot_stage_blocked_reason || ''
    ).trim(),
    repairBundleHotspotStageCompletionGate: String(
      row?.repair_bundle_hotspot_stage_completion_gate || ''
    ).trim(),
    repairBundleHotspotStageDoneState: String(
      row?.repair_bundle_hotspot_stage_done_state || ''
    ).trim(),
    repairBundleHotspotStageFailState: String(
      row?.repair_bundle_hotspot_stage_fail_state || ''
    ).trim(),
    repairBundleHotspotStageRollbackTrigger: String(
      row?.repair_bundle_hotspot_stage_rollback_trigger || ''
    ).trim(),
    repairBundleHotspotNextStageCode: String(
      row?.repair_bundle_hotspot_next_stage_code || ''
    ).trim(),
    repairBundleHotspotNextStageTitle: String(
      row?.repair_bundle_hotspot_next_stage_title || ''
    ).trim(),
    repairBundleHotspotActionInboxId:
      Number(row?.repair_bundle_hotspot_action_inbox_id) || null,
    repairBundleResolvedSummary: String(
      row?.repair_bundle_resolved_summary || ''
    ).trim(),
    repairBundleResolvedActionTitle: String(
      row?.repair_bundle_resolved_action_title || ''
    ).trim(),
    repairBundleResolvedStageCode: String(
      row?.repair_bundle_resolved_stage_code || ''
    ).trim(),
    repairBundleResolvedStageTitle: String(
      row?.repair_bundle_resolved_stage_title || ''
    ).trim(),
    repairBundleResolvedStageEvidence: String(
      row?.repair_bundle_resolved_stage_evidence || ''
    ).trim(),
    repairBundleResolvedStageFocus: String(
      row?.repair_bundle_resolved_stage_focus || ''
    ).trim(),
    repairBundleResolvedStageDecision: String(
      row?.repair_bundle_resolved_stage_decision || ''
    ).trim(),
    repairBundleResolvedStageRepairMove: String(
      row?.repair_bundle_resolved_stage_repair_move || ''
    ).trim(),
    repairBundleResolvedStageWatchpoint: String(
      row?.repair_bundle_resolved_stage_watchpoint || ''
    ).trim(),
    repairBundleResolvedStageExecutionBrief: String(
      row?.repair_bundle_resolved_stage_execution_brief || ''
    ).trim(),
    repairBundleResolvedStageValidationFocus: String(
      row?.repair_bundle_resolved_stage_validation_focus || ''
    ).trim(),
    repairBundleResolvedStageOwnerPacketTitle: String(
      row?.repair_bundle_resolved_stage_owner_packet_title || ''
    ).trim(),
    repairBundleResolvedStageOwnerPacketBody: String(
      row?.repair_bundle_resolved_stage_owner_packet_body || ''
    ).trim(),
    repairBundleResolvedStageOwnerRole: String(
      row?.repair_bundle_resolved_stage_owner_role || ''
    ).trim(),
    repairBundleResolvedStageHandoffTarget: String(
      row?.repair_bundle_resolved_stage_handoff_target || ''
    ).trim(),
    repairBundleResolvedStageSuccessCheck: String(
      row?.repair_bundle_resolved_stage_success_check || ''
    ).trim(),
    repairBundleResolvedStageLedgerStatus: String(
      row?.repair_bundle_resolved_stage_ledger_status || ''
    ).trim(),
    repairBundleResolvedStageLastAction: String(
      row?.repair_bundle_resolved_stage_last_action || ''
    ).trim(),
    repairBundleResolvedStageReopenRule: String(
      row?.repair_bundle_resolved_stage_reopen_rule || ''
    ).trim(),
    repairBundleResolvedStageNextOwner: String(
      row?.repair_bundle_resolved_stage_next_owner || ''
    ).trim(),
    repairBundleResolvedStageBlockedReason: String(
      row?.repair_bundle_resolved_stage_blocked_reason || ''
    ).trim(),
    repairBundleResolvedStageCompletionGate: String(
      row?.repair_bundle_resolved_stage_completion_gate || ''
    ).trim(),
    repairBundleResolvedStageDoneState: String(
      row?.repair_bundle_resolved_stage_done_state || ''
    ).trim(),
    repairBundleResolvedStageFailState: String(
      row?.repair_bundle_resolved_stage_fail_state || ''
    ).trim(),
    repairBundleResolvedStageRollbackTrigger: String(
      row?.repair_bundle_resolved_stage_rollback_trigger || ''
    ).trim(),
    repairBundleResolvedNextStageCode: String(
      row?.repair_bundle_resolved_next_stage_code || ''
    ).trim(),
    repairBundleResolvedNextStageTitle: String(
      row?.repair_bundle_resolved_next_stage_title || ''
    ).trim(),
    repairBundleResolvedActionInboxId:
      Number(row?.repair_bundle_resolved_action_inbox_id) || null,
    repairBundleResolvedActionConversationId:
      Number(row?.repair_bundle_resolved_action_conversation_id) || null,
    repairBundleResolvedActionOutcome: String(
      row?.repair_bundle_resolved_action_outcome || ''
    ).trim(),
    repairBundleEscalatedSummary: String(
      row?.repair_bundle_escalated_summary || ''
    ).trim(),
    repairBundleEscalatedActionTitle: String(
      row?.repair_bundle_escalated_action_title || ''
    ).trim(),
    repairBundleEscalatedStageCode: String(
      row?.repair_bundle_escalated_stage_code || ''
    ).trim(),
    repairBundleEscalatedStageTitle: String(
      row?.repair_bundle_escalated_stage_title || ''
    ).trim(),
    repairBundleEscalatedStageEvidence: String(
      row?.repair_bundle_escalated_stage_evidence || ''
    ).trim(),
    repairBundleEscalatedStageFocus: String(
      row?.repair_bundle_escalated_stage_focus || ''
    ).trim(),
    repairBundleEscalatedStageDecision: String(
      row?.repair_bundle_escalated_stage_decision || ''
    ).trim(),
    repairBundleEscalatedStageRepairMove: String(
      row?.repair_bundle_escalated_stage_repair_move || ''
    ).trim(),
    repairBundleEscalatedStageWatchpoint: String(
      row?.repair_bundle_escalated_stage_watchpoint || ''
    ).trim(),
    repairBundleEscalatedStageExecutionBrief: String(
      row?.repair_bundle_escalated_stage_execution_brief || ''
    ).trim(),
    repairBundleEscalatedStageValidationFocus: String(
      row?.repair_bundle_escalated_stage_validation_focus || ''
    ).trim(),
    repairBundleEscalatedStageOwnerPacketTitle: String(
      row?.repair_bundle_escalated_stage_owner_packet_title || ''
    ).trim(),
    repairBundleEscalatedStageOwnerPacketBody: String(
      row?.repair_bundle_escalated_stage_owner_packet_body || ''
    ).trim(),
    repairBundleEscalatedStageOwnerRole: String(
      row?.repair_bundle_escalated_stage_owner_role || ''
    ).trim(),
    repairBundleEscalatedStageHandoffTarget: String(
      row?.repair_bundle_escalated_stage_handoff_target || ''
    ).trim(),
    repairBundleEscalatedStageSuccessCheck: String(
      row?.repair_bundle_escalated_stage_success_check || ''
    ).trim(),
    repairBundleEscalatedStageLedgerStatus: String(
      row?.repair_bundle_escalated_stage_ledger_status || ''
    ).trim(),
    repairBundleEscalatedStageLastAction: String(
      row?.repair_bundle_escalated_stage_last_action || ''
    ).trim(),
    repairBundleEscalatedStageReopenRule: String(
      row?.repair_bundle_escalated_stage_reopen_rule || ''
    ).trim(),
    repairBundleEscalatedStageNextOwner: String(
      row?.repair_bundle_escalated_stage_next_owner || ''
    ).trim(),
    repairBundleEscalatedStageBlockedReason: String(
      row?.repair_bundle_escalated_stage_blocked_reason || ''
    ).trim(),
    repairBundleEscalatedStageCompletionGate: String(
      row?.repair_bundle_escalated_stage_completion_gate || ''
    ).trim(),
    repairBundleEscalatedStageDoneState: String(
      row?.repair_bundle_escalated_stage_done_state || ''
    ).trim(),
    repairBundleEscalatedStageFailState: String(
      row?.repair_bundle_escalated_stage_fail_state || ''
    ).trim(),
    repairBundleEscalatedStageRollbackTrigger: String(
      row?.repair_bundle_escalated_stage_rollback_trigger || ''
    ).trim(),
    repairBundleEscalatedNextStageCode: String(
      row?.repair_bundle_escalated_next_stage_code || ''
    ).trim(),
    repairBundleEscalatedNextStageTitle: String(
      row?.repair_bundle_escalated_next_stage_title || ''
    ).trim(),
    repairBundleEscalatedActionInboxId:
      Number(row?.repair_bundle_escalated_action_inbox_id) || null,
    repairBundleEscalatedActionConversationId:
      Number(row?.repair_bundle_escalated_action_conversation_id) || null,
    repairBundleEscalatedActionOutcome: String(
      row?.repair_bundle_escalated_action_outcome || ''
    ).trim(),
    repairBundleValidationSummary: String(
      row?.repair_bundle_validation_summary || ''
    ).trim(),
    repairBundleValidationActionTitle: String(
      row?.repair_bundle_validation_action_title || ''
    ).trim(),
    repairBundleValidationStageCode: String(
      row?.repair_bundle_validation_stage_code || ''
    ).trim(),
    repairBundleValidationStageTitle: String(
      row?.repair_bundle_validation_stage_title || ''
    ).trim(),
    repairBundleValidationStageEvidence: String(
      row?.repair_bundle_validation_stage_evidence || ''
    ).trim(),
    repairBundleValidationStageFocus: String(
      row?.repair_bundle_validation_stage_focus || ''
    ).trim(),
    repairBundleValidationStageDecision: String(
      row?.repair_bundle_validation_stage_decision || ''
    ).trim(),
    repairBundleValidationStageRepairMove: String(
      row?.repair_bundle_validation_stage_repair_move || ''
    ).trim(),
    repairBundleValidationStageWatchpoint: String(
      row?.repair_bundle_validation_stage_watchpoint || ''
    ).trim(),
    repairBundleValidationStageExecutionBrief: String(
      row?.repair_bundle_validation_stage_execution_brief || ''
    ).trim(),
    repairBundleValidationStageValidationFocus: String(
      row?.repair_bundle_validation_stage_validation_focus || ''
    ).trim(),
    repairBundleValidationStageOwnerPacketTitle: String(
      row?.repair_bundle_validation_stage_owner_packet_title || ''
    ).trim(),
    repairBundleValidationStageOwnerPacketBody: String(
      row?.repair_bundle_validation_stage_owner_packet_body || ''
    ).trim(),
    repairBundleValidationStageOwnerRole: String(
      row?.repair_bundle_validation_stage_owner_role || ''
    ).trim(),
    repairBundleValidationStageHandoffTarget: String(
      row?.repair_bundle_validation_stage_handoff_target || ''
    ).trim(),
    repairBundleValidationStageSuccessCheck: String(
      row?.repair_bundle_validation_stage_success_check || ''
    ).trim(),
    repairBundleValidationStageLedgerStatus: String(
      row?.repair_bundle_validation_stage_ledger_status || ''
    ).trim(),
    repairBundleValidationStageLastAction: String(
      row?.repair_bundle_validation_stage_last_action || ''
    ).trim(),
    repairBundleValidationStageReopenRule: String(
      row?.repair_bundle_validation_stage_reopen_rule || ''
    ).trim(),
    repairBundleValidationStageNextOwner: String(
      row?.repair_bundle_validation_stage_next_owner || ''
    ).trim(),
    repairBundleValidationStageBlockedReason: String(
      row?.repair_bundle_validation_stage_blocked_reason || ''
    ).trim(),
    repairBundleValidationStageCompletionGate: String(
      row?.repair_bundle_validation_stage_completion_gate || ''
    ).trim(),
    repairBundleValidationStageDoneState: String(
      row?.repair_bundle_validation_stage_done_state || ''
    ).trim(),
    repairBundleValidationStageFailState: String(
      row?.repair_bundle_validation_stage_fail_state || ''
    ).trim(),
    repairBundleValidationStageRollbackTrigger: String(
      row?.repair_bundle_validation_stage_rollback_trigger || ''
    ).trim(),
    repairBundleValidationNextStageCode: String(
      row?.repair_bundle_validation_next_stage_code || ''
    ).trim(),
    repairBundleValidationNextStageTitle: String(
      row?.repair_bundle_validation_next_stage_title || ''
    ).trim(),
    repairBundleValidationActionInboxId:
      Number(row?.repair_bundle_validation_action_inbox_id) || null,
    repairBundleValidationActionOutcome: String(
      row?.repair_bundle_validation_action_outcome || ''
    ).trim(),
  }));

const overrideHotspotRowsForRule = (ruleIndex, limit = 3) => {
  const numericRuleIndex = Number(ruleIndex);
  if (!Number.isFinite(numericRuleIndex)) return [];
  return [...(attributeOverrideInboxRowsByRule.value[numericRuleIndex] || [])]
    .sort(
      (a, b) =>
        (b.escalatedCount || 0) - (a.escalatedCount || 0) ||
        (b.unresolvedCount || 0) - (a.unresolvedCount || 0) ||
        (b.usedCount || 0) - (a.usedCount || 0) ||
        (a.inboxId || 0) - (b.inboxId || 0)
    )
    .slice(0, limit);
};

const formatOverrideHotspotSummary = row => {
  const inboxLabel = resolveInboxName(row?.inboxId) || `Inbox ${row?.inboxId}`;
  return `${inboxLabel} ${row?.escalatedCount || 0}/${row?.usedCount || 0} escalated`;
};

const operationalAggregateBaseParams = () => {
  const params = {
    assistant_id: assistantId.value,
    aggregate: 'count',
  };
  if (filters.value.inbox_id) params.inbox_id = filters.value.inbox_id;
  if (filters.value.conversation_id) params.conversation_id = filters.value.conversation_id;
  if (filters.value.mode) params.mode = filters.value.mode;
  if (filters.value.delivery_mode) params.delivery_mode = filters.value.delivery_mode.trim();
  if (filters.value.auto_send_allowed !== '') params.auto_send_allowed = filters.value.auto_send_allowed;
  if (filters.value.article_scope_warning) {
    params.article_scope_warning = filters.value.article_scope_warning.trim();
  }
  if (filters.value.content_gap_reason) {
    params.content_gap_reason = filters.value.content_gap_reason.trim();
  }
  if (filters.value.content_gap_cluster_key) {
    params.content_gap_cluster_key = filters.value.content_gap_cluster_key.trim();
  }
  if (filters.value.attribute_key) params.attribute_key = filters.value.attribute_key.trim();
  if (filters.value.attribute_value) params.attribute_value = filters.value.attribute_value.trim();
  if (filters.value.attribute_override_rule_index) {
    params.attribute_override_rule_index = filters.value.attribute_override_rule_index.trim();
  }
  if (filters.value.attribute_override_label) params.attribute_override_label = filters.value.attribute_override_label.trim();
  if (filters.value.attribute_override_action) params.attribute_override_action = filters.value.attribute_override_action.trim();
  if (filters.value.attribute_override_match_type) params.attribute_override_match_type = filters.value.attribute_override_match_type.trim();
  if (filters.value.attribute_override_outcome) {
    params.attribute_override_outcome = filters.value.attribute_override_outcome.trim();
  }
  if (filters.value.attribute_override_condition) {
    params.attribute_override_condition = filters.value.attribute_override_condition.trim();
  }
  if (filters.value.attribute_override_guard) {
    params.attribute_override_guard = filters.value.attribute_override_guard.trim();
  }
  if (filters.value.guidance_section) {
    params.guidance_section = filters.value.guidance_section.trim();
  }
  if (filters.value.guidance_card) {
    params.guidance_card = filters.value.guidance_card.trim();
  }
  if (filters.value.guidance_scenario) {
    params.guidance_scenario = filters.value.guidance_scenario.trim();
  }
  if (filters.value.forced_reply_language) {
    params.forced_reply_language = filters.value.forced_reply_language.trim();
  }
  if (filters.value.communication_style_mode) {
    params.communication_style_mode = filters.value.communication_style_mode.trim();
  }
  if (filters.value.channel_runtime_default_reply_mode) {
    params.channel_runtime_default_reply_mode =
      filters.value.channel_runtime_default_reply_mode.trim();
  }
  if (filters.value.channel_runtime_binding_scope) {
    params.channel_runtime_binding_scope =
      filters.value.channel_runtime_binding_scope.trim();
  }
  if (filters.value.channel_runtime_active_segment_type) {
    params.channel_runtime_active_segment_type =
      filters.value.channel_runtime_active_segment_type.trim();
  }
  applyStrategyFiltersToParams(params);
  if (createdAfterIso.value) params.created_after = createdAfterIso.value;
  return params;
};

const retrievalAggregateFilterParams = () => {
  const params = {};
  if (retrievalRollupFilters.value.top_priority_source) {
    params.retrieval_top_priority_source =
      retrievalRollupFilters.value.top_priority_source;
  }
  if (retrievalRollupFilters.value.search_balance) {
    params.retrieval_search_balance = retrievalRollupFilters.value.search_balance;
  }
  if (retrievalRollupFilters.value.citation_mode) {
    params.retrieval_citation_mode = retrievalRollupFilters.value.citation_mode;
  }
  if (retrievalRollupFilters.value.active_binding_scope) {
    params.retrieval_active_segment_binding_scope =
      retrievalRollupFilters.value.active_binding_scope;
  }
  if (retrievalRollupFilters.value.no_evidence_behavior) {
    params.retrieval_no_evidence_behavior =
      retrievalRollupFilters.value.no_evidence_behavior;
  }
  return params;
};

const captureRetrievalSnapshot = async () => {
  if (!assistantId.value || !accountId.value || isCapturingRetrievalSnapshot.value) return;

  isCapturingRetrievalSnapshot.value = true;
  try {
    const res = await getWootAxios().post(
      `/api/v1/accounts/${accountId.value}/ailab/ai_failure_tags/capture_content_gap_snapshot`,
      {
        assistant_id: assistantId.value,
        status: 'open',
        weeks: retrievalSnapshotWeeks.value,
      }
    );
    retrievalSnapshotSummary.value = res?.data?.snapshot_summary || {};
    alert('Retrieval queue snapshot captured.');
  } catch (error) {
    alert(error?.message || 'Failed to capture retrieval queue snapshot.');
  } finally {
    isCapturingRetrievalSnapshot.value = false;
  }
};

const fetchOperationalRollups = async () => {
  if (!assistantId.value) return;
  breakdownLoadingCount.value += 1;
  try {
    const baseParams = operationalAggregateBaseParams();
    const previousRecommendationPromise = comparisonWindowRange.value
      ? getWootAxios().get(
          `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
          {
            params: {
              ...baseParams,
              aggregate: 'trace_strategy_recommendations',
              limit: 8,
              decision_type_like: 'response.trace',
              created_after: comparisonWindowRange.value.createdAfter,
              created_before: comparisonWindowRange.value.createdBefore,
            },
          }
        )
      : Promise.resolve(null);
    const [
      timeoutTrendRes,
      deliveryTrendRes,
      timeoutIntentRes,
      deliveryModeRes,
      articleScopeWarningRes,
      contentGapClusterRes,
      contentGapReasonRes,
      contentGapTopicRes,
      attributeSourceRes,
      attributeKeyRes,
      attributeOverrideTrendRes,
      attributeOverrideLabelRes,
      attributeOverrideActionRes,
      attributeOverrideMatchTypeRes,
      attributeOverrideOutcomeRes,
      attributeOverrideInboxOutcomeRes,
      attributeOverrideConditionOutcomeRes,
      attributeOverrideGuardOutcomeRes,
      attributeOverrideConditionRecommendationRes,
      attributeOverrideGuardRecommendationRes,
      attributeOverrideRecentCaseRes,
      attributeOverrideRecommendationRes,
      forcedReplyLanguageRes,
      communicationStyleRes,
      channelRuntimeDefaultReplyModeRes,
      channelRuntimeBindingScopeRes,
      channelRuntimeActiveSegmentTypeRes,
      businessHoursActiveRes,
      businessHoursReplyModeRes,
      businessHoursResponseBehaviorRes,
      regionComplianceBindingScopeRes,
      regionComplianceActiveCountryCodeRes,
      regionComplianceActiveProfileRes,
      answerLengthStrategyRes,
      directnessStrategyRes,
      sensitiveTopicsStrategyRes,
      toneEnforcementStrategyRes,
      memoryDepthStrategyRes,
      answerConfidenceStrategyRes,
      outsideKnowledgeStrategyRes,
      promiseSafetyStrategyRes,
      uncertainActionRes,
      highRiskActionRes,
      procedureRoutingPrecedenceRes,
      procedureStepConfirmationRes,
      procedureFallbackActionRes,
      answerLengthOutcomeRes,
      directnessOutcomeRes,
      sensitiveTopicsOutcomeRes,
      toneEnforcementOutcomeRes,
      memoryDepthOutcomeRes,
      answerConfidenceOutcomeRes,
      outsideKnowledgeOutcomeRes,
      promiseSafetyOutcomeRes,
      uncertainActionOutcomeRes,
      highRiskActionOutcomeRes,
      procedureRoutingPrecedenceOutcomeRes,
      procedureStepConfirmationOutcomeRes,
      procedureFallbackActionOutcomeRes,
      channelRuntimeDefaultReplyModeOutcomeRes,
      channelRuntimeBindingScopeOutcomeRes,
      channelRuntimeActiveSegmentTypeOutcomeRes,
      businessHoursReplyModeOutcomeRes,
      businessHoursResponseBehaviorOutcomeRes,
      regionComplianceBindingScopeOutcomeRes,
      regionComplianceActiveCountryCodeOutcomeRes,
      regionComplianceActiveProfileOutcomeRes,
      strategyRecommendationRes,
      guidanceCardOutcomeRes,
      guidanceScenarioOutcomeRes,
      previousStrategyRecommendationRes,
    ] = await Promise.all([
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            group_by: 'day',
            limit: operationalTrendLimit.value,
            action: 'timeout_expired',
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            group_by: 'day',
            limit: operationalTrendLimit.value,
            decision_type_like: 'delivery_blocked',
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            group_by: 'intent',
            limit: 5,
            action: 'timeout_expired',
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            group_by: 'delivery_mode',
            limit: 5,
            decision_type_like: 'delivery_blocked',
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            group_by: 'article_scope_warning',
            limit: 5,
            decision_type_like: 'response.trace',
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            content_gap_only: true,
            group_by: 'content_gap_cluster_key',
            limit: 5,
            decision_type_like: 'response.trace',
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            content_gap_only: true,
            group_by: 'content_gap_reason',
            limit: 5,
            decision_type_like: 'response.trace',
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            content_gap_only: true,
            group_by: 'content_gap_topic',
            limit: 5,
            decision_type_like: 'response.trace',
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            group_by: 'attribute_source',
            limit: 5,
            decision_type_like: 'response.trace',
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            group_by: 'attribute_key',
            limit: 8,
            decision_type_like: 'response.trace',
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            attribute_override_only: true,
            group_by: 'day',
            limit: operationalTrendLimit.value,
            decision_type_like: 'response.trace',
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            attribute_override_only: true,
            group_by: 'attribute_override_label',
            limit: 6,
            decision_type_like: 'response.trace',
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            attribute_override_only: true,
            group_by: 'attribute_override_action',
            limit: 4,
            decision_type_like: 'response.trace',
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            attribute_override_only: true,
            group_by: 'attribute_override_match_type',
            limit: 6,
            decision_type_like: 'response.trace',
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            attribute_override_only: true,
            aggregate: 'attribute_override_outcomes',
            limit: 8,
            decision_type_like: 'response.trace',
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            attribute_override_only: true,
            aggregate: 'attribute_override_inbox_outcomes',
            limit: 12,
            decision_type_like: 'response.trace',
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            attribute_override_only: true,
            aggregate: 'attribute_override_condition_outcomes',
            limit: 12,
            decision_type_like: 'response.trace',
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            attribute_override_only: true,
            aggregate: 'attribute_override_guard_outcomes',
            limit: 12,
            decision_type_like: 'response.trace',
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            attribute_override_only: true,
            aggregate: 'attribute_override_condition_recommendations',
            limit: 8,
            decision_type_like: 'response.trace',
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            attribute_override_only: true,
            aggregate: 'attribute_override_guard_recommendations',
            limit: 8,
            decision_type_like: 'response.trace',
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            attribute_override_only: true,
            aggregate: 'attribute_override_recent_cases',
            limit: 12,
            decision_type_like: 'response.trace',
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            attribute_override_only: true,
            aggregate: 'attribute_override_recommendations',
            limit: 5,
            decision_type_like: 'response.trace',
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            group_by: 'forced_reply_language',
            limit: 5,
            decision_type_like: 'response.trace',
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            group_by: 'communication_style_mode',
            limit: 5,
            decision_type_like: 'response.trace',
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            group_by: 'channel_runtime_default_reply_mode',
            limit: 5,
            decision_type_like: 'response.trace',
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            group_by: 'channel_runtime_binding_scope',
            limit: 5,
            decision_type_like: 'response.trace',
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            group_by: 'channel_runtime_active_segment_type',
            limit: 5,
            decision_type_like: 'response.trace',
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            group_by: 'business_hours_active',
            limit: 5,
            decision_type_like: 'response.trace',
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            group_by: 'business_hours_reply_mode',
            limit: 5,
            decision_type_like: 'response.trace',
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            group_by: 'business_hours_response_behavior',
            limit: 5,
            decision_type_like: 'response.trace',
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            group_by: 'region_compliance_binding_scope',
            limit: 5,
            decision_type_like: 'response.trace',
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            group_by: 'region_compliance_active_country_code',
            limit: 5,
            decision_type_like: 'response.trace',
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            group_by: 'region_compliance_active_profile',
            limit: 5,
            decision_type_like: 'response.trace',
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            group_by: 'answer_length_strategy',
            limit: 5,
            decision_type_like: 'response.trace',
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            group_by: 'directness_strategy',
            limit: 5,
            decision_type_like: 'response.trace',
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            group_by: 'sensitive_topics_strategy',
            limit: 5,
            decision_type_like: 'response.trace',
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            group_by: 'tone_enforcement_strategy',
            limit: 5,
            decision_type_like: 'response.trace',
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            group_by: 'memory_depth_strategy',
            limit: 5,
            decision_type_like: 'response.trace',
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            group_by: 'answer_confidence_strategy',
            limit: 5,
            decision_type_like: 'response.trace',
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            group_by: 'outside_knowledge_strategy',
            limit: 5,
            decision_type_like: 'response.trace',
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            group_by: 'promise_safety_strategy',
            limit: 5,
            decision_type_like: 'response.trace',
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            group_by: 'uncertain_action',
            limit: 5,
            decision_type_like: 'response.trace',
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            group_by: 'high_risk_action',
            limit: 5,
            decision_type_like: 'response.trace',
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            group_by: 'procedure_routing_precedence',
            limit: 5,
            decision_type_like: 'response.trace',
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            group_by: 'procedure_step_confirmation_strategy',
            limit: 5,
            decision_type_like: 'response.trace',
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            group_by: 'procedure_fallback_action',
            limit: 5,
            decision_type_like: 'response.trace',
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            aggregate: 'trace_strategy_outcomes',
            group_by: 'answer_length_strategy',
            limit: 6,
            decision_type_like: 'response.trace',
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            aggregate: 'trace_strategy_outcomes',
            group_by: 'directness_strategy',
            limit: 6,
            decision_type_like: 'response.trace',
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            aggregate: 'trace_strategy_outcomes',
            group_by: 'sensitive_topics_strategy',
            limit: 6,
            decision_type_like: 'response.trace',
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            aggregate: 'trace_strategy_outcomes',
            group_by: 'tone_enforcement_strategy',
            limit: 6,
            decision_type_like: 'response.trace',
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            aggregate: 'trace_strategy_outcomes',
            group_by: 'memory_depth_strategy',
            limit: 6,
            decision_type_like: 'response.trace',
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            aggregate: 'trace_strategy_outcomes',
            group_by: 'answer_confidence_strategy',
            limit: 6,
            decision_type_like: 'response.trace',
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            aggregate: 'trace_strategy_outcomes',
            group_by: 'outside_knowledge_strategy',
            limit: 6,
            decision_type_like: 'response.trace',
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            aggregate: 'trace_strategy_outcomes',
            group_by: 'promise_safety_strategy',
            limit: 6,
            decision_type_like: 'response.trace',
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            aggregate: 'trace_strategy_outcomes',
            group_by: 'uncertain_action',
            limit: 6,
            decision_type_like: 'response.trace',
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            aggregate: 'trace_strategy_outcomes',
            group_by: 'high_risk_action',
            limit: 6,
            decision_type_like: 'response.trace',
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            aggregate: 'trace_strategy_outcomes',
            group_by: 'procedure_routing_precedence',
            limit: 6,
            decision_type_like: 'response.trace',
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            aggregate: 'trace_strategy_outcomes',
            group_by: 'procedure_step_confirmation_strategy',
            limit: 6,
            decision_type_like: 'response.trace',
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            aggregate: 'trace_strategy_outcomes',
            group_by: 'procedure_fallback_action',
            limit: 6,
            decision_type_like: 'response.trace',
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            aggregate: 'trace_strategy_outcomes',
            group_by: 'channel_runtime_default_reply_mode',
            limit: 6,
            decision_type_like: 'response.trace',
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            aggregate: 'trace_strategy_outcomes',
            group_by: 'channel_runtime_binding_scope',
            limit: 6,
            decision_type_like: 'response.trace',
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            aggregate: 'trace_strategy_outcomes',
            group_by: 'channel_runtime_active_segment_type',
            limit: 6,
            decision_type_like: 'response.trace',
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            aggregate: 'trace_strategy_outcomes',
            group_by: 'business_hours_reply_mode',
            limit: 6,
            decision_type_like: 'response.trace',
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            aggregate: 'trace_strategy_outcomes',
            group_by: 'business_hours_response_behavior',
            limit: 6,
            decision_type_like: 'response.trace',
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            aggregate: 'trace_strategy_outcomes',
            group_by: 'region_compliance_binding_scope',
            limit: 6,
            decision_type_like: 'response.trace',
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            aggregate: 'trace_strategy_outcomes',
            group_by: 'region_compliance_active_country_code',
            limit: 6,
            decision_type_like: 'response.trace',
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            aggregate: 'trace_strategy_outcomes',
            group_by: 'region_compliance_active_profile',
            limit: 6,
            decision_type_like: 'response.trace',
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            aggregate: 'trace_strategy_recommendations',
            limit: 8,
            decision_type_like: 'response.trace',
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            aggregate: 'guidance_card_outcomes',
            limit: 8,
            decision_type_like: 'response.trace',
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            aggregate: 'guidance_scenario_outcomes',
            limit: 8,
            decision_type_like: 'response.trace',
          },
        }
      ),
      previousRecommendationPromise,
    ]);
    const retrievalBaseParams = {
      ...baseParams,
      ...retrievalAggregateFilterParams(),
      decision_type_like: 'response.trace',
    };
    const [
      retrievalTopPriorityRes,
      retrievalWinningSourceRes,
      retrievalSearchBalanceRes,
      retrievalCitationModeRes,
      retrievalFallbackRes,
      retrievalSegmentScopeRes,
      retrievalTopPriorityOutcomeRes,
      retrievalWinningSourceOutcomeRes,
    ] = await Promise.all([
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...retrievalBaseParams,
            group_by: 'retrieval_top_priority_source',
            limit: 6,
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...retrievalBaseParams,
            group_by: 'retrieval_winning_source',
            limit: 6,
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...retrievalBaseParams,
            group_by: 'retrieval_search_balance',
            limit: 6,
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...retrievalBaseParams,
            group_by: 'retrieval_citation_mode',
            limit: 6,
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...retrievalBaseParams,
            group_by: 'retrieval_no_evidence_behavior',
            limit: 6,
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...retrievalBaseParams,
            group_by: 'retrieval_active_segment_binding_scope',
            limit: 6,
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...retrievalBaseParams,
            aggregate: 'trace_strategy_outcomes',
            group_by: 'retrieval_top_priority_source',
            limit: 6,
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...retrievalBaseParams,
            aggregate: 'trace_strategy_outcomes',
            group_by: 'retrieval_winning_source',
            limit: 6,
          },
        }
      ),
    ]);

    timeoutDailySeries.value = normalizeAggregateRows(timeoutTrendRes);
    deliveryBlockedDailySeries.value = normalizeAggregateRows(deliveryTrendRes);
    timeoutIntentBreakdown.value = normalizeAggregateRows(timeoutIntentRes);
    deliveryBlockedModeBreakdown.value = normalizeAggregateRows(deliveryModeRes);
    articleScopeWarningBreakdown.value = normalizeAggregateRows(articleScopeWarningRes);
    contentGapReasonBreakdown.value = normalizeAggregateRows(contentGapReasonRes);
    contentGapTopicBreakdown.value = normalizeAggregateRows(contentGapTopicRes);
    contentGapClusterBreakdown.value = normalizeAggregateRows(contentGapClusterRes);
    attributeSourceBreakdown.value = normalizeAggregateRows(attributeSourceRes);
    attributeKeyBreakdown.value = normalizeAggregateRows(attributeKeyRes);
    attributeOverrideDailySeries.value = normalizeAggregateRows(attributeOverrideTrendRes);
    attributeOverrideLabelBreakdown.value = normalizeAggregateRows(attributeOverrideLabelRes);
    attributeOverrideActionBreakdown.value = normalizeAggregateRows(attributeOverrideActionRes);
    attributeOverrideMatchTypeBreakdown.value = normalizeAggregateRows(attributeOverrideMatchTypeRes);
    attributeOverrideOutcomeBreakdown.value = normalizeAttributeOverrideOutcomeRows(attributeOverrideOutcomeRes);
    attributeOverrideInboxOutcomeRows.value =
      normalizeAttributeOverrideInboxOutcomeRows(attributeOverrideInboxOutcomeRes);
    attributeOverrideConditionOutcomeRows.value =
      normalizeAttributeOverrideBranchOutcomeRows(attributeOverrideConditionOutcomeRes);
    attributeOverrideGuardOutcomeRows.value =
      normalizeAttributeOverrideBranchOutcomeRows(attributeOverrideGuardOutcomeRes);
    attributeOverrideConditionRecommendationRows.value =
      normalizeAttributeOverrideBranchRecommendationRows(
        attributeOverrideConditionRecommendationRes
      );
    attributeOverrideGuardRecommendationRows.value =
      normalizeAttributeOverrideBranchRecommendationRows(
        attributeOverrideGuardRecommendationRes
      );
    attributeOverrideRecentCaseRows.value =
      normalizeAttributeOverrideRecentCaseRows(attributeOverrideRecentCaseRes);
    attributeOverrideRecommendationRows.value =
      normalizeAttributeOverrideRecommendationRows(attributeOverrideRecommendationRes);
    forcedReplyLanguageBreakdown.value = normalizeAggregateRows(forcedReplyLanguageRes);
    communicationStyleBreakdown.value = normalizeAggregateRows(communicationStyleRes);
    channelRuntimeDefaultReplyModeBreakdown.value =
      normalizeAggregateRows(channelRuntimeDefaultReplyModeRes);
    channelRuntimeBindingScopeBreakdown.value =
      normalizeAggregateRows(channelRuntimeBindingScopeRes);
    channelRuntimeActiveSegmentTypeBreakdown.value =
      normalizeAggregateRows(channelRuntimeActiveSegmentTypeRes);
    businessHoursActiveBreakdown.value = normalizeAggregateRows(businessHoursActiveRes);
    businessHoursReplyModeBreakdown.value =
      normalizeAggregateRows(businessHoursReplyModeRes);
    businessHoursResponseBehaviorBreakdown.value =
      normalizeAggregateRows(businessHoursResponseBehaviorRes);
    regionComplianceBindingScopeBreakdown.value =
      normalizeAggregateRows(regionComplianceBindingScopeRes);
    regionComplianceActiveCountryCodeBreakdown.value =
      normalizeAggregateRows(regionComplianceActiveCountryCodeRes);
    regionComplianceActiveProfileBreakdown.value =
      normalizeAggregateRows(regionComplianceActiveProfileRes);
    answerLengthStrategyBreakdown.value = normalizeAggregateRows(answerLengthStrategyRes);
    directnessStrategyBreakdown.value = normalizeAggregateRows(directnessStrategyRes);
    sensitiveTopicsStrategyBreakdown.value =
      normalizeAggregateRows(sensitiveTopicsStrategyRes);
    toneEnforcementStrategyBreakdown.value =
      normalizeAggregateRows(toneEnforcementStrategyRes);
    memoryDepthStrategyBreakdown.value =
      normalizeAggregateRows(memoryDepthStrategyRes);
    answerConfidenceStrategyBreakdown.value =
      normalizeAggregateRows(answerConfidenceStrategyRes);
    outsideKnowledgeStrategyBreakdown.value = normalizeAggregateRows(outsideKnowledgeStrategyRes);
    promiseSafetyStrategyBreakdown.value =
      normalizeAggregateRows(promiseSafetyStrategyRes);
    uncertainActionBreakdown.value = normalizeAggregateRows(uncertainActionRes);
    highRiskActionBreakdown.value = normalizeAggregateRows(highRiskActionRes);
    procedureRoutingPrecedenceBreakdown.value =
      normalizeAggregateRows(procedureRoutingPrecedenceRes);
    procedureStepConfirmationBreakdown.value =
      normalizeAggregateRows(procedureStepConfirmationRes);
    procedureFallbackActionBreakdown.value =
      normalizeAggregateRows(procedureFallbackActionRes);
    answerLengthOutcomeBreakdown.value = normalizeGuidanceOutcomeRows(answerLengthOutcomeRes);
    directnessOutcomeBreakdown.value = normalizeGuidanceOutcomeRows(directnessOutcomeRes);
    sensitiveTopicsOutcomeBreakdown.value =
      normalizeGuidanceOutcomeRows(sensitiveTopicsOutcomeRes);
    toneEnforcementOutcomeBreakdown.value =
      normalizeGuidanceOutcomeRows(toneEnforcementOutcomeRes);
    memoryDepthOutcomeBreakdown.value =
      normalizeGuidanceOutcomeRows(memoryDepthOutcomeRes);
    answerConfidenceOutcomeBreakdown.value =
      normalizeGuidanceOutcomeRows(answerConfidenceOutcomeRes);
    outsideKnowledgeOutcomeBreakdown.value = normalizeGuidanceOutcomeRows(outsideKnowledgeOutcomeRes);
    promiseSafetyOutcomeBreakdown.value =
      normalizeGuidanceOutcomeRows(promiseSafetyOutcomeRes);
    uncertainActionOutcomeBreakdown.value = normalizeGuidanceOutcomeRows(uncertainActionOutcomeRes);
    highRiskActionOutcomeBreakdown.value = normalizeGuidanceOutcomeRows(highRiskActionOutcomeRes);
    procedureRoutingPrecedenceOutcomeBreakdown.value =
      normalizeGuidanceOutcomeRows(procedureRoutingPrecedenceOutcomeRes);
    procedureStepConfirmationOutcomeBreakdown.value =
      normalizeGuidanceOutcomeRows(procedureStepConfirmationOutcomeRes);
    procedureFallbackActionOutcomeBreakdown.value =
      normalizeGuidanceOutcomeRows(procedureFallbackActionOutcomeRes);
    channelRuntimeDefaultReplyModeOutcomeBreakdown.value =
      normalizeGuidanceOutcomeRows(channelRuntimeDefaultReplyModeOutcomeRes);
    channelRuntimeBindingScopeOutcomeBreakdown.value =
      normalizeGuidanceOutcomeRows(channelRuntimeBindingScopeOutcomeRes);
    channelRuntimeActiveSegmentTypeOutcomeBreakdown.value =
      normalizeGuidanceOutcomeRows(channelRuntimeActiveSegmentTypeOutcomeRes);
    businessHoursReplyModeOutcomeBreakdown.value =
      normalizeGuidanceOutcomeRows(businessHoursReplyModeOutcomeRes);
    businessHoursResponseBehaviorOutcomeBreakdown.value =
      normalizeGuidanceOutcomeRows(businessHoursResponseBehaviorOutcomeRes);
    regionComplianceBindingScopeOutcomeBreakdown.value =
      normalizeGuidanceOutcomeRows(regionComplianceBindingScopeOutcomeRes);
    regionComplianceActiveCountryCodeOutcomeBreakdown.value =
      normalizeGuidanceOutcomeRows(regionComplianceActiveCountryCodeOutcomeRes);
    regionComplianceActiveProfileOutcomeBreakdown.value =
      normalizeGuidanceOutcomeRows(regionComplianceActiveProfileOutcomeRes);
    strategyRecommendationRows.value =
      normalizeTraceStrategyRecommendationRows(
        strategyRecommendationRes
      );
    previousStrategyRecommendationRows.value =
      normalizeTraceStrategyRecommendationRows(
        previousStrategyRecommendationRes
      );
    guidanceCardOutcomeBreakdown.value =
      normalizeGuidanceOutcomeRows(guidanceCardOutcomeRes);
    guidanceScenarioOutcomeBreakdown.value = normalizeGuidanceOutcomeRows(guidanceScenarioOutcomeRes);
    retrievalTopPriorityBreakdownRows.value =
      normalizeAggregateRows(retrievalTopPriorityRes);
    retrievalWinningSourceBreakdownRows.value =
      normalizeAggregateRows(retrievalWinningSourceRes);
    retrievalSearchBalanceBreakdownRows.value =
      normalizeAggregateRows(retrievalSearchBalanceRes);
    retrievalCitationModeBreakdownRows.value =
      normalizeAggregateRows(retrievalCitationModeRes);
    retrievalFallbackBreakdownRows.value =
      normalizeAggregateRows(retrievalFallbackRes);
    retrievalSegmentBindingBreakdownRows.value =
      normalizeAggregateRows(retrievalSegmentScopeRes);
    retrievalTopPriorityOutcomeRows.value =
      normalizeGuidanceOutcomeRows(retrievalTopPriorityOutcomeRes);
    retrievalWinningSourceOutcomeRows.value =
      normalizeGuidanceOutcomeRows(retrievalWinningSourceOutcomeRes);
  } catch (error) {
    alert(
      error?.response?.data?.error ||
        error?.message ||
        'Failed to load operational signal rollups'
    );
  } finally {
    breakdownLoadingCount.value = Math.max(0, breakdownLoadingCount.value - 1);
  }
};

const fetchSummary = async () => {
  if (!assistantId.value) return;
  isLoadingSummary.value = true;
  try {
    const baseParams = { assistant_id: assistantId.value, limit: 1, offset: 0 };
    if (filters.value.inbox_id) baseParams.inbox_id = filters.value.inbox_id;
    if (filters.value.conversation_id) baseParams.conversation_id = filters.value.conversation_id;
    if (filters.value.mode) baseParams.mode = filters.value.mode;
    if (filters.value.intent) baseParams.intent = filters.value.intent.trim();
    if (filters.value.delivery_mode) baseParams.delivery_mode = filters.value.delivery_mode.trim();
    if (filters.value.auto_send_allowed !== '') baseParams.auto_send_allowed = filters.value.auto_send_allowed;
    if (filters.value.content_gap_reason) baseParams.content_gap_reason = filters.value.content_gap_reason.trim();
    if (filters.value.content_gap_cluster_key) baseParams.content_gap_cluster_key = filters.value.content_gap_cluster_key.trim();
    if (filters.value.attribute_key) baseParams.attribute_key = filters.value.attribute_key.trim();
    if (filters.value.attribute_value) baseParams.attribute_value = filters.value.attribute_value.trim();
    if (filters.value.attribute_override_rule_index) {
      baseParams.attribute_override_rule_index = filters.value.attribute_override_rule_index.trim();
    }
    if (filters.value.attribute_override_label) baseParams.attribute_override_label = filters.value.attribute_override_label.trim();
    if (filters.value.attribute_override_action) baseParams.attribute_override_action = filters.value.attribute_override_action.trim();
    if (filters.value.attribute_override_match_type) baseParams.attribute_override_match_type = filters.value.attribute_override_match_type.trim();
    if (filters.value.attribute_override_outcome) {
      baseParams.attribute_override_outcome = filters.value.attribute_override_outcome.trim();
    }
    if (filters.value.attribute_override_condition) {
      baseParams.attribute_override_condition = filters.value.attribute_override_condition.trim();
    }
    if (filters.value.attribute_override_guard) {
      baseParams.attribute_override_guard = filters.value.attribute_override_guard.trim();
    }
    if (filters.value.guidance_section) baseParams.guidance_section = filters.value.guidance_section.trim();
    if (filters.value.guidance_card) baseParams.guidance_card = filters.value.guidance_card.trim();
    if (filters.value.guidance_scenario) baseParams.guidance_scenario = filters.value.guidance_scenario.trim();
    if (filters.value.forced_reply_language) baseParams.forced_reply_language = filters.value.forced_reply_language.trim();
    if (filters.value.communication_style_mode) baseParams.communication_style_mode = filters.value.communication_style_mode.trim();
    if (filters.value.channel_runtime_default_reply_mode) baseParams.channel_runtime_default_reply_mode = filters.value.channel_runtime_default_reply_mode.trim();
    if (filters.value.channel_runtime_binding_scope) baseParams.channel_runtime_binding_scope = filters.value.channel_runtime_binding_scope.trim();
    if (filters.value.channel_runtime_active_segment_type) baseParams.channel_runtime_active_segment_type = filters.value.channel_runtime_active_segment_type.trim();
    applyStrategyFiltersToParams(baseParams);
    if (createdAfterIso.value) baseParams.created_after = createdAfterIso.value;

    const [traceRes, openRes, reviewedRes, convertedRes, timeoutRes, blockedRes, attributeOverrideRes] = await Promise.all([
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            decision_type_like: 'response.trace',
            source_kind: filters.value.source_kind || undefined,
            action: filters.value.action || undefined,
            response_kind: filters.value.response_kind || undefined,
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_failure_tags`,
        { params: { ...baseParams, status: 'open' } }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_failure_tags`,
        { params: { ...baseParams, status: 'reviewed' } }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_failure_tags`,
        { params: { ...baseParams, status: 'converted' } }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        { params: { ...baseParams, action: 'timeout_expired' } }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        { params: { ...baseParams, decision_type_like: 'delivery_blocked' } }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            attribute_override_only: true,
            decision_type_like: 'response.trace',
          },
        }
      ),
    ]);

    summary.value = {
      trace_total: traceRes?.data?.meta?.total || 0,
      open_failures: openRes?.data?.meta?.total || 0,
      reviewed_failures: reviewedRes?.data?.meta?.total || 0,
      converted_failures: convertedRes?.data?.meta?.total || 0,
      timeout_expired: timeoutRes?.data?.meta?.total || 0,
      delivery_blocked: blockedRes?.data?.meta?.total || 0,
      attribute_override_hits: attributeOverrideRes?.data?.meta?.total || 0,
    };
  } catch (error) {
    alert(
      error?.response?.data?.error ||
        error?.message ||
        'Failed to load analyze summary'
    );
  } finally {
    isLoadingSummary.value = false;
  }
};

const refreshAll = async () => {
  expandedTraceIds.value = new Set();
  await Promise.allSettled([
    (async () => {
      if (!assistantId.value) return;
      const response = await AiCloserAPI.getReleaseReadiness(assistantId.value);
      releaseReadinessSummary.value = response?.data?.release_readiness || {};
      if (response?.data?.published_version_id !== undefined) {
        releaseReadinessSummary.value = {
          ...(releaseReadinessSummary.value || {}),
          published_version_id: response?.data?.published_version_id,
          draft_version_id: response?.data?.draft_version_id,
        };
      }
    })(),
    fetchSummary(),
    fetchDecisionEvents({ offset: 0 }),
    fetchConversationTraceTimeline(),
    fetchTraceSample(),
    fetchOperationalRollups(),
    fetchFailureTags(),
    fetchFailureSample(),
  ]);
};

watch(
  () => routeAnalyzeOwnerFocus.value,
  ownerLabel => {
    repairOwnerFocus.value = String(ownerLabel || '').trim();
  },
  { immediate: true }
);

watch(
  () => [
    routeAnalyzeSeed.value.enabled,
    routeAnalyzeSeed.value.inboxId,
    routeAnalyzeSeed.value.contentGapReason,
    routeAnalyzeSeed.value.articleScopeWarning,
    routeAnalyzeSeed.value.deliveryMode,
    routeAnalyzeSeed.value.timeWindow,
  ],
  async () => {
    if (!analyzeInitialized.value) return;
    syncAnalyzeSeedFilters(routeAnalyzeSeed.value);
    await refreshAll();
    if (routeAnalyzeSeed.value.enabled) scrollToTraceSection();
  },
  { deep: false }
);

watch(
  () => [
    routeOverrideDrilldown.value.enabled,
    routeOverrideDrilldown.value.ruleIndex,
    routeOverrideDrilldown.value.label,
    routeOverrideDrilldown.value.action,
    routeOverrideDrilldown.value.matchType,
    routeOverrideDrilldown.value.outcome,
    routeOverrideDrilldown.value.fixIntentCode,
    routeOverrideDrilldown.value.fixIntentTitle,
    routeOverrideDrilldown.value.inboxId,
    routeOverrideDrilldown.value.conversationId,
    routeOverrideDrilldown.value.timeWindow,
  ],
  async () => {
    if (!analyzeInitialized.value) return;
    const drilldown = routeOverrideDrilldown.value;
    syncOverrideDrilldownFilters(drilldown);
    await refreshAll();
    if (drilldown.enabled) scrollToTraceSection();
  },
  { deep: false }
);

watch(
  () => [
    retrievalRollupFilters.value.top_priority_source,
    retrievalRollupFilters.value.search_balance,
    retrievalRollupFilters.value.citation_mode,
    retrievalRollupFilters.value.active_binding_scope,
    retrievalRollupFilters.value.no_evidence_behavior,
  ],
  async () => {
    if (!analyzeInitialized.value) return;
    await fetchOperationalRollups();
  },
  { deep: false }
);

watch(
  () => [
    showAdvancedDiagnostics.value,
    repairOwnerFocusSummary.value?.ownerLabel || '',
    operatingUrgentRows.value.length,
    operatingReviewRows.value.length,
    focusedRepairTeamQueueRows.value.length,
    focusedRepairBoardRows.value.length,
    strategyRecommendationRows.value.length,
    traceSampleItems.value.length,
    failureItems.value.length,
  ],
  async () => {
    if (!analyzeInitialized.value) return;
    await nextTick();
    scheduleAnalyzeSectionSync();
  },
  { deep: false }
);

const updateFailureStatus = async (tag, status) => {
  if (!tag?.id) return;
  try {
    const res = await getWootAxios().patch(
      `/api/v1/accounts/${accountId.value}/ailab/ai_failure_tags/${tag.id}`,
      { status }
    );
    const updated = res?.data?.tag;
    if (updated?.id) {
      failureItems.value = failureItems.value.map(item =>
        item.id === updated.id ? updated : item
      );
    }
    await fetchFailureTags();
    await fetchSummary();
  } catch (error) {
    alert(
      error?.response?.data?.error ||
        error?.message ||
        'Failed to update failure status'
    );
  }
};

const convertToTemplate = async tag => {
  if (!tag?.id) return;
  try {
    const res = await getWootAxios().post(
      `/api/v1/accounts/${accountId.value}/ailab/ai_failure_tags/${tag.id}/convert_to_template`
    );
    const updated = res?.data?.tag;
    if (updated?.id) {
      failureItems.value = failureItems.value.map(item =>
        item.id === updated.id ? updated : item
      );
    }
    if (res?.data?.created_template_id) {
      alert(`Draft template created (#${res.data.created_template_id})`);
    }
    await fetchFailureTags();
    await fetchSummary();
  } catch (error) {
    alert(
      error?.response?.data?.error ||
        error?.message ||
        'Failed to convert failure tag'
    );
  }
};

const createArticleDraft = async tag => {
  if (!tag?.id) return;
  try {
    const res = await getWootAxios().post(
      `/api/v1/accounts/${accountId.value}/ailab/ai_failure_tags/${tag.id}/create_article_draft`
    );
    const updated = res?.data?.tag;
    if (updated?.id) {
      failureItems.value = failureItems.value.map(item =>
        item.id === updated.id ? updated : item
      );
    }
    if (res?.data?.created_article_id) {
      alert(`Draft article created (#${res.data.created_article_id})`);
    }
    await fetchFailureTags();
    await fetchSummary();
  } catch (error) {
    alert(
      error?.response?.data?.error ||
        error?.message ||
        'Failed to create draft article'
    );
  }
};

const prevTracePage = async () => {
  const nextOffset = Math.max(
    0,
    (traceMeta.value.offset || 0) - (traceMeta.value.limit || 50)
  );
  await fetchDecisionEvents({ offset: nextOffset });
};

const nextTracePage = async () => {
  const nextOffset =
    (traceMeta.value.offset || 0) + (traceMeta.value.limit || 50);
  if (nextOffset >= (traceMeta.value.total || 0)) return;
  await fetchDecisionEvents({ offset: nextOffset });
};

onMounted(async () => {
  if (typeof window !== 'undefined') {
    window.addEventListener('scroll', scheduleAnalyzeSectionSync, {
      passive: true,
    });
    window.addEventListener('resize', scheduleAnalyzeSectionSync);
  }
  if (!assistantId.value) return;
  try {
    await store.dispatch('aiCloser/fetchConfig', {
      assistantId: assistantId.value,
    });
    await store.dispatch('ailabInboxes/get', {
      assistantId: assistantId.value,
    });
    await store.dispatch('inboxes/get');
  } catch {
    // ignore
  }
  syncAnalyzeSeedFilters(routeAnalyzeSeed.value);
  syncOverrideDrilldownFilters(routeOverrideDrilldown.value);
  analyzeInitialized.value = true;
  await refreshAll();
  if (routeAnalyzeSeed.value.enabled || routeOverrideDrilldown.value.enabled) {
    scrollToTraceSection();
  }
  await nextTick();
  scheduleAnalyzeSectionSync();
});

onBeforeUnmount(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('scroll', scheduleAnalyzeSectionSync);
    window.removeEventListener('resize', scheduleAnalyzeSectionSync);
    if (analyzeSectionSyncFrame) {
      window.cancelAnimationFrame(analyzeSectionSyncFrame);
    }
  }
});
</script>

<template>
  <div class="fin-ai flex flex-col h-full min-h-0">
    <div class="fin-page-header-row analyze-header">
      <div>
        <h1 class="heading o__size-1">Analyze</h1>
        <p class="analyze-muted mt-1">
          Start with a customer-safe overview, then open advanced diagnostics
          only when you need raw traces and routing detail.
        </p>
      </div>
      <div class="analyze-header-actions">
        <button
          type="button"
          class="btn o__secondary"
          :disabled="isLoadingSummary || isLoadingTraces || isLoadingFailures"
          @click="refreshAll"
        >
          Refresh analyze
        </button>
      </div>
    </div>

    <div class="fin-page-body analyze-shell">
      <div class="analyze-summary-grid">
        <div
          v-for="card in traceSummaryCards"
          :key="card.label"
          class="analyze-stat-card"
        >
          <div class="analyze-label">{{ card.label }}</div>
          <div class="analyze-value">{{ card.value }}</div>
          <div class="analyze-note">{{ card.note }}</div>
        </div>
      </div>

      <div
        v-if="showAnalyzeContextBar"
        class="analyze-card analyze-card--overview analyze-context-rail"
      >
        <div class="analyze-context-top">
          <div class="analyze-context-copy">
            <div class="analyze-label">Workbench state</div>
            <div class="analyze-card-title">Current context</div>
            <div class="analyze-alert-title">{{ analyzeContextHeadline }}</div>
            <div class="analyze-note">
              {{ analyzeContextSummary }}
            </div>
          </div>
          <div class="analyze-context-actions">
            <button
              type="button"
              class="btn o__secondary"
              @click="jumpToAnalyzeSection(suggestedAnalyzeSectionJump.key)"
            >
              {{ suggestedAnalyzeSectionActionLabel }}
            </button>
            <button
              v-if="hasResettableAnalyzeContext"
              type="button"
              class="btn o__secondary"
              @click="clearAnalyzeWorkingContext"
            >
              Reset context
            </button>
          </div>
        </div>
        <div class="analyze-preset-chip-list">
          <button
            v-for="preset in analyzeFilterPresetCards"
            :key="preset.key"
            type="button"
            class="analyze-preset-chip"
            :class="{ 'is-active': preset.active }"
            :disabled="!preset.available"
            @click="applyAnalyzeFilterPreset(preset.key)"
          >
            <span class="analyze-preset-chip-copy">
              <span class="analyze-alert-title">{{ preset.title }}</span>
              <span class="analyze-note">{{ preset.body }}</span>
            </span>
            <span class="analyze-jump-chip-badge">
              {{ preset.badge }}
            </span>
          </button>
        </div>
        <div class="analyze-jump-chip-list">
          <button
            v-for="section in analyzeSectionJumpCards"
            :key="section.key"
            type="button"
            class="analyze-jump-chip"
            :class="{
              'is-recommended': section.recommended,
              'is-active': section.active,
            }"
            @click="jumpToAnalyzeSection(section.key)"
          >
            <span class="analyze-jump-chip-copy">
              <span class="analyze-jump-chip-top">
                <span class="analyze-label">{{ section.label }}</span>
                <span class="analyze-jump-chip-badges">
                  <span class="analyze-jump-chip-badge">
                    {{ section.badge }}
                  </span>
                  <span
                    v-if="section.secondaryBadge"
                    class="analyze-jump-chip-badge analyze-jump-chip-badge--muted"
                  >
                    {{ section.secondaryBadge }}
                  </span>
                </span>
              </span>
              <span class="analyze-alert-title">{{ section.title }}</span>
              <span class="analyze-note">{{ section.body }}</span>
            </span>
            <span class="analyze-jump-chip-action">
              {{ section.actionLabel }}
            </span>
          </button>
        </div>
        <div class="analyze-context-chip-list">
          <div
            v-for="card in analyzeContextCards"
            :key="card.key"
            class="analyze-context-chip"
            :class="[`tone-${card.tone}`]"
          >
            <div class="analyze-context-chip-copy">
              <div class="analyze-label">{{ card.label }}</div>
              <div class="analyze-alert-title">{{ card.title }}</div>
              <div class="analyze-note">{{ card.body }}</div>
            </div>
            <div class="analyze-context-chip-actions">
              <button
                type="button"
                class="btn o__secondary"
                @click="openAnalyzeContextAction(card)"
              >
                {{ card.actionLabel }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="!isAnalyzeSectionExpanded('operating-summary')"
        ref="operatingSummarySectionRef"
        class="analyze-card analyze-card--overview analyze-card--collapsed"
      >
        <div class="analyze-collapsed-card">
          <div>
            <div class="analyze-label">
              Minimized by {{ activeAnalyzeFilterPresetLabel }}
            </div>
            <div class="analyze-card-title">
              {{ analyzeCollapsedSectionCards['operating-summary'].title }}
            </div>
            <div class="analyze-note">
              {{ analyzeCollapsedSectionCards['operating-summary'].body }}
            </div>
          </div>
          <div class="analyze-context-actions">
            <span class="analyze-jump-chip-badge">
              {{ analyzeCollapsedSectionCards['operating-summary'].badge }}
            </span>
            <button
              type="button"
              class="btn o__secondary"
              @click="expandAnalyzeSection('operating-summary')"
            >
              Show section
            </button>
          </div>
        </div>
      </div>
      <div
        v-else
        ref="operatingSummarySectionRef"
        class="analyze-card analyze-card--overview"
      >
        <div class="analyze-section-header">
          <div>
            <div class="analyze-card-title">Today's operating summary</div>
            <div class="analyze-note">
              Read this first. It compresses the current repair queue into the
              most urgent work, the next deploy-ready reviews, and how owner
              backlog is shifting versus the previous window.
            </div>
          </div>
        </div>
        <div class="analyze-summary-grid">
          <div
            v-for="card in operatingSummaryCards"
            :key="card.label"
            class="analyze-stat-card"
          >
            <div class="analyze-label">{{ card.label }}</div>
            <div class="analyze-value">{{ card.value }}</div>
            <div class="analyze-note">{{ card.note }}</div>
          </div>
        </div>
        <div class="analyze-breakdown-grid mt-3">
          <div class="analyze-breakdown-card">
            <div class="analyze-label">Most urgent 3</div>
            <div class="analyze-note mb-2">
              Highest-priority repair work across fix now and validate next.
            </div>
            <div
              v-if="operatingUrgentRows.length"
              class="analyze-breakdown-list"
            >
              <div
                v-for="row in operatingUrgentRows"
                :key="`operating-urgent-${row.strategyGroup}-${row.key}`"
                class="analyze-breakdown-row analyze-breakdown-row--stacked"
              >
                <span>
                  {{ row.strategyGroupTitle }}: {{ row.label }}
                  <small class="analyze-note">
                    owner {{ row.ownerLabel }} • latest verdict
                    {{ row.latestVerdict }} • confidence {{ row.repairLevel }}
                  </small>
                  <small class="analyze-note">
                    {{ row.repairBody || row.body }}
                  </small>
                </span>
                <div class="analyze-breakdown-actions">
                  <button
                    type="button"
                    class="btn o__secondary"
                    @click="focusRepairOwner(row.ownerLabel)"
                  >
                    Focus {{ row.ownerLabel }}
                  </button>
                  <button
                    type="button"
                    class="btn o__secondary"
                    @click="openAnalyzeAction(row.primaryAction)"
                  >
                    {{ row.primaryAction?.actionLabel || 'Open repair' }}
                  </button>
                  <button
                    v-if="row.testAction?.testQuery"
                    type="button"
                    class="btn o__secondary"
                    @click="retestAnalyzeHotspot(row.testAction)"
                  >
                    Open Test bundle
                  </button>
                </div>
              </div>
            </div>
            <div v-else class="analyze-note">
              No urgent repairs in this window.
            </div>
          </div>

          <div class="analyze-breakdown-card">
            <div class="analyze-label">Review today 3</div>
            <div class="analyze-note mb-2">
              Repairs already strong enough to move into deploy review.
            </div>
            <div
              v-if="operatingReviewRows.length"
              class="analyze-breakdown-list"
            >
              <div
                v-for="row in operatingReviewRows"
                :key="`operating-review-${row.strategyGroup}-${row.key}`"
                class="analyze-breakdown-row analyze-breakdown-row--stacked"
              >
                <span>
                  {{ row.strategyGroupTitle }}: {{ row.label }}
                  <small class="analyze-note">
                    confidence {{ row.repairLevel }} •
                    {{ row.runsCount }} recent run{{
                      row.runsCount === 1 ? '' : 's'
                    }}
                  </small>
                  <small class="analyze-note">
                    {{ row.repairBody || row.body }}
                  </small>
                </span>
                <div class="analyze-breakdown-actions">
                  <button
                    type="button"
                    class="btn o__secondary"
                    @click="focusRepairOwner(row.ownerLabel)"
                  >
                    Focus {{ row.ownerLabel }}
                  </button>
                  <button
                    v-if="row.deployAction"
                    type="button"
                    class="btn o__secondary"
                    @click="openAnalyzeAction(row.deployAction)"
                  >
                    Open Deploy
                  </button>
                  <button
                    v-if="row.testAction?.testQuery"
                    type="button"
                    class="btn o__secondary"
                    @click="retestAnalyzeHotspot(row.testAction)"
                  >
                    Open Test bundle
                  </button>
                </div>
              </div>
            </div>
            <div v-else class="analyze-note">
              No repairs are ready for deploy review yet.
            </div>
          </div>

          <div class="analyze-breakdown-card">
            <div class="analyze-label">Owner backlog change</div>
            <div class="analyze-note mb-2">
              Compare owner pressure now versus the previous time window.
            </div>
            <div
              v-if="operatingOwnerBacklogRows.length"
              class="analyze-breakdown-list"
            >
              <div
                v-for="row in operatingOwnerBacklogRows"
                :key="`operating-owner-${row.ownerLabel}`"
                class="analyze-breakdown-row analyze-breakdown-row--stacked"
              >
                <span>
                  {{ row.ownerLabel }}: {{ row.currentCount }}
                  <small class="analyze-note">
                    trend {{ row.deltaLabel }} • previous {{ row.previousCount }}
                  </small>
                  <small class="analyze-note">
                    {{ row.note }}
                  </small>
                </span>
                <div class="analyze-breakdown-actions">
                  <button
                    type="button"
                    class="btn o__secondary"
                    @click="focusRepairOwner(row.ownerLabel)"
                  >
                    Focus {{ row.ownerLabel }}
                  </button>
                  <button
                    v-if="row.actionPayload"
                    type="button"
                    class="btn o__secondary"
                    @click="openAnalyzeAction(row.actionPayload)"
                  >
                    {{ row.actionPayload?.actionLabel || 'Open repair' }}
                  </button>
                  <button
                    v-if="row.testPayload?.testQuery"
                    type="button"
                    class="btn o__secondary"
                    @click="retestAnalyzeHotspot(row.testPayload)"
                  >
                    Open Test bundle
                  </button>
                </div>
              </div>
            </div>
            <div v-else class="analyze-note">
              No owner backlog signal is available for this window.
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="!isAnalyzeSectionExpanded('release-readiness')"
        ref="releaseReadinessSectionRef"
        class="analyze-card analyze-card--overview analyze-card--collapsed"
      >
        <div class="analyze-collapsed-card">
          <div>
            <div class="analyze-label">
              Minimized by {{ activeAnalyzeFilterPresetLabel }}
            </div>
            <div class="analyze-card-title">
              {{ analyzeCollapsedSectionCards['release-readiness'].title }}
            </div>
            <div class="analyze-note">
              {{ analyzeCollapsedSectionCards['release-readiness'].body }}
            </div>
          </div>
          <div class="analyze-context-actions">
            <span class="analyze-jump-chip-badge">
              {{ analyzeCollapsedSectionCards['release-readiness'].badge }}
            </span>
            <button
              type="button"
              class="btn o__secondary"
              @click="expandAnalyzeSection('release-readiness')"
            >
              Show section
            </button>
          </div>
        </div>
      </div>
      <div
        v-else
        ref="releaseReadinessSectionRef"
        class="analyze-card analyze-card--overview"
      >
        <div class="analyze-section-header">
          <div>
            <div class="analyze-card-title">Release readiness</div>
            <div class="analyze-note">
              {{
                repairOwnerFocusSummary
                  ? `This section is following ${repairOwnerFocusSummary.ownerLabel} focus, so it highlights whether that owner already has a real release candidate or still needs repair work.`
                  : 'This is the current go-live state pulled from real runtime status, inbox binding, and review backlog signals.'
              }}
            </div>
          </div>
        </div>
        <div class="analyze-summary-grid">
          <div
            v-for="card in releaseReadinessCards"
            :key="card.label"
            class="analyze-stat-card"
          >
            <div class="analyze-label">{{ card.label }}</div>
            <div class="analyze-value">{{ card.value }}</div>
            <div class="analyze-note">{{ card.note }}</div>
          </div>
        </div>
        <div class="analyze-action-list">
          <div
            v-for="item in releaseChecklist"
            :key="item.title"
            class="analyze-alert"
            :class="[`tone-${item.tone}`]"
          >
            <div class="analyze-alert-title">{{ item.title }}</div>
            <div class="analyze-note">{{ item.body }}</div>
            <div class="analyze-breakdown-actions mt-2">
              <button
                type="button"
                class="btn o__secondary"
                @click="openAnalyzeAction(item)"
              >
                {{ item.actionLabel }}
              </button>
              <button
                v-if="item.testQuery"
                type="button"
                class="btn o__secondary"
                @click="retestAnalyzeHotspot(item)"
              >
                {{
                  item.testCompareBundle
                    ? 'Open Test bundle'
                    : 'Retest hotspot'
                }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="!isAnalyzeSectionExpanded('learning-loop')"
        ref="learningLoopSectionRef"
        class="analyze-card analyze-card--overview analyze-card--collapsed"
      >
        <div class="analyze-collapsed-card">
          <div>
            <div class="analyze-label">
              Minimized by {{ activeAnalyzeFilterPresetLabel }}
            </div>
            <div class="analyze-card-title">
              {{ analyzeCollapsedSectionCards['learning-loop'].title }}
            </div>
            <div class="analyze-note">
              {{ analyzeCollapsedSectionCards['learning-loop'].body }}
            </div>
          </div>
          <div class="analyze-context-actions">
            <span class="analyze-jump-chip-badge">
              {{ analyzeCollapsedSectionCards['learning-loop'].badge }}
            </span>
            <button
              type="button"
              class="btn o__secondary"
              @click="expandAnalyzeSection('learning-loop')"
            >
              Show section
            </button>
          </div>
        </div>
      </div>
      <div
        v-else
        ref="learningLoopSectionRef"
        class="analyze-card analyze-card--overview"
      >
        <div class="analyze-section-header">
          <div>
            <div class="analyze-card-title">Testing and improvement loop</div>
            <div class="analyze-note">
              {{
                repairOwnerFocusSummary
                  ? `Use this loop to move ${repairOwnerFocusSummary.ownerLabel} from validation to repair to release review without re-scanning the full dashboard.`
                  : 'Use Analyze to spot what changed, then jump straight into the next workspace that should act on it.'
              }}
            </div>
          </div>
        </div>
        <div class="analyze-breakdown-grid">
          <div
            v-for="card in learningLoopCards"
            :key="card.label"
            class="analyze-breakdown-card"
          >
            <div class="analyze-label">{{ card.label }}</div>
            <div class="analyze-alert-title">{{ card.title }}</div>
            <div class="analyze-note">{{ card.body }}</div>
            <div class="analyze-breakdown-actions mt-2">
              <button
                type="button"
                class="btn o__secondary"
                @click="openAnalyzeAction(card)"
              >
                {{ card.actionLabel }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="!isAnalyzeSectionExpanded('repair-queue')"
        ref="repairTeamQueueSectionRef"
        class="analyze-card analyze-card--overview analyze-card--collapsed"
      >
        <div class="analyze-collapsed-card">
          <div>
            <div class="analyze-label">
              Minimized by {{ activeAnalyzeFilterPresetLabel }}
            </div>
            <div class="analyze-card-title">
              {{ analyzeCollapsedSectionCards['repair-queue'].title }}
            </div>
            <div class="analyze-note">
              {{ analyzeCollapsedSectionCards['repair-queue'].body }}
            </div>
          </div>
          <div class="analyze-context-actions">
            <span class="analyze-jump-chip-badge">
              {{ analyzeCollapsedSectionCards['repair-queue'].badge }}
            </span>
            <button
              type="button"
              class="btn o__secondary"
              @click="expandAnalyzeSection('repair-queue')"
            >
              Show section
            </button>
          </div>
        </div>
      </div>
      <div
        v-else
        ref="repairTeamQueueSectionRef"
        class="analyze-card analyze-card--overview"
      >
        <div class="analyze-section-header">
          <div>
            <div class="analyze-card-title">Repair team queue</div>
            <div class="analyze-note">
              After reading the operating summary above, use this ordered queue to
              see the top items inside each working lane.
            </div>
            <div v-if="repairOwnerFocusSummary" class="analyze-note mt-2">
              Focused on {{ repairOwnerFocusSummary.ownerLabel }}. Showing
              {{ repairOwnerFocusSummary.queueVisible }} of
              {{ repairOwnerFocusSummary.queueTotal }} queue repairs.
            </div>
          </div>
          <div v-if="repairOwnerFocusSummary" class="analyze-breakdown-actions">
            <button
              type="button"
              class="btn o__secondary"
              @click="clearRepairOwnerFocus"
            >
              Clear owner focus
            </button>
          </div>
        </div>
        <div class="analyze-summary-grid">
          <div
            v-for="card in focusedRepairTeamQueueSummaryCards"
            :key="card.label"
            class="analyze-stat-card"
          >
            <div class="analyze-label">{{ card.label }}</div>
            <div class="analyze-value">{{ card.value }}</div>
            <div class="analyze-note">{{ card.note }}</div>
          </div>
          <div
            v-for="card in focusedRepairOwnerLoadCards"
            :key="card.label"
            class="analyze-stat-card"
          >
            <div class="analyze-label">{{ card.label }}</div>
            <div class="analyze-value">{{ card.value }}</div>
            <div class="analyze-note">{{ card.note }}</div>
          </div>
        </div>
        <div class="analyze-breakdown-grid mt-3">
          <div
            v-for="section in focusedRepairTeamQueueSections"
            :key="section.key"
            class="analyze-breakdown-card"
          >
            <div class="analyze-label">{{ section.title }}</div>
            <div class="analyze-note mb-2">{{ section.note }}</div>
            <div v-if="section.rows.length" class="analyze-breakdown-list">
              <div
                v-for="row in section.rows"
                :key="`repair-team-${section.key}-${row.strategyGroup}-${row.key}`"
                class="analyze-breakdown-row analyze-breakdown-row--stacked"
              >
                <span>
                  {{ row.strategyGroupTitle }}: {{ row.label }}
                  <small class="analyze-note">
                    owner {{ row.ownerLabel }} • latest verdict {{ row.latestVerdict }}
                    • confidence {{ row.repairLevel }}
                  </small>
                  <small class="analyze-note">
                    {{ row.repairBody || row.body }}
                  </small>
                </span>
                <div class="analyze-breakdown-actions">
                  <button
                    type="button"
                    class="btn o__secondary"
                    @click="openAnalyzeAction(row.primaryAction)"
                  >
                    {{ row.primaryAction?.actionLabel || 'Open repair' }}
                  </button>
                  <button
                    v-if="row.deployAction"
                    type="button"
                    class="btn o__secondary"
                    @click="openAnalyzeAction(row.deployAction)"
                  >
                    Open Deploy
                  </button>
                  <button
                    v-if="row.testAction?.testQuery"
                    type="button"
                    class="btn o__secondary"
                    @click="retestAnalyzeHotspot(row.testAction)"
                  >
                    Open Test bundle
                  </button>
                </div>
              </div>
            </div>
            <div v-else class="analyze-note">{{ section.emptyNote }}</div>
          </div>
        </div>
      </div>

      <div
        v-if="!isAnalyzeSectionExpanded('repair-board')"
        ref="repairBoardSectionRef"
        class="analyze-card analyze-card--overview analyze-card--collapsed"
      >
        <div class="analyze-collapsed-card">
          <div>
            <div class="analyze-label">
              Minimized by {{ activeAnalyzeFilterPresetLabel }}
            </div>
            <div class="analyze-card-title">
              {{ analyzeCollapsedSectionCards['repair-board'].title }}
            </div>
            <div class="analyze-note">
              {{ analyzeCollapsedSectionCards['repair-board'].body }}
            </div>
          </div>
          <div class="analyze-context-actions">
            <span class="analyze-jump-chip-badge">
              {{ analyzeCollapsedSectionCards['repair-board'].badge }}
            </span>
            <button
              type="button"
              class="btn o__secondary"
              @click="expandAnalyzeSection('repair-board')"
            >
              Show section
            </button>
          </div>
        </div>
      </div>
      <div
        v-else
        ref="repairBoardSectionRef"
        class="analyze-card analyze-card--overview"
      >
        <div class="analyze-section-header">
          <div>
            <div class="analyze-card-title">Active repairs board</div>
            <div class="analyze-note">
              Track every current strategy repair by its latest validation state,
              then jump straight into the next action instead of re-reading each
              recommendation from scratch. Use the team queue above when you need
              ordered priorities; use this board when you need the full state map.
            </div>
            <div v-if="repairOwnerFocusSummary" class="analyze-note mt-2">
              Focused on {{ repairOwnerFocusSummary.ownerLabel }}. Showing
              {{ repairOwnerFocusSummary.boardVisible }} of
              {{ repairOwnerFocusSummary.boardTotal }} board repairs.
            </div>
          </div>
          <div v-if="repairOwnerFocusSummary" class="analyze-breakdown-actions">
            <button
              type="button"
              class="btn o__secondary"
              @click="clearRepairOwnerFocus"
            >
              Clear owner focus
            </button>
          </div>
        </div>
        <div class="analyze-summary-grid">
          <div
            v-for="card in focusedRepairBoardSummaryCards"
            :key="card.label"
            class="analyze-stat-card"
          >
            <div class="analyze-label">{{ card.label }}</div>
            <div class="analyze-value">{{ card.value }}</div>
            <div class="analyze-note">{{ card.note }}</div>
          </div>
        </div>
        <div class="analyze-breakdown-grid mt-3">
          <div class="analyze-breakdown-card">
            <div class="analyze-label">Release review loop</div>
            <div class="analyze-note mb-2">
              Keep validation-ready repairs, active reviews, releases, and rollback signals in one place.
            </div>
            <div class="analyze-breakdown-list">
              <div class="analyze-breakdown-row">
                <span>Ready but not reviewed</span>
                <strong>{{ focusedReleaseReviewCounts.readyPending }}</strong>
              </div>
              <div class="analyze-breakdown-row">
                <span>Review open</span>
                <strong>{{ focusedReleaseReviewCounts.reviewing }}</strong>
              </div>
              <div class="analyze-breakdown-row">
                <span>Released</span>
                <strong>{{ focusedReleaseReviewCounts.released }}</strong>
              </div>
              <div class="analyze-breakdown-row">
                <span>Rollback triggered</span>
                <strong>{{ focusedReleaseReviewCounts.rolledBack }}</strong>
              </div>
              <div class="analyze-breakdown-row">
                <span>Batch baselines ready</span>
                <strong>{{ focusedBatchBaselineCounts.ready }}</strong>
              </div>
              <div class="analyze-breakdown-row">
                <span>Batch baselines watch / blocked</span>
                <strong>
                  {{
                    focusedBatchBaselineCounts.watch +
                    focusedBatchBaselineCounts.blocked
                  }}
                </strong>
              </div>
            </div>
            <div class="mt-2 rounded-large border border-neutral-border bg-white px-3 py-2 text-xs text-muted">
              {{
                latestFocusedReleaseReviewRow
                  ? `${latestFocusedReleaseReviewRow.strategyGroupTitle || 'Strategy'}: ${latestFocusedReleaseReviewRow.label} • ${latestFocusedReleaseReviewRow.releaseStatusLabel || 'reviewed'}${latestFocusedReleaseReviewRow.releaseReviewedAt ? ` • ${formatDateTime(latestFocusedReleaseReviewRow.releaseReviewedAt)}` : ''}`
                  : 'No release review snapshot has been saved for the visible repair set yet.'
              }}
            </div>
            <div class="mt-2 rounded-large border border-neutral-border bg-white px-3 py-2 text-xs text-muted">
              {{
                latestFocusedBatchBaselineRow
                  ? `${latestFocusedBatchBaselineRow.strategyGroupTitle || 'Strategy'}: ${latestFocusedBatchBaselineRow.label} • batch ${humanBatchBaselineLabel(latestFocusedBatchBaselineRow.batchBaselineLevel)}${latestFocusedBatchBaselineRow.batchBaselineRanAt ? ` • ${formatDateTime(latestFocusedBatchBaselineRow.batchBaselineRanAt)}` : ''}${latestFocusedBatchBaselineRow.batchBaselineTitle ? ` • ${latestFocusedBatchBaselineRow.batchBaselineTitle}` : ''}`
                  : 'No durable batch baseline has been captured for the visible repair set yet.'
              }}
            </div>
            <div class="mt-2 rounded-large border border-neutral-border bg-white px-3 py-2 text-xs text-muted">
              {{ releaseVersionContextNote }}
            </div>
            <div
              v-if="
                releaseReadinessSummary.review_status_counts ||
                releaseReadinessSummary.batch_level_counts
              "
              class="mt-2 rounded-large border border-neutral-border bg-white px-3 py-2 text-xs text-muted"
            >
              Global release readiness:
              {{
                `reviews ${releaseReadinessSummary.review_status_counts?.reviewing || 0} open / ${releaseReadinessSummary.review_status_counts?.released || 0} released / ${releaseReadinessSummary.review_status_counts?.rolled_back || 0} rolled back`
              }}
              •
              {{
                `batch ${releaseReadinessSummary.batch_level_counts?.ready || 0} ready / ${releaseReadinessSummary.batch_level_counts?.watch || 0} watch / ${releaseReadinessSummary.batch_level_counts?.blocked || 0} blocked`
              }}
              •
              {{
                `saved sets ${releaseReadinessSummary.saved_test_set_count || 0}`
              }}
              •
              {{
                `split rollouts ${releaseReadinessSummary.split_rollout_count || 0}`
              }}
              •
              {{
                `pinned rollouts ${releaseReadinessSummary.pinned_rollout_count || 0}`
              }}
              <span v-if="releaseReadinessSummary.latest_rollback_outcome?.status">
                •
                {{
                  `rollback outcome ${releaseReadinessSummary.latest_rollback_outcome.status}`
                }}
              </span>
              <span v-if="releaseReadinessSummary.bundle_rollback_outcome_counts">
                •
                {{
                  `rollback bundles helped ${releaseReadinessSummary.bundle_rollback_outcome_counts.helped || 0} / risky ${releaseReadinessSummary.bundle_rollback_outcome_counts.risky || 0} / waiting ${releaseReadinessSummary.bundle_rollback_outcome_counts.waiting || 0}`
                }}
              </span>
            </div>
            <div
              v-if="releaseReadinessSummary.latest_rollback_outcome?.summary"
              class="mt-2 rounded-large border border-neutral-border bg-white px-3 py-2 text-xs text-muted"
            >
              {{
                releaseReadinessSummary.latest_rollback_outcome.summary
              }}
              <span v-if="releaseReadinessSummary.latest_rollback_outcome.operator_note">
                • {{ releaseReadinessSummary.latest_rollback_outcome.operator_note }}
              </span>
              <span v-if="releaseReadinessSummary.latest_rollback_outcome.recommended_action_label">
                • {{ releaseReadinessSummary.latest_rollback_outcome.recommended_action_label }}
              </span>
              <span v-if="releaseReadinessSummary.latest_rollback_outcome.confidence_label">
                • {{ releaseReadinessSummary.latest_rollback_outcome.confidence_label }}
              </span>
              <span v-if="releaseReadinessSummary.latest_rollback_outcome.evidence_gap_count">
                • {{ releaseReadinessSummary.latest_rollback_outcome.evidence_gap_count }} evidence gap<span v-if="releaseReadinessSummary.latest_rollback_outcome.evidence_gap_count !== 1">s</span>
              </span>
              <span
                v-if="releaseReadinessSummary.latest_rollback_outcome.operator_actions?.[0]?.intent_label || releaseReadinessSummary.latest_rollback_outcome.operator_actions?.[0]?.label"
              >
                • next in Deploy:
                {{
                  releaseReadinessSummary.latest_rollback_outcome.operator_actions[0].intent_label ||
                  releaseReadinessSummary.latest_rollback_outcome.operator_actions[0].label
                }}
              </span>
              <span v-if="releaseReadinessSummary.latest_rollback_outcome.target_version_label">
                • {{ releaseReadinessSummary.latest_rollback_outcome.target_version_label }}
                <span v-if="releaseReadinessSummary.latest_rollback_outcome.previous_version_label">
                  vs {{ releaseReadinessSummary.latest_rollback_outcome.previous_version_label }}
                </span>
              </span>
            </div>
            <div
              v-if="rollbackEvidenceHistoryEntries.length"
              class="mt-2 rounded-large border border-neutral-border bg-white px-3 py-2 text-xs text-muted"
            >
              <div class="font-medium text-slate-700">Recent evidence notes</div>
              <div
                v-if="rollbackEvidenceDrilldownBadges.length"
                class="mt-2 flex flex-wrap gap-2"
              >
                <span
                  v-for="badge in rollbackEvidenceDrilldownBadges"
                  :key="badge.label"
                  class="analyze-jump-chip-badge"
                  :class="{ 'analyze-jump-chip-badge--muted': badge.tone !== 'primary' }"
                >
                  {{ badge.label }}
                </span>
              </div>
              <div
                v-for="(entry, index) in rollbackEvidenceHistoryEntries"
                :key="rollbackEvidenceHistoryEntryKey(entry, index)"
                :class="index > 0 ? 'mt-2' : 'mt-2'"
              >
                <div>{{ entry.body }}</div>
                <div
                  v-if="rollbackEvidenceHistoryEntryBadges(entry).length"
                  class="mt-2 flex flex-wrap gap-2"
                >
                  <span
                    v-for="badge in rollbackEvidenceHistoryEntryBadges(entry)"
                    :key="badge.label"
                    class="analyze-jump-chip-badge"
                    :class="{ 'analyze-jump-chip-badge--muted': badge.tone !== 'primary' }"
                  >
                    {{ badge.label }}
                  </span>
                </div>
                <div
                  v-if="rollbackEvidenceHistoryEntryMeta(entry)"
                  class="analyze-note mt-1"
                >
                  {{ rollbackEvidenceHistoryEntryMeta(entry) }}
                </div>
              </div>
              <div
                v-if="rollbackEvidenceHistoryTotalCount > rollbackEvidenceHistoryEntries.length"
                class="analyze-note mt-2"
              >
                {{
                  `${rollbackEvidenceHistoryTotalCount - rollbackEvidenceHistoryEntries.length} older rollback note${rollbackEvidenceHistoryTotalCount - rollbackEvidenceHistoryEntries.length === 1 ? '' : 's'} remain in linked evidence`
                }}
              </div>
            </div>
            <div class="mt-2 flex flex-wrap gap-2">
              <button
                type="button"
                class="analyze-chip-button"
                @click="
                  openDeployWorkspaceWithSeed({
                    focus: 'release',
                    reason: hasUnpublishedVersion ? 'publish_review' : 'version_baseline_review',
                    source: 'Analyze',
                  })
                "
              >
                {{ hasUnpublishedVersion ? 'Open publish review' : 'Open release baseline' }}
              </button>
              <button
                v-if="releaseReadinessSummary.latest_rollback_outcome?.summary"
                type="button"
                class="analyze-chip-button"
                @click="openDeployRollbackEvidence()"
              >
                Open evidence in Deploy
              </button>
              <button
                v-if="releaseReadinessSummary.latest_rollback_outcome?.summary"
                type="button"
                class="analyze-chip-button"
                @click="openDeployRollbackEvidence({ openVersions: true })"
              >
                Open versions in Deploy
              </button>
              <button
                v-if="releaseReadinessSummary.latest_rollback_outcome?.summary"
                type="button"
                class="analyze-chip-button"
                :disabled="isPersistingRollbackFollowup"
                @click="persistRollbackFollowupToEvidence"
              >
                {{
                  isPersistingRollbackFollowup
                    ? 'Saving rollback note...'
                    : 'Append rollback note to evidence'
                }}
              </button>
              <button
                v-if="releaseReadinessSummary.latest_rollback_outcome?.summary"
                type="button"
                class="analyze-chip-button"
                @click="openDeployRollbackFollowup"
              >
                Write rollback follow-up to Deploy
              </button>
            </div>
          </div>
        </div>
        <div class="analyze-breakdown-grid mt-3">
          <div
            v-for="section in focusedRepairBoardSections"
            :key="section.key"
            class="analyze-breakdown-card"
          >
            <div class="analyze-label">{{ section.title }}</div>
            <div class="analyze-note mb-2">{{ section.note }}</div>
            <div v-if="section.rows.length" class="analyze-breakdown-list">
              <div
                v-for="row in section.rows"
                :key="`repair-board-${section.key}-${row.strategyGroup}-${row.key}`"
                class="analyze-breakdown-row analyze-breakdown-row--stacked"
              >
                <span>
                  {{ row.strategyGroupTitle }}: {{ row.label }}
                  <small class="analyze-note">
                    latest verdict {{ row.latestVerdict }} • confidence
                    {{ row.repairLevel }} • {{ row.runsCount }} recent run{{
                      row.runsCount === 1 ? '' : 's'
                    }}
                  </small>
                  <small class="analyze-note">
                    {{ row.repairBody || row.body }}
                  </small>
                  <small
                    v-if="row.releaseStatusLabel || row.releaseRollbackTrigger || row.releaseNote"
                    class="analyze-note"
                  >
                    Release:
                    {{ row.releaseStatusLabel || 'snapshot saved' }}
                    <span v-if="row.releaseReviewedAt">
                      • {{ formatDateTime(row.releaseReviewedAt) }}
                    </span>
                    <span v-if="row.releaseRollbackTrigger">
                      • rollback {{ row.releaseRollbackTrigger }}
                    </span>
                    <span v-else-if="row.releaseNote">
                      • {{ row.releaseNote }}
                    </span>
                  </small>
                  <small
                    v-if="row.batchBaselineLevel || row.batchBaselineTitle || row.batchBaselineTopSignal"
                    class="analyze-note"
                  >
                    Batch:
                    {{ humanBatchBaselineLabel(row.batchBaselineLevel) }}
                    <span v-if="row.batchBaselineRanAt">
                      • {{ formatDateTime(row.batchBaselineRanAt) }}
                    </span>
                    <span v-if="row.batchBaselineTitle">
                      • {{ row.batchBaselineTitle }}
                    </span>
                    <span v-if="row.batchBaselineTotalCases">
                      • {{ row.batchBaselineTotalCases }} case{{
                        row.batchBaselineTotalCases === 1 ? '' : 's'
                      }}
                    </span>
                    <span v-if="row.batchBaselineTopSignal">
                      • {{ row.batchBaselineTopSignal }}
                    </span>
                  </small>
                </span>
                <div class="analyze-breakdown-actions">
                  <button
                    type="button"
                    class="btn o__secondary"
                    @click="openAnalyzeAction(row.primaryAction)"
                  >
                    {{ row.primaryAction?.actionLabel || 'Open repair' }}
                  </button>
                  <button
                    v-if="row.deployAction"
                    type="button"
                    class="btn o__secondary"
                    @click="openAnalyzeAction(row.deployAction)"
                  >
                    Open Deploy
                  </button>
                  <button
                    v-if="row.testAction?.testQuery"
                    type="button"
                    class="btn o__secondary"
                    @click="retestAnalyzeHotspot(row.testAction)"
                  >
                    Open Test bundle
                  </button>
                </div>
              </div>
            </div>
            <div v-else class="analyze-note">{{ section.emptyNote }}</div>
          </div>
        </div>
      </div>

      <div
        v-if="!isAnalyzeSectionExpanded('optimization-center')"
        ref="optimizationCenterSectionRef"
        class="analyze-card analyze-card--collapsed"
      >
        <div class="analyze-collapsed-card">
          <div>
            <div class="analyze-label">
              Minimized by {{ activeAnalyzeFilterPresetLabel }}
            </div>
            <div class="analyze-card-title">
              {{ analyzeCollapsedSectionCards['optimization-center'].title }}
            </div>
            <div class="analyze-note">
              {{ analyzeCollapsedSectionCards['optimization-center'].body }}
            </div>
          </div>
          <div class="analyze-context-actions">
            <span class="analyze-jump-chip-badge">
              {{ analyzeCollapsedSectionCards['optimization-center'].badge }}
            </span>
            <button
              type="button"
              class="btn o__secondary"
              @click="expandAnalyzeSection('optimization-center')"
            >
              Show section
            </button>
          </div>
        </div>
      </div>
      <div
        v-else
        ref="optimizationCenterSectionRef"
        class="analyze-card"
      >
        <div class="analyze-section-header">
          <div>
            <div class="analyze-card-title">Optimization center</div>
            <div class="analyze-note">
              {{
                repairOwnerFocusSummary
                  ? `This section is currently following ${repairOwnerFocusSummary.ownerLabel} focus, so it only surfaces the highest-leverage repair moves for that owner.`
                  : 'Use this section for targeted drilldown after the summary and queue tell you where to look. It keeps the single highest-leverage move visible without re-reading the whole board.'
              }}
            </div>
          </div>
          <div v-if="repairOwnerFocusSummary" class="analyze-breakdown-actions">
            <button
              type="button"
              class="btn o__secondary"
              @click="clearRepairOwnerFocus"
            >
              Clear owner focus
            </button>
          </div>
        </div>
        <div class="analyze-action-list">
          <div
            v-for="item in optimizationCenterCards"
            :key="item.title"
            class="analyze-alert"
            :class="[`tone-${item.tone}`]"
          >
            <div class="analyze-alert-title">{{ item.title }}</div>
            <div class="analyze-note">{{ item.body }}</div>
            <div class="analyze-breakdown-actions mt-2">
              <button
                type="button"
                class="btn o__secondary"
                @click="openAnalyzeAction(item)"
              >
                {{ item.actionLabel }}
              </button>
              <button
                v-if="item.testQuery"
                type="button"
                class="btn o__secondary"
                @click="retestAnalyzeHotspot(item)"
              >
                {{
                  item.testCompareBundle
                    ? 'Open Test bundle'
                    : 'Retest hotspot'
                }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="analyze-card analyze-card--overview">
        <div class="analyze-section-header">
          <div>
            <div class="analyze-card-title">Quick filters</div>
            <div class="analyze-note">
              {{ quickFiltersLeadNote }}
            </div>
          </div>
          <div class="analyze-context-actions">
            <div class="analyze-note">
              {{ quickFiltersSideNote }}
            </div>
            <button
              v-if="activeAnalyzeFilterPresetKey !== 'overview'"
              type="button"
              class="btn o__secondary"
              @click="applyAnalyzeFilterPreset('overview')"
            >
              Reset to overview
            </button>
          </div>
        </div>
        <div class="analyze-filters analyze-filters--overview">
          <label class="analyze-field">
            <span class="analyze-label">Inbox</span>
            <select v-model="filters.inbox_id" class="analyze-input">
              <option value="">All inboxes</option>
              <option
                v-for="ci in connectedInboxes"
                :key="ci.id"
                :value="ci.id"
              >
                {{ ci.name || resolveInboxName(ci.id) }}
              </option>
            </select>
          </label>
          <label class="analyze-field">
            <span class="analyze-label">Time window</span>
            <select v-model="filters.time_window" class="analyze-input">
              <option value="24h">Last 24h</option>
              <option value="7d">Last 7d</option>
              <option value="30d">Last 30d</option>
              <option value="all">All time</option>
            </select>
          </label>
          <label class="analyze-field">
            <span class="analyze-label">Review status</span>
            <select v-model="filters.failure_status" class="analyze-input">
              <option value="open">Needs review</option>
              <option value="reviewed">Reviewed</option>
              <option value="converted">Converted</option>
              <option value="ignored">Ignored</option>
              <option value="all">All</option>
            </select>
          </label>
        </div>
      </div>

      <div class="analyze-card">
        <div class="analyze-section-header">
          <div>
            <div class="analyze-card-title">Suggested next actions</div>
            <div class="analyze-note">
              {{
                repairOwnerFocusSummary
                  ? `Use these signals to decide what ${repairOwnerFocusSummary.ownerLabel} should do next.`
                  : 'Use these signals to decide what the team should do next.'
              }}
            </div>
          </div>
        </div>
        <div class="analyze-action-list">
          <div
            v-for="action in customerActionCards"
            :key="action.title"
            class="analyze-alert"
            :class="[`tone-${action.tone}`]"
          >
            <div class="analyze-alert-title">{{ action.title }}</div>
            <div class="analyze-note">{{ action.body }}</div>
            <div
              v-if="action.action || action.testQuery"
              class="analyze-breakdown-actions mt-2"
            >
              <button
                v-if="action.action"
                type="button"
                class="btn o__secondary"
                @click="openAnalyzeAction(action)"
              >
                {{ action.actionLabel || 'Open repair' }}
              </button>
              <button
                v-if="action.testQuery"
                type="button"
                class="btn o__secondary"
                @click="retestAnalyzeHotspot(action)"
              >
                {{
                  action.testCompareBundle
                    ? 'Open Test bundle'
                    : 'Retest hotspot'
                }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div v-if="showAdvancedDiagnostics" class="analyze-card">
        <div
          v-if="activeOverrideFixRecommendation"
          class="analyze-breakdown-card mb-3"
        >
          <div class="analyze-label">
            {{
              activeOverrideFixRecommendation.fixBriefTitle ||
              activeOverrideFixRecommendation.fixIntentTitle
            }}
          </div>
          <div
            v-if="activeOverrideFixRecommendation.fixBriefBody"
            class="analyze-note"
          >
            {{ activeOverrideFixRecommendation.fixBriefBody }}
          </div>
          <div
            v-if="activeOverrideFixRecommendation.fixBriefValidation"
            class="analyze-note"
          >
            Validate: {{ activeOverrideFixRecommendation.fixBriefValidation }}
          </div>
          <div v-if="activeOverrideCompareStageTitle" class="analyze-note">
            Current compare stage: {{ activeOverrideCompareStageTitle }}
          </div>
          <div
            v-if="activeOverrideCompareStageGuide?.compareStageEvidence"
            class="analyze-note"
          >
            Evidence: {{ activeOverrideCompareStageGuide.compareStageEvidence }}
          </div>
          <div
            v-if="activeOverrideCompareStageGuide?.compareStageFocus"
            class="analyze-note"
          >
            Check: {{ activeOverrideCompareStageGuide.compareStageFocus }}
          </div>
          <div
            v-if="activeOverrideCompareStageGuide?.compareStageDecision"
            class="analyze-note"
          >
            Decide: {{ activeOverrideCompareStageGuide.compareStageDecision }}
          </div>
          <div
            v-if="activeOverrideCompareStageGuide?.compareStageRepairMove"
            class="analyze-note"
          >
            Repair: {{ activeOverrideCompareStageGuide.compareStageRepairMove }}
          </div>
          <div
            v-if="activeOverrideCompareStageGuide?.compareStageExecutionBrief"
            class="analyze-note"
          >
            Execution:
            {{ activeOverrideCompareStageGuide.compareStageExecutionBrief }}
          </div>
          <div
            v-if="activeOverrideCompareStageGuide?.compareStageWatchpoint"
            class="analyze-note"
          >
            Watch: {{ activeOverrideCompareStageGuide.compareStageWatchpoint }}
          </div>
          <div
            v-if="activeOverrideCompareStageGuide?.compareStageValidationFocus"
            class="analyze-note"
          >
            Validation focus:
            {{ activeOverrideCompareStageGuide.compareStageValidationFocus }}
          </div>
          <div
            v-if="activeOverrideCompareStageGuide?.compareStageOwnerPacketTitle"
            class="analyze-note"
          >
            Owner packet:
            {{ activeOverrideCompareStageGuide.compareStageOwnerPacketTitle }}
          </div>
          <div
            v-if="activeOverrideCompareStageGuide?.compareStageOwnerPacketBody"
            class="analyze-note"
          >
            {{ activeOverrideCompareStageGuide.compareStageOwnerPacketBody }}
          </div>
          <div
            v-if="activeOverrideCompareStageGuide?.compareStageOwnerRole"
            class="analyze-note"
          >
            Owner role:
            {{ activeOverrideCompareStageGuide.compareStageOwnerRole }}
          </div>
          <div
            v-if="activeOverrideCompareStageGuide?.compareStageHandoffTarget"
            class="analyze-note"
          >
            Handoff target:
            {{ activeOverrideCompareStageGuide.compareStageHandoffTarget }}
          </div>
          <div
            v-if="activeOverrideCompareStageGuide?.compareStageSuccessCheck"
            class="analyze-note"
          >
            Success check:
            {{ activeOverrideCompareStageGuide.compareStageSuccessCheck }}
          </div>
          <div
            v-if="activeOverrideCompareStageGuide?.compareStageLedgerStatus"
            class="analyze-note"
          >
            Ledger status:
            {{ activeOverrideCompareStageGuide.compareStageLedgerStatus }}
          </div>
          <div
            v-if="activeOverrideCompareStageGuide?.compareStageLastAction"
            class="analyze-note"
          >
            Last action:
            {{ activeOverrideCompareStageGuide.compareStageLastAction }}
          </div>
          <div
            v-if="activeOverrideCompareStageGuide?.compareStageReopenRule"
            class="analyze-note"
          >
            Reopen rule:
            {{ activeOverrideCompareStageGuide.compareStageReopenRule }}
          </div>
          <div
            v-if="activeOverrideCompareStageGuide?.compareStageNextOwner"
            class="analyze-note"
          >
            Next owner:
            {{ activeOverrideCompareStageGuide.compareStageNextOwner }}
          </div>
          <div
            v-if="activeOverrideCompareStageGuide?.compareStageBlockedReason"
            class="analyze-note"
          >
            Blocked reason:
            {{ activeOverrideCompareStageGuide.compareStageBlockedReason }}
          </div>
          <div
            v-if="activeOverrideCompareStageGuide?.compareStageCompletionGate"
            class="analyze-note"
          >
            Completion gate:
            {{ activeOverrideCompareStageGuide.compareStageCompletionGate }}
          </div>
          <div
            v-if="activeOverrideCompareStageGuide?.compareStageDoneState"
            class="analyze-note"
          >
            Done state:
            {{ activeOverrideCompareStageGuide.compareStageDoneState }}
          </div>
          <div
            v-if="activeOverrideCompareStageGuide?.compareStageFailState"
            class="analyze-note"
          >
            Fail state:
            {{ activeOverrideCompareStageGuide.compareStageFailState }}
          </div>
          <div
            v-if="activeOverrideCompareStageGuide?.compareStageRollbackTrigger"
            class="analyze-note"
          >
            Rollback trigger:
            {{ activeOverrideCompareStageGuide.compareStageRollbackTrigger }}
          </div>
          <div
            v-if="activeOverrideCompareStageGuide?.nextStageTitle"
            class="analyze-note"
          >
            Next stage: {{ activeOverrideCompareStageGuide.nextStageTitle }}
          </div>
          <div
            v-if="activeOverrideFixRecommendation.repairBundleTitle"
            class="analyze-label mt-2"
          >
            {{ activeOverrideFixRecommendation.repairBundleTitle }}
          </div>
          <div
            v-if="activeOverrideFixRecommendation.repairBundleHotspotSummary"
            class="analyze-note"
          >
            {{ activeOverrideFixRecommendation.repairBundleHotspotSummary }}
          </div>
          <div
            v-if="activeOverrideFixRecommendation.repairBundleResolvedSummary"
            class="analyze-note"
          >
            {{ activeOverrideFixRecommendation.repairBundleResolvedSummary }}
          </div>
          <div
            v-if="activeOverrideFixRecommendation.repairBundleEscalatedSummary"
            class="analyze-note"
          >
            {{ activeOverrideFixRecommendation.repairBundleEscalatedSummary }}
          </div>
          <div
            v-if="activeOverrideFixRecommendation.repairBundleValidationSummary"
            class="analyze-note"
          >
            Validate slice:
            {{ activeOverrideFixRecommendation.repairBundleValidationSummary }}
          </div>
          <div class="analyze-breakdown-actions mt-2">
            <button
              v-if="activeOverrideFixRecommendation.repairBundleHotspotActionTitle"
              type="button"
              :class="[
                'btn',
                isActiveOverrideFixRecommendationStage(
                  activeOverrideFixRecommendation,
                  'hotspot'
                )
                  ? 'o__primary'
                  : 'o__secondary',
              ]"
              @click="
                inspectActiveOverrideFixRecommendationAction(
                  activeOverrideFixRecommendation,
                  'hotspot'
                )
              "
            >
              {{ activeOverrideFixRecommendation.repairBundleHotspotActionTitle }}
            </button>
            <button
              v-if="activeOverrideFixRecommendation.repairBundleResolvedActionTitle"
              type="button"
              :class="[
                'btn',
                isActiveOverrideFixRecommendationStage(
                  activeOverrideFixRecommendation,
                  'resolved'
                )
                  ? 'o__primary'
                  : 'o__secondary',
              ]"
              @click="
                inspectActiveOverrideFixRecommendationAction(
                  activeOverrideFixRecommendation,
                  'resolved'
                )
              "
            >
              {{ activeOverrideFixRecommendation.repairBundleResolvedActionTitle }}
            </button>
            <button
              v-if="activeOverrideFixRecommendation.repairBundleEscalatedActionTitle"
              type="button"
              :class="[
                'btn',
                isActiveOverrideFixRecommendationStage(
                  activeOverrideFixRecommendation,
                  'escalated'
                )
                  ? 'o__primary'
                  : 'o__secondary',
              ]"
              @click="
                inspectActiveOverrideFixRecommendationAction(
                  activeOverrideFixRecommendation,
                  'escalated'
                )
              "
            >
              {{ activeOverrideFixRecommendation.repairBundleEscalatedActionTitle }}
            </button>
            <button
              v-if="activeOverrideFixRecommendation.repairBundleValidationActionTitle"
              type="button"
              :class="[
                'btn',
                isActiveOverrideFixRecommendationStage(
                  activeOverrideFixRecommendation,
                  'validation'
                )
                  ? 'o__primary'
                  : 'o__secondary',
              ]"
              @click="
                inspectActiveOverrideFixRecommendationAction(
                  activeOverrideFixRecommendation,
                  'validation'
                )
              "
            >
              {{
                activeOverrideFixRecommendation.repairBundleValidationActionTitle
              }}
            </button>
            <button
              v-if="
                activeOverrideCompareStageGuide?.nextStageCode &&
                repairBundleActionKeyForStageCode(
                  activeOverrideCompareStageGuide.nextStageCode
                )
              "
              type="button"
              class="btn o__secondary"
              @click="
                inspectActiveOverrideFixRecommendationAction(
                  activeOverrideFixRecommendation,
                  repairBundleActionKeyForStageCode(
                    activeOverrideCompareStageGuide.nextStageCode
                  )
                )
              "
            >
              Next:
              {{ activeOverrideCompareStageGuide.nextStageTitle }}
            </button>
          </div>
        </div>
        <div class="analyze-filters">
          <label class="analyze-field">
            <span class="analyze-label">Inbox</span>
            <select v-model="filters.inbox_id" class="analyze-input">
              <option value="">All inboxes</option>
              <option v-for="ci in connectedInboxes" :key="ci.id" :value="ci.id">
                {{ ci.name || resolveInboxName(ci.id) }}
              </option>
            </select>
          </label>
          <label class="analyze-field">
            <span class="analyze-label">Conversation ID</span>
            <input
              v-model="filters.conversation_id"
              class="analyze-input"
              type="text"
              placeholder="12345"
            />
          </label>
          <label class="analyze-field">
            <span class="analyze-label">Mode</span>
            <select v-model="filters.mode" class="analyze-input">
              <option value="">All modes</option>
              <option value="ai_closer">ai_closer</option>
              <option value="ai_gamer">ai_gamer</option>
            </select>
          </label>
          <label class="analyze-field">
            <span class="analyze-label">Time window</span>
            <select v-model="filters.time_window" class="analyze-input">
              <option value="24h">Last 24h</option>
              <option value="7d">Last 7d</option>
              <option value="30d">Last 30d</option>
              <option value="all">All time</option>
            </select>
          </label>
          <label class="analyze-field">
            <span class="analyze-label">Trace type contains</span>
            <input v-model="filters.decision_type_like" class="analyze-input" type="text" placeholder="response.trace" />
          </label>
          <label class="analyze-field">
            <span class="analyze-label">Failure status</span>
            <select v-model="filters.failure_status" class="analyze-input">
              <option value="open">open</option>
              <option value="reviewed">reviewed</option>
              <option value="converted">converted</option>
              <option value="ignored">ignored</option>
              <option value="all">all</option>
            </select>
          </label>
          <label class="analyze-field">
            <span class="analyze-label">Source kind</span>
            <input v-model="filters.source_kind" class="analyze-input" type="text" placeholder="llm_generated" />
          </label>
          <label class="analyze-field">
            <span class="analyze-label">Intent</span>
            <input v-model="filters.intent" class="analyze-input" type="text" placeholder="support" />
          </label>
          <label class="analyze-field">
            <span class="analyze-label">Action</span>
            <input v-model="filters.action" class="analyze-input" type="text" placeholder="handoff" />
          </label>
          <label class="analyze-field">
            <span class="analyze-label">Response kind</span>
            <select v-model="filters.response_kind" class="analyze-input">
              <option value="">All</option>
              <option value="single">single</option>
              <option value="multi_segment">multi_segment</option>
            </select>
          </label>
          <label class="analyze-field">
            <span class="analyze-label">Delivery mode</span>
            <select v-model="filters.delivery_mode" class="analyze-input">
              <option value="">All</option>
              <option value="auto_reply">auto_reply</option>
              <option value="auto_suggest">auto_suggest</option>
              <option value="suggest">suggest</option>
              <option value="handoff">handoff</option>
            </select>
          </label>
          <label class="analyze-field">
            <span class="analyze-label">Auto-send</span>
            <select v-model="filters.auto_send_allowed" class="analyze-input">
              <option value="">All</option>
              <option value="true">true</option>
              <option value="false">false</option>
            </select>
          </label>
          <label class="analyze-field">
            <span class="analyze-label">Article scope warning</span>
            <input v-model="filters.article_scope_warning" class="analyze-input" type="text" placeholder="ambiguous_assistant_portals" />
          </label>
          <label class="analyze-field">
            <span class="analyze-label">Content gap reason</span>
            <input v-model="filters.content_gap_reason" class="analyze-input" type="text" placeholder="no_runtime_knowledge" />
          </label>
          <label class="analyze-field">
            <span class="analyze-label">Content gap cluster</span>
            <input v-model="filters.content_gap_cluster_key" class="analyze-input" type="text" placeholder="refund_timeline" />
          </label>
          <label class="analyze-field">
            <span class="analyze-label">Attribute key</span>
            <input v-model="filters.attribute_key" class="analyze-input" type="text" placeholder="intent" />
          </label>
          <label class="analyze-field">
            <span class="analyze-label">Attribute value</span>
            <input v-model="filters.attribute_value" class="analyze-input" type="text" placeholder="deposit" />
          </label>
          <label class="analyze-field">
            <span class="analyze-label">Override rule #</span>
            <input
              v-model="filters.attribute_override_rule_index"
              class="analyze-input"
              type="text"
              placeholder="0"
            />
          </label>
          <label class="analyze-field">
            <span class="analyze-label">Override label</span>
            <input v-model="filters.attribute_override_label" class="analyze-input" type="text" placeholder="Late payment override" />
          </label>
          <label class="analyze-field">
            <span class="analyze-label">Override action</span>
            <select v-model="filters.attribute_override_action" class="analyze-input">
              <option value="">All</option>
              <option value="inherit">inherit</option>
              <option value="auto_suggest">auto_suggest</option>
              <option value="suggest">suggest</option>
              <option value="handoff">handoff</option>
            </select>
          </label>
          <label class="analyze-field">
            <span class="analyze-label">Override match type</span>
            <select v-model="filters.attribute_override_match_type" class="analyze-input">
              <option value="">All</option>
              <option value="equals">equals</option>
              <option value="contains">contains</option>
              <option value="starts_with">starts_with</option>
              <option value="ends_with">ends_with</option>
              <option value="in">in</option>
              <option value="regex">regex</option>
              <option value="present">present</option>
              <option value="boolean">boolean</option>
            </select>
          </label>
          <label class="analyze-field">
            <span class="analyze-label">Override outcome</span>
            <select v-model="filters.attribute_override_outcome" class="analyze-input">
              <option value="">All</option>
              <option value="resolved">resolved</option>
              <option value="escalated">escalated</option>
              <option value="pending">pending</option>
            </select>
          </label>
          <label class="analyze-field">
            <span class="analyze-label">Matched condition</span>
            <input
              v-model="filters.attribute_override_condition"
              class="analyze-input"
              type="text"
              placeholder="all:intent equals &quot;deposit&quot;"
            />
          </label>
          <label class="analyze-field">
            <span class="analyze-label">Passed guard</span>
            <input
              v-model="filters.attribute_override_guard"
              class="analyze-input"
              type="text"
              placeholder="not:vip_whitelisted is true"
            />
          </label>
          <label class="analyze-field">
            <span class="analyze-label">Guidance section</span>
            <input v-model="filters.guidance_section" class="analyze-input" type="text" placeholder="instructions" />
          </label>
          <label class="analyze-field">
            <span class="analyze-label">Guidance card</span>
            <input v-model="filters.guidance_card" class="analyze-input" type="text" placeholder="runtime_assembly" />
          </label>
          <label class="analyze-field">
            <span class="analyze-label">Guidance scenario</span>
            <input v-model="filters.guidance_scenario" class="analyze-input" type="text" placeholder="legacy:support-flow:1" />
          </label>
          <label class="analyze-field">
            <span class="analyze-label">Answer length</span>
            <select v-model="filters.answer_length_strategy" class="analyze-input">
              <option value="">All</option>
              <option value="concise">concise</option>
              <option value="balanced">balanced</option>
              <option value="detailed">detailed</option>
            </select>
          </label>
          <label class="analyze-field">
            <span class="analyze-label">Directness</span>
            <select v-model="filters.directness_strategy" class="analyze-input">
              <option value="">All</option>
              <option value="answer_first">answer_first</option>
              <option value="clarify_first">clarify_first</option>
              <option value="guided">guided</option>
            </select>
          </label>
          <label class="analyze-field">
            <span class="analyze-label">Sensitive-topic handling</span>
            <select v-model="filters.sensitive_topics_strategy" class="analyze-input">
              <option value="">All</option>
              <option value="conservative">conservative</option>
              <option value="standard">standard</option>
            </select>
          </label>
          <label class="analyze-field">
            <span class="analyze-label">Tone enforcement</span>
            <select v-model="filters.tone_enforcement_strategy" class="analyze-input">
              <option value="">All</option>
              <option value="flexible">flexible</option>
              <option value="balanced">balanced</option>
              <option value="strict">strict</option>
            </select>
          </label>
          <label class="analyze-field">
            <span class="analyze-label">Memory depth</span>
            <select v-model="filters.memory_depth_strategy" class="analyze-input">
              <option value="">All</option>
              <option value="recent">recent</option>
              <option value="standard">standard</option>
              <option value="deep">deep</option>
            </select>
          </label>
          <label class="analyze-field">
            <span class="analyze-label">Answer confidence</span>
            <select v-model="filters.answer_confidence_strategy" class="analyze-input">
              <option value="">All</option>
              <option value="off">off</option>
              <option value="standard">standard</option>
              <option value="strict">strict</option>
            </select>
          </label>
          <label class="analyze-field">
            <span class="analyze-label">Outside knowledge rule</span>
            <select v-model="filters.outside_knowledge_strategy" class="analyze-input">
              <option value="">All</option>
              <option value="clarify">clarify</option>
              <option value="escalate">escalate</option>
            </select>
          </label>
          <label class="analyze-field">
            <span class="analyze-label">Promise safety</span>
            <select v-model="filters.promise_safety_strategy" class="analyze-input">
              <option value="">All</option>
              <option value="standard">standard</option>
              <option value="strict">strict</option>
            </select>
          </label>
          <label class="analyze-field">
            <span class="analyze-label">Forced reply language</span>
            <select v-model="filters.forced_reply_language" class="analyze-input">
              <option value="">All</option>
              <option value="en">en</option>
              <option value="th">th</option>
              <option value="ms">ms</option>
            </select>
          </label>
          <label class="analyze-field">
            <span class="analyze-label">Communication style</span>
            <select v-model="filters.communication_style_mode" class="analyze-input">
              <option value="">All</option>
              <option value="friendly">friendly</option>
              <option value="professional">professional</option>
              <option value="casual">casual</option>
              <option value="formal">formal</option>
              <option value="adaptive">adaptive</option>
            </select>
          </label>
          <label class="analyze-field">
            <span class="analyze-label">Deploy default mode</span>
            <select
              v-model="filters.channel_runtime_default_reply_mode"
              class="analyze-input"
            >
              <option value="">All</option>
              <option value="auto_reply">auto_reply</option>
              <option value="auto_suggest">auto_suggest</option>
              <option value="suggest">suggest</option>
              <option value="handoff">handoff</option>
            </select>
          </label>
          <label class="analyze-field">
            <span class="analyze-label">Deploy binding scope</span>
            <select
              v-model="filters.channel_runtime_binding_scope"
              class="analyze-input"
            >
              <option value="">All</option>
              <option value="assistant_default">assistant_default</option>
              <option value="inbox">inbox</option>
              <option value="segment">segment</option>
              <option value="business_hours">business_hours</option>
            </select>
          </label>
          <label class="analyze-field">
            <span class="analyze-label">Deploy segment rule</span>
            <select
              v-model="filters.channel_runtime_active_segment_type"
              class="analyze-input"
            >
              <option value="">All</option>
              <option value="reply_language">reply_language</option>
              <option value="conversation_language">conversation_language</option>
              <option value="vip_conversation">vip_conversation</option>
            </select>
          </label>
          <label class="analyze-field">
            <span class="analyze-label">Business-hours active</span>
            <select v-model="filters.business_hours_active" class="analyze-input">
              <option value="">All</option>
              <option value="true">true</option>
              <option value="false">false</option>
            </select>
          </label>
          <label class="analyze-field">
            <span class="analyze-label">Business-hours mode</span>
            <select v-model="filters.business_hours_reply_mode" class="analyze-input">
              <option value="">All</option>
              <option value="inherit">inherit</option>
              <option value="auto_reply">auto_reply</option>
              <option value="auto_suggest">auto_suggest</option>
              <option value="suggest">suggest</option>
              <option value="handoff">handoff</option>
            </select>
          </label>
          <label class="analyze-field">
            <span class="analyze-label">Business-hours behavior</span>
            <select
              v-model="filters.business_hours_response_behavior"
              class="analyze-input"
            >
              <option value="">All</option>
              <option value="normal_ai">normal_ai</option>
              <option value="inbox_out_of_office_message">
                inbox_out_of_office_message
              </option>
            </select>
          </label>
          <label class="analyze-field">
            <span class="analyze-label">Region binding scope</span>
            <select
              v-model="filters.region_compliance_binding_scope"
              class="analyze-input"
            >
              <option value="">All</option>
              <option value="default_profile">default_profile</option>
              <option value="region_binding">region_binding</option>
            </select>
          </label>
          <label class="analyze-field">
            <span class="analyze-label">Active market</span>
            <select
              v-model="filters.region_compliance_active_country_code"
              class="analyze-input"
            >
              <option value="">All</option>
              <option value="MY">MY</option>
              <option value="ID">ID</option>
              <option value="SG">SG</option>
              <option value="TH">TH</option>
              <option value="VN">VN</option>
              <option value="PH">PH</option>
            </select>
          </label>
          <label class="analyze-field">
            <span class="analyze-label">Compliance profile</span>
            <select
              v-model="filters.region_compliance_active_profile"
              class="analyze-input"
            >
              <option value="">All</option>
              <option value="standard">standard</option>
              <option value="strict">strict</option>
              <option value="regulated">regulated</option>
            </select>
          </label>
          <label class="analyze-field">
            <span class="analyze-label">Uncertain route</span>
            <select v-model="filters.uncertain_action" class="analyze-input">
              <option value="">All</option>
              <option value="fallback_ai">fallback_ai</option>
              <option value="ask_clarify">ask_clarify</option>
              <option value="default_reply">default_reply</option>
            </select>
          </label>
          <label class="analyze-field">
            <span class="analyze-label">High-risk default</span>
            <select v-model="filters.high_risk_action" class="analyze-input">
              <option value="">All</option>
              <option value="inherit">inherit</option>
              <option value="suggest">suggest</option>
              <option value="auto_suggest">auto_suggest</option>
              <option value="handoff">handoff</option>
            </select>
          </label>
          <label class="analyze-field">
            <span class="analyze-label">Procedure precedence</span>
            <select v-model="filters.procedure_routing_precedence" class="analyze-input">
              <option value="">All</option>
              <option value="procedure_before_fallback">procedure_before_fallback</option>
              <option value="fallback_before_procedure">fallback_before_procedure</option>
            </select>
          </label>
          <label class="analyze-field">
            <span class="analyze-label">Step confirmation</span>
            <select
              v-model="filters.procedure_step_confirmation_strategy"
              class="analyze-input"
            >
              <option value="">All</option>
              <option value="confirm_each_step">confirm_each_step</option>
              <option value="balanced">balanced</option>
              <option value="collect_brief_batch">collect_brief_batch</option>
            </select>
          </label>
          <label class="analyze-field">
            <span class="analyze-label">Procedure fallback</span>
            <select v-model="filters.procedure_fallback_action" class="analyze-input">
              <option value="">All</option>
              <option value="ask_clarify">ask_clarify</option>
              <option value="default_reply">default_reply</option>
              <option value="handoff">handoff</option>
            </select>
          </label>
          <div class="analyze-filter-actions">
            <button type="button" class="btn o__secondary" @click="refreshAll">
              Apply
            </button>
          </div>
        </div>
      </div>

      <div v-if="showAdvancedDiagnostics" class="analyze-two-col">
        <div class="analyze-card">
          <div class="analyze-section-header">
            <div>
              <div class="analyze-card-title">What needs attention</div>
              <div class="analyze-note">
                Signals generated from the current {{ timeWindowLabel.toLowerCase() }} sample.
              </div>
            </div>
          </div>

          <div v-if="healthAlerts.length" class="analyze-alert-list">
            <div
              v-for="alertItem in healthAlerts"
              :key="alertItem.title"
              class="analyze-alert"
              :class="[`tone-${alertItem.tone}`]"
            >
              <div class="analyze-alert-title">{{ alertItem.title }}</div>
              <div class="analyze-note">{{ alertItem.body }}</div>
            </div>
          </div>
          <div v-else class="analyze-empty">
            No alerts in the current sample window.
          </div>
        </div>

        <div class="analyze-card">
          <div class="analyze-section-header">
            <div>
              <div class="analyze-card-title">Recent changes</div>
              <div class="analyze-note">
                Trace, failure, timeout, and delivery-guard volume in the current window.
              </div>
            </div>
          </div>

          <div class="analyze-trend-stack">
            <div class="analyze-trend-card">
              <div class="analyze-label">Response traces</div>
              <div v-if="traceDailySeries.length" class="analyze-series-list">
                <div v-for="row in traceDailySeries" :key="`trace-${row.label}`" class="analyze-series-row">
                  <span class="analyze-series-label">{{ row.label }}</span>
                  <div class="analyze-series-bar-track">
                    <div
                      class="analyze-series-bar is-trace"
                      :style="{ width: `${(row.count / maxSeriesCount(traceDailySeries)) * 100}%` }"
                    />
                  </div>
                  <strong>{{ row.count }}</strong>
                </div>
              </div>
              <div v-else class="analyze-note">No trace trend data.</div>
            </div>

            <div class="analyze-trend-card">
              <div class="analyze-label">Failure tags</div>
              <div v-if="failureDailySeries.length" class="analyze-series-list">
                <div v-for="row in failureDailySeries" :key="`failure-${row.label}`" class="analyze-series-row">
                  <span class="analyze-series-label">{{ row.label }}</span>
                  <div class="analyze-series-bar-track">
                    <div
                      class="analyze-series-bar is-failure"
                      :style="{ width: `${(row.count / maxSeriesCount(failureDailySeries)) * 100}%` }"
                    />
                  </div>
                  <strong>{{ row.count }}</strong>
                </div>
              </div>
              <div v-else class="analyze-note">No failure trend data.</div>
            </div>

            <div class="analyze-trend-card">
              <div class="analyze-label">Timeout expired</div>
              <div v-if="timeoutDailySeries.length" class="analyze-series-list">
                <div v-for="row in timeoutDailySeries" :key="`timeout-${row.label}`" class="analyze-series-row">
                  <span class="analyze-series-label">{{ row.label }}</span>
                  <div class="analyze-series-bar-track">
                    <div
                      class="analyze-series-bar is-warning"
                      :style="{ width: `${(row.count / maxSeriesCount(timeoutDailySeries)) * 100}%` }"
                    />
                  </div>
                  <strong>{{ row.count }}</strong>
                </div>
              </div>
              <div v-else class="analyze-note">No timeout trend data.</div>
            </div>

            <div class="analyze-trend-card">
              <div class="analyze-label">Delivery blocked</div>
              <div v-if="deliveryBlockedDailySeries.length" class="analyze-series-list">
                <div v-for="row in deliveryBlockedDailySeries" :key="`blocked-${row.label}`" class="analyze-series-row">
                  <span class="analyze-series-label">{{ row.label }}</span>
                  <div class="analyze-series-bar-track">
                    <div
                      class="analyze-series-bar is-neutral"
                      :style="{ width: `${(row.count / maxSeriesCount(deliveryBlockedDailySeries)) * 100}%` }"
                    />
                  </div>
                  <strong>{{ row.count }}</strong>
                </div>
              </div>
              <div v-else class="analyze-note">No delivery-blocked trend data.</div>
            </div>

            <div class="analyze-trend-card">
              <div class="analyze-label">Attribute override hits</div>
              <div v-if="attributeOverrideDailySeries.length" class="analyze-series-list">
                <div v-for="row in attributeOverrideDailySeries" :key="`override-${row.label}`" class="analyze-series-row">
                  <span class="analyze-series-label">{{ row.label }}</span>
                  <div class="analyze-series-bar-track">
                    <div
                      class="analyze-series-bar is-warning"
                      :style="{ width: `${(row.count / maxSeriesCount(attributeOverrideDailySeries)) * 100}%` }"
                    />
                  </div>
                  <strong>{{ row.count }}</strong>
                </div>
              </div>
              <div v-else class="analyze-note">No attribute-override trend data.</div>
            </div>
          </div>
        </div>
      </div>

      <div class="analyze-two-col">
        <div class="analyze-card">
          <div class="analyze-section-header">
            <div>
              <div class="analyze-card-title">Trace breakdowns</div>
              <div class="analyze-note">
                Top patterns from the latest trace sample (up to 200 events).
              </div>
            </div>
          </div>

          <div v-if="isLoadingBreakdowns" class="analyze-empty">Loading breakdowns...</div>
          <div v-else class="analyze-breakdown-grid">
            <div class="analyze-breakdown-card">
              <div class="analyze-label">Top sources</div>
              <div v-if="traceSourceBreakdown.length" class="analyze-breakdown-list">
                <div v-for="row in traceSourceBreakdown" :key="row.label" class="analyze-breakdown-row">
                  <span>{{ row.label }}</span>
                  <strong>{{ row.count }}</strong>
                </div>
              </div>
              <div v-else class="analyze-note">No source data.</div>
            </div>
            <div class="analyze-breakdown-card">
              <div class="analyze-label">Top reasons</div>
              <div v-if="traceReasonBreakdown.length" class="analyze-breakdown-list">
                <div v-for="row in traceReasonBreakdown" :key="row.label" class="analyze-breakdown-row">
                  <span>{{ row.label }}</span>
                  <strong>{{ row.count }}</strong>
                </div>
              </div>
              <div v-else class="analyze-note">No reason data.</div>
            </div>
            <div class="analyze-breakdown-card">
              <div class="analyze-label">Top intents</div>
              <div v-if="traceIntentBreakdown.length" class="analyze-breakdown-list">
                <div v-for="row in traceIntentBreakdown" :key="row.label" class="analyze-breakdown-row">
                  <span>{{ row.label }}</span>
                  <strong>{{ row.count }}</strong>
                </div>
              </div>
              <div v-else class="analyze-note">No intent data.</div>
            </div>
            <div class="analyze-breakdown-card">
              <div class="analyze-label">Top actions</div>
              <div v-if="traceActionBreakdown.length" class="analyze-breakdown-list">
                <div v-for="row in traceActionBreakdown" :key="row.label" class="analyze-breakdown-row">
                  <span>{{ row.label }}</span>
                  <strong>{{ row.count }}</strong>
                </div>
              </div>
              <div v-else class="analyze-note">No action data.</div>
            </div>
            <div class="analyze-breakdown-card">
              <div class="analyze-label">Response kinds</div>
              <div v-if="responseKindBreakdown.length" class="analyze-breakdown-list">
                <div v-for="row in responseKindBreakdown" :key="row.label" class="analyze-breakdown-row">
                  <span>{{ row.label }}</span>
                  <strong>{{ row.count }}</strong>
                </div>
              </div>
              <div v-else class="analyze-note">No response kind data.</div>
            </div>
            <div class="analyze-breakdown-card">
              <div class="analyze-label">Statuses</div>
              <div v-if="traceStatusBreakdown.length" class="analyze-breakdown-list">
                <div v-for="row in traceStatusBreakdown" :key="row.label" class="analyze-breakdown-row">
                  <span>{{ row.label }}</span>
                  <strong>{{ row.count }}</strong>
                </div>
              </div>
              <div v-else class="analyze-note">No status data.</div>
            </div>
            <div class="analyze-breakdown-card">
              <div class="analyze-label">Delivery modes</div>
              <div v-if="traceDeliveryBreakdown.length" class="analyze-breakdown-list">
                <div v-for="row in traceDeliveryBreakdown" :key="row.label" class="analyze-breakdown-row">
                  <span>{{ row.label }}</span>
                  <strong>{{ row.count }}</strong>
                </div>
              </div>
              <div v-else class="analyze-note">No delivery mode data.</div>
            </div>
            <div class="analyze-breakdown-card">
              <div class="analyze-label">Guidance sections</div>
              <div v-if="guidanceSectionBreakdown.length" class="analyze-breakdown-list">
                <div v-for="row in guidanceSectionBreakdown" :key="row.label" class="analyze-breakdown-row">
                  <span>{{ row.label }}</span>
                  <strong>{{ row.count }}</strong>
                </div>
              </div>
              <div v-else class="analyze-note">No guidance section data.</div>
            </div>
            <div class="analyze-breakdown-card">
              <div class="analyze-label">Guidance cards</div>
              <div v-if="traceGuidanceCardBreakdown.length" class="analyze-breakdown-list">
                <div v-for="row in traceGuidanceCardBreakdown" :key="row.label" class="analyze-breakdown-row">
                  <span>{{ row.label }}</span>
                  <strong>{{ row.count }}</strong>
                </div>
              </div>
              <div v-else class="analyze-note">No guidance card data.</div>
            </div>
            <div class="analyze-breakdown-card">
              <div class="analyze-label">Guidance scenarios</div>
              <div v-if="traceGuidanceScenarioBreakdown.length" class="analyze-breakdown-list">
                <div v-for="row in traceGuidanceScenarioBreakdown" :key="row.label" class="analyze-breakdown-row">
                  <span>{{ row.label }}</span>
                  <strong>{{ row.count }}</strong>
                </div>
              </div>
              <div v-else class="analyze-note">No guidance scenario data.</div>
            </div>
            <div class="analyze-breakdown-card">
              <div class="analyze-label">Forced reply language</div>
              <div v-if="traceForcedLanguageBreakdown.length" class="analyze-breakdown-list">
                <div v-for="row in traceForcedLanguageBreakdown" :key="row.label" class="analyze-breakdown-row">
                  <span>{{ row.label }}</span>
                  <strong>{{ row.count }}</strong>
                </div>
              </div>
              <div v-else class="analyze-note">No forced language data.</div>
            </div>
            <div class="analyze-breakdown-card">
              <div class="analyze-label">Communication styles</div>
              <div v-if="traceCommunicationStyleBreakdown.length" class="analyze-breakdown-list">
                <div v-for="row in traceCommunicationStyleBreakdown" :key="row.label" class="analyze-breakdown-row">
                  <span>{{ row.label }}</span>
                  <strong>{{ row.count }}</strong>
                </div>
              </div>
              <div v-else class="analyze-note">No communication style data.</div>
            </div>
            <div class="analyze-breakdown-card">
              <div class="analyze-label">Answer length strategies</div>
              <div v-if="traceAnswerLengthStrategyBreakdown.length" class="analyze-breakdown-list">
                <div v-for="row in traceAnswerLengthStrategyBreakdown" :key="row.label" class="analyze-breakdown-row">
                  <span>{{ row.label }}</span>
                  <strong>{{ row.count }}</strong>
                </div>
              </div>
              <div v-else class="analyze-note">No answer-length data.</div>
            </div>
            <div class="analyze-breakdown-card">
              <div class="analyze-label">Directness strategies</div>
              <div v-if="traceDirectnessStrategyBreakdown.length" class="analyze-breakdown-list">
                <div v-for="row in traceDirectnessStrategyBreakdown" :key="row.label" class="analyze-breakdown-row">
                  <span>{{ row.label }}</span>
                  <strong>{{ row.count }}</strong>
                </div>
              </div>
              <div v-else class="analyze-note">No directness data.</div>
            </div>
            <div class="analyze-breakdown-card">
              <div class="analyze-label">Sensitive-topic strategies</div>
              <div v-if="traceSensitiveTopicsStrategyBreakdown.length" class="analyze-breakdown-list">
                <div v-for="row in traceSensitiveTopicsStrategyBreakdown" :key="row.label" class="analyze-breakdown-row">
                  <span>{{ row.label }}</span>
                  <strong>{{ row.count }}</strong>
                </div>
              </div>
              <div v-else class="analyze-note">No sensitive-topic data.</div>
            </div>
            <div class="analyze-breakdown-card">
              <div class="analyze-label">Tone-enforcement strategies</div>
              <div v-if="traceToneEnforcementStrategyBreakdown.length" class="analyze-breakdown-list">
                <div v-for="row in traceToneEnforcementStrategyBreakdown" :key="row.label" class="analyze-breakdown-row">
                  <span>{{ row.label }}</span>
                  <strong>{{ row.count }}</strong>
                </div>
              </div>
              <div v-else class="analyze-note">No tone-enforcement data.</div>
            </div>
            <div class="analyze-breakdown-card">
              <div class="analyze-label">Memory-depth strategies</div>
              <div v-if="traceMemoryDepthStrategyBreakdown.length" class="analyze-breakdown-list">
                <div v-for="row in traceMemoryDepthStrategyBreakdown" :key="row.label" class="analyze-breakdown-row">
                  <span>{{ row.label }}</span>
                  <strong>{{ row.count }}</strong>
                </div>
              </div>
              <div v-else class="analyze-note">No memory-depth data.</div>
            </div>
            <div class="analyze-breakdown-card">
              <div class="analyze-label">Answer-confidence strategies</div>
              <div v-if="traceAnswerConfidenceStrategyBreakdown.length" class="analyze-breakdown-list">
                <div v-for="row in traceAnswerConfidenceStrategyBreakdown" :key="row.label" class="analyze-breakdown-row">
                  <span>{{ row.label }}</span>
                  <strong>{{ row.count }}</strong>
                </div>
              </div>
              <div v-else class="analyze-note">No answer-confidence data.</div>
            </div>
            <div class="analyze-breakdown-card">
              <div class="analyze-label">Outside-knowledge rules</div>
              <div v-if="traceOutsideKnowledgeStrategyBreakdown.length" class="analyze-breakdown-list">
                <div v-for="row in traceOutsideKnowledgeStrategyBreakdown" :key="row.label" class="analyze-breakdown-row">
                  <span>{{ row.label }}</span>
                  <strong>{{ row.count }}</strong>
                </div>
              </div>
              <div v-else class="analyze-note">No outside-knowledge rule data.</div>
            </div>
            <div class="analyze-breakdown-card">
              <div class="analyze-label">Promise-safety rules</div>
              <div v-if="tracePromiseSafetyStrategyBreakdown.length" class="analyze-breakdown-list">
                <div v-for="row in tracePromiseSafetyStrategyBreakdown" :key="row.label" class="analyze-breakdown-row">
                  <span>{{ row.label }}</span>
                  <strong>{{ row.count }}</strong>
                </div>
              </div>
              <div v-else class="analyze-note">No promise-safety data.</div>
            </div>
            <div class="analyze-breakdown-card">
              <div class="analyze-label">Uncertain routes</div>
              <div v-if="traceUncertainActionBreakdown.length" class="analyze-breakdown-list">
                <div v-for="row in traceUncertainActionBreakdown" :key="row.label" class="analyze-breakdown-row">
                  <span>{{ row.label }}</span>
                  <strong>{{ row.count }}</strong>
                </div>
              </div>
              <div v-else class="analyze-note">No uncertain-route data.</div>
            </div>
            <div class="analyze-breakdown-card">
              <div class="analyze-label">High-risk defaults</div>
              <div v-if="traceHighRiskActionBreakdown.length" class="analyze-breakdown-list">
                <div v-for="row in traceHighRiskActionBreakdown" :key="row.label" class="analyze-breakdown-row">
                  <span>{{ row.label }}</span>
                  <strong>{{ row.count }}</strong>
                </div>
              </div>
              <div v-else class="analyze-note">No high-risk default data.</div>
            </div>
            <div class="analyze-breakdown-card">
              <div class="analyze-label">Content gap reasons</div>
              <div v-if="contentGapReasonBreakdown.length" class="analyze-breakdown-list">
                <div v-for="row in contentGapReasonBreakdown" :key="row.label" class="analyze-breakdown-row">
                  <span>{{ row.label }}</span>
                  <strong>{{ row.count }}</strong>
                </div>
              </div>
              <div v-else class="analyze-note">No content-gap traces in this window.</div>
            </div>
            <div class="analyze-breakdown-card">
              <div class="analyze-label">Attribute keys</div>
              <div v-if="attributeKeyBreakdown.length" class="analyze-breakdown-list">
                <div v-for="row in attributeKeyBreakdown" :key="row.label" class="analyze-breakdown-row">
                  <span>{{ row.label }}</span>
                  <strong>{{ row.count }}</strong>
                </div>
              </div>
              <div v-else class="analyze-note">No attribute key data in this window.</div>
            </div>
            <div class="analyze-breakdown-card">
              <div class="analyze-label">Override labels</div>
              <div v-if="attributeOverrideLabelBreakdown.length" class="analyze-breakdown-list">
                <div v-for="row in attributeOverrideLabelBreakdown" :key="row.label" class="analyze-breakdown-row">
                  <span>{{ row.label }}</span>
                  <strong>{{ row.count }}</strong>
                </div>
              </div>
              <div v-else class="analyze-note">No override label data in this window.</div>
            </div>
            <div class="analyze-breakdown-card">
              <div class="analyze-label">Override matchers</div>
              <div v-if="attributeOverrideMatchTypeBreakdown.length" class="analyze-breakdown-list">
                <div v-for="row in attributeOverrideMatchTypeBreakdown" :key="row.label" class="analyze-breakdown-row">
                  <span>{{ row.label }}</span>
                  <strong>{{ row.count }}</strong>
                </div>
              </div>
              <div v-else class="analyze-note">No override matcher data in this window.</div>
            </div>
          </div>
        </div>

        <div class="analyze-card">
          <div class="analyze-section-header">
            <div>
              <div class="analyze-card-title">Hotspots</div>
              <div class="analyze-note">
                Failure concentration, timeout pressure, content-gap topic and cluster hotspots, exact-trace-anchored guidance outcomes when available, fallback next-trace-bounded outcomes for older rows, blocked delivery mix, and the latest trace snapshot.
              </div>
            </div>
          </div>

          <div class="analyze-breakdown-card mb-3">
            <div class="analyze-label">Failure intents</div>
            <div v-if="failureIntentBreakdown.length" class="analyze-breakdown-list">
              <div v-for="row in failureIntentBreakdown" :key="row.label" class="analyze-breakdown-row">
                <span>{{ row.label }}</span>
                <strong>{{ row.count }}</strong>
              </div>
            </div>
            <div v-else class="analyze-note">No failure intent data.</div>
          </div>

          <div class="analyze-breakdown-card mb-3">
            <div class="analyze-label">Failure statuses</div>
            <div v-if="failureStatusBreakdown.length" class="analyze-breakdown-list">
              <div v-for="row in failureStatusBreakdown" :key="row.label" class="analyze-breakdown-row">
                <span>{{ row.label }}</span>
                <strong>{{ row.count }}</strong>
              </div>
            </div>
            <div v-else class="analyze-note">No failure status data.</div>
          </div>

          <div class="analyze-breakdown-card mb-3">
            <div class="analyze-label">Timeout intents</div>
            <div v-if="timeoutIntentBreakdown.length" class="analyze-breakdown-list">
              <div v-for="row in timeoutIntentBreakdown" :key="row.label" class="analyze-breakdown-row">
                <span>{{ row.label }}</span>
                <strong>{{ row.count }}</strong>
              </div>
            </div>
            <div v-else class="analyze-note">No timeout hotspot data.</div>
          </div>

          <div class="analyze-breakdown-card mb-3">
            <div class="analyze-label">Blocked delivery modes</div>
            <div v-if="deliveryBlockedModeBreakdown.length" class="analyze-breakdown-list">
              <div v-for="row in deliveryBlockedModeBreakdown" :key="row.label" class="analyze-breakdown-row">
                <span>{{ row.label }}</span>
                <strong>{{ row.count }}</strong>
              </div>
            </div>
            <div v-else class="analyze-note">No blocked delivery data.</div>
          </div>

          <div class="analyze-breakdown-card mb-3">
            <div class="analyze-label">Article scope warnings</div>
            <div v-if="articleScopeWarningBreakdown.length" class="analyze-breakdown-list">
              <div v-for="row in articleScopeWarningBreakdown" :key="row.label" class="analyze-breakdown-row">
                <span>{{ row.label }}</span>
                <strong>{{ row.count }}</strong>
              </div>
            </div>
            <div v-else class="analyze-note">No article scope warnings in this window.</div>
          </div>

          <div class="analyze-breakdown-card mb-3">
            <div class="analyze-label">Content gap topics</div>
            <div v-if="contentGapTopicBreakdown.length" class="analyze-breakdown-list">
              <div v-for="row in contentGapTopicBreakdown" :key="row.label" class="analyze-breakdown-row">
                <span>{{ row.label }}</span>
                <strong>{{ row.count }}</strong>
              </div>
            </div>
            <div v-else class="analyze-note">No content-gap topic data in this window.</div>
          </div>

          <div class="analyze-breakdown-card mb-3">
            <div class="analyze-label">Content gap clusters</div>
            <div v-if="contentGapClusterBreakdown.length" class="analyze-breakdown-list">
              <div v-for="row in contentGapClusterBreakdown" :key="row.label" class="analyze-breakdown-row">
                <span>{{ row.label }}</span>
                <strong>{{ row.count }}</strong>
              </div>
            </div>
            <div v-else class="analyze-note">No content-gap cluster data in this window.</div>
          </div>

          <div class="analyze-breakdown-card mb-3">
            <div class="analyze-label">Attribute sources</div>
            <div v-if="attributeSourceBreakdown.length" class="analyze-breakdown-list">
              <div v-for="row in attributeSourceBreakdown" :key="row.label" class="analyze-breakdown-row">
                <span>{{ row.label }}</span>
                <strong>{{ row.count }}</strong>
              </div>
            </div>
            <div v-else class="analyze-note">No attribute source data in this window.</div>
          </div>

          <div class="analyze-breakdown-card mb-3">
            <div class="analyze-label">Override actions</div>
            <div v-if="attributeOverrideActionBreakdown.length" class="analyze-breakdown-list">
              <div v-for="row in attributeOverrideActionBreakdown" :key="row.label" class="analyze-breakdown-row">
                <span>{{ row.label }}</span>
                <strong>{{ row.count }}</strong>
              </div>
            </div>
            <div v-else class="analyze-note">No override-action data in this window.</div>
          </div>

          <div class="analyze-breakdown-card mb-3">
            <div class="analyze-label">Override effectiveness</div>
            <div v-if="topAttributeOverrideOutcomeRows.length" class="analyze-breakdown-list">
              <div
                v-for="row in topAttributeOverrideOutcomeRows"
                :key="`${row.key}-${row.label}`"
                class="analyze-breakdown-row analyze-breakdown-row--stacked"
              >
                <span>
                  {{ row.label }}
                  <small class="analyze-note">
                    used {{ row.usedCount }} • resolved {{ row.resolvedCount }} • escalated
                    {{ row.escalatedCount }}
                    <template v-if="resolveOverrideTopInboxLabel(row)">
                      • top inbox {{ resolveOverrideTopInboxLabel(row) }}
                    </template>
                  </small>
                </span>
                <div class="analyze-breakdown-actions">
                  <strong>
                    {{ formatPercent(row.resolvedRate) }} / {{ formatPercent(row.escalatedRate) }}
                  </strong>
                  <button type="button" class="btn o__secondary" @click="inspectOverrideRule(row)">
                    Inspect
                  </button>
                </div>
              </div>
            </div>
            <div v-else class="analyze-note">No override outcome data in this window.</div>
          </div>

          <div class="analyze-breakdown-card mb-3">
            <div class="analyze-label">High-escalation override rules</div>
            <div
              v-if="highEscalationAttributeOverrideRows.length"
              class="analyze-breakdown-list"
            >
              <div
                v-for="row in highEscalationAttributeOverrideRows"
                :key="`high-escalation-${row.key}`"
                class="analyze-breakdown-row analyze-breakdown-row--stacked"
              >
                <span>
                  {{ row.label }}
                  <small class="analyze-note">
                    {{ formatPercent(row.escalatedRate) }} escalated • {{ row.escalatedCount }}
                    of {{ row.usedCount }}
                    <template v-if="resolveOverrideTopInboxLabel(row)">
                      • top inbox {{ resolveOverrideTopInboxLabel(row) }}
                    </template>
                  </small>
                </span>
                <div class="analyze-breakdown-actions">
                  <strong>{{ row.escalatedCount }}</strong>
                  <button type="button" class="btn o__secondary" @click="inspectOverrideEscalations(row)">
                    Inspect
                  </button>
                </div>
              </div>
            </div>
            <div v-else class="analyze-note">No escalation-heavy override rules in this window.</div>
          </div>

          <div class="analyze-breakdown-card mb-3">
            <div class="analyze-label">Low-resolution override rules</div>
            <div
              v-if="lowResolutionAttributeOverrideRows.length"
              class="analyze-breakdown-list"
            >
              <div
                v-for="row in lowResolutionAttributeOverrideRows"
                :key="`low-resolution-${row.key}`"
                class="analyze-breakdown-row analyze-breakdown-row--stacked"
              >
                <span>
                  {{ row.label }}
                  <small class="analyze-note">
                    {{ formatPercent(row.resolvedRate) }} resolved • {{ row.resolvedCount }}
                    of {{ row.usedCount }}
                    <template v-if="resolveOverrideTopInboxLabel(row)">
                      • top inbox {{ resolveOverrideTopInboxLabel(row) }}
                    </template>
                  </small>
                </span>
                <div class="analyze-breakdown-actions">
                  <strong>{{ row.usedCount }}</strong>
                  <button type="button" class="btn o__secondary" @click="inspectOverridePending(row)">
                    Inspect
                  </button>
                </div>
              </div>
            </div>
            <div v-else class="analyze-note">No low-resolution override hotspots in this window.</div>
          </div>

          <div class="analyze-breakdown-card mb-3">
            <div class="analyze-label">Override hotspot inboxes</div>
            <div
              v-if="topAttributeOverrideInboxHotspots.length"
              class="analyze-breakdown-list"
            >
              <div
                v-for="row in topAttributeOverrideInboxHotspots"
                :key="`override-hotspot-${row.key}`"
                class="analyze-breakdown-row analyze-breakdown-row--stacked"
              >
                <span>
                  {{ row.label }} <small class="analyze-note">in {{ resolveInboxName(row.inboxId) || `Inbox ${row.inboxId}` }}</small>
                  <small class="analyze-note">
                    used {{ row.usedCount }} • resolved {{ row.resolvedCount }} • escalated
                    {{ row.escalatedCount }} • pending {{ row.unresolvedCount }}
                  </small>
                </span>
                <div class="analyze-breakdown-actions">
                  <strong>{{ formatPercent(row.escalatedRate) }}</strong>
                  <button type="button" class="btn o__secondary" @click="inspectOverrideHotspot(row)">
                    Inspect
                  </button>
                </div>
              </div>
            </div>
            <div v-else class="analyze-note">No override inbox hotspots in this window.</div>
          </div>

          <div class="analyze-breakdown-card mb-3">
            <div class="analyze-label">Matched override conditions</div>
            <div
              v-if="topAttributeOverrideConditionOutcomeRows.length"
              class="analyze-breakdown-list"
            >
              <div
                v-for="row in topAttributeOverrideConditionOutcomeRows"
                :key="`override-condition-${row.key}`"
                class="analyze-breakdown-row analyze-breakdown-row--stacked"
              >
                <span>
                  {{ row.label }}
                  <small
                    v-if="row.ruleLabel && !filters.attribute_override_rule_index"
                    class="analyze-note"
                  >
                    • {{ row.ruleLabel }}
                  </small>
                  <small class="analyze-note">
                    used {{ row.usedCount }} • resolved {{ row.resolvedCount }} •
                    escalated {{ row.escalatedCount }} • pending {{ row.unresolvedCount }}
                  </small>
                  <small
                    v-if="resolveOverrideBranchTopInboxLabel(row)"
                    class="analyze-note"
                  >
                    • top inbox {{ resolveOverrideBranchTopInboxLabel(row) }}
                    {{ row.topInboxEscalatedCount }}/{{ row.topInboxUsedCount }} escalated
                  </small>
                  <small
                    v-if="formatOverrideBranchLatestCaseSummary(row)"
                    class="analyze-note"
                  >
                    • latest {{ formatOverrideBranchLatestCaseSummary(row) }}
                  </small>
                  <small
                    v-if="formatOverrideBranchComparedCaseSummary(row, 'resolved')"
                    class="analyze-note"
                  >
                    • compare resolved
                    {{ formatOverrideBranchComparedCaseSummary(row, 'resolved') }}
                  </small>
                  <small
                    v-if="formatOverrideBranchComparedCaseSummary(row, 'escalated')"
                    class="analyze-note"
                  >
                    • compare escalated
                    {{ formatOverrideBranchComparedCaseSummary(row, 'escalated') }}
                  </small>
                  <small
                    v-if="overrideConditionRecommendationForRow(row)"
                    class="analyze-note"
                  >
                    Recommendation:
                    {{
                      overrideConditionRecommendationForRow(row).title
                    }}
                    •
                    {{
                      overrideConditionRecommendationForRow(row).suggestedChange ||
                        overrideConditionRecommendationForRow(row).body
                    }}
                  </small>
                  <small
                    v-if="overrideConditionRecommendationForRow(row)?.evidenceSummary"
                    class="analyze-note"
                  >
                    Why:
                    {{ overrideConditionRecommendationForRow(row).evidenceSummary }}
                  </small>
                  <small
                    v-if="overrideConditionRecommendationForRow(row)?.fixIntentSummary"
                    class="analyze-note"
                  >
                    Fix intent:
                    {{ overrideConditionRecommendationForRow(row).fixIntentSummary }}
                  </small>
                  <small
                    v-if="overrideConditionRecommendationForRow(row)?.fixPresetTitle"
                    class="analyze-note"
                  >
                    Preset:
                    {{ overrideConditionRecommendationForRow(row).fixPresetTitle }}
                  </small>
                  <small
                    v-if="overrideConditionRecommendationForRow(row)?.fixPresetBody"
                    class="analyze-note"
                  >
                    Pattern:
                    {{ overrideConditionRecommendationForRow(row).fixPresetBody }}
                  </small>
                  <small
                    v-if="overrideConditionRecommendationForRow(row)?.compareFocusTitle"
                    class="analyze-note"
                  >
                    Diagnosis:
                    {{ overrideConditionRecommendationForRow(row).compareFocusTitle }}
                  </small>
                  <small
                    v-if="overrideConditionRecommendationForRow(row)?.compareSummary"
                    class="analyze-note"
                  >
                    Compare:
                    {{ overrideConditionRecommendationForRow(row).compareSummary }}
                  </small>
                  <small
                    v-if="overrideConditionRecommendationForRow(row)?.compareFocus"
                    class="analyze-note"
                  >
                    Focus:
                    {{ overrideConditionRecommendationForRow(row).compareFocus }}
                  </small>
                  <small
                    v-if="overrideConditionRecommendationForRow(row)?.compareNextStep"
                    class="analyze-note"
                  >
                    Compare next:
                    {{ overrideConditionRecommendationForRow(row).compareNextStep }}
                  </small>
                </span>
                <div class="analyze-breakdown-actions">
                  <strong>{{ formatPercent(row.escalatedRate) }}</strong>
                  <button
                    type="button"
                    class="btn o__secondary"
                    @click="inspectOverrideCondition(row)"
                  >
                    Inspect
                  </button>
                  <button
                    v-if="row.topInboxId"
                    type="button"
                    class="btn o__secondary"
                    @click="inspectOverrideConditionTopInbox(row)"
                  >
                    Top inbox
                  </button>
                  <button
                    v-if="row.latestConversationId"
                    type="button"
                    class="btn o__secondary"
                    @click="inspectOverrideConditionLatestCase(row)"
                  >
                    Latest case
                  </button>
                  <button
                    v-if="row.latestResolvedConversationId"
                    type="button"
                    class="btn o__secondary"
                    @click="inspectOverrideConditionResolvedCase(row)"
                  >
                    Resolved case
                  </button>
                  <button
                    v-if="row.latestEscalatedConversationId"
                    type="button"
                    class="btn o__secondary"
                    @click="inspectOverrideConditionEscalatedCase(row)"
                  >
                    Escalated case
                  </button>
                  <button
                    v-if="overrideConditionRecommendationForRow(row)?.compareSummary"
                    type="button"
                    class="btn o__secondary"
                    @click="
                      inspectOverrideBranchCompare(
                        overrideConditionRecommendationForRow(row)
                      )
                    "
                  >
                    Compare
                  </button>
                  <button
                    v-if="overrideConditionRecommendationForRow(row)"
                    type="button"
                    class="btn o__secondary"
                    @click="
                      inspectOverrideBranchRecommendation(
                        overrideConditionRecommendationForRow(row)
                      )
                    "
                  >
                    Fix
                  </button>
                </div>
              </div>
            </div>
            <div v-else class="analyze-note">No matched override condition data in this slice.</div>
          </div>

          <div class="analyze-breakdown-card mb-3">
            <div class="analyze-label">Passed override guards</div>
            <div
              v-if="topAttributeOverrideGuardOutcomeRows.length"
              class="analyze-breakdown-list"
            >
              <div
                v-for="row in topAttributeOverrideGuardOutcomeRows"
                :key="`override-guard-${row.key}`"
                class="analyze-breakdown-row analyze-breakdown-row--stacked"
              >
                <span>
                  {{ row.label }}
                  <small
                    v-if="row.ruleLabel && !filters.attribute_override_rule_index"
                    class="analyze-note"
                  >
                    • {{ row.ruleLabel }}
                  </small>
                  <small class="analyze-note">
                    used {{ row.usedCount }} • resolved {{ row.resolvedCount }} •
                    escalated {{ row.escalatedCount }} • pending {{ row.unresolvedCount }}
                  </small>
                  <small
                    v-if="resolveOverrideBranchTopInboxLabel(row)"
                    class="analyze-note"
                  >
                    • top inbox {{ resolveOverrideBranchTopInboxLabel(row) }}
                    {{ row.topInboxEscalatedCount }}/{{ row.topInboxUsedCount }} escalated
                  </small>
                  <small
                    v-if="formatOverrideBranchLatestCaseSummary(row)"
                    class="analyze-note"
                  >
                    • latest {{ formatOverrideBranchLatestCaseSummary(row) }}
                  </small>
                  <small
                    v-if="formatOverrideBranchComparedCaseSummary(row, 'resolved')"
                    class="analyze-note"
                  >
                    • compare resolved
                    {{ formatOverrideBranchComparedCaseSummary(row, 'resolved') }}
                  </small>
                  <small
                    v-if="formatOverrideBranchComparedCaseSummary(row, 'escalated')"
                    class="analyze-note"
                  >
                    • compare escalated
                    {{ formatOverrideBranchComparedCaseSummary(row, 'escalated') }}
                  </small>
                  <small
                    v-if="overrideGuardRecommendationForRow(row)"
                    class="analyze-note"
                  >
                    Recommendation:
                    {{
                      overrideGuardRecommendationForRow(row).title
                    }}
                    •
                    {{
                      overrideGuardRecommendationForRow(row).suggestedChange ||
                        overrideGuardRecommendationForRow(row).body
                    }}
                  </small>
                  <small
                    v-if="overrideGuardRecommendationForRow(row)?.evidenceSummary"
                    class="analyze-note"
                  >
                    Why:
                    {{ overrideGuardRecommendationForRow(row).evidenceSummary }}
                  </small>
                  <small
                    v-if="overrideGuardRecommendationForRow(row)?.fixIntentSummary"
                    class="analyze-note"
                  >
                    Fix intent:
                    {{ overrideGuardRecommendationForRow(row).fixIntentSummary }}
                  </small>
                  <small
                    v-if="overrideGuardRecommendationForRow(row)?.fixPresetTitle"
                    class="analyze-note"
                  >
                    Preset:
                    {{ overrideGuardRecommendationForRow(row).fixPresetTitle }}
                  </small>
                  <small
                    v-if="overrideGuardRecommendationForRow(row)?.fixPresetBody"
                    class="analyze-note"
                  >
                    Pattern:
                    {{ overrideGuardRecommendationForRow(row).fixPresetBody }}
                  </small>
                  <small
                    v-if="overrideGuardRecommendationForRow(row)?.compareFocusTitle"
                    class="analyze-note"
                  >
                    Diagnosis:
                    {{ overrideGuardRecommendationForRow(row).compareFocusTitle }}
                  </small>
                  <small
                    v-if="overrideGuardRecommendationForRow(row)?.compareSummary"
                    class="analyze-note"
                  >
                    Compare:
                    {{ overrideGuardRecommendationForRow(row).compareSummary }}
                  </small>
                  <small
                    v-if="overrideGuardRecommendationForRow(row)?.compareFocus"
                    class="analyze-note"
                  >
                    Focus:
                    {{ overrideGuardRecommendationForRow(row).compareFocus }}
                  </small>
                  <small
                    v-if="overrideGuardRecommendationForRow(row)?.compareNextStep"
                    class="analyze-note"
                  >
                    Compare next:
                    {{ overrideGuardRecommendationForRow(row).compareNextStep }}
                  </small>
                </span>
                <div class="analyze-breakdown-actions">
                  <strong>{{ formatPercent(row.escalatedRate) }}</strong>
                  <button
                    type="button"
                    class="btn o__secondary"
                    @click="inspectOverrideGuard(row)"
                  >
                    Inspect
                  </button>
                  <button
                    v-if="row.topInboxId"
                    type="button"
                    class="btn o__secondary"
                    @click="inspectOverrideGuardTopInbox(row)"
                  >
                    Top inbox
                  </button>
                  <button
                    v-if="row.latestConversationId"
                    type="button"
                    class="btn o__secondary"
                    @click="inspectOverrideGuardLatestCase(row)"
                  >
                    Latest case
                  </button>
                  <button
                    v-if="row.latestResolvedConversationId"
                    type="button"
                    class="btn o__secondary"
                    @click="inspectOverrideGuardResolvedCase(row)"
                  >
                    Resolved case
                  </button>
                  <button
                    v-if="row.latestEscalatedConversationId"
                    type="button"
                    class="btn o__secondary"
                    @click="inspectOverrideGuardEscalatedCase(row)"
                  >
                    Escalated case
                  </button>
                  <button
                    v-if="overrideGuardRecommendationForRow(row)?.compareSummary"
                    type="button"
                    class="btn o__secondary"
                    @click="
                      inspectOverrideBranchCompare(
                        overrideGuardRecommendationForRow(row)
                      )
                    "
                  >
                    Compare
                  </button>
                  <button
                    v-if="overrideGuardRecommendationForRow(row)"
                    type="button"
                    class="btn o__secondary"
                    @click="
                      inspectOverrideBranchRecommendation(
                        overrideGuardRecommendationForRow(row)
                      )
                    "
                  >
                    Fix
                  </button>
                </div>
              </div>
            </div>
            <div v-else class="analyze-note">No passed override guard data in this slice.</div>
          </div>

          <div class="analyze-breakdown-card mb-3">
            <div class="analyze-label">Recent override cases</div>
            <div
              v-if="topAttributeOverrideRecentCases.length"
              class="analyze-breakdown-list"
            >
              <div
                v-for="row in topAttributeOverrideRecentCases"
                :key="`override-case-${row.key}`"
                class="analyze-breakdown-row analyze-breakdown-row--stacked"
              >
                <span>
                  {{ row.label }}
                  <small class="analyze-note">
                    {{ resolveInboxName(row.inboxId) || `Inbox ${row.inboxId}` }} •
                    conversation {{ row.conversationId }} • {{ row.outcome }}
                  </small>
                  <small class="analyze-note">
                    {{ row.conditionSummary || row.action || row.matchType || 'Recent override case' }}
                  </small>
                  <small class="analyze-note">
                    {{ formatOverrideOutcomeDetail(row) }}
                  </small>
                </span>
                <div class="analyze-breakdown-actions">
                  <strong>{{ formatDateTime(row.occurredAt) }}</strong>
                  <button type="button" class="btn o__secondary" @click="inspectOverrideCase(row)">
                    Inspect
                  </button>
                </div>
              </div>
            </div>
            <div v-else class="analyze-note">No recent override cases in this window.</div>
          </div>

          <div class="analyze-breakdown-card mb-3">
            <div class="analyze-label">Override optimization suggestions</div>
            <div
              v-if="filteredAttributeOverrideRecommendationRows.length"
              class="analyze-breakdown-list"
            >
              <div
                v-for="row in filteredAttributeOverrideRecommendationRows"
                :key="`override-recommendation-${row.key}`"
                class="analyze-breakdown-row analyze-breakdown-row--stacked"
              >
                <span>
                  {{ row.label }}
                  <small class="analyze-note">
                    {{ row.title }} • used {{ row.usedCount }} • escalated
                    {{ row.escalatedCount }} • pending {{ row.unresolvedCount }}
                    <template v-if="resolveOverrideTopInboxLabel(row)">
                      • top inbox {{ resolveOverrideTopInboxLabel(row) }}
                    </template>
                  </small>
                  <small class="analyze-note">
                    {{ row.evidenceSummary || row.body }}
                  </small>
                  <small
                    v-if="overrideHotspotRowsForRule(row.ruleIndex).length"
                    class="analyze-note"
                  >
                    Hotspots:
                    {{
                      overrideHotspotRowsForRule(row.ruleIndex)
                        .map(formatOverrideHotspotSummary)
                        .join(' • ')
                    }}
                  </small>
                  <small
                    v-if="overrideRecentCasesForRule(row.ruleIndex).length"
                    class="analyze-note"
                  >
                    Recent cases:
                    {{
                      overrideRecentCasesForRule(row.ruleIndex)
                        .map(formatOverrideCaseSummary)
                        .join(' • ')
                    }}
                  </small>
                  <small class="analyze-note">
                    Change: {{ row.suggestedChange || row.body }}
                  </small>
                  <small class="analyze-note">
                    Next: {{ row.nextStep || 'Review recent matches for this rule.' }}
                  </small>
                </span>
                <div class="analyze-breakdown-actions">
                  <strong>{{ row.severity }}</strong>
                  <button type="button" class="btn o__secondary" @click="inspectOverrideRecommendation(row)">
                    Inspect
                  </button>
                </div>
              </div>
            </div>
            <div v-else class="analyze-note">No override optimization suggestions in this window.</div>
          </div>

          <div class="analyze-breakdown-card mb-3">
            <div class="analyze-label">Forced reply language</div>
            <div v-if="forcedReplyLanguageBreakdown.length" class="analyze-breakdown-list">
              <div v-for="row in forcedReplyLanguageBreakdown" :key="row.label" class="analyze-breakdown-row">
                <span>{{ row.label }}</span>
                <strong>{{ row.count }}</strong>
              </div>
            </div>
            <div v-else class="analyze-note">No forced-language hotspot data.</div>
          </div>

          <div class="analyze-breakdown-card mb-3">
            <div class="analyze-label">Communication styles</div>
            <div v-if="communicationStyleBreakdown.length" class="analyze-breakdown-list">
              <div v-for="row in communicationStyleBreakdown" :key="row.label" class="analyze-breakdown-row">
                <span>{{ row.label }}</span>
                <strong>{{ row.count }}</strong>
              </div>
            </div>
            <div v-else class="analyze-note">No communication-style hotspot data.</div>
          </div>

          <div class="analyze-breakdown-card mb-3">
            <div class="analyze-label">Deploy default modes</div>
            <div
              v-if="channelRuntimeDefaultReplyModeBreakdown.length"
              class="analyze-breakdown-list"
            >
              <div
                v-for="row in channelRuntimeDefaultReplyModeBreakdown"
                :key="row.label"
                class="analyze-breakdown-row"
              >
                <span>{{ row.label }}</span>
                <strong>{{ row.count }}</strong>
              </div>
            </div>
            <div v-else class="analyze-note">No deploy-default hotspot data.</div>
          </div>

          <div class="analyze-breakdown-card mb-3">
            <div class="analyze-label">Deploy binding scopes</div>
            <div
              v-if="channelRuntimeBindingScopeBreakdown.length"
              class="analyze-breakdown-list"
            >
              <div
                v-for="row in channelRuntimeBindingScopeBreakdown"
                :key="row.label"
                class="analyze-breakdown-row"
              >
                <span>{{ row.label }}</span>
                <strong>{{ row.count }}</strong>
              </div>
            </div>
            <div v-else class="analyze-note">No deploy binding-scope hotspot data.</div>
          </div>

          <div class="analyze-breakdown-card mb-3">
            <div class="analyze-label">Deploy segment rules</div>
            <div
              v-if="channelRuntimeActiveSegmentTypeBreakdown.length"
              class="analyze-breakdown-list"
            >
              <div
                v-for="row in channelRuntimeActiveSegmentTypeBreakdown"
                :key="row.label"
                class="analyze-breakdown-row"
              >
                <span>{{ row.label }}</span>
                <strong>{{ row.count }}</strong>
              </div>
            </div>
            <div v-else class="analyze-note">No deploy segment hotspot data.</div>
          </div>

          <div class="analyze-breakdown-card mb-3">
            <div class="analyze-label">Business-hours active</div>
            <div v-if="businessHoursActiveBreakdown.length" class="analyze-breakdown-list">
              <div v-for="row in businessHoursActiveBreakdown" :key="row.label" class="analyze-breakdown-row">
                <span>{{ row.label }}</span>
                <strong>{{ row.count }}</strong>
              </div>
            </div>
            <div v-else class="analyze-note">No business-hours activation data.</div>
          </div>

          <div class="analyze-breakdown-card mb-3">
            <div class="analyze-label">Business-hours reply modes</div>
            <div v-if="businessHoursReplyModeBreakdown.length" class="analyze-breakdown-list">
              <div v-for="row in businessHoursReplyModeBreakdown" :key="row.label" class="analyze-breakdown-row">
                <span>{{ row.label }}</span>
                <strong>{{ row.count }}</strong>
              </div>
            </div>
            <div v-else class="analyze-note">No business-hours mode hotspot data.</div>
          </div>

          <div class="analyze-breakdown-card mb-3">
            <div class="analyze-label">Business-hours behaviors</div>
            <div
              v-if="businessHoursResponseBehaviorBreakdown.length"
              class="analyze-breakdown-list"
            >
              <div
                v-for="row in businessHoursResponseBehaviorBreakdown"
                :key="row.label"
                class="analyze-breakdown-row"
              >
                <span>{{ row.label }}</span>
                <strong>{{ row.count }}</strong>
              </div>
            </div>
            <div v-else class="analyze-note">No business-hours behavior hotspot data.</div>
          </div>

          <div class="analyze-breakdown-card mb-3">
            <div class="analyze-label">Region binding scopes</div>
            <div
              v-if="regionComplianceBindingScopeBreakdown.length"
              class="analyze-breakdown-list"
            >
              <div
                v-for="row in regionComplianceBindingScopeBreakdown"
                :key="row.label"
                class="analyze-breakdown-row"
              >
                <span>{{ row.label }}</span>
                <strong>{{ row.count }}</strong>
              </div>
            </div>
            <div v-else class="analyze-note">No region binding-scope hotspot data.</div>
          </div>

          <div class="analyze-breakdown-card mb-3">
            <div class="analyze-label">Active markets</div>
            <div
              v-if="regionComplianceActiveCountryCodeBreakdown.length"
              class="analyze-breakdown-list"
            >
              <div
                v-for="row in regionComplianceActiveCountryCodeBreakdown"
                :key="row.label"
                class="analyze-breakdown-row"
              >
                <span>{{ row.label }}</span>
                <strong>{{ row.count }}</strong>
              </div>
            </div>
            <div v-else class="analyze-note">No active-market hotspot data.</div>
          </div>

          <div class="analyze-breakdown-card mb-3">
            <div class="analyze-label">Compliance profiles</div>
            <div
              v-if="regionComplianceActiveProfileBreakdown.length"
              class="analyze-breakdown-list"
            >
              <div
                v-for="row in regionComplianceActiveProfileBreakdown"
                :key="row.label"
                class="analyze-breakdown-row"
              >
                <span>{{ row.label }}</span>
                <strong>{{ row.count }}</strong>
              </div>
            </div>
            <div v-else class="analyze-note">No compliance-profile hotspot data.</div>
          </div>

          <div class="analyze-breakdown-card mb-3">
            <div class="analyze-label">Answer length strategies</div>
            <div v-if="answerLengthStrategyBreakdown.length" class="analyze-breakdown-list">
              <div v-for="row in answerLengthStrategyBreakdown" :key="row.label" class="analyze-breakdown-row">
                <span>{{ row.label }}</span>
                <strong>{{ row.count }}</strong>
              </div>
            </div>
            <div v-else class="analyze-note">No answer-length hotspot data.</div>
          </div>

          <div class="analyze-breakdown-card mb-3">
            <div class="analyze-label">Directness strategies</div>
            <div v-if="directnessStrategyBreakdown.length" class="analyze-breakdown-list">
              <div v-for="row in directnessStrategyBreakdown" :key="row.label" class="analyze-breakdown-row">
                <span>{{ row.label }}</span>
                <strong>{{ row.count }}</strong>
              </div>
            </div>
            <div v-else class="analyze-note">No directness hotspot data.</div>
          </div>

          <div class="analyze-breakdown-card mb-3">
            <div class="analyze-label">Sensitive-topic strategies</div>
            <div v-if="sensitiveTopicsStrategyBreakdown.length" class="analyze-breakdown-list">
              <div v-for="row in sensitiveTopicsStrategyBreakdown" :key="row.label" class="analyze-breakdown-row">
                <span>{{ row.label }}</span>
                <strong>{{ row.count }}</strong>
              </div>
            </div>
            <div v-else class="analyze-note">No sensitive-topic hotspot data.</div>
          </div>

          <div class="analyze-breakdown-card mb-3">
            <div class="analyze-label">Tone-enforcement strategies</div>
            <div v-if="toneEnforcementStrategyBreakdown.length" class="analyze-breakdown-list">
              <div v-for="row in toneEnforcementStrategyBreakdown" :key="row.label" class="analyze-breakdown-row">
                <span>{{ row.label }}</span>
                <strong>{{ row.count }}</strong>
              </div>
            </div>
            <div v-else class="analyze-note">No tone-enforcement hotspot data.</div>
          </div>

          <div class="analyze-breakdown-card mb-3">
            <div class="analyze-label">Memory-depth strategies</div>
            <div v-if="memoryDepthStrategyBreakdown.length" class="analyze-breakdown-list">
              <div v-for="row in memoryDepthStrategyBreakdown" :key="row.label" class="analyze-breakdown-row">
                <span>{{ row.label }}</span>
                <strong>{{ row.count }}</strong>
              </div>
            </div>
            <div v-else class="analyze-note">No memory-depth hotspot data.</div>
          </div>

          <div class="analyze-breakdown-card mb-3">
            <div class="analyze-label">Answer-confidence strategies</div>
            <div v-if="answerConfidenceStrategyBreakdown.length" class="analyze-breakdown-list">
              <div v-for="row in answerConfidenceStrategyBreakdown" :key="row.label" class="analyze-breakdown-row">
                <span>{{ row.label }}</span>
                <strong>{{ row.count }}</strong>
              </div>
            </div>
            <div v-else class="analyze-note">No answer-confidence hotspot data.</div>
          </div>

          <div class="analyze-breakdown-card mb-3">
            <div class="analyze-label">Outside-knowledge rules</div>
            <div v-if="outsideKnowledgeStrategyBreakdown.length" class="analyze-breakdown-list">
              <div v-for="row in outsideKnowledgeStrategyBreakdown" :key="row.label" class="analyze-breakdown-row">
                <span>{{ row.label }}</span>
                <strong>{{ row.count }}</strong>
              </div>
            </div>
            <div v-else class="analyze-note">No outside-knowledge hotspot data.</div>
          </div>

          <div class="analyze-breakdown-card mb-3">
            <div class="analyze-label">Promise-safety rules</div>
            <div v-if="promiseSafetyStrategyBreakdown.length" class="analyze-breakdown-list">
              <div v-for="row in promiseSafetyStrategyBreakdown" :key="row.label" class="analyze-breakdown-row">
                <span>{{ row.label }}</span>
                <strong>{{ row.count }}</strong>
              </div>
            </div>
            <div v-else class="analyze-note">No promise-safety hotspot data.</div>
          </div>

          <div class="analyze-breakdown-card mb-3">
            <div class="analyze-label">Uncertain routes</div>
            <div v-if="uncertainActionBreakdown.length" class="analyze-breakdown-list">
              <div v-for="row in uncertainActionBreakdown" :key="row.label" class="analyze-breakdown-row">
                <span>{{ row.label }}</span>
                <strong>{{ row.count }}</strong>
              </div>
            </div>
            <div v-else class="analyze-note">No uncertain-route hotspot data.</div>
          </div>

          <div class="analyze-breakdown-card mb-3">
            <div class="analyze-label">High-risk defaults</div>
            <div v-if="highRiskActionBreakdown.length" class="analyze-breakdown-list">
              <div v-for="row in highRiskActionBreakdown" :key="row.label" class="analyze-breakdown-row">
                <span>{{ row.label }}</span>
                <strong>{{ row.count }}</strong>
              </div>
            </div>
            <div v-else class="analyze-note">No high-risk default hotspot data.</div>
          </div>

          <div class="analyze-breakdown-card mb-3">
            <div class="analyze-label">Procedure precedence</div>
            <div
              v-if="procedureRoutingPrecedenceBreakdown.length"
              class="analyze-breakdown-list"
            >
              <div
                v-for="row in procedureRoutingPrecedenceBreakdown"
                :key="row.label"
                class="analyze-breakdown-row"
              >
                <span>{{ row.label }}</span>
                <strong>{{ row.count }}</strong>
              </div>
            </div>
            <div v-else class="analyze-note">No procedure precedence hotspot data.</div>
          </div>

          <div class="analyze-breakdown-card mb-3">
            <div class="analyze-label">Step confirmation</div>
            <div
              v-if="procedureStepConfirmationBreakdown.length"
              class="analyze-breakdown-list"
            >
              <div
                v-for="row in procedureStepConfirmationBreakdown"
                :key="row.label"
                class="analyze-breakdown-row"
              >
                <span>{{ row.label }}</span>
                <strong>{{ row.count }}</strong>
              </div>
            </div>
            <div v-else class="analyze-note">No step-confirmation hotspot data.</div>
          </div>

          <div class="analyze-breakdown-card mb-3">
            <div class="analyze-label">Procedure fallback</div>
            <div
              v-if="procedureFallbackActionBreakdown.length"
              class="analyze-breakdown-list"
            >
              <div
                v-for="row in procedureFallbackActionBreakdown"
                :key="row.label"
                class="analyze-breakdown-row"
              >
                <span>{{ row.label }}</span>
                <strong>{{ row.count }}</strong>
              </div>
            </div>
            <div v-else class="analyze-note">No procedure-fallback hotspot data.</div>
          </div>

          <div class="analyze-breakdown-card mb-3">
            <div class="analyze-label">Answer length outcomes</div>
            <div v-if="topAnswerLengthOutcomeRows.length" class="analyze-breakdown-list">
              <div v-for="row in topAnswerLengthOutcomeRows" :key="row.label" class="analyze-breakdown-row">
                <span>{{ row.label }} <small class="analyze-note">used {{ row.usedCount }} • {{ formatPercent(row.resolvedRate) }} resolved • {{ formatPercent(row.escalatedRate) }} escalated</small></span>
                <strong>{{ row.unresolvedCount }}</strong>
              </div>
            </div>
            <div v-else class="analyze-note">No answer-length outcome data.</div>
          </div>

          <div class="analyze-breakdown-card mb-3">
            <div class="analyze-label">Directness outcomes</div>
            <div v-if="topDirectnessOutcomeRows.length" class="analyze-breakdown-list">
              <div v-for="row in topDirectnessOutcomeRows" :key="row.label" class="analyze-breakdown-row">
                <span>{{ row.label }} <small class="analyze-note">used {{ row.usedCount }} • {{ formatPercent(row.resolvedRate) }} resolved • {{ formatPercent(row.escalatedRate) }} escalated</small></span>
                <strong>{{ row.unresolvedCount }}</strong>
              </div>
            </div>
            <div v-else class="analyze-note">No directness outcome data.</div>
          </div>

          <div class="analyze-breakdown-card mb-3">
            <div class="analyze-label">Sensitive-topic outcomes</div>
            <div v-if="topSensitiveTopicsOutcomeRows.length" class="analyze-breakdown-list">
              <div v-for="row in topSensitiveTopicsOutcomeRows" :key="row.label" class="analyze-breakdown-row">
                <span>{{ row.label }} <small class="analyze-note">used {{ row.usedCount }} • {{ formatPercent(row.resolvedRate) }} resolved • {{ formatPercent(row.escalatedRate) }} escalated</small></span>
                <strong>{{ row.unresolvedCount }}</strong>
              </div>
            </div>
            <div v-else class="analyze-note">No sensitive-topic outcome data.</div>
          </div>

          <div class="analyze-breakdown-card mb-3">
            <div class="analyze-label">Tone-enforcement outcomes</div>
            <div v-if="topToneEnforcementOutcomeRows.length" class="analyze-breakdown-list">
              <div v-for="row in topToneEnforcementOutcomeRows" :key="row.label" class="analyze-breakdown-row">
                <span>{{ row.label }} <small class="analyze-note">used {{ row.usedCount }} • {{ formatPercent(row.resolvedRate) }} resolved • {{ formatPercent(row.escalatedRate) }} escalated</small></span>
                <strong>{{ row.unresolvedCount }}</strong>
              </div>
            </div>
            <div v-else class="analyze-note">No tone-enforcement outcome data.</div>
          </div>

          <div class="analyze-breakdown-card mb-3">
            <div class="analyze-label">Memory-depth outcomes</div>
            <div v-if="topMemoryDepthOutcomeRows.length" class="analyze-breakdown-list">
              <div v-for="row in topMemoryDepthOutcomeRows" :key="row.label" class="analyze-breakdown-row">
                <span>{{ row.label }} <small class="analyze-note">used {{ row.usedCount }} • {{ formatPercent(row.resolvedRate) }} resolved • {{ formatPercent(row.escalatedRate) }} escalated</small></span>
                <strong>{{ row.unresolvedCount }}</strong>
              </div>
            </div>
            <div v-else class="analyze-note">No memory-depth outcome data.</div>
          </div>

          <div class="analyze-breakdown-card mb-3">
            <div class="analyze-label">Answer-confidence outcomes</div>
            <div v-if="topAnswerConfidenceOutcomeRows.length" class="analyze-breakdown-list">
              <div v-for="row in topAnswerConfidenceOutcomeRows" :key="row.label" class="analyze-breakdown-row">
                <span>{{ row.label }} <small class="analyze-note">used {{ row.usedCount }} • {{ formatPercent(row.resolvedRate) }} resolved • {{ formatPercent(row.escalatedRate) }} escalated</small></span>
                <strong>{{ row.unresolvedCount }}</strong>
              </div>
            </div>
            <div v-else class="analyze-note">No answer-confidence outcome data.</div>
          </div>

          <div class="analyze-breakdown-card mb-3">
            <div class="analyze-label">Outside-knowledge outcomes</div>
            <div v-if="topOutsideKnowledgeOutcomeRows.length" class="analyze-breakdown-list">
              <div v-for="row in topOutsideKnowledgeOutcomeRows" :key="row.label" class="analyze-breakdown-row">
                <span>{{ row.label }} <small class="analyze-note">used {{ row.usedCount }} • {{ formatPercent(row.resolvedRate) }} resolved • {{ formatPercent(row.escalatedRate) }} escalated</small></span>
                <strong>{{ row.unresolvedCount }}</strong>
              </div>
            </div>
            <div v-else class="analyze-note">No outside-knowledge outcome data.</div>
          </div>

          <div class="analyze-breakdown-card mb-3">
            <div class="analyze-label">Promise-safety outcomes</div>
            <div v-if="topPromiseSafetyOutcomeRows.length" class="analyze-breakdown-list">
              <div v-for="row in topPromiseSafetyOutcomeRows" :key="row.label" class="analyze-breakdown-row">
                <span>{{ row.label }} <small class="analyze-note">used {{ row.usedCount }} • {{ formatPercent(row.resolvedRate) }} resolved • {{ formatPercent(row.escalatedRate) }} escalated</small></span>
                <strong>{{ row.unresolvedCount }}</strong>
              </div>
            </div>
            <div v-else class="analyze-note">No promise-safety outcome data.</div>
          </div>

          <div class="analyze-breakdown-card mb-3">
            <div class="analyze-label">Uncertain route outcomes</div>
            <div v-if="topUncertainActionOutcomeRows.length" class="analyze-breakdown-list">
              <div v-for="row in topUncertainActionOutcomeRows" :key="row.label" class="analyze-breakdown-row">
                <span>{{ row.label }} <small class="analyze-note">used {{ row.usedCount }} • {{ formatPercent(row.resolvedRate) }} resolved • {{ formatPercent(row.escalatedRate) }} escalated</small></span>
                <strong>{{ row.unresolvedCount }}</strong>
              </div>
            </div>
            <div v-else class="analyze-note">No uncertain-route outcome data.</div>
          </div>

          <div class="analyze-breakdown-card mb-3">
            <div class="analyze-label">High-risk default outcomes</div>
            <div v-if="topHighRiskActionOutcomeRows.length" class="analyze-breakdown-list">
              <div v-for="row in topHighRiskActionOutcomeRows" :key="row.label" class="analyze-breakdown-row">
                <span>{{ row.label }} <small class="analyze-note">used {{ row.usedCount }} • {{ formatPercent(row.resolvedRate) }} resolved • {{ formatPercent(row.escalatedRate) }} escalated</small></span>
                <strong>{{ row.unresolvedCount }}</strong>
              </div>
            </div>
            <div v-else class="analyze-note">No high-risk default outcome data.</div>
          </div>

          <div class="analyze-breakdown-card mb-3">
            <div class="analyze-label">Procedure precedence outcomes</div>
            <div
              v-if="topProcedureRoutingPrecedenceOutcomeRows.length"
              class="analyze-breakdown-list"
            >
              <div
                v-for="row in topProcedureRoutingPrecedenceOutcomeRows"
                :key="row.label"
                class="analyze-breakdown-row"
              >
                <span>{{ row.label }} <small class="analyze-note">used {{ row.usedCount }} • {{ formatPercent(row.resolvedRate) }} resolved • {{ formatPercent(row.escalatedRate) }} escalated</small></span>
                <strong>{{ row.unresolvedCount }}</strong>
              </div>
            </div>
            <div v-else class="analyze-note">No procedure precedence outcome data.</div>
          </div>

          <div class="analyze-breakdown-card mb-3">
            <div class="analyze-label">Step confirmation outcomes</div>
            <div
              v-if="topProcedureStepConfirmationOutcomeRows.length"
              class="analyze-breakdown-list"
            >
              <div
                v-for="row in topProcedureStepConfirmationOutcomeRows"
                :key="row.label"
                class="analyze-breakdown-row"
              >
                <span>{{ row.label }} <small class="analyze-note">used {{ row.usedCount }} • {{ formatPercent(row.resolvedRate) }} resolved • {{ formatPercent(row.escalatedRate) }} escalated</small></span>
                <strong>{{ row.unresolvedCount }}</strong>
              </div>
            </div>
            <div v-else class="analyze-note">No step-confirmation outcome data.</div>
          </div>

          <div class="analyze-breakdown-card mb-3">
            <div class="analyze-label">Procedure fallback outcomes</div>
            <div
              v-if="topProcedureFallbackActionOutcomeRows.length"
              class="analyze-breakdown-list"
            >
              <div
                v-for="row in topProcedureFallbackActionOutcomeRows"
                :key="row.label"
                class="analyze-breakdown-row"
              >
                <span>{{ row.label }} <small class="analyze-note">used {{ row.usedCount }} • {{ formatPercent(row.resolvedRate) }} resolved • {{ formatPercent(row.escalatedRate) }} escalated</small></span>
                <strong>{{ row.unresolvedCount }}</strong>
              </div>
            </div>
            <div v-else class="analyze-note">No procedure-fallback outcome data.</div>
          </div>

          <div class="analyze-breakdown-card mb-3">
            <div class="analyze-label">Deploy default mode outcomes</div>
            <div
              v-if="topChannelRuntimeDefaultReplyModeOutcomeRows.length"
              class="analyze-breakdown-list"
            >
              <div
                v-for="row in topChannelRuntimeDefaultReplyModeOutcomeRows"
                :key="row.label"
                class="analyze-breakdown-row"
              >
                <span>{{ row.label }} <small class="analyze-note">used {{ row.usedCount }} • {{ formatPercent(row.resolvedRate) }} resolved • {{ formatPercent(row.escalatedRate) }} escalated</small></span>
                <strong>{{ row.unresolvedCount }}</strong>
              </div>
            </div>
            <div v-else class="analyze-note">No deploy-default outcome data.</div>
          </div>

          <div class="analyze-breakdown-card mb-3">
            <div class="analyze-label">Deploy binding-scope outcomes</div>
            <div
              v-if="topChannelRuntimeBindingScopeOutcomeRows.length"
              class="analyze-breakdown-list"
            >
              <div
                v-for="row in topChannelRuntimeBindingScopeOutcomeRows"
                :key="row.label"
                class="analyze-breakdown-row"
              >
                <span>{{ row.label }} <small class="analyze-note">used {{ row.usedCount }} • {{ formatPercent(row.resolvedRate) }} resolved • {{ formatPercent(row.escalatedRate) }} escalated</small></span>
                <strong>{{ row.unresolvedCount }}</strong>
              </div>
            </div>
            <div v-else class="analyze-note">No deploy binding-scope outcome data.</div>
          </div>

          <div class="analyze-breakdown-card mb-3">
            <div class="analyze-label">Deploy segment-rule outcomes</div>
            <div
              v-if="topChannelRuntimeActiveSegmentTypeOutcomeRows.length"
              class="analyze-breakdown-list"
            >
              <div
                v-for="row in topChannelRuntimeActiveSegmentTypeOutcomeRows"
                :key="row.label"
                class="analyze-breakdown-row"
              >
                <span>{{ row.label }} <small class="analyze-note">used {{ row.usedCount }} • {{ formatPercent(row.resolvedRate) }} resolved • {{ formatPercent(row.escalatedRate) }} escalated</small></span>
                <strong>{{ row.unresolvedCount }}</strong>
              </div>
            </div>
            <div v-else class="analyze-note">No deploy segment-rule outcome data.</div>
          </div>

          <div class="analyze-breakdown-card mb-3">
            <div class="analyze-label">Business-hours mode outcomes</div>
            <div
              v-if="topBusinessHoursReplyModeOutcomeRows.length"
              class="analyze-breakdown-list"
            >
              <div
                v-for="row in topBusinessHoursReplyModeOutcomeRows"
                :key="row.label"
                class="analyze-breakdown-row"
              >
                <span>{{ row.label }} <small class="analyze-note">used {{ row.usedCount }} • {{ formatPercent(row.resolvedRate) }} resolved • {{ formatPercent(row.escalatedRate) }} escalated</small></span>
                <strong>{{ row.unresolvedCount }}</strong>
              </div>
            </div>
            <div v-else class="analyze-note">No business-hours mode outcome data.</div>
          </div>

          <div class="analyze-breakdown-card mb-3">
            <div class="analyze-label">Business-hours behavior outcomes</div>
            <div
              v-if="topBusinessHoursResponseBehaviorOutcomeRows.length"
              class="analyze-breakdown-list"
            >
              <div
                v-for="row in topBusinessHoursResponseBehaviorOutcomeRows"
                :key="row.label"
                class="analyze-breakdown-row"
              >
                <span>{{ row.label }} <small class="analyze-note">used {{ row.usedCount }} • {{ formatPercent(row.resolvedRate) }} resolved • {{ formatPercent(row.escalatedRate) }} escalated</small></span>
                <strong>{{ row.unresolvedCount }}</strong>
              </div>
            </div>
            <div v-else class="analyze-note">No business-hours behavior outcome data.</div>
          </div>

          <div class="analyze-breakdown-card mb-3">
            <div class="analyze-label">Region binding-scope outcomes</div>
            <div
              v-if="topRegionComplianceBindingScopeOutcomeRows.length"
              class="analyze-breakdown-list"
            >
              <div
                v-for="row in topRegionComplianceBindingScopeOutcomeRows"
                :key="row.label"
                class="analyze-breakdown-row"
              >
                <span>{{ row.label }} <small class="analyze-note">used {{ row.usedCount }} • {{ formatPercent(row.resolvedRate) }} resolved • {{ formatPercent(row.escalatedRate) }} escalated</small></span>
                <strong>{{ row.unresolvedCount }}</strong>
              </div>
            </div>
            <div v-else class="analyze-note">No region binding-scope outcome data.</div>
          </div>

          <div class="analyze-breakdown-card mb-3">
            <div class="analyze-label">Active market outcomes</div>
            <div
              v-if="topRegionComplianceActiveCountryCodeOutcomeRows.length"
              class="analyze-breakdown-list"
            >
              <div
                v-for="row in topRegionComplianceActiveCountryCodeOutcomeRows"
                :key="row.label"
                class="analyze-breakdown-row"
              >
                <span>{{ row.label }} <small class="analyze-note">used {{ row.usedCount }} • {{ formatPercent(row.resolvedRate) }} resolved • {{ formatPercent(row.escalatedRate) }} escalated</small></span>
                <strong>{{ row.unresolvedCount }}</strong>
              </div>
            </div>
            <div v-else class="analyze-note">No active-market outcome data.</div>
          </div>

          <div class="analyze-breakdown-card mb-3">
            <div class="analyze-label">Compliance profile outcomes</div>
            <div
              v-if="topRegionComplianceActiveProfileOutcomeRows.length"
              class="analyze-breakdown-list"
            >
              <div
                v-for="row in topRegionComplianceActiveProfileOutcomeRows"
                :key="row.label"
                class="analyze-breakdown-row"
              >
                <span>{{ row.label }} <small class="analyze-note">used {{ row.usedCount }} • {{ formatPercent(row.resolvedRate) }} resolved • {{ formatPercent(row.escalatedRate) }} escalated</small></span>
                <strong>{{ row.unresolvedCount }}</strong>
              </div>
            </div>
            <div v-else class="analyze-note">No compliance-profile outcome data.</div>
          </div>

          <div class="analyze-breakdown-card mb-3">
            <div class="analyze-label">Strategy optimization suggestions</div>
            <div v-if="strategyRecommendationRows.length" class="analyze-breakdown-list">
              <div
                v-for="row in strategyRecommendationRows"
                :key="`strategy-recommendation-${row.strategyGroup}-${row.key}`"
                class="analyze-breakdown-row analyze-breakdown-row--stacked"
              >
                <span>
                  {{ row.strategyGroupTitle }}: {{ row.label }}
                  <small class="analyze-note">
                    {{ row.title }} • used {{ row.usedCount }} • resolved
                    {{ row.resolvedCount }} • escalated {{ row.escalatedCount }} • pending
                    {{ row.unresolvedCount }}
                  </small>
                  <small class="analyze-note">
                    {{ row.evidenceSummary || row.body }}
                  </small>
                  <small class="analyze-note">
                    Change: {{ row.suggestedChange || row.body }}
                  </small>
                  <small class="analyze-note">
                    Next: {{ row.nextStep || 'Retest the same sample after updating this strategy.' }}
                  </small>
                  <small
                    v-if="row.repairBundleHotspotSummary"
                    class="analyze-note"
                  >
                    Hotspot: {{ row.repairBundleHotspotSummary }}
                  </small>
                  <small
                    v-if="row.repairBundleResolvedSummary"
                    class="analyze-note"
                  >
                    Resolved example: {{ row.repairBundleResolvedSummary }}
                  </small>
                  <small
                    v-if="row.repairBundleEscalatedSummary"
                    class="analyze-note"
                  >
                    Escalated example: {{ row.repairBundleEscalatedSummary }}
                  </small>
                  <small
                    v-if="row.repairBundleValidationSummary"
                    class="analyze-note"
                  >
                    Validation: {{ row.repairBundleValidationSummary }}
                  </small>
                  <small class="analyze-note">
                    Bundle: Open Test bundle to compare the hotspot against resolved
                    and escalated examples, then build repair confidence from repeated
                    runs.
                  </small>
                  <small
                    v-if="strategyRecommendationConfidenceForRow(row)"
                    class="analyze-note"
                  >
                    Confidence:
                    {{ strategyRecommendationConfidenceForRow(row).level }}
                    • {{ strategyRecommendationConfidenceForRow(row).runsCount }}
                    recent run{{
                      strategyRecommendationConfidenceForRow(row).runsCount === 1
                        ? ''
                        : 's'
                    }}
                  </small>
                  <small
                    v-if="strategyRecommendationRepairStatusForRow(row)"
                    class="analyze-note"
                  >
                    Latest verdict:
                    {{ strategyRecommendationRepairStatusForRow(row).latestVerdict }}
                    <span v-if="strategyRecommendationRepairStatusForRow(row).deployReady">
                      • ready for deploy review
                    </span>
                  </small>
                </span>
                <div class="analyze-breakdown-actions">
                  <strong>{{ row.severity }}</strong>
                  <button
                    type="button"
                    class="btn o__secondary"
                    @click="openAnalyzeAction(strategyRecommendationAction(row))"
                  >
                    {{ strategyRecommendationAction(row).actionLabel }}
                  </button>
                  <button
                    v-if="strategyRecommendationDeployAction(row)"
                    type="button"
                    class="btn o__secondary"
                    @click="openAnalyzeAction(strategyRecommendationDeployAction(row))"
                  >
                    Open Deploy
                  </button>
                  <button
                    v-if="row.repairBundleResolvedActionConversationId"
                    type="button"
                    class="btn o__secondary"
                    @click="inspectStrategyRecommendationCase(row, 'resolved')"
                  >
                    {{ row.repairBundleResolvedActionTitle || 'Resolved example' }}
                  </button>
                  <button
                    v-if="row.repairBundleEscalatedActionConversationId"
                    type="button"
                    class="btn o__secondary"
                    @click="inspectStrategyRecommendationCase(row, 'escalated')"
                  >
                    {{
                      row.repairBundleEscalatedActionTitle || 'Escalated example'
                    }}
                  </button>
                  <button
                    v-if="strategyRecommendationAction(row).testQuery"
                    type="button"
                    class="btn o__secondary"
                    @click="retestAnalyzeHotspot(strategyRecommendationAction(row))"
                  >
                    {{
                      row.repairBundleTitle
                        ? 'Open Test bundle'
                        : row.repairBundleValidationActionTitle || 'Retest hotspot'
                    }}
                  </button>
                </div>
              </div>
            </div>
            <div v-else class="analyze-note">No strategy optimization suggestions in this window.</div>
          </div>

          <div class="analyze-breakdown-card mb-3">
            <div class="analyze-label">Resolved guidance cards</div>
            <div v-if="resolvedGuidanceCardBreakdown.length" class="analyze-breakdown-list">
              <div v-for="row in resolvedGuidanceCardBreakdown" :key="row.label" class="analyze-breakdown-row">
                <span>{{ row.label }} <small class="analyze-note">used {{ row.usedCount }} • {{ formatPercent(row.resolvedRate) }}</small></span>
                <strong>{{ row.count }}</strong>
              </div>
            </div>
            <div v-else class="analyze-note">No resolved guidance-card outcome data.</div>
          </div>

          <div class="analyze-breakdown-card mb-3">
            <div class="analyze-label">Escalated guidance cards</div>
            <div v-if="escalatedGuidanceCardBreakdown.length" class="analyze-breakdown-list">
              <div v-for="row in escalatedGuidanceCardBreakdown" :key="row.label" class="analyze-breakdown-row">
                <span>{{ row.label }} <small class="analyze-note">used {{ row.usedCount }} • {{ formatPercent(row.escalatedRate) }}</small></span>
                <strong>{{ row.count }}</strong>
              </div>
            </div>
            <div v-else class="analyze-note">No escalated guidance-card outcome data.</div>
          </div>

          <div class="analyze-breakdown-card mb-3">
            <div class="analyze-label">Resolved guidance scenarios</div>
            <div v-if="resolvedGuidanceScenarioBreakdown.length" class="analyze-breakdown-list">
              <div v-for="row in resolvedGuidanceScenarioBreakdown" :key="row.label" class="analyze-breakdown-row">
                <span>{{ row.label }} <small class="analyze-note">used {{ row.usedCount }} • {{ formatPercent(row.resolvedRate) }}</small></span>
                <strong>{{ row.count }}</strong>
              </div>
            </div>
            <div v-else class="analyze-note">No resolved guidance-scenario outcome data.</div>
          </div>

          <div class="analyze-breakdown-card mb-3">
            <div class="analyze-label">Escalated guidance scenarios</div>
            <div v-if="escalatedGuidanceScenarioBreakdown.length" class="analyze-breakdown-list">
              <div v-for="row in escalatedGuidanceScenarioBreakdown" :key="row.label" class="analyze-breakdown-row">
                <span>{{ row.label }} <small class="analyze-note">used {{ row.usedCount }} • {{ formatPercent(row.escalatedRate) }}</small></span>
                <strong>{{ row.count }}</strong>
              </div>
            </div>
            <div v-else class="analyze-note">No escalated guidance-scenario outcome data.</div>
          </div>

          <div class="analyze-breakdown-card">
            <div class="analyze-label">Latest trace spotlight</div>
            <div v-if="latestTrace">
              <div class="analyze-note mb-2">
                {{ latestTracePayload.source_kind || '-' }} / {{ latestTracePayload.reason || '-' }}
              </div>
              <div class="analyze-spotlight-stack">
                <div v-for="section in latestTraceSections" :key="section.title" class="analyze-spotlight-card">
                  <div class="analyze-label">{{ section.title }}</div>
                  <div v-if="section.rows.length" class="analyze-breakdown-list">
                    <div v-for="row in section.rows" :key="section.title + row[0]" class="analyze-breakdown-row">
                      <span>{{ row[0] }}</span>
                      <strong class="analyze-breakdown-value">{{ Array.isArray(row[1]) ? row[1].join(', ') : row[1] }}</strong>
                    </div>
                  </div>
                  <div v-else class="analyze-note">No {{ section.title.toLowerCase() }} refs.</div>
                </div>
              </div>
            </div>
            <div v-else class="analyze-note">No latest trace loaded.</div>
          </div>

          <div class="analyze-breakdown-card mt-3">
            <div class="analyze-label">Retrieval strategy rollups</div>
            <div class="analyze-note mb-3">
              Server-side rollups for the current Analyze query. These retrieval filters narrow the backend aggregation slice without resetting the main diagnostics view.
            </div>
            <div class="analyze-filters mb-3">
              <label class="analyze-field">
                <span class="analyze-label">Top source</span>
                <select v-model="retrievalRollupFilters.top_priority_source" class="analyze-input">
                  <option value="">All</option>
                  <option v-for="option in RETRIEVAL_SOURCE_FILTER_OPTIONS" :key="`retrieval-top-${option.value}`" :value="option.value">
                    {{ option.label }}
                  </option>
                </select>
              </label>
              <label class="analyze-field">
                <span class="analyze-label">Search balance</span>
                <select v-model="retrievalRollupFilters.search_balance" class="analyze-input">
                  <option value="">All</option>
                  <option v-for="option in RETRIEVAL_SEARCH_BALANCE_FILTER_OPTIONS" :key="`retrieval-balance-${option.value}`" :value="option.value">
                    {{ option.label }}
                  </option>
                </select>
              </label>
              <label class="analyze-field">
                <span class="analyze-label">Citation mode</span>
                <select v-model="retrievalRollupFilters.citation_mode" class="analyze-input">
                  <option value="">All</option>
                  <option v-for="option in RETRIEVAL_CITATION_FILTER_OPTIONS" :key="`retrieval-citation-${option.value}`" :value="option.value">
                    {{ option.label }}
                  </option>
                </select>
              </label>
              <label class="analyze-field">
                <span class="analyze-label">Active segment binding</span>
                <select v-model="retrievalRollupFilters.active_binding_scope" class="analyze-input">
                  <option value="">All</option>
                  <option v-for="option in RETRIEVAL_ACTIVE_SCOPE_FILTER_OPTIONS" :key="`retrieval-segment-${option.value}`" :value="option.value">
                    {{ option.label }}
                  </option>
                </select>
              </label>
              <label class="analyze-field">
                <span class="analyze-label">No-evidence fallback</span>
                <select v-model="retrievalRollupFilters.no_evidence_behavior" class="analyze-input">
                  <option value="">All</option>
                  <option v-for="option in RETRIEVAL_NO_EVIDENCE_FILTER_OPTIONS" :key="`retrieval-fallback-${option.value}`" :value="option.value">
                    {{ option.label }}
                  </option>
                </select>
              </label>
            </div>
            <div class="grid gap-3 md:grid-cols-2 xl:grid-cols-4 mb-3">
              <div v-for="card in retrievalRollupCards" :key="card.label" class="analyze-spotlight-card">
                <div class="analyze-label">{{ card.label }}</div>
                <div class="mt-2 text-lg font-semibold text-default">{{ card.value }}</div>
                <div class="analyze-note mt-1">{{ card.note }}</div>
              </div>
            </div>
            <div class="grid gap-3 lg:grid-cols-2">
              <div class="analyze-spotlight-card">
                <div class="analyze-label">Top retrieval sources</div>
                <div v-if="retrievalTopSourceBreakdown.length" class="analyze-breakdown-list">
                  <div v-for="row in retrievalTopSourceBreakdown" :key="`top-source-${row.label}`" class="analyze-breakdown-row">
                    <span>{{ row.label }}</span>
                    <strong>{{ row.count }}</strong>
                  </div>
                </div>
                <div v-else class="analyze-note">No top-priority retrieval data in the current server slice.</div>
              </div>
              <div class="analyze-spotlight-card">
                <div class="analyze-label">Winning source mix</div>
                <div v-if="retrievalWinningSourceBreakdown.length" class="analyze-breakdown-list">
                  <div v-for="row in retrievalWinningSourceBreakdown" :key="`winning-source-${row.label}`" class="analyze-breakdown-row">
                    <span>{{ row.label }}</span>
                    <strong>{{ row.count }}</strong>
                  </div>
                </div>
                <div v-else class="analyze-note">No cross-source winner data in the current server slice.</div>
              </div>
              <div class="analyze-spotlight-card">
                <div class="analyze-label">Search balance mix</div>
                <div v-if="retrievalSearchBalanceBreakdown.length" class="analyze-breakdown-list">
                  <div v-for="row in retrievalSearchBalanceBreakdown" :key="`search-balance-${row.label}`" class="analyze-breakdown-row">
                    <span>{{ row.label }}</span>
                    <strong>{{ row.count }}</strong>
                  </div>
                </div>
                <div v-else class="analyze-note">No search-balance data in the current server slice.</div>
              </div>
              <div class="analyze-spotlight-card">
                <div class="analyze-label">Citation and fallback mix</div>
                <div v-if="retrievalCitationModeBreakdown.length || retrievalFallbackBreakdown.length" class="analyze-breakdown-list">
                  <div v-for="row in retrievalCitationModeBreakdown" :key="`citation-mode-${row.label}`" class="analyze-breakdown-row">
                    <span>citation: {{ row.label }}</span>
                    <strong>{{ row.count }}</strong>
                  </div>
                  <div v-for="row in retrievalFallbackBreakdown" :key="`fallback-mode-${row.label}`" class="analyze-breakdown-row">
                    <span>fallback: {{ row.label }}</span>
                    <strong>{{ row.count }}</strong>
                  </div>
                </div>
                <div v-else class="analyze-note">No citation or fallback data in the current server slice.</div>
              </div>
              <div class="analyze-spotlight-card">
                <div class="analyze-label">Segment binding activity</div>
                <div v-if="retrievalSegmentBindingBreakdown.length" class="analyze-breakdown-list">
                  <div v-for="row in retrievalSegmentBindingBreakdown" :key="`segment-binding-${row.label}`" class="analyze-breakdown-row">
                    <span>{{ row.label }}</span>
                    <strong>{{ row.count }}</strong>
                  </div>
                </div>
                <div v-else class="analyze-note">No retrieval segment binding is active in the current server slice.</div>
              </div>
              <div class="analyze-spotlight-card">
                <div class="analyze-label">Priority-source outcomes</div>
                <div v-if="retrievalTopPriorityOutcomeRows.length" class="analyze-breakdown-list">
                  <div
                    v-for="row in retrievalTopPriorityOutcomeRows.slice(0, 4)"
                    :key="`retrieval-priority-outcome-${row.key}`"
                    class="rounded-large border border-neutral-border bg-white px-3 py-2"
                  >
                    <div class="analyze-breakdown-row">
                      <span>{{ row.label }}</span>
                      <strong>{{ row.resolvedCount }}/{{ row.usedCount }}</strong>
                    </div>
                    <div class="mt-2 flex flex-wrap gap-2">
                      <button
                        type="button"
                        class="analyze-chip-button"
                        @click="openAnalyzeAction(retrievalRollupAction(row, 'retrieval_top_priority'))"
                      >
                        Open Content
                      </button>
                      <button
                        type="button"
                        class="analyze-chip-button"
                        @click="retestAnalyzeHotspot(retrievalRollupAction(row, 'retrieval_top_priority'))"
                      >
                        Retest
                      </button>
                    </div>
                  </div>
                </div>
                <div v-else class="analyze-note">No priority-source outcome rollups yet.</div>
              </div>
              <div class="analyze-spotlight-card">
                <div class="analyze-label">Winning-source outcomes</div>
                <div v-if="retrievalWinningSourceOutcomeRows.length" class="analyze-breakdown-list">
                  <div
                    v-for="row in retrievalWinningSourceOutcomeRows.slice(0, 4)"
                    :key="`retrieval-winning-outcome-${row.key}`"
                    class="rounded-large border border-neutral-border bg-white px-3 py-2"
                  >
                    <div class="analyze-breakdown-row">
                      <span>{{ row.label }}</span>
                      <strong>{{ row.resolvedCount }}/{{ row.usedCount }}</strong>
                    </div>
                    <div class="mt-2 flex flex-wrap gap-2">
                      <button
                        type="button"
                        class="analyze-chip-button"
                        @click="openAnalyzeAction(retrievalRollupAction(row, 'retrieval_winning_source'))"
                      >
                        Open Content
                      </button>
                      <button
                        type="button"
                        class="analyze-chip-button"
                        @click="retestAnalyzeHotspot(retrievalRollupAction(row, 'retrieval_winning_source'))"
                      >
                        Retest
                      </button>
                    </div>
                  </div>
                </div>
                <div v-else class="analyze-note">No winning-source outcome rollups yet.</div>
              </div>
              <div class="analyze-spotlight-card">
                <div class="analyze-label">Retrieval recommendations</div>
                <div v-if="retrievalStrategyRepairRows.length" class="analyze-breakdown-list">
                  <div
                    v-for="row in retrievalStrategyRepairRows"
                    :key="`retrieval-reco-${row.recommendationCode}-${row.key}`"
                    class="rounded-large border border-neutral-border bg-white px-3 py-2"
                  >
                    <div class="analyze-breakdown-row">
                      <span>{{ row.strategyGroupTitle || row.label }}</span>
                      <strong>{{ row.repairLevel || row.severity }}</strong>
                    </div>
                    <div class="mt-1 text-xs text-muted">
                      {{ row.repairBody || row.suggestedChange || row.body }}
                    </div>
                    <div class="mt-1 text-[11px] text-muted">
                      Latest verdict: {{ row.latestVerdict || 'pending' }} • Validation runs:
                      {{ row.runsCount || 0 }}
                    </div>
                    <div class="mt-2 flex flex-wrap gap-2">
                      <button
                        type="button"
                        class="analyze-chip-button"
                        @click="openAnalyzeAction(row.primaryAction || retrievalRollupAction(row, row.strategyGroup || 'retrieval_recommendation'))"
                      >
                        {{ row.primaryAction?.actionLabel || 'Open Content' }}
                      </button>
                      <button
                        type="button"
                        class="analyze-chip-button"
                        @click="retestAnalyzeHotspot(row.primaryAction || retrievalRollupAction(row, row.strategyGroup || 'retrieval_recommendation'))"
                      >
                        Retest
                      </button>
                      <button
                        v-if="row.deployAction"
                        type="button"
                        class="analyze-chip-button"
                        @click="openAnalyzeAction(row.deployAction)"
                      >
                        {{ row.deployAction.actionLabel || 'Open Deploy' }}
                      </button>
                    </div>
                  </div>
                </div>
                <div v-else class="analyze-note">No retrieval-specific optimization recommendations in this window.</div>
              </div>
              <div class="analyze-spotlight-card">
                <div class="analyze-label">Retrieval validation loop</div>
                <div v-if="retrievalStrategyRepairRows.length" class="analyze-breakdown-list">
                  <div class="analyze-breakdown-row">
                    <span>Blocked now</span>
                    <strong>{{ retrievalRepairSummary.blocked }}</strong>
                  </div>
                  <div class="analyze-breakdown-row">
                    <span>Observe next</span>
                    <strong>{{ retrievalRepairSummary.observe }}</strong>
                  </div>
                  <div class="analyze-breakdown-row">
                    <span>Ready for release review</span>
                    <strong>{{ retrievalRepairSummary.ready }}</strong>
                  </div>
                  <div class="analyze-breakdown-row">
                    <span>Still unproven</span>
                    <strong>{{ retrievalRepairSummary.unproven }}</strong>
                  </div>
                  <div class="mt-2 rounded-large border border-neutral-border bg-white px-3 py-2 text-xs text-muted">
                    {{
                      retrievalStrategyRepairRows[0]?.repairNextStep ||
                      retrievalStrategyRepairRows[0]?.repairBody ||
                      'Keep validating the top retrieval repair before release review.'
                    }}
                  </div>
                  <div
                    v-if="
                      retrievalSnapshotSummary?.latest_snapshot_at ||
                      retrievalSnapshotSummary?.total_clusters !== undefined
                    "
                    class="mt-2 rounded-large border border-neutral-border bg-white px-3 py-2 text-xs text-muted"
                  >
                    Snapshot:
                    {{
                      retrievalSnapshotSummary.latest_snapshot_at
                        ? `captured ${formatDateTime(retrievalSnapshotSummary.latest_snapshot_at)}`
                        : 'captured recently'
                    }}
                    <span v-if="retrievalSnapshotSummary.total_clusters !== undefined">
                      • clusters {{ retrievalSnapshotSummary.total_clusters }}
                    </span>
                    <span v-if="retrievalSnapshotSummary.write_now_count !== undefined">
                      • write now {{ retrievalSnapshotSummary.write_now_count }}
                    </span>
                    <span v-if="retrievalSnapshotSummary.active_now_count !== undefined">
                      • active now {{ retrievalSnapshotSummary.active_now_count }}
                    </span>
                  </div>
                  <div class="mt-2 flex flex-wrap gap-2">
                    <button
                      type="button"
                      class="analyze-chip-button"
                      @click="retestAnalyzeHotspot(retrievalStrategyRepairRows[0]?.testAction || retrievalRollupAction(retrievalStrategyRepairRows[0], 'retrieval_validation_loop'))"
                    >
                      Validate top repair
                    </button>
                    <button
                      type="button"
                      class="analyze-chip-button"
                      @click="openAnalyzeAction(retrievalStrategyRepairRows[0]?.primaryAction || retrievalRollupAction(retrievalStrategyRepairRows[0], 'retrieval_validation_loop'))"
                    >
                      Open owner
                    </button>
                    <button
                      type="button"
                      class="analyze-chip-button"
                      :disabled="isCapturingRetrievalSnapshot"
                      @click="captureRetrievalSnapshot"
                    >
                      {{ isCapturingRetrievalSnapshot ? 'Capturing snapshot...' : 'Capture snapshot' }}
                    </button>
                    <button
                      v-if="retrievalStrategyRepairRows[0]?.deployAction"
                      type="button"
                      class="analyze-chip-button"
                      @click="openAnalyzeAction(retrievalStrategyRepairRows[0].deployAction)"
                    >
                      Release review
                    </button>
                    <button
                      type="button"
                      class="analyze-chip-button"
                      @click="
                        openAnalyzeAction({
                          action: 'content',
                          actionLabel: 'Open Content',
                          contentFocus: 'recommendations',
                          contentQuery: topFailureSampleQuery,
                          contentReason: 'retrieval_snapshot_queue',
                          testQuery: topFailureSampleQuery,
                          testInboxId: topFailureSampleInboxId,
                          testReason: 'retrieval_snapshot_queue',
                        })
                      "
                    >
                      Open queue
                    </button>
                  </div>
                </div>
                <div v-else class="analyze-note">No retrieval repair loop is active in this window yet.</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="showAdvancedDiagnostics && filters.conversation_id" class="analyze-card">
        <div class="analyze-section-header">
          <div>
            <div class="analyze-card-title">Conversation routing timeline</div>
            <div class="analyze-note">
              Conversation {{ filters.conversation_id }} • {{ formatOverrideOutcomeLabel(selectedOverrideOutcome) }}
            </div>
          </div>
        </div>
        <div
          v-if="conversationOverrideRecentCases.length"
          class="analyze-note mt-2"
        >
          Matching override cases:
          {{ conversationOverrideRecentCases.map(formatOverrideCaseSummary).join(' • ') }}
        </div>
        <div v-if="isLoadingConversationTimeline" class="analyze-empty">
          Loading conversation timeline...
        </div>
        <div
          v-else-if="!conversationTraceTimelineRows.length"
          class="analyze-empty"
        >
          No response traces found for this conversation in the current window.
        </div>
        <div v-else class="analyze-trace-list mt-3">
          <div
            v-for="event in conversationTraceTimelineRows"
            :key="`conversation-trace-${event.id}`"
            class="analyze-trace-item"
            :class="{ 'analyze-trace-item--highlight': isConversationTimelineHighlighted(event) }"
          >
            <div class="analyze-trace-main">
              <div class="analyze-trace-left">
                <div class="analyze-trace-top">
                  <span class="analyze-pill">{{ event.decision_type }}</span>
                  <span class="analyze-muted">{{ formatDateTime(event.created_at) }}</span>
                  <span v-if="event.inbox_id" class="analyze-muted">
                    inbox {{ resolveInboxName(event.inbox_id) }}
                  </span>
                </div>
                <div class="analyze-trace-preview">
                  {{ event.payload?.response_preview || event.payload?.reason || 'No preview available' }}
                </div>
                <div
                  v-if="conversationOverrideRecentCaseByDecisionEventId[event.id]"
                  class="analyze-note mt-2"
                >
                  {{
                    formatOverrideOutcomeDetail(
                      conversationOverrideRecentCaseByDecisionEventId[event.id]
                    )
                  }}
                </div>
                <div class="analyze-tag-list">
                  <span v-for="tag in traceTags(event)" :key="tag" class="analyze-tag">
                    {{ tag }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="analyze-two-col">
        <div v-if="showAdvancedDiagnostics" ref="traceSectionRef" class="analyze-card">
          <div class="analyze-section-header">
            <div>
              <div class="analyze-card-title">Recent reply diagnostics</div>
              <div class="analyze-note">
                Showing {{ Math.min((traceMeta.offset || 0) + (traceItems.length || 0), traceMeta.total || 0) }}
                of {{ traceMeta.total || 0 }}
              </div>
            </div>
            <div class="analyze-pager">
              <button type="button" class="btn o__secondary" :disabled="isLoadingTraces || (traceMeta.offset || 0) <= 0" @click="prevTracePage">
                Prev
              </button>
              <button type="button" class="btn o__secondary" :disabled="isLoadingTraces || (traceMeta.offset || 0) + (traceMeta.limit || 50) >= (traceMeta.total || 0)" @click="nextTracePage">
                Next
              </button>
            </div>
          </div>

          <div v-if="isLoadingTraces" class="analyze-empty">Loading traces...</div>
          <div v-else-if="!traceItems.length" class="analyze-empty">No traces found for the current filters.</div>
          <div v-else class="analyze-trace-list">
            <div v-for="event in traceItems" :key="event.id" class="analyze-trace-item">
              <button type="button" class="analyze-trace-main" @click="toggleTraceExpanded(event.id)">
                <div class="analyze-trace-left">
                  <div class="analyze-trace-top">
                    <span class="analyze-pill">{{ event.decision_type }}</span>
                    <span class="analyze-muted">{{ event.source }}</span>
                    <span v-if="event.inbox_id" class="analyze-muted">inbox {{ resolveInboxName(event.inbox_id) }}</span>
                  </div>
                  <div class="analyze-trace-preview">
                    {{ event.payload?.response_preview || event.payload?.reason || 'No preview available' }}
                  </div>
                  <div class="analyze-tag-list">
                    <span v-for="tag in traceTags(event)" :key="tag" class="analyze-tag">{{ tag }}</span>
                  </div>
                </div>
                <div class="analyze-trace-right">
                  <span class="analyze-muted">{{ event.created_at }}</span>
                  <span
                    class="i-lucide-chevron-down size-4"
                    :class="[expandedTraceIds.has(event.id) ? 'rot' : '']"
                  />
                </div>
              </button>
              <div v-if="expandedTraceIds.has(event.id)">
                <div
                  v-if="traceAttributeOverride(event)"
                  class="analyze-override-trace-panel"
                >
                  <div class="analyze-override-trace-top">
                    <div>
                      <div class="analyze-label">
                        {{ MATCHED_OVERRIDE_LABEL }}
                      </div>
                      <div class="analyze-note">
                        {{
                          formatOverrideTraceHeadline(
                            traceAttributeOverride(event)
                          )
                        }}
                      </div>
                    </div>
                    <span class="analyze-pill analyze-pill--override">
                      {{
                        formatOverrideTraceConditionCountLabel(
                          traceAttributeOverride(event)
                        )
                      }}
                    </span>
                  </div>
                  <div
                    v-if="traceAttributeOverride(event)?.conditionSummary"
                    class="analyze-note mt-2"
                  >
                    {{ traceAttributeOverride(event)?.conditionSummary }}
                  </div>
                  <div
                    v-if="traceAttributeOverride(event)?.conditionGroups.length"
                    class="analyze-override-group-list"
                  >
                    <div
                      v-for="
                        group in traceAttributeOverride(event)?.conditionGroups
                      "
                      :key="`${event.id}-${group.key}`"
                      class="analyze-override-group"
                    >
                      <div class="analyze-override-group-top">
                        <span class="analyze-tag analyze-tag--override-group">
                          {{ formatOverrideTraceGroupLabel(group.key) }}
                        </span>
                        <span class="analyze-note">
                          {{ formatOverrideTraceGroupDetail(group) }}
                        </span>
                      </div>
                      <div class="analyze-override-condition-list">
                        <div
                          v-for="condition in group.conditions"
                          :key="`${event.id}-${group.key}-${condition.attributeKey}-${condition.summary}`"
                          class="analyze-override-condition"
                          :class="[
                            `analyze-override-condition--${overrideTraceConditionTone(group.key, condition.matched)}`,
                          ]"
                        >
                          <div class="analyze-override-condition-top">
                            <strong>
                              {{
                                condition.summary ||
                                condition.attributeKey ||
                                condition.matchType
                              }}
                            </strong>
                            <span class="analyze-note">
                              {{
                                formatOverrideTraceConditionStatus(
                                  group.key,
                                  condition.matched
                                )
                              }}
                            </span>
                          </div>
                          <div
                            v-if="formatOverrideTraceConditionMeta(condition)"
                            class="analyze-note"
                          >
                            {{ formatOverrideTraceConditionMeta(condition) }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <pre class="analyze-payload">
                  {{ JSON.stringify(event.payload || {}, null, 2) }}
                </pre>
              </div>
            </div>
          </div>
        </div>

        <div class="analyze-card">
          <div class="analyze-section-header">
            <div>
              <div class="analyze-card-title">Missed replies to review</div>
              <div class="analyze-note">
                Review replies that missed the mark, then decide whether to fix,
                ignore, or turn them into reusable content.
              </div>
            </div>
          </div>

          <div v-if="isLoadingFailures" class="analyze-empty">Loading failures...</div>
          <div v-else-if="!failureItems.length" class="analyze-empty">
            No missed replies were found for the current filters.
          </div>
          <div v-else class="analyze-failure-list">
            <div v-for="tag in failureItems" :key="tag.id" class="analyze-failure-item">
              <div class="analyze-failure-top">
                <span class="analyze-pill">{{ formatFailureStatusLabel(tag.status) }}</span>
                <span v-for="badge in failureBadges(tag)" :key="`${tag.id}-${badge}`" class="analyze-tag">
                  {{ badge }}
                </span>
              </div>
              <div class="analyze-failure-copy">
                <div class="analyze-failure-line">
                  <span class="analyze-label">Customer asked</span>
                  <div>{{ tag.user_text || '-' }}</div>
                </div>
                <div class="analyze-failure-line">
                  <span class="analyze-label">AI replied</span>
                  <div>{{ tag.ai_text || '-' }}</div>
                </div>
                <div class="analyze-failure-line">
                  <span class="analyze-label">Better reply</span>
                  <div>{{ tag.desired_text || 'Not added yet.' }}</div>
                </div>
                <div class="analyze-failure-line">
                  <span class="analyze-label">Team note</span>
                  <div>{{ tag.notes || 'No internal note yet.' }}</div>
                </div>
              </div>
              <div class="analyze-failure-actions">
                <button
                  v-if="tag.user_text || tag.desired_text"
                  type="button"
                  class="btn o__secondary"
                  @click="retestFailureInWorkspace(tag)"
                >
                  Retest in Test
                </button>
                <button type="button" class="btn o__secondary" @click="updateFailureStatus(tag, 'reviewed')">
                  Mark as reviewed
                </button>
                <button type="button" class="btn o__secondary" @click="updateFailureStatus(tag, 'ignored')">
                  Ignore
                </button>
                <button
                  v-if="tag.source === 'content_gap_auto' && !tag.created_article_id"
                  type="button"
                  class="btn o__secondary"
                  @click="createArticleDraft(tag)"
                >
                  Create help article draft
                </button>
                <button type="button" class="btn o__secondary" @click="convertToTemplate(tag)">
                  Turn into saved reply
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.analyze-shell {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: 0;
}
.analyze-header,
.analyze-section-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
}
.analyze-header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}
.analyze-summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 12px;
}
.analyze-card,
.analyze-stat-card,
.analyze-trace-item,
.analyze-failure-item {
  border: 1px solid var(--fin-neutral-border);
  background: #fff;
  border-radius: 14px;
}
.analyze-card {
  padding: 16px;
}
.analyze-card--overview {
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
}
.analyze-card--collapsed {
  border-style: dashed;
}
.analyze-collapsed-card {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
}
.analyze-context-rail {
  position: sticky;
  top: 0;
  z-index: 5;
  border-color: #bfdbfe;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.97) 0%,
    rgba(248, 251, 255, 0.97) 100%
  );
  box-shadow: 0 14px 28px rgba(15, 23, 42, 0.08);
  backdrop-filter: blur(10px);
}
.analyze-context-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
}
.analyze-context-copy {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}
.analyze-context-actions,
.analyze-context-chip-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.analyze-context-chip-list {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 10px;
  margin-top: 12px;
}
.analyze-preset-chip-list {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 10px;
  margin-top: 12px;
}
.analyze-preset-chip {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  text-align: left;
  border: 1px solid #dbeafe;
  border-radius: 12px;
  padding: 12px;
  background: #fff;
  color: #111827;
}
.analyze-preset-chip.is-active {
  border-color: #2563eb;
  background: linear-gradient(180deg, #eff6ff 0%, #ffffff 100%);
  box-shadow: inset 0 0 0 1px rgba(37, 99, 235, 0.12);
}
.analyze-preset-chip:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.analyze-preset-chip-copy {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}
.analyze-jump-chip-list {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 10px;
  margin-top: 12px;
}
.analyze-jump-chip {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  text-align: left;
  border: 1px solid #dbeafe;
  border-radius: 12px;
  padding: 12px;
  background: #fff;
  color: #111827;
  transition:
    border-color 0.15s ease,
    transform 0.15s ease,
    box-shadow 0.15s ease;
}
.analyze-jump-chip:hover {
  border-color: #93c5fd;
  box-shadow: 0 8px 18px rgba(59, 130, 246, 0.12);
  transform: translateY(-1px);
}
.analyze-jump-chip.is-recommended {
  border-color: #60a5fa;
  background: linear-gradient(180deg, #eff6ff 0%, #ffffff 100%);
  box-shadow: 0 10px 24px rgba(59, 130, 246, 0.14);
}
.analyze-jump-chip.is-active {
  border-color: #2563eb;
  background: linear-gradient(180deg, #dbeafe 0%, #eff6ff 100%);
  box-shadow: inset 0 0 0 1px rgba(37, 99, 235, 0.12);
}
.analyze-jump-chip-copy {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}
.analyze-jump-chip-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}
.analyze-jump-chip-badges {
  display: inline-flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 6px;
}
.analyze-jump-chip-badge {
  display: inline-flex;
  align-items: center;
  border-radius: 9999px;
  padding: 3px 7px;
  background: #dbeafe;
  color: #1d4ed8;
  font-size: 10px;
  line-height: 13px;
  font-weight: 700;
  white-space: nowrap;
}
.analyze-jump-chip-badge--muted {
  background: #f3f4f6;
  color: #4b5563;
}
.analyze-jump-chip-action {
  white-space: nowrap;
  color: #2563eb;
  font-size: 11px;
  line-height: 15px;
  font-weight: 700;
}
.analyze-context-chip {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  padding: 12px;
  border: 1px solid #dbeafe;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.9);
}
.analyze-context-chip.tone-info {
  border-color: #bfdbfe;
  background: #eff6ff;
}
.analyze-context-chip.tone-warning {
  border-color: #fde68a;
  background: #fffbeb;
}
.analyze-context-chip.tone-muted {
  border-color: #e5e7eb;
  background: #f8fafc;
}
.analyze-context-chip-copy {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}
.analyze-stat-card, .analyze-failure-item { padding: 12px; }
.analyze-stat-card { min-height: 96px; }
.analyze-label { font-size: 11px; line-height: 15px; font-weight: 600; color: #6b7280; display: block; margin-bottom: 4px; }
.analyze-value { font-size: 18px; line-height: 24px; font-weight: 700; color: #111827; }
.analyze-note, .analyze-muted { font-size: 12px; line-height: 17px; color: #6b7280; }
.analyze-filters { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 10px; }
.analyze-filters--overview { grid-template-columns: repeat(3, minmax(0, 1fr)); margin-top: 14px; }
.analyze-two-col { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; }
.analyze-field { display: flex; flex-direction: column; gap: 6px; }
.analyze-input { width: 100%; border: 1px solid #d1d5db; border-radius: 10px; padding: 9px 11px; font-size: 13px; line-height: 18px; background: #fff; color: #111827; }
.analyze-filter-actions, .analyze-pager, .analyze-failure-actions { display: flex; align-items: flex-end; gap: 8px; }
.analyze-alert-list, .analyze-trend-stack, .analyze-action-list { display: flex; flex-direction: column; gap: 10px; margin-top: 12px; }
.analyze-alert { border: 1px solid #e5e7eb; border-radius: 12px; padding: 12px; background: #fff; }
.analyze-alert.tone-danger { background: #fef2f2; border-color: #fecaca; }
.analyze-alert.tone-warning { background: #fffbeb; border-color: #fde68a; }
.analyze-alert.tone-info { background: #eff6ff; border-color: #bfdbfe; }
.analyze-alert-title { font-size: 13px; line-height: 18px; font-weight: 700; color: #111827; margin-bottom: 4px; }
.analyze-trend-card { border: 1px solid #e5e7eb; border-radius: 12px; padding: 12px; background: #fff; }
.analyze-series-list { display: flex; flex-direction: column; gap: 8px; margin-top: 10px; }
.analyze-series-row { display: grid; grid-template-columns: 82px minmax(0, 1fr) 36px; gap: 8px; align-items: center; font-size: 12px; line-height: 17px; color: #111827; }
.analyze-series-label { color: #6b7280; font-size: 11px; }
.analyze-series-bar-track { width: 100%; height: 10px; border-radius: 9999px; background: #e5e7eb; overflow: hidden; }
.analyze-series-bar { height: 100%; border-radius: 9999px; min-width: 6px; }
.analyze-series-bar.is-trace { background: #60a5fa; }
.analyze-series-bar.is-failure { background: #f59e0b; }
.analyze-series-bar.is-warning { background: #f97316; }
.analyze-series-bar.is-neutral { background: #6b7280; }
.analyze-empty { border: 1px dashed #d1d5db; border-radius: 12px; padding: 16px; color: #6b7280; font-size: 13px; text-align: center; margin-top: 12px; }
.analyze-trace-list, .analyze-failure-list { display: flex; flex-direction: column; gap: 10px; margin-top: 12px; }
.analyze-breakdown-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; margin-top: 12px; }
.analyze-breakdown-card, .analyze-spotlight-card { border: 1px solid #e5e7eb; border-radius: 12px; padding: 12px; background: #fff; }
.analyze-breakdown-list, .analyze-spotlight-stack { display: flex; flex-direction: column; gap: 8px; }
.analyze-breakdown-row { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; color: #111827; font-size: 12px; line-height: 17px; }
.analyze-breakdown-actions { display: flex; align-items: center; gap: 8px; }
.analyze-breakdown-value { word-break: break-word; text-align: right; }
.mb-3 { margin-bottom: 12px; }
.analyze-trace-item { overflow: hidden; }
.analyze-trace-item--highlight { border-color: #f59e0b; background: #fffbeb; }
.analyze-trace-main { width: 100%; display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; padding: 12px; background: transparent; border: 0; text-align: left; cursor: pointer; }
.analyze-trace-left { flex: 1; min-width: 0; }
.analyze-trace-right { display: flex; align-items: center; gap: 8px; }
.analyze-trace-top, .analyze-tag-list, .analyze-failure-top { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }
.analyze-trace-preview { margin-top: 8px; color: #111827; font-size: 13px; line-height: 18px; word-break: break-word; }
.analyze-pill, .analyze-tag { display: inline-flex; align-items: center; border-radius: 9999px; padding: 4px 8px; font-size: 10px; line-height: 13px; font-weight: 700; }
.analyze-pill { background: #eff6ff; color: #1d4ed8; }
.analyze-pill--override {
  background: #ecfdf5;
  color: #047857;
}
.analyze-tag { background: #f3f4f6; color: #374151; }
.analyze-tag--override-group {
  background: #e0e7ff;
  color: #4338ca;
}
.analyze-override-trace-panel {
  padding: 12px;
  border-top: 1px solid #e5e7eb;
  background: #f8fafc;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.analyze-override-trace-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}
.analyze-override-group-list,
.analyze-override-condition-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.analyze-override-group {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 10px;
  background: #fff;
}
.analyze-override-group-top,
.analyze-override-condition-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.analyze-override-condition {
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  padding: 8px 10px;
  background: #fff;
}
.analyze-override-condition--matched {
  border-color: #86efac;
  background: #f0fdf4;
}
.analyze-override-condition--guard {
  border-color: #93c5fd;
  background: #eff6ff;
}
.analyze-override-condition--blocked {
  border-color: #fca5a5;
  background: #fef2f2;
}
.analyze-override-condition--inactive {
  border-color: #e5e7eb;
  background: #f9fafb;
}
.analyze-payload { margin: 0; padding: 12px; border-top: 1px solid #e5e7eb; background: #f8fafc; color: #111827; font-size: 11px; line-height: 17px; white-space: pre-wrap; overflow-x: auto; max-height: 360px; overflow-y: auto; }
.analyze-failure-copy { display: flex; flex-direction: column; gap: 10px; margin-top: 10px; color: #111827; font-size: 13px; line-height: 18px; }
.analyze-failure-line { display: flex; flex-direction: column; gap: 4px; }
@media (max-width: 1100px) {
  .analyze-header,
  .analyze-section-header,
  .analyze-header-actions,
  .analyze-context-top,
  .analyze-context-chip,
  .analyze-jump-chip,
  .analyze-collapsed-card,
  .analyze-jump-chip-top {
    flex-direction: column;
    align-items: stretch;
  }

  .analyze-two-col,
  .analyze-filters,
  .analyze-filters--overview,
  .analyze-breakdown-grid,
  .analyze-preset-chip-list,
  .analyze-jump-chip-list,
  .analyze-context-chip-list {
    grid-template-columns: 1fr;
  }

  .analyze-filter-actions {
    align-items: stretch;
  }
}
</style>
